/**
 * Stage 5F — Diamond Detail primary experience.
 *
 * Reads ?id= from location.search, normalizes via IvanovAPI.normalizeDiamondId,
 * fetches IvanovAPI.getDiamond(), and drives LOADING / SUCCESS / UNAVAILABLE /
 * NOT FOUND / ERROR. Renders the Media Explorer thumbnail gallery and
 * decision panel.
 *
 * Gallery items are concrete available views, not a generic Diamond abstraction:
 * 360 (when usable), Photo (when image exists), Size (when real mm exist),
 * Side Profile and Top View as independent technical media (Stage 5D),
 * Certificate last when a valid grading-report URL exists. Certificate
 * opens the shared grading-report modal and does not replace main media.
 * Visible 360° / Photo text navigation is not shown.
 * applyDiamondSubview remains the programmatic 360 / Photo switch. It reuses
 * DiamondPreparedTurntableViewer + DiamondFrameEngine + DiamondFramePresenter
 * and does not mount the Viewer custom element or Viewer chrome.
 *
 * After the upper media + decision workspace, SUCCESS / UNAVAILABLE render
 * Understand Your Diamond. CARAT, CUT, COLOR, and CLARITY are populated
 * educational chapters.
 * CARAT: selected-diamond actual carat + API L×W at the inventory point, then
 * Search Carat AEC typical dimensions when the slider moves away.
 * CUT: Cut AEC Side Profile light-performance visuals with a discrete
 * Fair–Ideal slider. Missing laboratory Cut stays educational — never invented.
 * COLOR: Color AEC Side Profile appearance visuals with a discrete D–K slider
 * and a synchronized D–K comparison strip. On mobile the comparison rail
 * auto-follows the shared selected grade without fighting manual swipe.
 * Unsupported/FANCY color stays educational — never invented.
 * CLARITY: Clarity AEC Side Profile + 10× magnifier with a discrete SI2–FL
 * slider and a synchronized 10× comparison strip. Unsupported clarity stays
 * educational — never invented.
 * COMPARE-5A mounts Compare Similar Diamonds after Clarity. The Compare
 * request is independent of first paint and never blocks media, price, CTA,
 * Certificate, or 4C.
 *
 * 360 interaction matches the approved Viewer lifecycle (source of truth:
 * assets/diamond-viewer.js). Autoplay starts on activation after the
 * existing buffer gate. pointerdown calls beginDrag() so autoplay never
 * fights the pointer. A click/tap (movement at or below
 * CLICK_DRAG_THRESHOLD_PX) toggles autoplay. A drag leaves autoplay
 * stopped; pointerup after a drag does not resume. Leaving 360 tears
 * the host down. Returning to 360 is a new activation and starts
 * autoplay again. There is no Detail-only resume timer and no
 * same-thumbnail Play shortcut — the 360 stage click is the restart,
 * the same way Viewer uses a stage click.
 */
(function (window) {
  "use strict";

  var PAGE_PATH = "/pages/diamond-details";
  var SEARCH_PATH = "/pages/diamond-search";
  // One-shot same-tab signal so restored Diamond Search can close the
  // transient Quick Viewer. Armed when Detail was entered from canonical
  // Search, including Browser Back. Path and query only — no diamond or
  // supplier data.
  var SEARCH_RETURN_MARKER_KEY = "ivanov:diamond-search:close-viewer";
  var STATE_LOADING = "loading";
  var STATE_SUCCESS = "success";
  var STATE_UNAVAILABLE = "unavailable";
  var STATE_NOT_FOUND = "not-found";
  var STATE_ERROR = "error";
  var SELECT_EVENT = "diamond:select-this-diamond";
  var FALLBACK_DELIVERY = "Typically arrives within 7\u201314 business days";
  var TRUST_LINE = "Free Shipping \u00b7 30-Day Returns";
  /* Future Admin/CMS contract. Liquid host data-service-* attrs override
     these strings. Do not scatter Ivanov Service copy across render branches. */
  var IVANOV_SERVICE_CONTENT = {
    heading: "Ivanov Service",
    items: [
      {
        id: "shipping",
        title: "Complimentary Insured Shipping",
        body: "Secure, insured delivery at no additional cost."
      },
      {
        id: "returns",
        title: "30-Day Returns",
        body: "Return your diamond within 30 days."
      },
      {
        id: "certification",
        title: "Independent Certification",
        body: "IGI or GIA grading report included when available."
      },
      {
        id: "support",
        title: "Expert Support",
        body: "Questions about this diamond? Speak with a jeweler."
      }
    ]
  };
  var UNDERSTAND_DEFAULT_MODULE = "carat";
  var UNDERSTAND_MODULES = [
    { id: "carat", label: "Carat" },
    { id: "cut", label: "Cut" },
    { id: "color", label: "Color" },
    { id: "clarity", label: "Clarity" },
  ];
  /* Future Admin/CMS contract. Liquid host data-carat-edu-* attrs override
     these strings. Do not scatter Carat education copy across render branches. */
  var UNDERSTAND_CARAT_CONTENT = {
    index: "01",
    label: "CARAT",
    headingLine1: "Carat Weight",
    headingLine2: "& Size",
    lead: "Carat tells you how much a diamond weighs. Size tells you how it appears.",
    body: "Two diamonds of equal carat weight can have different dimensions and look noticeably different on the hand. Shape and proportions determine how that weight is distributed.",
    handRef: "US Size 5.5 Hand"
  };
  var UNDERSTAND_CARAT_INTRO = UNDERSTAND_CARAT_CONTENT.lead;
  var UNDERSTAND_CARAT_EXPLAIN = UNDERSTAND_CARAT_CONTENT.body;
  var UNDERSTAND_CARAT_VARY = "Actual dimensions vary by diamond.";
  var UNDERSTAND_CARAT_MATCH_DECIMALS = 2;
  /* Detail Carat Weight slider only. Search AEC CARAT_MAX=10 remains the
     educational typical-size contract and must not cap this control.
     Visual checkpoints are uniformly spaced; numeric interpolation is
     piecewise-linear inside each adjacent checkpoint segment. */
  var UNDERSTAND_CARAT_SLIDER_MIN = 0.25;
  var UNDERSTAND_CARAT_SLIDER_BASE_MAX = 30;
  var UNDERSTAND_CARAT_SLIDER_STEP = 0.05;
  var UNDERSTAND_CARAT_BUBBLE_HALF_PX = 40;
  var UNDERSTAND_CARAT_SLIDER_CHECKPOINTS = [0.25, 0.5, 1, 2, 3, 5, 10, 20, 30];
  /* Detail Carat Weight only. Visual rail stays full width; thumb travel
     is inset by one 18px handle radius so 0.25 / 30 stay fully inside.
     Checkpoint mapping remains the locked 0…1 equal-segment scale. */
  var UNDERSTAND_CARAT_HANDLE_SIZE_PX = 18;
  var UNDERSTAND_CARAT_TRAVEL_INSET_PX = UNDERSTAND_CARAT_HANDLE_SIZE_PX / 2;
  /* Future Admin/CMS contract. Liquid host data-cut-edu-* attrs override
     these strings. Do not scatter Cut education copy across render branches. */
  var UNDERSTAND_CUT_CONTENT = {
    index: "02",
    label: "CUT",
    headingLine1: "Cut",
    headingLine2: "& Light Performance",
    lead: "Cut describes how a diamond's proportions, angles, and facet alignment influence the way light travels through the stone.",
    body: "A well-cut diamond returns more light to the eye, creating greater brilliance, fire, and sparkle. Poorer proportions allow more light to escape through the sides or bottom.",
    caption: "Side Profile \u2014 Light Performance",
    controlLabel: "CUT GRADE",
    fallback: "Explore how cut quality can influence light performance.",
    modelNote: "Educational comparison"
  };
  var UNDERSTAND_CUT_GRADES = [
    {
      id: "fair",
      label: "Fair",
      caption: "Fair Cut",
      heading: "Limited light performance.",
      body: "Fair proportions allow more light to escape through the sides or bottom, reducing overall brilliance and sparkle."
    },
    {
      id: "good",
      label: "Good",
      caption: "Good Cut",
      heading: "Balanced light performance.",
      body: "Good proportions return a meaningful amount of light while allowing some visible leakage."
    },
    {
      id: "very-good",
      label: "Very Good",
      caption: "Very Good Cut",
      heading: "Strong light performance.",
      body: "Very Good proportions return most light efficiently, producing strong brilliance with limited leakage."
    },
    {
      id: "excellent",
      label: "Excellent",
      caption: "Excellent Cut",
      heading: "Outstanding light performance.",
      body: "Excellent proportions return most light through the top of the diamond, producing exceptional brilliance and sparkle with only minimal light leakage."
    },
    {
      id: "ideal",
      label: "Ideal",
      caption: "Ideal Cut",
      heading: "Maximum light performance.",
      body: "Ideal proportions are optimized for highly efficient light return, delivering exceptional brilliance, fire, and minimal light leakage."
    }
  ];
  var UNDERSTAND_CUT_GRADE_IDS = ["fair", "good", "very-good", "excellent", "ideal"];
  var UNDERSTAND_CUT_EDU_DEFAULT = "very-good";
  var UNDERSTAND_CUT_HANDLE_SIZE_PX = UNDERSTAND_CARAT_HANDLE_SIZE_PX;
  var UNDERSTAND_CUT_TRAVEL_INSET_PX = UNDERSTAND_CARAT_TRAVEL_INSET_PX;
  var UNDERSTAND_CUT_BUBBLE_HALF_PX = 44;
  /* Future Admin/CMS contract. Liquid host data-color-edu-* attrs override
     these strings. Do not scatter Color education copy across render branches. */
  var UNDERSTAND_COLOR_CONTENT = {
    index: "03",
    label: "COLOR",
    headingLine1: "Diamond Color",
    headingLine2: "",
    lead: "Diamond color measures the presence of subtle body color in an otherwise colorless diamond. Grades closer to D appear more colorless, while lower grades show progressively more warmth.",
    body: "Color differences between adjacent grades can be very subtle, especially once the diamond is set in jewelry.",
    caption: "Side Profile \u2014 Color Appearance",
    controlLabel: "COLOR GRADE",
    fallback: "Explore how color grades can influence diamond appearance.",
    modelNote: "Educational comparison",
    stripLabel: "D\u2013K Color Grades"
  };
  var UNDERSTAND_COLOR_GRADES = [
    {
      id: "d",
      label: "D",
      caption: "D Color",
      family: "Colorless",
      heading: "Colorless.",
      body: "The highest color grade, showing virtually no detectable body color."
    },
    {
      id: "e",
      label: "E",
      caption: "E Color",
      family: "Colorless",
      heading: "Colorless.",
      body: "Exceptionally colorless with differences from D generally difficult to detect without controlled comparison."
    },
    {
      id: "f",
      label: "F",
      caption: "F Color",
      family: "Colorless",
      heading: "Colorless.",
      body: "A highly colorless appearance with only extremely subtle warmth."
    },
    {
      id: "g",
      label: "G",
      caption: "G Color",
      family: "Near Colorless",
      heading: "Near colorless.",
      body: "A subtle degree of warmth that is generally difficult to detect without direct comparison."
    },
    {
      id: "h",
      label: "H",
      caption: "H Color",
      family: "Near Colorless",
      heading: "Near colorless.",
      body: "A slight warmth may become visible in direct comparison while the diamond still appears bright and near colorless."
    },
    {
      id: "i",
      label: "I",
      caption: "I Color",
      family: "Near Colorless",
      heading: "Near colorless.",
      body: "Noticeable warmth can begin to appear, particularly in larger diamonds or against very white metal."
    },
    {
      id: "j",
      label: "J",
      caption: "J Color",
      family: "Near Colorless",
      heading: "Near colorless / slightly warm.",
      body: "Warmth becomes easier to recognize while the diamond can still appear bright in many settings."
    },
    {
      id: "k",
      label: "K",
      caption: "K Color",
      family: "Warm",
      heading: "Warm.",
      body: "A visible warm tone is present compared with higher color grades."
    }
  ];
  var UNDERSTAND_COLOR_GRADE_IDS = ["d", "e", "f", "g", "h", "i", "j", "k"];
  var UNDERSTAND_COLOR_EDU_DEFAULT = "g";
  var UNDERSTAND_COLOR_HANDLE_SIZE_PX = UNDERSTAND_CARAT_HANDLE_SIZE_PX;
  var UNDERSTAND_COLOR_TRAVEL_INSET_PX = UNDERSTAND_CARAT_TRAVEL_INSET_PX;
  var UNDERSTAND_COLOR_BUBBLE_HALF_PX = 20;
  var UNDERSTAND_COLOR_STRIP_MOBILE_MAX_PX = 767;
  var UNDERSTAND_COLOR_STRIP_FOLLOW_EPSILON_PX = 1;
  var understandColorStripFollowToken = 0;
  /* Future Admin/CMS contract. Liquid host data-clarity-edu-* attrs override
     these strings. Do not scatter Clarity education copy across render branches. */
  var UNDERSTAND_CLARITY_CONTENT = {
    index: "04",
    label: "CLARITY",
    headingLine1: "Diamond Clarity",
    headingLine2: "",
    lead: "Clarity describes internal inclusions and external blemishes evaluated under 10\u00d7 magnification. Fewer and smaller clarity characteristics result in higher clarity grades.",
    body: "Most inclusions are not visible to the unaided eye, especially once the diamond is set in jewelry.",
    caption: "10\u00d7 Magnification \u2014 Clarity Characteristics",
    controlLabel: "CLARITY GRADE",
    fallback: "Explore how clarity grades appear under 10\u00d7 magnification.",
    modelNote: "Educational comparison"
  };
  var UNDERSTAND_CLARITY_INSPECT = { x: 0.579, y: 0.596 };
  var UNDERSTAND_CLARITY_GRADES = [
    {
      id: "si2",
      label: "SI2",
      caption: "SI2 Clarity",
      family: "Slightly Included 2",
      heading: "Slightly Included 2.",
      body: "Noticeable inclusions are present under 10\u00d7 magnification and may sometimes be visible without magnification depending on their size and location."
    },
    {
      id: "si1",
      label: "SI1",
      caption: "SI1 Clarity",
      family: "Slightly Included 1",
      heading: "Slightly Included 1.",
      body: "Inclusions are clearly visible under 10\u00d7 magnification but are generally less prominent than in SI2."
    },
    {
      id: "vs2",
      label: "VS2",
      caption: "VS2 Clarity",
      family: "Very Slightly Included 2",
      heading: "Very Slightly Included 2.",
      body: "Minor inclusions are visible under 10\u00d7 magnification and are typically difficult to see with the unaided eye."
    },
    {
      id: "vs1",
      label: "VS1",
      caption: "VS1 Clarity",
      family: "Very Slightly Included 1",
      heading: "Very Slightly Included 1.",
      body: "Small inclusions that are typically not noticeable to the unaided eye."
    },
    {
      id: "vvs2",
      label: "VVS2",
      caption: "VVS2 Clarity",
      family: "Very Very Slightly Included 2",
      heading: "Very Very Slightly Included 2.",
      body: "Very small inclusions are difficult to locate even under 10\u00d7 magnification."
    },
    {
      id: "vvs1",
      label: "VVS1",
      caption: "VVS1 Clarity",
      family: "Very Very Slightly Included 1",
      heading: "Very Very Slightly Included 1.",
      body: "Extremely small inclusions are very difficult to locate under 10\u00d7 magnification."
    },
    {
      id: "if",
      label: "IF",
      caption: "IF Clarity",
      family: "Internally Flawless",
      heading: "Internally Flawless.",
      body: "No internal inclusions are visible under 10\u00d7 magnification; only minor surface blemishes may be present."
    },
    {
      id: "fl",
      label: "FL",
      caption: "FL Clarity",
      family: "Flawless",
      heading: "Flawless.",
      body: "No inclusions or blemishes are visible under 10\u00d7 magnification."
    }
  ];
  var UNDERSTAND_CLARITY_GRADE_IDS = ["si2", "si1", "vs2", "vs1", "vvs2", "vvs1", "if", "fl"];
  var UNDERSTAND_CLARITY_EDU_DEFAULT = "vs1";
  var UNDERSTAND_CLARITY_HANDLE_SIZE_PX = UNDERSTAND_CARAT_HANDLE_SIZE_PX;
  var UNDERSTAND_CLARITY_TRAVEL_INSET_PX = UNDERSTAND_CARAT_TRAVEL_INSET_PX;
  var UNDERSTAND_CLARITY_BUBBLE_HALF_PX = 26;
  var MEDIA_MODE_DIAMOND = "diamond";
  var MEDIA_MODE_ACTUAL_SIZE = "actual-size";
  var MEDIA_MODE_PROPORTIONS = "proportions";
  var MEDIA_MODE_SIDE_PROFILE = "side-profile";
  var MEDIA_MODE_TOP_VIEW = "top-view";
  var MEDIA_MODE_CERTIFICATE = "certificate";
  var GALLERY_ITEM_360 = "360";
  var GALLERY_ITEM_PHOTO = "photo";
  var MEDIA_MODES = [
    { id: MEDIA_MODE_DIAMOND, label: "DIAMOND", accessibleName: "Diamond", capability: "ready" },
    { id: MEDIA_MODE_ACTUAL_SIZE, label: "ON HAND", accessibleName: "On Hand", capability: "ready" },
    { id: MEDIA_MODE_SIDE_PROFILE, label: "SIDE PROFILE", accessibleName: "Side Profile", capability: "ready" },
    { id: MEDIA_MODE_TOP_VIEW, label: "TOP VIEW", accessibleName: "Top View", capability: "ready" },
    { id: MEDIA_MODE_CERTIFICATE, label: "CERTIFICATE", accessibleName: "Certificate", capability: "ready" },
  ];
  var CERTIFICATE_KNOWN_LABS = { IGI: true, GIA: true, HRD: true };
  var CERTIFICATE_THUMB_SVG =
    '<svg class="diamond-details__media-thumb-cert-icon" viewBox="0 0 48 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<rect x="8.75" y="4.75" width="30.5" height="46.5" rx="2.25" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M16 18h16M16 24h16M16 30h10" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/>' +
    "</svg>";
  var MEDIA_GALLERY_COLUMNS = 3;
  var PROPORTIONS_TABLE_PCT_MIN = 50;
  var PROPORTIONS_TABLE_PCT_MAX = 80;
  var PROPORTIONS_DEPTH_PCT_MIN = 50;
  var PROPORTIONS_DEPTH_PCT_MAX = 80;
  var PROPORTIONS_CROWN_ANGLE_MIN = 20;
  var PROPORTIONS_CROWN_ANGLE_MAX = 50;
  var PROPORTIONS_PAVILION_ANGLE_MIN = 30;
  var PROPORTIONS_PAVILION_ANGLE_MAX = 60;
  var PROPORTIONS_CROWN_HEIGHT_MIN = 8;
  var PROPORTIONS_CROWN_HEIGHT_MAX = 70;
  var PROPORTIONS_PAVILION_DEPTH_MIN = 8;
  var PROPORTIONS_PAVILION_DEPTH_MAX = 70;
  var PROPORTIONS_RATIO_MAX = 5;
  var TECH_STROKE_OUTLINE = 1.5;
  var TECH_STROKE_FACET = 0.95;
  var TECH_STROKE_DIM = 0.8;
  var TECH_STROKE_TICK = 0.8;
  var TECH_MASTER_ROUND_TOP = "round";
  var TECH_MASTER_OVAL_TOP = "oval";
  var TECH_MASTER_PEAR_TOP = "pear";
  var TECH_MASTER_MARQUISE_TOP = "marquise";
  var TECH_MASTER_CUSHION_TOP = "cushion";
  var TECH_MASTER_PRINCESS_TOP = "princess";
  var TECH_MASTER_RADIANT_TOP = "radiant";
  var TECH_MASTER_HEART_TOP = "heart";
  var TECH_MASTER_EMERALD_TOP = "emerald";
  var TECH_MASTER_ASSCHER_TOP = "asscher";
  var TECH_MASTER_BRILLIANT_SIDE = "brilliant";
  var TECH_MASTER_FANCY_SIDE = "fancy";
  var TECH_MASTER_STEP_CUT_SIDE = "step";
  var TECH_MASTER_FALLBACK = "fallback";
  var ROUND_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-round-top-view-master.svg";
  var ROUND_TOP_VIEW_MASTER_VB_W = 1536;
  var ROUND_TOP_VIEW_MASTER_VB_H = 1024;
  var OVAL_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-oval-top-view-master.svg";
  var OVAL_TOP_VIEW_MASTER_VB_W = 1200;
  var OVAL_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Oval artwork inside the locked 1200×720 master.
   * The canvas is landscape; the stone is a tall centered silhouette.
   * Presentation crops to this bbox so empty side margins do not shrink
   * the rendered diamond. Do not stretch the master to stone L/W.
   */
  var OVAL_TOP_VISIBLE_BBOX = {
    x: 393.634,
    y: 88.5346,
    w: 400.936,
    h: 542.7274
  };
  /**
   * Oval Top View overlay zones in the locked 1200×720 master.
   * No dimension lines. Length left, Width top-center, Table/Ratio right.
   * Layout is a composition around the visible-bbox art frame; these
   * coordinates remain the semantic master anchors.
   */
  var OVAL_TOP_ZONES = {
    length: { zone: "left", x: 188, y: 360 },
    width: { zone: "top-center", x: 600, y: 42 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  var PEAR_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-pear-top-view-master.svg";
  var PEAR_TOP_VIEW_MASTER_VB_W = 1200;
  var PEAR_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Pear artwork inside the locked 1200×720 master.
   * The canvas is landscape; the stone is a tall centered silhouette.
   * Presentation crops to this bbox so empty side margins do not shrink
   * the rendered diamond. Do not stretch the master to stone L/W.
   * Do not rotate/flip the supplied orientation.
   */
  var PEAR_TOP_VISIBLE_BBOX = {
    x: 408.9999,
    y: 90,
    w: 381.9978,
    h: 540
  };
  /**
   * Pear Top View overlay zones follow the approved Fancy layout:
   * Length left, Width top-center, Table/Ratio right. No dimension lines.
   */
  var PEAR_TOP_ZONES = {
    length: { zone: "left", x: 188, y: 360 },
    width: { zone: "top-center", x: 600, y: 42 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  var MARQUISE_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-marquise-top-view-master.svg";
  var MARQUISE_TOP_VIEW_MASTER_VB_W = 1200;
  var MARQUISE_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Marquise artwork inside the locked 1200×720 master.
   * The supplied master is vertically oriented (points top/bottom).
   * Do not rotate/flip/stretch it. Presentation crops to this bbox.
   * Shopper axes for this master:
   *   Length = long vertical axis (TOP annotation)
   *   Width  = short horizontal axis (LEFT annotation)
   */
  var MARQUISE_TOP_VISIBLE_BBOX = {
    x: 460.0258,
    y: 89,
    w: 279.9607,
    h: 540
  };
  /**
   * Marquise Top View overlay zones. No dimension lines.
   * Length top-center, Width left, Table/Ratio right.
   */
  var MARQUISE_TOP_ZONES = {
    length: { zone: "top-center", x: 600, y: 42 },
    width: { zone: "left", x: 188, y: 360 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  /* Visual gaps from the visible Marquise artwork. Do not change art scale. */
  var MARQUISE_TOP_LENGTH_GAP_PX = 20;
  var MARQUISE_TOP_WIDTH_GAP_PX = 26;
  var MARQUISE_TOP_INFO_GAP_PX = 30;
  var CUSHION_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-cushion-top-view-master.svg";
  var CUSHION_TOP_VIEW_MASTER_VB_W = 1200;
  var CUSHION_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Cushion artwork inside the locked 1200×720 master.
   * Near-square family silhouette, centered. Do not rotate/stretch.
   * Presentation crops to this bbox. One master serves the whole
   * Cushion family: CUSHION / CUSHION BRILLIANT / CUSHION MODIFIED.
   */
  var CUSHION_TOP_VISIBLE_BBOX = {
    x: 387.521,
    y: 90,
    w: 424.463,
    h: 540
  };
  /**
   * Cushion Top View overlay zones follow approved Fancy layout:
   * Width top-center, Length left, Table/Ratio right. No dimension lines.
   */
  var CUSHION_TOP_ZONES = {
    width: { zone: "top-center", x: 600, y: 42 },
    length: { zone: "left", x: 188, y: 360 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  /* Visual gaps from the visible Cushion artwork. Keep labels attached. */
  var CUSHION_TOP_WIDTH_GAP_PX = 20;
  var CUSHION_TOP_LENGTH_GAP_PX = 26;
  var CUSHION_TOP_INFO_GAP_PX = 30;
  var PRINCESS_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-princess-top-view-master.svg";
  var PRINCESS_TOP_VIEW_MASTER_VB_W = 1200;
  var PRINCESS_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Princess artwork inside the locked 1200×720 master.
   * Near-square silhouette, centered. Do not rotate/stretch.
   * Presentation crops to this bbox. Real L × W are annotations only.
   */
  var PRINCESS_TOP_VISIBLE_BBOX = {
    x: 322,
    y: 88.6509,
    w: 556.358,
    h: 542.3491
  };
  /**
   * Princess Top View overlay zones follow approved Fancy layout:
   * Width top-center, Length left, Table/Ratio right. No dimension lines.
   */
  var PRINCESS_TOP_ZONES = {
    width: { zone: "top-center", x: 600, y: 42 },
    length: { zone: "left", x: 188, y: 360 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  /* Visual gaps from the visible Princess artwork. Keep labels attached. */
  var PRINCESS_TOP_WIDTH_GAP_PX = 20;
  var PRINCESS_TOP_LENGTH_GAP_PX = 26;
  var PRINCESS_TOP_INFO_GAP_PX = 30;
  var RADIANT_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-radiant-top-view-master.svg";
  var RADIANT_TOP_VIEW_MASTER_VB_W = 1200;
  var RADIANT_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Radiant artwork inside the locked 1200×720 master.
   * Broad clipped-corner rectangle, portrait on the canvas. Do not
   * rotate/stretch. Presentation crops to this bbox. Real L × W are
   * annotations only.
   */
  var RADIANT_TOP_VISIBLE_BBOX = {
    x: 406.852,
    y: 90,
    w: 386.411,
    h: 540.687
  };
  /**
   * Radiant Top View overlay zones follow approved Fancy layout:
   * Width top-center, Length left, Table/Ratio right. No dimension lines.
   */
  var RADIANT_TOP_ZONES = {
    width: { zone: "top-center", x: 600, y: 42 },
    length: { zone: "left", x: 188, y: 360 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  /* Visual gaps from the visible Radiant artwork. Keep labels attached. */
  var RADIANT_TOP_WIDTH_GAP_PX = 20;
  var RADIANT_TOP_LENGTH_GAP_PX = 26;
  var RADIANT_TOP_INFO_GAP_PX = 30;
  var HEART_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-heart-top-view-master.svg";
  var HEART_TOP_VIEW_MASTER_VB_W = 1200;
  var HEART_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Heart artwork inside the locked 1200×720 master.
   * Lobes/cleft at top, point at bottom. Do not rotate/flip/stretch.
   * Presentation crops to this bbox. Real L × W are annotations only.
   */
  var HEART_TOP_VISIBLE_BBOX = {
    x: 336.784,
    y: 89.54,
    w: 526.591,
    h: 540.46
  };
  /**
   * Heart Top View overlay zones follow approved Fancy layout:
   * Width top-center, Length left, Table/Ratio right. No dimension lines.
   */
  var HEART_TOP_ZONES = {
    width: { zone: "top-center", x: 600, y: 42 },
    length: { zone: "left", x: 188, y: 360 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  /* Visual gaps from the visible Heart artwork. Keep labels attached. */
  var HEART_TOP_WIDTH_GAP_PX = 20;
  var HEART_TOP_LENGTH_GAP_PX = 26;
  var HEART_TOP_INFO_GAP_PX = 30;
  var EMERALD_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-emerald-top-view-master.svg";
  var EMERALD_TOP_VIEW_MASTER_VB_W = 1200;
  var EMERALD_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Emerald artwork inside the locked 1200×720 master.
   * Elongated step-cut rectangle with clipped corners, portrait on the
   * canvas. Do not rotate/stretch. Presentation crops to this bbox.
   * Real L × W are annotations only. Do not reuse Radiant geometry.
   */
  var EMERALD_TOP_VISIBLE_BBOX = {
    x: 425.843,
    y: 89.449,
    w: 349.324,
    h: 540.867
  };
  /**
   * Emerald Top View overlay zones follow approved Fancy layout:
   * Width top-center, Length left, Table/Ratio right. No dimension lines.
   */
  var EMERALD_TOP_ZONES = {
    width: { zone: "top-center", x: 600, y: 42 },
    length: { zone: "left", x: 188, y: 360 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  /* Visual gaps from the visible Emerald artwork. Keep labels attached. */
  var EMERALD_TOP_WIDTH_GAP_PX = 20;
  var EMERALD_TOP_LENGTH_GAP_PX = 26;
  var EMERALD_TOP_INFO_GAP_PX = 30;
  var ASSCHER_TOP_VIEW_MASTER_ASSET = "ivanov-proportions-asscher-top-view-master.svg";
  var ASSCHER_TOP_VIEW_MASTER_VB_W = 1200;
  var ASSCHER_TOP_VIEW_MASTER_VB_H = 720;
  /**
   * Visible Asscher artwork inside the locked 1200×720 master.
   * Near-square step-cut octagon with clipped corners, centered.
   * Do not rotate/stretch. Presentation crops to this bbox.
   * Real L × W are annotations only. Do not reuse Emerald geometry.
   */
  var ASSCHER_TOP_VISIBLE_BBOX = {
    x: 322.694,
    y: 89.7855,
    w: 553.492,
    h: 540.4495
  };
  /**
   * Asscher Top View overlay zones follow approved Fancy layout:
   * Width top-center, Length left, Table/Ratio right. No dimension lines.
   */
  var ASSCHER_TOP_ZONES = {
    width: { zone: "top-center", x: 600, y: 42 },
    length: { zone: "left", x: 188, y: 360 },
    info: { zone: "right", x: 848, y: 292, w: 300, h: 140 }
  };
  /* Visual gaps from the visible Asscher artwork. Keep labels attached. */
  var ASSCHER_TOP_WIDTH_GAP_PX = 20;
  var ASSCHER_TOP_LENGTH_GAP_PX = 26;
  var ASSCHER_TOP_INFO_GAP_PX = 30;
  var BRILLIANT_SIDE_PROFILE_MASTER_ASSET = "ivanov-proportions-brilliant-side-profile-master.svg";
  var BRILLIANT_SIDE_PROFILE_MASTER_VB_W = 1200;
  var BRILLIANT_SIDE_PROFILE_MASTER_VB_H = 720;
  /**
   * Overlay anchors = empty gaps already cut into the locked 12-path 1200×720 master.
   * Coordinates are viewBox units (0 0 1200 720), not page pixels.
   *
   * Table:    gap between top-left bracket (x<=438.14) and top-right (x>=673.51)
   * Crown:    gap between left table tick (y<=185.68) and left mid-crown (y>=249.92)
   * Pavilion: gap between left girdle drop (y<=380.76) and left lower tick (y>=509.25)
   * Depth:    gap between right upper tick (y<=311.82) and right lower tick (y>=430.12)
   */
  var BRILLIANT_SIDE_ZONES = {
    table: { zone: "top", x: 555.8, y: 125.7 },
    crownHeight: { zone: "left-upper", x: 188.5, y: 217.8 },
    pavilionDepth: { zone: "left-lower", x: 188.5, y: 445.0 },
    totalDepth: { zone: "right", x: 1023.4, y: 371.0 }
  };
  var FANCY_SIDE_PROFILE_MASTER_ASSET = "ivanov-proportions-fancy-side-profile-master.svg";
  var FANCY_SIDE_PROFILE_MASTER_VB_W = 1200;
  var FANCY_SIDE_PROFILE_MASTER_VB_H = 720;
  /**
   * Overlay anchors for the locked 1200×720 Fancy Side Profile master.
   * Coordinates are viewBox units, calibrated to THIS master's
   * dimension-line gaps — not Brilliant or Step-Cut coordinates.
   *
   * Diamond body occupies roughly x=169–931, y=153–604.
   * Table:    gap between top-left bracket (x<=414.2) and top-right (x>=683)
   * Crown:    gap between left table tick (y<=178) and left mid-crown (y>=244)
   * Pavilion: gap between left girdle drop (y<=382) and left lower tick (y>=516)
   * Depth:    gap between right upper tick (y<=308) and right lower tick (y>=433)
   *
   * Left labels share one column. x is the LEFT EDGE of the text so
   * “Crown Height” / “Pavilion Depth” stay inside the media field.
   */
  var FANCY_SIDE_LEFT_SAFE_PX = 16;
  var FANCY_SIDE_ZONES = {
    table: { zone: "top", x: 548.6, y: 114 },
    crownHeight: { zone: "left-upper", x: 32, y: 211 },
    pavilionDepth: { zone: "left-lower", x: 32, y: 449 },
    totalDepth: { zone: "right", x: 1024, y: 370.5 }
  };
  var STEP_CUT_SIDE_PROFILE_MASTER_ASSET = "ivanov-proportions-step-cut-side-profile-master.svg";
  var STEP_CUT_SIDE_PROFILE_MASTER_VB_W = 1200;
  var STEP_CUT_SIDE_PROFILE_MASTER_VB_H = 720;
  /**
   * Overlay anchors for the locked 1200×720 Step-Cut master.
   * Coordinates are viewBox units. The diamond occupies roughly
   * x=89–1110, y=90–630. Labels sit in the surrounding canvas
   * gaps: table above the flat, crown/pavilion left of the profile,
   * total depth right of the pavilion taper.
   *
   * Left labels share one column. Their x is the LEFT EDGE of the
   * text (not the visual center) so “Crown Height” / “Pavilion Depth”
   * stay inside the media field. Combined with max(16px, x%) this
   * yields about 16–24px inset at typical desktop widths. Previous
   * centered x=108 let the strings clip at the stage’s overflow-x.
   */
  var STEP_CUT_SIDE_LEFT_SAFE_PX = 16;
  var STEP_CUT_SIDE_ZONES = {
    table: { zone: "top", x: 588, y: 66 },
    crownHeight: { zone: "left-upper", x: 32, y: 160 },
    pavilionDepth: { zone: "left-lower", x: 32, y: 432 },
    totalDepth: { zone: "right", x: 1074, y: 372 }
  };
  var SIDE_PROFILE_THUMB_SVG =
    '<svg viewBox="0 0 64 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M20 16 H44 L52 24 H12 Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
    '<path d="M12 24 L32 40 L52 24" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
    '<path d="M24 16 L32 24 L40 16 M20 28 L32 40 L44 28" stroke="currentColor" stroke-width="1" stroke-linejoin="round"/>' +
    "</svg>";
  var SILHOUETTE_THUMB_SVG =
    '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M16 24 L32 8 L48 24 L32 58 Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M16 24 L48 24 M25 24 L32 58 M39 24 L32 58 M22 24 L32 32 L42 24" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>' +
    "</svg>";
  // SIZE / ON HAND calibration — approved Carat AEC physical system only.
  // Native hand: carat-hand-tone-1-lightest.png, 859 × 1024.
  // Ring-finger crease-to-crease at y=428: 90 px ≡ 15.90 mm (US 5.5 inner diameter).
  // Anatomical anchor (325, 428). The old 289.5 X is superseded.
  // Overlay scale uses THIS stone's lengthMm × widthMm. Do not use AEC carat tables.
  var SIZE_HAND_NATIVE_WIDTH = 859;
  var SIZE_HAND_NATIVE_HEIGHT = 1024;
  var SIZE_NATIVE_PX_PER_MM = 90 / 15.9;
  var SIZE_ANCHOR_X_PX = 325;
  var SIZE_ANCHOR_Y_PX = 428;
  var SIZE_ANCHOR_X = 325 / 859;
  var SIZE_ANCHOR_Y = 428 / 1024;
  // Desktop presentation target only. Does not change px/mm calibration.
  var SIZE_HAND_DISPLAY_MAX_WIDTH = 520;
  var SIZE_SHAPE_KEYS = {
    round: true,
    oval: true,
    cushion: true,
    princess: true,
    emerald: true,
    radiant: true,
    pear: true,
    heart: true,
    asscher: true,
    marquise: true,
  };
  // All 10 ON HAND overlays reuse the approved photoreal Carat masters.
  // Physical L×W still comes from this stone's geometry, not carat tables.
  var SIZE_MASTER_KIND = "raster";
  var SIZE_SHAPE_MASTERS = {
    round: true,
    oval: true,
    cushion: true,
    princess: true,
    emerald: true,
    radiant: true,
    pear: true,
    heart: true,
    asscher: true,
    marquise: true,
  };
  // Asset-internal Pear alignment only. Shared anatomical anchor stays
  // (325, 428). Source PNG is point-up; display rotates 180° so the
  // rounded end faces up and the point faces down.
  var SIZE_PEAR_ASSET_ALIGN = {
    rotateDeg: 180,
    originX: 0.5,
    originY: 0.5,
    translateX: 0,
    translateY: 0,
  };
  // ON HAND Skin Tone — Search Carat AEC visual/functional copy.
  // Page-level in-memory only. Default Tone 1. Does not persist.
  // Must not mutate L×W, anchor, masters, or Version C geometry.
  var SIZE_SKIN_TONE_COUNT = 5;
  var SIZE_DEFAULT_SKIN_TONE = 1;
  var SIZE_TONE_MOBILE_INSET_PX = 8;
  var SIZE_TONE_HORIZONTAL_INSET_PX = 8;
  var SIZE_TONE_MOBILE_MQ = "(max-width: 989px)";
  var CARAT_TONE_VERTICAL_MQ = "(max-width: 899px)";
  var activeSizeTone = SIZE_DEFAULT_SKIN_TONE;
  var sizeTonePreloadMap = {};
  var sizeTonePointerBound = null;
  var sizeToneLayoutBound = false;
  var caratToneOrientationBound = false;
  var understandCaratPointerBound = null;
  var understandCutPointerBound = null;
  var understandColorPointerBound = null;
  var understandClarityPointerBound = null;
  var DIAMOND_VIEW_360 = "360";
  var DIAMOND_VIEW_PHOTO = "photo";
  var DIAMOND_VIEW_SILHOUETTE = "silhouette";
  // Same safe 360 playback constants the Viewer already approved.
  var AUTOPLAY_FPS = 15;
  var CLICK_DRAG_THRESHOLD_PX = 8;
  var START_THRESHOLD = 6;
  var INITIAL_BUFFER_TIMEOUT_MS = 1200;
  var ROLLING_TARGET = 15;
  var PREPARED_READY_TIMEOUT_MS = 20000;
  // One safe-area rule. 0% inset → content fills 100% of the limiting
  // viewport side. Remaining whitespace on many 360 sequences is baked
  // into the supplier frame, not a second CSS inset. Do not stack another
  // safe-area or overscale here — Emerald face-up already occupies ~90–95%
  // of its source frame on the limiting side.
  var MEDIA_SAFE_AREA = 0;
  var MEDIA_FILL = 1 - 2 * MEDIA_SAFE_AREA;
  var FRAME_HOST_MIN_PX = 224;

  function formatMediaPct(value) {
    var pct = Number(value) * 100;
    if (!Number.isFinite(pct)) return "0";
    return String(Math.round(pct * 10) / 10);
  }

  var CUT_LABELS = {
    ID: "Ideal",
    EX: "Excellent",
    VG: "Very Good",
    GD: "Good",
    FR: "Fair",
  };

  var GRADE_LABELS = {
    EX: "Excellent",
    VG: "Very Good",
    GD: "Good",
    FR: "Fair",
    PR: "Poor",
  };

  var FLUORESCENCE_LABELS = {
    NON: "None",
    VSL: "Faint",
    SLT: "Faint",
    FNT: "Faint",
    MED: "Medium",
    STG: "Strong",
    STN: "Strong",
    VST: "Very Strong",
  };

  var GIRDLE_LABELS = {
    ETN: "Extremely Thin",
    VTN: "Very Thin",
    THN: "Thin",
    MED: "Medium",
    STK: "Slightly Thick",
    THK: "Thick",
    VTK: "Very Thick",
    ETK: "Extremely Thick",
  };

  var CULET_LABELS = {
    NON: "None",
    NONE: "None",
    VSM: "Very Small",
    SML: "Small",
    MED: "Medium",
    SLG: "Slightly Large",
    LGE: "Large",
    VLG: "Very Large",
    ELG: "Extremely Large",
  };

  var HEART_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"></path>' +
    "</svg>";
  var IVANOV_SERVICE_ICONS = {
    shipping:
      '<svg class="diamond-details__service-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M3.5 7.5h10.5v9.5H3.5z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<path d="M14 10h3.6L20.5 13v4H14" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<circle cx="7" cy="17.5" r="1.35" stroke="currentColor" stroke-width="1.4"/>' +
      '<circle cx="17.25" cy="17.5" r="1.35" stroke="currentColor" stroke-width="1.4"/>' +
      "</svg>",
    returns:
      '<svg class="diamond-details__service-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M7.5 8.5h9v10h-9z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>' +
      '<path d="M7.5 11.5h9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
      '<path d="M12 8.5V6.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
      '<path d="M9.2 7.4L12 5.6l2.8 1.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>' +
      "</svg>",
    certification:
      '<svg class="diamond-details__service-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<rect x="6.25" y="3.75" width="11.5" height="16.5" rx="1.25" stroke="currentColor" stroke-width="1.4"/>' +
      '<path d="M8.75 8.5h6.5M8.75 11.5h6.5M8.75 14.5h4.25" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>' +
      "</svg>",
    support:
      '<svg class="diamond-details__service-icon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<circle cx="12" cy="8.25" r="2.6" stroke="currentColor" stroke-width="1.4"/>' +
      '<path d="M6.2 18.6c1.15-3.05 3.2-4.55 5.8-4.55s4.65 1.5 5.8 4.55" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>' +
      "</svg>"
  };

  var PLACEHOLDER_HTML =
    '<span class="diamond-details__placeholder" aria-hidden="true">' +
    '<svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M16 24 L32 8 L48 24 L32 58 Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>' +
    '<path d="M16 24 L48 24 M25 24 L32 58 M39 24 L32 58 M22 24 L32 32 L42 24" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>' +
    "</svg></span>";

  var rootEl = null;
  var inFlight = false;
  var activeController = null;
  var compareToken = 0;
  var compareController = null;
  var recentlyViewedToken = 0;
  var recentlyViewedController = null;
  var pageListenerBound = false;
  var escapeBound = false;
  var searchReturnBound = false;
  var activeDiamond = null;

  function escapeHtml(value) {
    return String(value == null ? "" : value).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function toTitleCase(value) {
    return String(value)
      .toLowerCase()
      .replace(/\b\w/g, function (c) {
        return c.toUpperCase();
      });
  }

  function isMeaningful(value) {
    var normalized = String(value == null ? "" : value).trim();
    if (!normalized) return false;
    var upper = normalized.toUpperCase();
    return upper !== "NONE" && upper !== "N/A" && upper !== "NA" && upper !== "-" && upper !== "\u2014";
  }

  function formatCarat(carat) {
    var n = Number(carat);
    return Number.isFinite(n) ? n.toFixed(2) : "";
  }

  function formatShape(shape) {
    if (!isMeaningful(shape)) return "";
    return toTitleCase(String(shape).replace(/[_-]+/g, " "));
  }

  function formatOrigin(origin) {
    var value = String(origin == null ? "" : origin).trim().toLowerCase();
    if (value === "lab-grown" || value === "labgrown" || value === "lab_grown") {
      return "Lab Grown";
    }
    if (value === "natural") return "Natural";
    return value ? toTitleCase(value.replace(/[_-]+/g, " ")) : "";
  }

  /**
   * Search URL origin param values (diamond-api.js ORIGIN_*).
   * Detail does not load diamond-api.js, so keep a local mirror.
   */
  function normalizeSearchOriginParam(origin) {
    var value = String(origin == null ? "" : origin).trim().toLowerCase();
    if (value === "lab-grown" || value === "labgrown" || value === "lab_grown") {
      return "lab-grown";
    }
    if (value === "natural") return "natural";
    return "";
  }

  /**
   * Build Diamond Search recovery URL for an unavailable stone.
   * Reuses the current Search query contract: origin + shape only.
   * Does not add carat/color/clarity/cut/lab/id constraints.
   */
  function buildSimilarSearchPath(diamond) {
    var origin = normalizeSearchOriginParam(diamond && diamond.origin);
    var shape = resolveSizeShape(diamond && diamond.shape);
    var parts = [];
    if (origin) parts.push("origin=" + encodeURIComponent(origin));
    if (shape) parts.push("shape=" + encodeURIComponent(shape));
    return parts.length ? SEARCH_PATH + "?" + parts.join("&") : SEARCH_PATH;
  }

  function renderSimilarDiamondsAction(diamond) {
    return (
      '<a class="diamond-details__similar" data-view-similar-diamonds="true" href="' +
      escapeHtml(buildSimilarSearchPath(diamond)) +
      '">View Similar Diamonds</a>'
    );
  }

  function formatCut(cut) {
    if (!isMeaningful(cut)) return "";
    var code = String(cut).trim().toUpperCase();
    if (code === "FANCY") return "";
    return CUT_LABELS[code] || toTitleCase(cut);
  }

  function formatColorGrade(color) {
    return isMeaningful(color) ? String(color).trim() : "";
  }

  function formatClarityGrade(clarity) {
    return isMeaningful(clarity) ? String(clarity).trim() : "";
  }

  function formatGatewayCarat(carat) {
    if (carat == null || carat === "") return "";
    var label = formatCarat(carat);
    return label ? label + " ct" : "";
  }

  function formatGatewayDisplay(value) {
    return value ? value : "\u2014";
  }

  function isUnusableSpecToken(value) {
    var normalized = String(value == null ? "" : value).trim();
    if (!normalized) return true;
    var upper = normalized.toUpperCase();
    return (
      upper === "N/A" ||
      upper === "NA" ||
      upper === "UNDEFINED" ||
      upper === "-" ||
      upper === "\u2014"
    );
  }

  function firstPresentSpecValue(values) {
    var i;
    var value;
    for (i = 0; i < values.length; i += 1) {
      value = values[i];
      if (value == null) continue;
      if (isUnusableSpecToken(value)) continue;
      return value;
    }
    return null;
  }

  function formatNamedGrade(value, labelMap) {
    if (isUnusableSpecToken(value)) return "";
    var raw = String(value).trim();
    var code = raw.toUpperCase();
    if (code === "NONE") return "";
    return labelMap[code] || toTitleCase(raw);
  }

  function formatPolish(polish) {
    return formatNamedGrade(polish, GRADE_LABELS);
  }

  function formatSymmetry(symmetry) {
    return formatNamedGrade(symmetry, GRADE_LABELS);
  }

  function expandGirdleToken(token) {
    var raw = String(token == null ? "" : token).trim();
    if (!raw || isUnusableSpecToken(raw)) return "";
    var code = raw.toUpperCase();
    if (code === "NONE") return "";
    return GIRDLE_LABELS[code] || toTitleCase(raw.replace(/[_]+/g, " "));
  }

  function formatGirdle(girdle) {
    var raw;
    var parts;
    var labels;
    var i;
    var label;
    if (isUnusableSpecToken(girdle)) return "";
    raw = String(girdle).trim();
    parts = raw.split(/\s+(?:to|[\u2013\u2014-])\s+/i);
    if (parts.length === 1 && /^[A-Za-z]{3}\s*-\s*[A-Za-z]{3}$/.test(raw)) {
      parts = raw.split(/\s*-\s*/);
    }
    if (parts.length > 1) {
      labels = [];
      for (i = 0; i < parts.length; i += 1) {
        if (isUnusableSpecToken(parts[i])) return "";
        label = expandGirdleToken(parts[i]);
        if (!label) return "";
        labels.push(label);
      }
      return labels.join(" \u2013 ");
    }
    return expandGirdleToken(raw);
  }

  function formatCulet(culet) {
    if (isUnusableSpecToken(culet)) return "";
    var raw = String(culet).trim();
    var code = raw.toUpperCase();
    return CULET_LABELS[code] || toTitleCase(raw.replace(/[_]+/g, " "));
  }

  function formatFluorescence(intensity) {
    if (isUnusableSpecToken(intensity)) return "";
    var raw = String(intensity).trim();
    var code = raw.toUpperCase();
    return FLUORESCENCE_LABELS[code] || toTitleCase(raw.replace(/[_]+/g, " "));
  }

  function detailTechnicalSources(diamond) {
    var geometry;
    var fluorescence;
    diamond = diamond || {};
    geometry = diamond.geometry && typeof diamond.geometry === "object" ? diamond.geometry : {};
    fluorescence = diamond.fluorescence;
    return {
      polish: diamond.polish,
      symmetry: diamond.symmetry,
      girdle: firstPresentSpecValue([diamond.girdle, geometry.girdle]),
      culet: firstPresentSpecValue([diamond.culet, geometry.culetSize]),
      fluorescence:
        fluorescence && typeof fluorescence === "object"
          ? fluorescence.intensity
          : fluorescence,
      fluorescenceColor:
        fluorescence && typeof fluorescence === "object"
          ? fluorescence.color
          : diamond.fluorescenceColor,
    };
  }

  function formatLab(lab) {
    return isMeaningful(lab) ? String(lab).trim() : "";
  }

  /**
   * Stage 5D Detail contract: grading laboratory is `diamond.lab` only.
   * Do not use origin (Lab Grown / Natural) or Search-shaped certificate.lab.
   */
  function gradingLab(diamond) {
    return formatLab(diamond && diamond.lab);
  }

  function formatStoneIdentity(diamond) {
    diamond = diamond || {};
    var caratLabel = formatCarat(diamond.carat);
    var shapeLabel = formatShape(diamond.shape);
    var originLabel = formatOrigin(diamond.origin);
    return [caratLabel && caratLabel + " ct", shapeLabel, originLabel, "Diamond"]
      .filter(Boolean)
      .join(" ");
  }

  function formatCutSegment(cut) {
    var label = formatCut(cut);
    if (!label) return "";
    if (/\bcut$/i.test(label)) return label;
    return label + " Cut";
  }

  function formatColorSegment(color) {
    var label = formatColorGrade(color);
    return label ? label + " Color" : "";
  }

  function formatClaritySegment(clarity) {
    var label = formatClarityGrade(clarity);
    return label ? label + " Clarity" : "";
  }

  function formatSpecLine(diamond) {
    diamond = diamond || {};
    return [
      formatCutSegment(diamond.cut),
      formatColorSegment(diamond.color),
      formatClaritySegment(diamond.clarity),
      gradingLab(diamond),
    ]
      .filter(isMeaningful)
      .join(" \u00b7 ");
  }

  function formatPriceUsd(retailPriceUsd) {
    if (retailPriceUsd == null || retailPriceUsd === "") return "";
    var n = Number(retailPriceUsd);
    if (!Number.isFinite(n)) return "";
    return "$" + Math.round(n).toLocaleString("en-US");
  }

  function formatDeliveryLine(diamond) {
    var days = diamond && diamond.delivery ? diamond.delivery.shopperWindowDays : null;
    var n = Number(days);
    if (!Number.isFinite(n) || n <= 0) return FALLBACK_DELIVERY;
    var rounded = Math.round(n);
    return "Typically arrives within " + rounded + " business day" + (rounded === 1 ? "" : "s");
  }

  function mediaImageUrl(diamond) {
    return diamond && diamond.media && diamond.media.image
      ? String(diamond.media.image)
      : "";
  }

  function nestedMedia(diamond) {
    return diamond && diamond.media && typeof diamond.media === "object" ? diamond.media : {};
  }

  function isUsableV360(v360) {
    return !!(v360 && typeof v360 === "object" && typeof v360.url === "string" && v360.url.trim());
  }

  /**
   * Frame Engine reads snake_case frame_count / top_index.
   * Stage 5D Detail media.v360 may arrive as camelCase frameCount / topIndex.
   * Do not call Search mapDiamondItem(); only alias the existing engine fields.
   */
  function toEngineV360(v360) {
    if (!isUsableV360(v360)) return v360 || null;
    var frameCount = v360.frame_count != null ? v360.frame_count : v360.frameCount;
    var topIndex = v360.top_index != null ? v360.top_index : v360.topIndex;
    var media = {
      url: String(v360.url).trim(),
      frame_count: frameCount,
      top_index: topIndex,
    };
    if (Array.isArray(v360.frames)) media.frames = v360.frames;
    if (v360.__preparedTurntable) media.__preparedTurntable = true;
    if (v360.__pocPrepared) media.__pocPrepared = true;
    return media;
  }

  function isUsablePreparedTurntable(prepared) {
    var Loader = window.DiamondPreparedTurntableLoader;
    if (Loader && typeof Loader.isPreparedTurntableAvailable === "function") {
      return Loader.isPreparedTurntableAvailable(prepared);
    }
    return !!(
      prepared &&
      prepared.available === true &&
      typeof prepared.manifestUrl === "string" &&
      prepared.manifestUrl.trim()
    );
  }

  /**
   * Stage 5D nested media → flat shape the existing prepared-turntable
   * resolver already understands. Does not invent media.
   */
  function toFrameDiamond(diamond) {
    var media = nestedMedia(diamond);
    return {
      id: diamond && diamond.id,
      image: media.image || null,
      v360: toEngineV360(media.v360),
      preparedTurntable: media.preparedTurntable || null,
    };
  }

  function hasUsable360(diamond) {
    var media = nestedMedia(diamond);
    return isUsableV360(media.v360) || isUsablePreparedTurntable(media.preparedTurntable);
  }

  /**
   * DIAMOND-mode media priority (metadata only — never fetches):
   *   360 when usable → Photo when image exists → silhouette.
   */
  function diamondViewCapabilities(diamond) {
    var turntable = hasUsable360(diamond);
    var photo = !!mediaImageUrl(diamond);
    return {
      turntable: turntable,
      photo: photo,
      defaultView: turntable ? DIAMOND_VIEW_360 : photo ? DIAMOND_VIEW_PHOTO : DIAMOND_VIEW_SILHOUETTE,
      showSubControl: false,
      hasBothViews: turntable && photo,
    };
  }

  function toPositiveMm(value) {
    var n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function usableSizeMeasurements(diamond) {
    var geometry = diamond && diamond.geometry && typeof diamond.geometry === "object" ? diamond.geometry : null;
    if (!geometry) return null;
    var lengthMm = toPositiveMm(geometry.lengthMm);
    var widthMm = toPositiveMm(geometry.widthMm);
    if (lengthMm == null || widthMm == null) return null;
    return { lengthMm: lengthMm, widthMm: widthMm };
  }

  /**
   * Overlay fractions of the master hand. Width is a fraction of hand width
   * using the verified native px/mm constant. Height follows real L × W
   * (length along the finger). Carat is never an input.
   */
  function sizeOverlayMetrics(lengthMm, widthMm) {
    var length = toPositiveMm(lengthMm);
    var width = toPositiveMm(widthMm);
    if (length == null || width == null) return null;
    return {
      lengthMm: length,
      widthMm: width,
      widthPct: (width * SIZE_NATIVE_PX_PER_MM / SIZE_HAND_NATIVE_WIDTH) * 100,
      heightPct: (length * SIZE_NATIVE_PX_PER_MM / SIZE_HAND_NATIVE_HEIGHT) * 100,
      aspect: width / length,
    };
  }

  /**
   * Displayed SIZE box at a given CSS hand width. Overlay and hand share
   * this width as their only scale factor. Calibration is unchanged.
   */
  function sizeCompositionAtHandWidth(handCssWidth, lengthMm, widthMm) {
    var metrics = sizeOverlayMetrics(lengthMm, widthMm);
    var handWidth = Number(handCssWidth);
    if (!metrics || !Number.isFinite(handWidth) || handWidth <= 0) return null;
    var handHeight = handWidth * SIZE_HAND_NATIVE_HEIGHT / SIZE_HAND_NATIVE_WIDTH;
    var overlayWidth = handWidth * metrics.widthPct / 100;
    var overlayHeight = handHeight * metrics.heightPct / 100;
    var fingerWidth = handWidth * (90 / SIZE_HAND_NATIVE_WIDTH);
    return {
      handWidth: handWidth,
      handHeight: handHeight,
      overlayWidth: overlayWidth,
      overlayHeight: overlayHeight,
      fingerWidth: fingerWidth,
      diamondToFinger: overlayWidth / fingerWidth,
    };
  }

  function resolveSizeShape(shape) {
    var key = String(shape == null ? "" : shape)
      .trim()
      .toLowerCase()
      .replace(/[_]+/g, "-")
      .replace(/\s+/g, "-");
    if (key === "cushion-brilliant" || key === "cushion-modified") return "cushion";
    return SIZE_SHAPE_KEYS[key] ? key : "";
  }

  function sizeShapeIsMaster(shapeKey) {
    return !!SIZE_SHAPE_MASTERS[shapeKey];
  }

  function sizeVisibleRatio(lengthMm, widthMm) {
    var metrics = sizeOverlayMetrics(lengthMm, widthMm);
    if (!metrics) return null;
    return metrics.lengthMm / metrics.widthMm;
  }

  function formatSizeMm(value) {
    var n = Number(value);
    if (!Number.isFinite(n)) return "";
    return String(Math.round(n * 100) / 100);
  }

  function formatPropNumber(value, maxDecimals) {
    var n = Number(value);
    if (!Number.isFinite(n)) return "";
    var decimals = maxDecimals == null ? 2 : maxDecimals;
    return String(Number(n.toFixed(decimals)));
  }

  function inClosedRange(value, min, max) {
    var n = Number(value);
    return Number.isFinite(n) && n >= min && n <= max;
  }

  function validateProportionsMm(value) {
    return toPositiveMm(value);
  }

  function validateProportionsPct(value, min, max) {
    var n = Number(value);
    if (!Number.isFinite(n) || n === 0) return null;
    return inClosedRange(n, min, max) ? n : null;
  }

  function validateProportionsRatio(value) {
    var n = Number(value);
    if (!Number.isFinite(n) || n < 1 || n > PROPORTIONS_RATIO_MAX) return null;
    return n;
  }

  function resolveProportionsShape(shape) {
    var raw = String(shape == null ? "" : shape).trim();
    var normalized = raw.toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
    if (normalized === "cushion brilliant") {
      return { key: "cushion-brilliant", family: "cushion", variant: "brilliant", raw: raw };
    }
    if (normalized === "cushion modified") {
      return { key: "cushion-modified", family: "cushion", variant: "modified", raw: raw };
    }
    var first = normalized.split(" ")[0] || "";
    var known = {
      round: "brilliant",
      oval: "brilliant",
      cushion: "brilliant",
      princess: "brilliant",
      emerald: "step",
      radiant: "brilliant",
      pear: "brilliant",
      heart: "brilliant",
      asscher: "step",
      marquise: "brilliant",
    };
    if (known[first]) {
      return { key: first, family: first, variant: "", raw: raw };
    }
    return { key: "neutral", family: "neutral", variant: "", raw: raw };
  }

  function proportionsSideFamily(shapeInfo) {
    if (!shapeInfo) return "brilliant";
    var key = shapeInfo.key;
    if (key === "emerald" || key === "asscher") return "step";
    if (
      key === "pear" ||
      key === "marquise" ||
      key === "princess" ||
      key === "radiant" ||
      key === "heart" ||
      shapeInfo.family === "cushion"
    ) {
      return "fancy";
    }
    return "brilliant";
  }

  /**
   * Detail-local Proportions resolver. Reads Stage 5D geometry only.
   * Rejects garbage. Never estimates from carat or invents missing mm.
   * Side Profile and Top View use independent availability.
   */
  function resolveProportions(diamond) {
    diamond = diamond || {};
    var geometry = diamond.geometry && typeof diamond.geometry === "object" ? diamond.geometry : {};
    var shapeInfo = resolveProportionsShape(diamond.shape);
    var lengthMm = validateProportionsMm(geometry.lengthMm);
    var widthMm = validateProportionsMm(geometry.widthMm);
    var depthMm = validateProportionsMm(geometry.depthMm);
    var measurementsReady = lengthMm != null && widthMm != null && depthMm != null;
    var topViewAvailable = lengthMm != null && widthMm != null;
    var ratio = validateProportionsRatio(geometry.lengthWidthRatio);
    var tablePct = validateProportionsPct(
      geometry.tablePct,
      PROPORTIONS_TABLE_PCT_MIN,
      PROPORTIONS_TABLE_PCT_MAX
    );
    var depthPct = validateProportionsPct(
      geometry.depthPct,
      PROPORTIONS_DEPTH_PCT_MIN,
      PROPORTIONS_DEPTH_PCT_MAX
    );
    var crownAngle = validateProportionsPct(
      geometry.crownAngle,
      PROPORTIONS_CROWN_ANGLE_MIN,
      PROPORTIONS_CROWN_ANGLE_MAX
    );
    var crownHeight = validateProportionsPct(
      geometry.crownHeight,
      PROPORTIONS_CROWN_HEIGHT_MIN,
      PROPORTIONS_CROWN_HEIGHT_MAX
    );
    var pavilionAngle = validateProportionsPct(
      geometry.pavilionAngle,
      PROPORTIONS_PAVILION_ANGLE_MIN,
      PROPORTIONS_PAVILION_ANGLE_MAX
    );
    var pavilionDepth = validateProportionsPct(
      geometry.pavilionDepth,
      PROPORTIONS_PAVILION_DEPTH_MIN,
      PROPORTIONS_PAVILION_DEPTH_MAX
    );
    var sideProfileAvailable = !!(
      depthMm != null ||
      tablePct != null ||
      depthPct != null ||
      crownAngle != null ||
      crownHeight != null ||
      pavilionAngle != null ||
      pavilionDepth != null
    );
    return {
      available: measurementsReady,
      status: measurementsReady ? "ready" : "unavailable",
      topViewAvailable: topViewAvailable,
      sideProfileAvailable: sideProfileAvailable,
      schematic: true,
      shape: shapeInfo,
      sideFamily: proportionsSideFamily(shapeInfo),
      lengthMm: lengthMm,
      widthMm: widthMm,
      depthMm: depthMm,
      lengthWidthRatio: ratio,
      tablePct: tablePct,
      depthPct: depthPct,
      crownAngle: crownAngle,
      crownHeight: crownHeight,
      pavilionAngle: pavilionAngle,
      pavilionDepth: pavilionDepth,
      drawingAspect: topViewAvailable ? widthMm / lengthMm : null,
    };
  }

  function usesRoundTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "round");
  }

  function usesOvalTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "oval");
  }

  function usesPearTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "pear");
  }

  function usesMarquiseTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "marquise");
  }

  function usesCushionTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.family === "cushion");
  }

  function usesPrincessTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "princess");
  }

  function usesRadiantTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "radiant");
  }

  function usesHeartTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "heart");
  }

  function usesEmeraldTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "emerald");
  }

  function usesAsscherTopMaster(resolved) {
    return !!(resolved && resolved.shape && resolved.shape.key === "asscher");
  }

  function usesBrilliantSideMaster(resolved) {
    return !!(resolved && resolved.sideFamily === "brilliant");
  }

  function usesFancySideMaster(resolved) {
    return !!(resolved && resolved.sideFamily === "fancy");
  }

  function usesStepCutSideMaster(resolved) {
    return !!(resolved && resolved.sideFamily === "step");
  }

  function sideProfileMasterKind(resolved) {
    if (usesStepCutSideMaster(resolved)) return TECH_MASTER_STEP_CUT_SIDE;
    if (usesFancySideMaster(resolved)) return TECH_MASTER_FANCY_SIDE;
    if (usesBrilliantSideMaster(resolved)) return TECH_MASTER_BRILLIANT_SIDE;
    return TECH_MASTER_FALLBACK;
  }

  function topViewMasterKind(resolved) {
    if (usesAsscherTopMaster(resolved)) return TECH_MASTER_ASSCHER_TOP;
    if (usesEmeraldTopMaster(resolved)) return TECH_MASTER_EMERALD_TOP;
    if (usesHeartTopMaster(resolved)) return TECH_MASTER_HEART_TOP;
    if (usesRadiantTopMaster(resolved)) return TECH_MASTER_RADIANT_TOP;
    if (usesPrincessTopMaster(resolved)) return TECH_MASTER_PRINCESS_TOP;
    if (usesCushionTopMaster(resolved)) return TECH_MASTER_CUSHION_TOP;
    if (usesMarquiseTopMaster(resolved)) return TECH_MASTER_MARQUISE_TOP;
    if (usesPearTopMaster(resolved)) return TECH_MASTER_PEAR_TOP;
    if (usesOvalTopMaster(resolved)) return TECH_MASTER_OVAL_TOP;
    if (usesRoundTopMaster(resolved)) return TECH_MASTER_ROUND_TOP;
    return TECH_MASTER_FALLBACK;
  }

  function roundTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-round-top-view-master") || "").trim() : "";
  }

  function ovalTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-oval-top-view-master") || "").trim() : "";
  }

  function pearTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-pear-top-view-master") || "").trim() : "";
  }

  function marquiseTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-marquise-top-view-master") || "").trim() : "";
  }

  function cushionTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-cushion-top-view-master") || "").trim() : "";
  }

  function princessTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-princess-top-view-master") || "").trim() : "";
  }

  function radiantTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-radiant-top-view-master") || "").trim() : "";
  }

  function heartTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-heart-top-view-master") || "").trim() : "";
  }

  function emeraldTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-emerald-top-view-master") || "").trim() : "";
  }

  function asscherTopViewMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-asscher-top-view-master") || "").trim() : "";
  }

  function brilliantSideProfileMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-brilliant-side-profile-master") || "").trim() : "";
  }

  function fancySideProfileMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-fancy-side-profile-master") || "").trim() : "";
  }

  function stepCutSideProfileMasterUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-step-cut-side-profile-master") || "").trim() : "";
  }

  function roundTopPct(value, axis) {
    var total = axis === "y" ? ROUND_TOP_VIEW_MASTER_VB_H : ROUND_TOP_VIEW_MASTER_VB_W;
    return (Number(value) / total) * 100;
  }

  function roundTopBoxStyle(x, y, w, h) {
    return (
      "left:" +
      roundTopPct(x, "x") +
      "%;top:" +
      roundTopPct(y, "y") +
      "%;width:" +
      roundTopPct(w, "x") +
      "%;height:" +
      roundTopPct(h, "y") +
      "%;"
    );
  }

  function roundTopLabelStyle(x, y, extra) {
    return (
      "left:" +
      roundTopPct(x, "x") +
      "%;top:" +
      roundTopPct(y, "y") +
      "%;" +
      (extra || "")
    );
  }

  function roundTopCover(name, x, y, w, h) {
    return (
      '<span class="diamond-details__tech-round-cover" data-top-view-cover="' +
      escapeHtml(name) +
      '" aria-hidden="true" style="' +
      roundTopBoxStyle(x, y, w, h) +
      '"></span>'
    );
  }

  function roundTopLabel(field, x, y, text, extraClass, extraAttrs) {
    return (
      '<p class="diamond-details__tech-round-label' +
      (extraClass ? " " + extraClass : "") +
      '"' +
      (field ? ' data-top-view-field="' + escapeHtml(field) + '"' : "") +
      (extraAttrs || "") +
      ' style="' +
      roundTopLabelStyle(x, y) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function roundTopDimLabel(field, x, y, key, value, axis, extraAttrs) {
    return (
      '<p class="diamond-details__tech-round-dim diamond-details__tech-round-dim--' +
      escapeHtml(field) +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-dim="' +
      escapeHtml(field) +
      '" data-top-view-axis="' +
      escapeHtml(axis) +
      '"' +
      (extraAttrs || "") +
      ' style="' +
      roundTopLabelStyle(x, y) +
      '"><span class="diamond-details__tech-round-dim-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-round-dim-val">' +
      escapeHtml(value) +
      "</span></p>"
    );
  }

  function roundTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-round-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-round-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-round-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  function roundTopMasterArt(options) {
    options = options || {};
    var url = roundTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-round-art' +
      (compact ? " diamond-details__media-thumb-round-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="round" data-top-view-master-asset="' +
      escapeHtml(ROUND_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      ROUND_TOP_VIEW_MASTER_VB_W +
      " " +
      ROUND_TOP_VIEW_MASTER_VB_H +
      '">'
    );
  }

  /**
   * Round Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1536×1024 viewBox. Do not stretch this drawing to tiny
   * Round L/W differences. Do not recreate the facet paths in JS.
   */
  function proportionsRoundTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = roundTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-round-board" data-top-view-master-board data-top-view-master-svg="round" data-top-view-coordinate-system="svg-viewbox" data-top-view-overlap="false" data-top-view-table-above="false" data-top-view-width-edge="top" data-top-view-measurements="false" data-top-view-zones="dimensions,info">' +
      art +
      roundTopCover("table-top", 500, 40, 260, 42) +
      roundTopCover("width-top", 528, 46, 92, 30) +
      roundTopCover("dims-left", 12, 400, 168, 140) +
      roundTopCover("right-panel", 1088, 188, 448, 670);

    if (resolved.lengthMm != null) {
      html += roundTopDimLabel(
        "length",
        28,
        523.5,
        "Length:",
        formatPropNumber(resolved.lengthMm, 2) + " mm",
        "vertical",
        ' data-top-view-length-clearance="left-of-line" data-top-view-length-x="28"'
      );
    }
    if (resolved.widthMm != null) {
      html += roundTopDimLabel(
        "width",
        623.5,
        80,
        "Width:",
        formatPropNumber(resolved.widthMm, 2) + " mm",
        "horizontal",
        ' data-top-view-width-edge="top"'
      );
    }

    html +=
      '<div class="diamond-details__tech-round-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" style="' +
      roundTopBoxStyle(1108, 430, 412, 160) +
      '">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += roundTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += roundTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function ovalTopPct(value, axis) {
    var total = axis === "y" ? OVAL_TOP_VIEW_MASTER_VB_H : OVAL_TOP_VIEW_MASTER_VB_W;
    return (Number(value) / total) * 100;
  }

  function ovalTopBoxStyle(x, y, w, h) {
    return (
      "left:" +
      ovalTopPct(x, "x") +
      "%;top:" +
      ovalTopPct(y, "y") +
      "%;width:" +
      ovalTopPct(w, "x") +
      "%;height:" +
      ovalTopPct(h, "y") +
      "%;"
    );
  }

  function ovalTopLabelStyle(x, y) {
    return "left:" + ovalTopPct(x, "x") + "%;top:" + ovalTopPct(y, "y") + "%;";
  }

  function ovalTopMasterArt(options) {
    options = options || {};
    var url = ovalTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-oval-art' +
      (compact ? " diamond-details__media-thumb-oval-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="oval" data-top-view-master-asset="' +
      escapeHtml(OVAL_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      OVAL_TOP_VIEW_MASTER_VB_W +
      " " +
      OVAL_TOP_VIEW_MASTER_VB_H +
      '">'
    );
  }

  function ovalTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-oval-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function ovalTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-oval-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-oval-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-oval-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Oval Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   */
  function proportionsOvalTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = ovalTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-oval-board" data-top-view-master-board data-top-view-master-svg="oval" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="oval" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-zones="length,width,info">' +
      '<div class="diamond-details__tech-oval-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div>";

    if (resolved.lengthMm != null) {
      html += ovalTopLabel(
        "length",
        OVAL_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-oval-label--length"
      );
    }
    if (resolved.widthMm != null) {
      html += ovalTopLabel(
        "width",
        OVAL_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-oval-label--width"
      );
    }

    html +=
      '<div class="diamond-details__tech-oval-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += ovalTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += ovalTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function pearTopMasterArt(options) {
    options = options || {};
    var url = pearTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-pear-art' +
      (compact ? " diamond-details__media-thumb-pear-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="pear" data-top-view-master-asset="' +
      escapeHtml(PEAR_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      PEAR_TOP_VIEW_MASTER_VB_W +
      " " +
      PEAR_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied">'
    );
  }

  function pearTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-pear-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function pearTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-pear-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-pear-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-pear-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Pear Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   * Do not rotate/flip the supplied orientation.
   */
  function proportionsPearTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = pearTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-pear-board" data-top-view-master-board data-top-view-master-svg="pear" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="pear" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-width-attach="pear-tip" data-top-view-width-gap="20" data-top-view-zones="length,width,info">' +
      '<div class="diamond-details__tech-pear-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div>";

    if (resolved.lengthMm != null) {
      html += pearTopLabel(
        "length",
        PEAR_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-pear-label--length"
      );
    }
    if (resolved.widthMm != null) {
      html += pearTopLabel(
        "width",
        PEAR_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-pear-label--width"
      );
    }

    html +=
      '<div class="diamond-details__tech-pear-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += pearTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += pearTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function marquiseTopMasterArt(options) {
    options = options || {};
    var url = marquiseTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-marquise-art' +
      (compact ? " diamond-details__media-thumb-marquise-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="marquise" data-top-view-master-asset="' +
      escapeHtml(MARQUISE_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      MARQUISE_TOP_VIEW_MASTER_VB_W +
      " " +
      MARQUISE_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied">'
    );
  }

  function marquiseTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-marquise-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function marquiseTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-marquise-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-marquise-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-marquise-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Marquise Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   * Do not rotate/flip the supplied orientation.
   * Length is the long axis and sits TOP. Width sits LEFT.
   */
  function proportionsMarquiseTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = marquiseTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-marquise-board" data-top-view-master-board data-top-view-master-svg="marquise" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="marquise" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-axes="length-top,width-left" data-top-view-length-zone="top" data-top-view-width-zone="left" data-top-view-zones="length,width,info" data-top-view-annotation-spacing="attached" data-top-view-length-gap="' +
      MARQUISE_TOP_LENGTH_GAP_PX +
      '" data-top-view-width-gap="' +
      MARQUISE_TOP_WIDTH_GAP_PX +
      '" data-top-view-info-gap="' +
      MARQUISE_TOP_INFO_GAP_PX +
      '">' +
      '<div class="diamond-details__tech-marquise-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div>";

    if (resolved.lengthMm != null) {
      html += marquiseTopLabel(
        "length",
        MARQUISE_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-marquise-label--length"
      );
    }
    if (resolved.widthMm != null) {
      html += marquiseTopLabel(
        "width",
        MARQUISE_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-marquise-label--width"
      );
    }

    html +=
      '<div class="diamond-details__tech-marquise-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += marquiseTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += marquiseTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function cushionTopMasterArt(options) {
    options = options || {};
    var url = cushionTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-cushion-art' +
      (compact ? " diamond-details__media-thumb-cushion-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="cushion" data-top-view-master-asset="' +
      escapeHtml(CUSHION_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      CUSHION_TOP_VIEW_MASTER_VB_W +
      " " +
      CUSHION_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied" data-top-view-family="cushion">'
    );
  }

  function cushionTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-cushion-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function cushionTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-cushion-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-cushion-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-cushion-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Cushion Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   * One master for CUSHION / CUSHION BRILLIANT / CUSHION MODIFIED.
   * Width sits TOP. Length sits LEFT.
   */
  function proportionsCushionTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = cushionTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-cushion-board" data-top-view-master-board data-top-view-master-svg="cushion" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="cushion" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-family="cushion" data-top-view-axes="width-top,length-left" data-top-view-width-zone="top" data-top-view-length-zone="left" data-top-view-zones="length,width,info" data-top-view-annotation-spacing="attached" data-top-view-width-gap="' +
      CUSHION_TOP_WIDTH_GAP_PX +
      '" data-top-view-length-gap="' +
      CUSHION_TOP_LENGTH_GAP_PX +
      '" data-top-view-info-gap="' +
      CUSHION_TOP_INFO_GAP_PX +
      '">' +
      '<div class="diamond-details__tech-cushion-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div>";

    if (resolved.lengthMm != null) {
      html += cushionTopLabel(
        "length",
        CUSHION_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-cushion-label--length"
      );
    }
    if (resolved.widthMm != null) {
      html += cushionTopLabel(
        "width",
        CUSHION_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-cushion-label--width"
      );
    }

    html +=
      '<div class="diamond-details__tech-cushion-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += cushionTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += cushionTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function princessTopMasterArt(options) {
    options = options || {};
    var url = princessTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-princess-art' +
      (compact ? " diamond-details__media-thumb-princess-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="princess" data-top-view-master-asset="' +
      escapeHtml(PRINCESS_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      PRINCESS_TOP_VIEW_MASTER_VB_W +
      " " +
      PRINCESS_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied">'
    );
  }

  function princessTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-princess-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function princessTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-princess-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-princess-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-princess-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Princess Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   * Width sits TOP. Length sits LEFT.
   */
  function proportionsPrincessTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = princessTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-princess-board" data-top-view-master-board data-top-view-master-svg="princess" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="princess" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-axes="width-top,length-left" data-top-view-width-zone="top" data-top-view-length-zone="left" data-top-view-zones="length,width,info" data-top-view-annotation-spacing="attached" data-top-view-width-gap="' +
      PRINCESS_TOP_WIDTH_GAP_PX +
      '" data-top-view-length-gap="' +
      PRINCESS_TOP_LENGTH_GAP_PX +
      '" data-top-view-info-gap="' +
      PRINCESS_TOP_INFO_GAP_PX +
      '">';

    if (resolved.lengthMm != null) {
      html += princessTopLabel(
        "length",
        PRINCESS_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-princess-label--length"
      );
    }

    html +=
      '<div class="diamond-details__tech-princess-art-stack" data-top-view-art-stack>';
    if (resolved.widthMm != null) {
      html += princessTopLabel(
        "width",
        PRINCESS_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-princess-label--width"
      );
    }
    html +=
      '<div class="diamond-details__tech-princess-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div></div>";

    html +=
      '<div class="diamond-details__tech-princess-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += princessTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += princessTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function radiantTopMasterArt(options) {
    options = options || {};
    var url = radiantTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-radiant-art' +
      (compact ? " diamond-details__media-thumb-radiant-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="radiant" data-top-view-master-asset="' +
      escapeHtml(RADIANT_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      RADIANT_TOP_VIEW_MASTER_VB_W +
      " " +
      RADIANT_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied">'
    );
  }

  function radiantTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-radiant-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function radiantTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-radiant-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-radiant-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-radiant-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Radiant Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   * Width sits TOP. Length sits LEFT.
   */
  function proportionsRadiantTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = radiantTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-radiant-board" data-top-view-master-board data-top-view-master-svg="radiant" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="radiant" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-axes="width-top,length-left" data-top-view-width-zone="top" data-top-view-length-zone="left" data-top-view-zones="length,width,info" data-top-view-annotation-spacing="attached" data-top-view-width-gap="' +
      RADIANT_TOP_WIDTH_GAP_PX +
      '" data-top-view-length-gap="' +
      RADIANT_TOP_LENGTH_GAP_PX +
      '" data-top-view-info-gap="' +
      RADIANT_TOP_INFO_GAP_PX +
      '">';

    if (resolved.lengthMm != null) {
      html += radiantTopLabel(
        "length",
        RADIANT_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-radiant-label--length"
      );
    }

    html +=
      '<div class="diamond-details__tech-radiant-art-stack" data-top-view-art-stack>';
    if (resolved.widthMm != null) {
      html += radiantTopLabel(
        "width",
        RADIANT_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-radiant-label--width"
      );
    }
    html +=
      '<div class="diamond-details__tech-radiant-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div></div>";

    html +=
      '<div class="diamond-details__tech-radiant-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += radiantTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += radiantTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function heartTopMasterArt(options) {
    options = options || {};
    var url = heartTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-heart-art' +
      (compact ? " diamond-details__media-thumb-heart-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="heart" data-top-view-master-asset="' +
      escapeHtml(HEART_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      HEART_TOP_VIEW_MASTER_VB_W +
      " " +
      HEART_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied">'
    );
  }

  function heartTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-heart-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function heartTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-heart-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-heart-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-heart-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Heart Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not rotate/flip. Do not add dimension lines. Do not recreate the path in JS.
   * Width sits TOP (lobes/cleft). Length sits LEFT. Point remains at bottom.
   */
  function proportionsHeartTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = heartTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-heart-board" data-top-view-master-board data-top-view-master-svg="heart" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="heart" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-axes="width-top,length-left" data-top-view-width-zone="top" data-top-view-length-zone="left" data-top-view-zones="length,width,info" data-top-view-annotation-spacing="attached" data-top-view-width-gap="' +
      HEART_TOP_WIDTH_GAP_PX +
      '" data-top-view-length-gap="' +
      HEART_TOP_LENGTH_GAP_PX +
      '" data-top-view-info-gap="' +
      HEART_TOP_INFO_GAP_PX +
      '">';

    if (resolved.lengthMm != null) {
      html += heartTopLabel(
        "length",
        HEART_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-heart-label--length"
      );
    }

    html +=
      '<div class="diamond-details__tech-heart-art-stack" data-top-view-art-stack>';
    if (resolved.widthMm != null) {
      html += heartTopLabel(
        "width",
        HEART_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-heart-label--width"
      );
    }
    html +=
      '<div class="diamond-details__tech-heart-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div></div>";

    html +=
      '<div class="diamond-details__tech-heart-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += heartTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += heartTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function emeraldTopMasterArt(options) {
    options = options || {};
    var url = emeraldTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-emerald-art' +
      (compact ? " diamond-details__media-thumb-emerald-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="emerald" data-top-view-master-asset="' +
      escapeHtml(EMERALD_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      EMERALD_TOP_VIEW_MASTER_VB_W +
      " " +
      EMERALD_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied">'
    );
  }

  function emeraldTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-emerald-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function emeraldTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-emerald-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-emerald-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-emerald-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Emerald Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   * Do not reuse Radiant. Width sits TOP. Length sits LEFT.
   */
  function proportionsEmeraldTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = emeraldTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-emerald-board" data-top-view-master-board data-top-view-master-svg="emerald" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="emerald" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-axes="width-top,length-left" data-top-view-width-zone="top" data-top-view-length-zone="left" data-top-view-zones="length,width,info" data-top-view-annotation-spacing="attached" data-top-view-width-gap="' +
      EMERALD_TOP_WIDTH_GAP_PX +
      '" data-top-view-length-gap="' +
      EMERALD_TOP_LENGTH_GAP_PX +
      '" data-top-view-info-gap="' +
      EMERALD_TOP_INFO_GAP_PX +
      '">';

    if (resolved.lengthMm != null) {
      html += emeraldTopLabel(
        "length",
        EMERALD_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-emerald-label--length"
      );
    }

    html +=
      '<div class="diamond-details__tech-emerald-art-stack" data-top-view-art-stack>';
    if (resolved.widthMm != null) {
      html += emeraldTopLabel(
        "width",
        EMERALD_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-emerald-label--width"
      );
    }
    html +=
      '<div class="diamond-details__tech-emerald-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div></div>";

    html +=
      '<div class="diamond-details__tech-emerald-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += emeraldTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += emeraldTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function asscherTopMasterArt(options) {
    options = options || {};
    var url = asscherTopViewMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-asscher-art' +
      (compact ? " diamond-details__media-thumb-asscher-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-top-view-master-svg="asscher" data-top-view-master-asset="' +
      escapeHtml(ASSCHER_TOP_VIEW_MASTER_ASSET) +
      '" data-top-view-master-vb="' +
      ASSCHER_TOP_VIEW_MASTER_VB_W +
      " " +
      ASSCHER_TOP_VIEW_MASTER_VB_H +
      '" data-top-view-orientation="supplied">'
    );
  }

  function asscherTopLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-asscher-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-zone="' +
      escapeHtml(zone) +
      '" data-top-view-anchor="svg-viewbox" data-top-view-vb-x="' +
      escapeHtml(String(x)) +
      '" data-top-view-vb-y="' +
      escapeHtml(String(y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  function asscherTopPanelRow(field, key, value) {
    return (
      '<div class="diamond-details__tech-asscher-row" data-top-view-field="' +
      escapeHtml(field) +
      '" data-top-view-row="' +
      escapeHtml(field) +
      '"><span class="diamond-details__tech-asscher-key">' +
      escapeHtml(key) +
      '</span><span class="diamond-details__tech-asscher-val">' +
      escapeHtml(value) +
      "</span></div>"
    );
  }

  /**
   * Asscher Top View production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not stretch this drawing to stone L/W.
   * Do not add dimension lines. Do not recreate the path in JS.
   * Do not reuse Emerald. Width sits TOP. Length sits LEFT.
   */
  function proportionsAsscherTopMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = asscherTopMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-asscher-board" data-top-view-master-board data-top-view-master-svg="asscher" data-top-view-coordinate-system="svg-viewbox" data-top-view-presentation="visible-bbox" data-top-view-scale="asscher" data-top-view-art-fit="visible-bbox" data-top-view-dimension-lines="false" data-top-view-measurements="false" data-top-view-orientation="supplied" data-top-view-axes="width-top,length-left" data-top-view-width-zone="top" data-top-view-length-zone="left" data-top-view-zones="length,width,info" data-top-view-annotation-spacing="attached" data-top-view-width-gap="' +
      ASSCHER_TOP_WIDTH_GAP_PX +
      '" data-top-view-length-gap="' +
      ASSCHER_TOP_LENGTH_GAP_PX +
      '" data-top-view-info-gap="' +
      ASSCHER_TOP_INFO_GAP_PX +
      '">';

    if (resolved.lengthMm != null) {
      html += asscherTopLabel(
        "length",
        ASSCHER_TOP_ZONES.length,
        "Length: " + formatPropNumber(resolved.lengthMm, 2) + " mm",
        "diamond-details__tech-asscher-label--length"
      );
    }

    html +=
      '<div class="diamond-details__tech-asscher-art-stack" data-top-view-art-stack>';
    if (resolved.widthMm != null) {
      html += asscherTopLabel(
        "width",
        ASSCHER_TOP_ZONES.width,
        "Width: " + formatPropNumber(resolved.widthMm, 2) + " mm",
        "diamond-details__tech-asscher-label--width"
      );
    }
    html +=
      '<div class="diamond-details__tech-asscher-art-frame" data-top-view-art-frame data-top-view-art-fit="visible-bbox">' +
      art +
      "</div></div>";

    html +=
      '<div class="diamond-details__tech-asscher-panel" data-top-view-panel data-top-view-info="compact" data-top-view-info-fields="table,ratio" data-top-view-row-layout="columns" data-top-view-info-gap="compact" data-top-view-zone="right">';
    if (resolved.tablePct != null && options.omitTable !== true) {
      html += asscherTopPanelRow("table", "Table:", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.lengthWidthRatio != null) {
      html += asscherTopPanelRow("ratio", "L/W Ratio:", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    html += "</div></div>";
    return html;
  }

  function brilliantSidePct(value, axis) {
    var total = axis === "y" ? BRILLIANT_SIDE_PROFILE_MASTER_VB_H : BRILLIANT_SIDE_PROFILE_MASTER_VB_W;
    return (Number(value) / total) * 100;
  }

  function brilliantSideLabelStyle(x, y, extra) {
    return (
      "left:" +
      brilliantSidePct(x, "x") +
      "%;top:" +
      brilliantSidePct(y, "y") +
      "%;" +
      (extra || "")
    );
  }

  function brilliantSideMasterArt(options) {
    options = options || {};
    var url = brilliantSideProfileMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-side-art' +
      (compact ? " diamond-details__media-thumb-side-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-side-profile-master-svg="brilliant" data-side-profile-master-asset="' +
      escapeHtml(BRILLIANT_SIDE_PROFILE_MASTER_ASSET) +
      '" data-side-profile-master-vb="' +
      BRILLIANT_SIDE_PROFILE_MASTER_VB_W +
      " " +
      BRILLIANT_SIDE_PROFILE_MASTER_VB_H +
      '">'
    );
  }

  function brilliantSideLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    return (
      '<p class="diamond-details__tech-side-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-side-profile-field="' +
      escapeHtml(field) +
      '" data-side-profile-zone="' +
      escapeHtml(zone) +
      '" data-side-profile-anchor="svg-dimension-gap" data-side-profile-vb-x="' +
      escapeHtml(String(x)) +
      '" data-side-profile-vb-y="' +
      escapeHtml(String(y)) +
      '" style="' +
      brilliantSideLabelStyle(x, y) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  /**
   * Brilliant-family Side Profile production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not recreate the 12 vector paths in JS.
   * Shopper fields: Table, Crown Height, Pavilion Depth, Total Depth.
   * Crown Angle and Pavilion Angle are omitted even when valid.
   */
  function proportionsBrilliantSideMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = brilliantSideMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-side-board" data-side-profile-master-board data-side-profile-master-svg="brilliant" data-side-profile-coordinate-system="svg-viewbox" data-side-profile-angles="false" data-side-profile-compose-model="natural-fit">' +
      art;

    if (resolved.tablePct != null) {
      html += brilliantSideLabel(
        "table",
        BRILLIANT_SIDE_ZONES.table,
        "Table: " + formatPropNumber(resolved.tablePct, 2) + "%",
        "diamond-details__tech-side-label--table"
      );
    }
    if (resolved.crownHeight != null) {
      html += brilliantSideLabel(
        "crown-height",
        BRILLIANT_SIDE_ZONES.crownHeight,
        "Crown Height: " + formatPropNumber(resolved.crownHeight, 1) + "%",
        "diamond-details__tech-side-label--crown"
      );
    }
    if (resolved.pavilionDepth != null) {
      html += brilliantSideLabel(
        "pavilion-depth",
        BRILLIANT_SIDE_ZONES.pavilionDepth,
        "Pavilion Depth: " + formatPropNumber(resolved.pavilionDepth, 1) + "%",
        "diamond-details__tech-side-label--pavilion"
      );
    }
    if (resolved.depthPct != null || resolved.depthMm != null) {
      html +=
        '<div class="diamond-details__tech-side-depth" data-side-profile-field="depth" data-side-profile-zone="right" data-side-profile-anchor="svg-dimension-gap" data-side-profile-vb-x="' +
        escapeHtml(String(BRILLIANT_SIDE_ZONES.totalDepth.x)) +
        '" data-side-profile-vb-y="' +
        escapeHtml(String(BRILLIANT_SIDE_ZONES.totalDepth.y)) +
        '" style="' +
        brilliantSideLabelStyle(BRILLIANT_SIDE_ZONES.totalDepth.x, BRILLIANT_SIDE_ZONES.totalDepth.y) +
        '">';
      if (resolved.depthPct != null) {
        html +=
          '<p class="diamond-details__tech-side-depth-pct" data-side-profile-field="depth-pct">Total Depth: ' +
          escapeHtml(formatPropNumber(resolved.depthPct, 2)) +
          "%</p>";
      }
      if (resolved.depthMm != null) {
        html +=
          '<p class="diamond-details__tech-side-depth-mm" data-side-profile-field="depth-mm">' +
          escapeHtml(formatPropNumber(resolved.depthMm, 2)) +
          " mm</p>";
      }
      html += "</div>";
    }

    html += "</div>";
    return html;
  }

  function fancySidePct(value, axis) {
    var total = axis === "y" ? FANCY_SIDE_PROFILE_MASTER_VB_H : FANCY_SIDE_PROFILE_MASTER_VB_W;
    return (Number(value) / total) * 100;
  }

  function fancySideLabelStyle(x, y, extra) {
    return (
      "left:" +
      fancySidePct(x, "x") +
      "%;top:" +
      fancySidePct(y, "y") +
      "%;" +
      (extra || "")
    );
  }

  function fancySideIsLeftZone(zone) {
    return zone === "left-upper" || zone === "left-lower";
  }

  function fancySideLeftLabelStyle(x, y) {
    return (
      "left:max(" +
      FANCY_SIDE_LEFT_SAFE_PX +
      "px," +
      fancySidePct(x, "x") +
      "%);top:" +
      fancySidePct(y, "y") +
      "%;"
    );
  }

  function fancySideMasterArt(options) {
    options = options || {};
    var url = fancySideProfileMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-side-art' +
      (compact ? " diamond-details__media-thumb-side-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-side-profile-master-svg="fancy" data-side-profile-master-asset="' +
      escapeHtml(FANCY_SIDE_PROFILE_MASTER_ASSET) +
      '" data-side-profile-master-vb="' +
      FANCY_SIDE_PROFILE_MASTER_VB_W +
      " " +
      FANCY_SIDE_PROFILE_MASTER_VB_H +
      '">'
    );
  }

  function fancySideLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    var leftColumn = fancySideIsLeftZone(zone);
    return (
      '<p class="diamond-details__tech-side-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-side-profile-field="' +
      escapeHtml(field) +
      '" data-side-profile-zone="' +
      escapeHtml(zone) +
      '" data-side-profile-anchor="svg-viewbox"' +
      (leftColumn
        ? ' data-side-profile-align="start" data-side-profile-left-safe="' +
          FANCY_SIDE_LEFT_SAFE_PX +
          'px"'
        : "") +
      ' data-side-profile-vb-x="' +
      escapeHtml(String(x)) +
      '" data-side-profile-vb-y="' +
      escapeHtml(String(y)) +
      '" style="' +
      (leftColumn ? fancySideLeftLabelStyle(x, y) : fancySideLabelStyle(x, y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  /**
   * Pear / Marquise / Cushion family / Princess / Radiant / Heart
   * Fancy Side Profile production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not recreate the vector paths in JS.
   * Shopper fields: Table, Crown Height, Pavilion Depth, Total Depth.
   * Crown Angle and Pavilion Angle are omitted even when valid.
   */
  function proportionsFancySideMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = fancySideMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-side-board" data-side-profile-master-board data-side-profile-master-svg="fancy" data-side-profile-coordinate-system="svg-viewbox" data-side-profile-angles="false" data-side-profile-compose-model="natural-fit">' +
      art;

    if (resolved.tablePct != null) {
      html += fancySideLabel(
        "table",
        FANCY_SIDE_ZONES.table,
        "Table: " + formatPropNumber(resolved.tablePct, 2) + "%",
        "diamond-details__tech-side-label--table"
      );
    }
    if (resolved.crownHeight != null) {
      html += fancySideLabel(
        "crown-height",
        FANCY_SIDE_ZONES.crownHeight,
        "Crown Height: " + formatPropNumber(resolved.crownHeight, 1) + "%",
        "diamond-details__tech-side-label--crown"
      );
    }
    if (resolved.pavilionDepth != null) {
      html += fancySideLabel(
        "pavilion-depth",
        FANCY_SIDE_ZONES.pavilionDepth,
        "Pavilion Depth: " + formatPropNumber(resolved.pavilionDepth, 1) + "%",
        "diamond-details__tech-side-label--pavilion"
      );
    }
    if (resolved.depthPct != null || resolved.depthMm != null) {
      html +=
        '<div class="diamond-details__tech-side-depth" data-side-profile-field="depth" data-side-profile-zone="right" data-side-profile-anchor="svg-viewbox" data-side-profile-vb-x="' +
        escapeHtml(String(FANCY_SIDE_ZONES.totalDepth.x)) +
        '" data-side-profile-vb-y="' +
        escapeHtml(String(FANCY_SIDE_ZONES.totalDepth.y)) +
        '" style="' +
        fancySideLabelStyle(FANCY_SIDE_ZONES.totalDepth.x, FANCY_SIDE_ZONES.totalDepth.y) +
        '">';
      if (resolved.depthPct != null) {
        html +=
          '<p class="diamond-details__tech-side-depth-pct" data-side-profile-field="depth-pct">Total Depth: ' +
          escapeHtml(formatPropNumber(resolved.depthPct, 2)) +
          "%</p>";
      }
      if (resolved.depthMm != null) {
        html +=
          '<p class="diamond-details__tech-side-depth-mm" data-side-profile-field="depth-mm">' +
          escapeHtml(formatPropNumber(resolved.depthMm, 2)) +
          " mm</p>";
      }
      html += "</div>";
    }

    html += "</div>";
    return html;
  }

  function stepCutSidePct(value, axis) {
    var total = axis === "y" ? STEP_CUT_SIDE_PROFILE_MASTER_VB_H : STEP_CUT_SIDE_PROFILE_MASTER_VB_W;
    return (Number(value) / total) * 100;
  }

  function stepCutSideLabelStyle(x, y, extra) {
    return (
      "left:" +
      stepCutSidePct(x, "x") +
      "%;top:" +
      stepCutSidePct(y, "y") +
      "%;" +
      (extra || "")
    );
  }

  function stepCutSideIsLeftZone(zone) {
    return zone === "left-upper" || zone === "left-lower";
  }

  function stepCutSideLeftLabelStyle(x, y) {
    return (
      "left:max(" +
      STEP_CUT_SIDE_LEFT_SAFE_PX +
      "px," +
      stepCutSidePct(x, "x") +
      "%);top:" +
      stepCutSidePct(y, "y") +
      "%;"
    );
  }

  function stepCutSideMasterArt(options) {
    options = options || {};
    var url = stepCutSideProfileMasterUrl();
    var compact = options.compact === true;
    return (
      '<img class="diamond-details__tech-side-art' +
      (compact ? " diamond-details__media-thumb-side-master" : "") +
      '" src="' +
      escapeHtml(url) +
      '" alt="" decoding="async" draggable="false" data-side-profile-master-svg="step" data-side-profile-master-asset="' +
      escapeHtml(STEP_CUT_SIDE_PROFILE_MASTER_ASSET) +
      '" data-side-profile-master-vb="' +
      STEP_CUT_SIDE_PROFILE_MASTER_VB_W +
      " " +
      STEP_CUT_SIDE_PROFILE_MASTER_VB_H +
      '">'
    );
  }

  function stepCutSideLabel(field, zoneSpec, text, extraClass) {
    var zone = zoneSpec && zoneSpec.zone ? zoneSpec.zone : "";
    var x = zoneSpec && zoneSpec.x;
    var y = zoneSpec && zoneSpec.y;
    var leftColumn = stepCutSideIsLeftZone(zone);
    return (
      '<p class="diamond-details__tech-side-label' +
      (extraClass ? " " + extraClass : "") +
      '" data-side-profile-field="' +
      escapeHtml(field) +
      '" data-side-profile-zone="' +
      escapeHtml(zone) +
      '" data-side-profile-anchor="svg-viewbox"' +
      (leftColumn
        ? ' data-side-profile-align="start" data-side-profile-left-safe="' +
          STEP_CUT_SIDE_LEFT_SAFE_PX +
          'px"'
        : "") +
      ' data-side-profile-vb-x="' +
      escapeHtml(String(x)) +
      '" data-side-profile-vb-y="' +
      escapeHtml(String(y)) +
      '" style="' +
      (leftColumn ? stepCutSideLeftLabelStyle(x, y) : stepCutSideLabelStyle(x, y)) +
      '">' +
      escapeHtml(text) +
      "</p>"
    );
  }

  /**
   * Emerald / Asscher Step-Cut Side Profile production master.
   * Approved SVG geometry is static. Stage 5D values are HTML annotations
   * in the same 1200×720 viewBox. Do not recreate the vector paths in JS.
   * Shopper fields: Table, Crown Height, Pavilion Depth, Total Depth.
   * Crown Angle and Pavilion Angle are omitted even when valid.
   */
  function proportionsStepCutSideMasterSvg(resolved, options) {
    options = options || {};
    var compact = options.compact === true;
    var art = stepCutSideMasterArt({ compact: compact });
    if (compact) return art;

    var html =
      '<div class="diamond-details__tech-side-board" data-side-profile-master-board data-side-profile-master-svg="step" data-side-profile-coordinate-system="svg-viewbox" data-side-profile-angles="false" data-side-profile-compose-model="natural-fit">' +
      art;

    if (resolved.tablePct != null) {
      html += stepCutSideLabel(
        "table",
        STEP_CUT_SIDE_ZONES.table,
        "Table: " + formatPropNumber(resolved.tablePct, 2) + "%",
        "diamond-details__tech-side-label--table"
      );
    }
    if (resolved.crownHeight != null) {
      html += stepCutSideLabel(
        "crown-height",
        STEP_CUT_SIDE_ZONES.crownHeight,
        "Crown Height: " + formatPropNumber(resolved.crownHeight, 1) + "%",
        "diamond-details__tech-side-label--crown"
      );
    }
    if (resolved.pavilionDepth != null) {
      html += stepCutSideLabel(
        "pavilion-depth",
        STEP_CUT_SIDE_ZONES.pavilionDepth,
        "Pavilion Depth: " + formatPropNumber(resolved.pavilionDepth, 1) + "%",
        "diamond-details__tech-side-label--pavilion"
      );
    }
    if (resolved.depthPct != null || resolved.depthMm != null) {
      html +=
        '<div class="diamond-details__tech-side-depth" data-side-profile-field="depth" data-side-profile-zone="right" data-side-profile-anchor="svg-viewbox" data-side-profile-vb-x="' +
        escapeHtml(String(STEP_CUT_SIDE_ZONES.totalDepth.x)) +
        '" data-side-profile-vb-y="' +
        escapeHtml(String(STEP_CUT_SIDE_ZONES.totalDepth.y)) +
        '" style="' +
        stepCutSideLabelStyle(STEP_CUT_SIDE_ZONES.totalDepth.x, STEP_CUT_SIDE_ZONES.totalDepth.y) +
        '">';
      if (resolved.depthPct != null) {
        html +=
          '<p class="diamond-details__tech-side-depth-pct" data-side-profile-field="depth-pct">Total Depth: ' +
          escapeHtml(formatPropNumber(resolved.depthPct, 2)) +
          "%</p>";
      }
      if (resolved.depthMm != null) {
        html +=
          '<p class="diamond-details__tech-side-depth-mm" data-side-profile-field="depth-mm">' +
          escapeHtml(formatPropNumber(resolved.depthMm, 2)) +
          " mm</p>";
      }
      html += "</div>";
    }

    html += "</div>";
    return html;
  }

  function proportionsTopPath(shapeKey) {
    switch (shapeKey) {
      case "pear":
        return "M50 2 C74 8 93 28 93 58 C93 82 74 98 50 98 C26 98 7 82 7 58 C7 28 26 8 50 2 Z";
      case "marquise":
        return "M50 2 C80 22 98 40 98 50 C98 60 80 78 50 98 C20 78 2 60 2 50 C2 40 20 22 50 2 Z";
      case "heart":
        return "M50 96 C22 74 4 54 4 32 C4 16 16 6 30 6 C40 6 47 12 50 20 C53 12 60 6 70 6 C84 6 96 16 96 32 C96 54 78 74 50 96 Z";
      case "emerald":
        return "M18 4 H82 L96 18 V82 L82 96 H18 L4 82 V18 Z";
      case "asscher":
        return "M28 4 H72 L96 28 V72 L72 96 H28 L4 72 V28 Z";
      case "princess":
        return "M8 4 H92 L96 8 V92 L92 96 H8 L4 92 V8 Z";
      case "cushion-brilliant":
        return "M24 4 H76 Q96 4 96 24 V76 Q96 96 76 96 H24 Q4 96 4 76 V24 Q4 4 24 4 Z";
      case "cushion-modified":
        return "M16 4 H84 Q96 4 96 16 V84 Q96 96 84 96 H16 Q4 96 4 84 V16 Q4 4 16 4 Z";
      case "cushion":
        return "M20 4 H80 Q96 4 96 20 V80 Q96 96 80 96 H20 Q4 96 4 80 V20 Q4 4 20 4 Z";
      case "radiant":
        return "M16 4 H84 L96 16 V84 L84 96 H16 L4 84 V16 Z";
      case "round":
      case "oval":
      case "neutral":
      default:
        return "M50 2 A48 48 0 1 1 49.9 2 Z";
    }
  }

  function proportionsTopFacets(shapeKey) {
    if (shapeKey === "emerald" || shapeKey === "asscher" || shapeKey === "radiant") {
      return (
        '<g transform="translate(50 50) scale(0.74) translate(-50 -50)">' +
        '<path class="diamond-details__tech-facet" d="' +
        proportionsTopPath(shapeKey) +
        '"></path></g>' +
        '<g transform="translate(50 50) scale(0.48) translate(-50 -50)">' +
        '<path class="diamond-details__tech-facet" d="' +
        proportionsTopPath(shapeKey) +
        '"></path></g>'
      );
    }
    if (shapeKey === "princess") {
      return (
        '<path class="diamond-details__tech-facet" d="M18 18 L82 82 M82 18 L18 82"></path>' +
        '<path class="diamond-details__tech-facet" d="M22 8 H78 L92 22 V78 L78 92 H22 L8 78 V22 Z"></path>'
      );
    }
    return (
      '<path class="diamond-details__tech-facet" d="M50 10 L82 38 L50 90 L18 38 Z"></path>' +
      '<path class="diamond-details__tech-facet" d="M18 38 L50 10 L82 38 M18 38 L8 50 L18 62 M82 38 L92 50 L82 62 M50 90 L18 62 L82 62 Z"></path>'
    );
  }

  function proportionsTopViewSvg(resolved, options) {
    options = options || {};
    if (usesRoundTopMaster(resolved)) {
      return proportionsRoundTopMasterSvg(resolved, options);
    }
    var path = proportionsTopPath(resolved.shape.key);
    var tableScale = resolved.tablePct != null ? resolved.tablePct / 100 : 0;
    var html =
      '<svg class="diamond-details__tech-svg diamond-details__tech-svg--top" viewBox="0 0 100 100" preserveAspectRatio="none" focusable="false" aria-hidden="true">' +
      '<path class="diamond-details__tech-outline" d="' +
      path +
      '"></path>' +
      proportionsTopFacets(resolved.shape.key);
    if (tableScale > 0 && !options.omitTable) {
      html +=
        '<g transform="translate(50 50) scale(' +
        tableScale +
        ') translate(-50 -50)">' +
        '<path class="diamond-details__tech-table" d="' +
        path +
        '"></path></g>';
    }
    html += "</svg>";
    return html;
  }

  function svgText(x, y, className, value, anchor) {
    return (
      '<text class="' +
      className +
      '" x="' +
      x +
      '" y="' +
      y +
      '" text-anchor="' +
      (anchor || "start") +
      '">' +
      escapeHtml(value) +
      "</text>"
    );
  }

  function proportionsSideProfileSvg(resolved, options) {
    options = options || {};
    if (usesStepCutSideMaster(resolved)) {
      return proportionsStepCutSideMasterSvg(resolved, options);
    }
    if (usesFancySideMaster(resolved)) {
      return proportionsFancySideMasterSvg(resolved, options);
    }
    if (usesBrilliantSideMaster(resolved)) {
      return proportionsBrilliantSideMasterSvg(resolved, options);
    }
    var compact = options.compact === true;
    var annotate = options.annotate !== false && !compact;
    var tableScale = resolved.tablePct != null ? resolved.tablePct / 100 : 0.55;
    var step = resolved.sideFamily === "step";
    var cx = 158;
    var girdleHalf = 96;
    var tableHalf = girdleHalf * Math.max(0.36, Math.min(0.82, tableScale));
    var tableY = step ? 78 : 68;
    var girdleY = 122;
    var culetY = step ? 216 : 228;
    var girdleLeft = cx - girdleHalf;
    var girdleRight = cx + girdleHalf;
    var tableLeft = cx - tableHalf;
    var tableRight = cx + tableHalf;
    var viewBox = compact ? "40 52 236 188" : "0 0 360 280";
    var html =
      '<svg class="diamond-details__tech-svg diamond-details__tech-svg--side" viewBox="' +
      viewBox +
      '" preserveAspectRatio="xMidYMid meet" focusable="false" aria-hidden="true">';

    if (step) {
      html +=
        '<path class="diamond-details__tech-outline" d="M' +
        tableLeft +
        " " +
        tableY +
        " H" +
        tableRight +
        " L" +
        girdleRight +
        " " +
        girdleY +
        " H" +
        girdleLeft +
        ' Z"></path>' +
        '<path class="diamond-details__tech-outline" d="M' +
        girdleLeft +
        " " +
        girdleY +
        " L" +
        (girdleLeft + 22) +
        " " +
        culetY +
        " H" +
        (girdleRight - 22) +
        " L" +
        girdleRight +
        " " +
        girdleY +
        '"></path>' +
        '<path class="diamond-details__tech-facet" d="M' +
        (girdleLeft + 10) +
        " " +
        (girdleY + 28) +
        " H" +
        (girdleRight - 10) +
        " M" +
        (girdleLeft + 16) +
        " " +
        (girdleY + 56) +
        " H" +
        (girdleRight - 16) +
        '"></path>' +
        '<path class="diamond-details__tech-facet" d="M' +
        (tableLeft + 10) +
        " " +
        (tableY + 16) +
        " H" +
        (tableRight - 10) +
        '"></path>';
    }
    html +=
      '<path class="diamond-details__tech-midline" d="M' +
      girdleLeft +
      " " +
      girdleY +
      " H" +
      girdleRight +
      '"></path>';

    if (annotate) {
      if (resolved.tablePct != null) {
        html +=
          '<path class="diamond-details__tech-dimline" d="M' +
          tableLeft +
          " " +
          (tableY - 22) +
          " H" +
          tableRight +
          '"></path>' +
          '<path class="diamond-details__tech-dimline" d="M' +
          tableLeft +
          " " +
          (tableY - 26) +
          " V" +
          (tableY - 4) +
          " M" +
          tableRight +
          " " +
          (tableY - 26) +
          " V" +
          (tableY - 4) +
          '"></path>' +
          svgText(cx, tableY - 28, "diamond-details__tech-label", "Table: " + formatPropNumber(resolved.tablePct, 2) + "%", "middle");
      }
      if (resolved.depthPct != null || resolved.depthMm != null) {
        var depthX = girdleRight + 28;
        html +=
          '<path class="diamond-details__tech-dimline" d="M' +
          depthX +
          " " +
          tableY +
          " V" +
          culetY +
          '"></path>' +
          '<path class="diamond-details__tech-dimline" d="M' +
          (depthX - 5) +
          " " +
          tableY +
          " H" +
          (depthX + 5) +
          " M" +
          (depthX - 5) +
          " " +
          culetY +
          " H" +
          (depthX + 5) +
          '"></path>';
        if (resolved.depthPct != null) {
          html += svgText(
            depthX + 10,
            (tableY + culetY) / 2 - 6,
            "diamond-details__tech-label",
            "Depth: " + formatPropNumber(resolved.depthPct, 2) + "%"
          );
        }
        if (resolved.depthMm != null) {
          html += svgText(
            depthX + 10,
            (tableY + culetY) / 2 + 12,
            "diamond-details__tech-label",
            formatPropNumber(resolved.depthMm, 2) + " mm"
          );
        }
      }
      if (resolved.crownAngle != null || resolved.crownHeight != null) {
        var crownY = (tableY + girdleY) / 2;
        if (resolved.crownAngle != null) {
          html += svgText(
            girdleLeft - 8,
            crownY - 4,
            "diamond-details__tech-label diamond-details__tech-label--end",
            "Crown " + formatPropNumber(resolved.crownAngle, 1) + "\u00b0",
            "end"
          );
        }
        if (resolved.crownHeight != null) {
          html += svgText(
            girdleLeft - 8,
            crownY + 12,
            "diamond-details__tech-label diamond-details__tech-label--end",
            formatPropNumber(resolved.crownHeight, 1) + "%",
            "end"
          );
        }
      }
      if (resolved.pavilionAngle != null || resolved.pavilionDepth != null) {
        var pavilionY = (girdleY + culetY) / 2 + 6;
        if (resolved.pavilionAngle != null) {
          html += svgText(
            girdleLeft - 8,
            pavilionY,
            "diamond-details__tech-label diamond-details__tech-label--end",
            "Pavilion " + formatPropNumber(resolved.pavilionAngle, 1) + "\u00b0",
            "end"
          );
        }
        if (resolved.pavilionDepth != null) {
          html += svgText(
            girdleLeft - 8,
            pavilionY + 16,
            "diamond-details__tech-label diamond-details__tech-label--end",
            formatPropNumber(resolved.pavilionDepth, 1) + "%",
            "end"
          );
        }
      }
    }

    html += "</svg>";
    return html;
  }

  function topViewAccessibleText(diamond, resolved) {
    var parts = [formatShape(diamond && diamond.shape) || "Diamond", "top view"];
    if (resolved.lengthMm != null) parts.push("Length " + formatPropNumber(resolved.lengthMm, 2) + " mm");
    if (resolved.widthMm != null) parts.push("Width " + formatPropNumber(resolved.widthMm, 2) + " mm");
    if (resolved.lengthWidthRatio != null) parts.push("L/W Ratio " + formatPropNumber(resolved.lengthWidthRatio, 3));
    if (resolved.tablePct != null) parts.push("Table " + formatPropNumber(resolved.tablePct, 2) + "%");
    return parts.join(", ");
  }

  function sideProfileAccessibleText(diamond, resolved) {
    var parts = [formatShape(diamond && diamond.shape) || "Diamond", "side profile"];
    if (resolved.tablePct != null) parts.push("Table " + formatPropNumber(resolved.tablePct, 2) + "%");
    if (resolved.crownHeight != null) parts.push("Crown Height " + formatPropNumber(resolved.crownHeight, 1) + "%");
    if (resolved.pavilionDepth != null) parts.push("Pavilion Depth " + formatPropNumber(resolved.pavilionDepth, 1) + "%");
    if (resolved.depthPct != null) parts.push("Total Depth " + formatPropNumber(resolved.depthPct, 2) + "%");
    if (resolved.depthMm != null) parts.push(formatPropNumber(resolved.depthMm, 2) + " mm");
    return parts.join(", ");
  }

  function sizeHost() {
    return rootEl || (window.document && window.document.querySelector("[data-diamond-details]"));
  }

  function sizeHandUrl() {
    var host = sizeHost();
    return host ? String(host.getAttribute("data-size-hand") || "").trim() : "";
  }

  function sizeToneScope(root) {
    var scope = root || sizeHost() || (window.document && window.document);
    if (scope && scope.querySelector) {
      if (scope.querySelector("[data-size-media]") || scope.querySelector("[data-size-tone-slider]")) {
        return scope;
      }
      var viewport = scope.querySelector("[data-media-viewport]");
      if (viewport) return viewport;
    }
    return scope;
  }

  function isUsableSizeToneUrl(url) {
    var value = url == null ? "" : String(url).trim();
    if (!value) return false;
    if (/^javascript:/i.test(value)) return false;
    return true;
  }

  function sizeToneConfiguredUrl(tone) {
    var host = sizeHost();
    var n = normalizeSizeTone(tone);
    return host ? String(host.getAttribute("data-size-hand-tone-" + n) || "").trim() : "";
  }

  function sizeToneUrl(tone) {
    var url = sizeToneConfiguredUrl(tone);
    if (isUsableSizeToneUrl(url)) return url;
    url = sizeToneConfiguredUrl(SIZE_DEFAULT_SKIN_TONE);
    if (isUsableSizeToneUrl(url)) return url;
    url = sizeHandUrl();
    return isUsableSizeToneUrl(url) ? url : "";
  }

  function sizeTonePreloadStatus(url) {
    var entry = isUsableSizeToneUrl(url) ? sizeTonePreloadMap[String(url).trim()] : null;
    return entry && entry.status ? entry.status : "";
  }

  function sizeToneImageCtor() {
    if (window && typeof window.Image === "function") return window.Image;
    return null;
  }

  function decodeSizeToneUrl(url, cb) {
    var value = isUsableSizeToneUrl(url) ? String(url).trim() : "";
    var entry;
    var ImageCtor;
    var img;
    var finished = false;

    function done(ok) {
      var waiters;
      var i;
      if (finished) return;
      finished = true;
      entry.status = ok ? "ok" : "fail";
      waiters = entry.waiters || [];
      entry.waiters = [];
      for (i = 0; i < waiters.length; i += 1) waiters[i](ok);
    }

    if (!value) {
      if (cb) cb(false);
      return;
    }
    entry = sizeTonePreloadMap[value];
    if (entry && entry.status === "ok") {
      if (cb) cb(true);
      return;
    }
    if (entry && entry.status === "fail") {
      if (cb) cb(false);
      return;
    }
    if (entry && entry.status === "pending") {
      if (cb) entry.waiters.push(cb);
      return;
    }
    entry = { status: "pending", waiters: [] };
    sizeTonePreloadMap[value] = entry;
    if (cb) entry.waiters.push(cb);
    ImageCtor = sizeToneImageCtor();
    if (!ImageCtor) {
      done(true);
      return;
    }
    img = new ImageCtor();
    entry.img = img;
    img.onload = function () {
      if (typeof img.decode === "function") {
        img.decode().then(
          function () {
            done(true);
          },
          function () {
            done(true);
          }
        );
        return;
      }
      done(true);
    };
    img.onerror = function () {
      done(false);
    };
    img.src = value;
    if (img.complete && img.naturalWidth > 0) done(true);
  }

  function preloadSizeToneAssets() {
    var i;
    var url;
    for (i = 1; i <= SIZE_SKIN_TONE_COUNT; i += 1) {
      url = sizeToneUrl(i);
      if (isUsableSizeToneUrl(url)) decodeSizeToneUrl(url);
    }
  }

  function sizeToneDefault() {
    var host = sizeHost();
    var raw = host && host.getAttribute("data-size-hand-tone-default");
    var n = parseInt(raw, 10);
    if (n >= 1 && n <= SIZE_SKIN_TONE_COUNT) return n;
    return SIZE_DEFAULT_SKIN_TONE;
  }

  function countSizeToneAssets() {
    var count = 0;
    var i;
    var url;
    for (i = 1; i <= SIZE_SKIN_TONE_COUNT; i += 1) {
      url = sizeToneConfiguredUrl(i);
      if (!isUsableSizeToneUrl(url) && i === SIZE_DEFAULT_SKIN_TONE) url = sizeHandUrl();
      if (isUsableSizeToneUrl(url)) count += 1;
    }
    return count;
  }

  function normalizeSizeTone(tone) {
    var next = Math.round(Number(tone));
    if (!isFinite(next)) next = activeSizeTone || sizeToneDefault();
    return Math.max(1, Math.min(SIZE_SKIN_TONE_COUNT, next));
  }

  function getActiveSizeTone() {
    return activeSizeTone;
  }

  function isMobileSizeToneLayout() {
    return !!(window.matchMedia && window.matchMedia(SIZE_TONE_MOBILE_MQ).matches);
  }

  function isHorizontalSizeTone(control) {
    return !!(
      control &&
      typeof control.getAttribute === "function" &&
      control.getAttribute("aria-orientation") === "horizontal"
    );
  }

  function sizeToneLinear(tone) {
    return (normalizeSizeTone(tone) - 1) / (SIZE_SKIN_TONE_COUNT - 1);
  }

  /* Horizontal Carat education keeps the 0/25/50/75/100 light→dark
     mapping. Visual thumb travel is inset by one 16px-thumb radius so
     LIGHTER / DARKER stay fully inside the control. Vertical ON HAND
     still uses percent positioning; mobile endpoints only inset Tone 1
     and Tone 5. */
  function sizeTonePercent(tone, sliderEl) {
    var n = normalizeSizeTone(tone);
    var linear = sizeToneLinear(n) * 100;
    if (isHorizontalSizeTone(sliderEl)) return linear;
    if ((n !== 1 && n !== 5) || !isMobileSizeToneLayout()) return linear;
    var h = (sliderEl && sliderEl.clientHeight) || 72;
    var insetPct = (SIZE_TONE_MOBILE_INSET_PX / h) * 100;
    return n === 1 ? insetPct : 100 - insetPct;
  }

  function sizeToneHorizontalThumbCenterPx(tone, trackWidth) {
    var width = Number(trackWidth);
    var inset = SIZE_TONE_HORIZONTAL_INSET_PX;
    if (!(width > 0)) return inset;
    return inset + sizeToneLinear(tone) * Math.max(0, width - 2 * inset);
  }

  function syncSizeToneTicks(sliderEl, root) {
    var scope = sizeToneScope(root);
    var ticks = scope && scope.querySelectorAll
      ? scope.querySelectorAll(".diamond-details__size-tone-tick")
      : [];
    var list = ticks && typeof ticks.length === "number" ? ticks : ticks ? [ticks] : [];
    var horizontal = isHorizontalSizeTone(sliderEl);
    var i;
    var pct;
    for (i = 0; i < list.length; i += 1) {
      pct = sizeTonePercent(i + 1, sliderEl) + "%";
      if (horizontal) {
        list[i].style.left = pct;
        list[i].style.top = "";
      } else {
        list[i].style.top = pct;
        list[i].style.left = "";
      }
    }
  }

  function collectSizeToneSurfaces(root) {
    var host = sizeHost() || root;
    var surfaces = [];
    var viewport = host && host.querySelector ? host.querySelector("[data-media-viewport]") : null;
    var understand = host && host.querySelector ? host.querySelector("[data-understand-carat]") : null;
    if (viewport) surfaces.push(viewport);
    if (understand) surfaces.push(understand);
    if (!surfaces.length && host) surfaces.push(sizeToneScope(root));
    return surfaces;
  }

  function applySizeToneControl(control, next, enabled) {
    if (!control) return;
    control.setAttribute("aria-valuenow", String(next));
    control.setAttribute(
      "aria-valuetext",
      enabled ? next + " of " + SIZE_SKIN_TONE_COUNT : "Default hand"
    );
    control.setAttribute("data-size-tone-value", String(next));
    if (control.style && typeof control.style.setProperty === "function") {
      control.style.setProperty("--dd-size-tone-pct", sizeTonePercent(next, control) + "%");
      control.style.setProperty("--dd-size-tone-n", String(sizeToneLinear(next)));
    }
    syncSizeToneTicks(control, control.parentNode || control);
    if (enabled) {
      control.removeAttribute("aria-disabled");
      control.setAttribute("tabindex", "0");
    } else {
      control.setAttribute("aria-disabled", "true");
      control.setAttribute("tabindex", "-1");
    }
  }

  function hideSizeToneFade(fade) {
    if (!fade) return;
    if (fade.classList && typeof fade.classList.remove === "function") {
      fade.classList.remove("is-visible");
    }
  }

  function sizeToneElementUrl(el) {
    if (!el) return "";
    return String(el.getAttribute("src") || el.src || "").trim();
  }

  function applySizeToneHand(base, fade, nextUrl) {
    var currentUrl;
    var fadeUrl;
    var gen;
    if (!base) return;
    currentUrl = sizeToneElementUrl(base);
    fadeUrl = sizeToneElementUrl(fade);
    if (fade && !isUsableSizeToneUrl(fadeUrl) && isUsableSizeToneUrl(currentUrl)) {
      fade.setAttribute("src", currentUrl);
    }
    hideSizeToneFade(fade);
    if (!isUsableSizeToneUrl(nextUrl)) return;
    if (nextUrl === currentUrl) return;
    gen = (Number(base._sizeToneFadeGen) || 0) + 1;
    base._sizeToneFadeGen = gen;
    decodeSizeToneUrl(nextUrl, function (ok) {
      if (base._sizeToneFadeGen !== gen) return;
      if (!ok) return;
      base.setAttribute("src", nextUrl);
      if (fade) fade.setAttribute("src", nextUrl);
      hideSizeToneFade(fade);
    });
  }

  function applySizeTone(tone, root) {
    var next = normalizeSizeTone(tone);
    var nextUrl = sizeToneUrl(next);
    var enabled = countSizeToneAssets() >= 2;
    var surfaces = collectSizeToneSurfaces(root);
    var seenHands = [];
    var i;
    var scope;
    var control;
    var base;
    var fade;

    function applyHandOnce(hand, overlay) {
      var idx;
      if (!hand) return;
      for (idx = 0; idx < seenHands.length; idx += 1) {
        if (seenHands[idx] === hand) return;
      }
      seenHands.push(hand);
      applySizeToneHand(hand, overlay, nextUrl);
    }

    preloadSizeToneAssets();
    activeSizeTone = next;

    for (i = 0; i < surfaces.length; i += 1) {
      scope = surfaces[i];
      if (!scope || !scope.querySelector) continue;
      control = scope.querySelector("[data-size-tone-slider]");
      base = scope.querySelector("[data-size-hand-layer='base']") || scope.querySelector(".diamond-details__size-hand");
      fade = scope.querySelector("[data-size-hand-layer='fade']");
      applySizeToneControl(control, next, enabled);
      applyHandOnce(base, fade);
    }
    scope = sizeHost() || root;
    if (scope && scope.querySelectorAll) {
      var extraControls = scope.querySelectorAll("[data-size-tone-slider]");
      var extraBases = scope.querySelectorAll("[data-size-hand-layer='base']");
      var extraFades = scope.querySelectorAll("[data-size-hand-layer='fade']");
      var extraList = extraControls && extraControls.length != null ? extraControls : extraControls ? [extraControls] : [];
      var extraBaseList = extraBases && extraBases.length != null ? extraBases : extraBases ? [extraBases] : [];
      var extraFadeList = extraFades && extraFades.length != null ? extraFades : extraFades ? [extraFades] : [];
      for (i = 0; i < extraList.length; i += 1) applySizeToneControl(extraList[i], next, enabled);
      for (i = 0; i < extraBaseList.length; i += 1) {
        applyHandOnce(extraBaseList[i], extraFadeList[i] || null);
      }
    }
    return next;
  }

  function sizeToneFromPointer(control, clientX, clientY) {
    var rect = control && typeof control.getBoundingClientRect === "function"
      ? control.getBoundingClientRect()
      : null;
    var t;
    var inset;
    var usable;
    if (isHorizontalSizeTone(control)) {
      if (!rect || !rect.width) return activeSizeTone || sizeToneDefault();
      inset = SIZE_TONE_HORIZONTAL_INSET_PX;
      usable = Math.max(1, rect.width - 2 * inset);
      t = (clientX - rect.left - inset) / usable;
      t = Math.max(0, Math.min(1, t));
    } else {
      if (!rect || !rect.height) return activeSizeTone || sizeToneDefault();
      t = (clientY - rect.top) / rect.height;
    }
    return normalizeSizeTone(Math.round(t * (SIZE_SKIN_TONE_COUNT - 1)) + 1);
  }

  function sizeToneFromClientY(control, clientY) {
    return sizeToneFromPointer(control, 0, clientY);
  }

  function onSizeTonePointerDown(e) {
    var control = e && e.target && e.target.closest
      ? e.target.closest("[data-size-tone-slider]")
      : null;
    if (!control && e && e.currentTarget) control = e.currentTarget;
    if (!control || control.getAttribute("aria-disabled") === "true") return;
    if (e.button != null && e.button !== 0) return;
    if (e.preventDefault) e.preventDefault();
    sizeTonePointerBound = control;
    if (control.setPointerCapture && e.pointerId != null) {
      try {
        control.setPointerCapture(e.pointerId);
      } catch (err) {}
    }
    applySizeTone(sizeToneFromPointer(control, e.clientX, e.clientY));
    if (typeof control.focus === "function") control.focus();
  }

  function onSizeTonePointerMove(e) {
    if (!sizeTonePointerBound) return;
    applySizeTone(sizeToneFromPointer(sizeTonePointerBound, e.clientX, e.clientY));
  }

  function onSizeTonePointerUp() {
    sizeTonePointerBound = null;
  }

  function onSizeToneKeydown(e) {
    var control = e && e.target && e.target.closest
      ? e.target.closest("[data-size-tone-slider]")
      : null;
    if (!control && e && e.currentTarget) control = e.currentTarget;
    var current;
    var next;
    if (!control || control.getAttribute("aria-disabled") === "true") return;
    current = parseInt(control.getAttribute("data-size-tone-value") || "", 10) || activeSizeTone;
    if (e.key === "ArrowUp" || e.key === "ArrowLeft") next = current - 1;
    else if (e.key === "ArrowDown" || e.key === "ArrowRight") next = current + 1;
    else if (e.key === "Home") next = 1;
    else if (e.key === "End") next = SIZE_SKIN_TONE_COUNT;
    else return;
    if (e.preventDefault) e.preventDefault();
    applySizeTone(next);
  }

  function syncUnderstandCaratToneOrientation() {
    var vertical;
    var nodes;
    var list;
    var i;
    var control;
    var next;
    if (!window.document || typeof window.document.querySelectorAll !== "function" || !window.matchMedia) return;
    vertical = !!window.matchMedia(CARAT_TONE_VERTICAL_MQ).matches;
    nodes = window.document.querySelectorAll("[data-understand-carat-tone-slider]");
    list = nodes && typeof nodes.length === "number" ? nodes : nodes ? [nodes] : [];
    next = vertical ? "vertical" : "horizontal";
    for (i = 0; i < list.length; i += 1) {
      control = list[i];
      if (!control || typeof control.setAttribute !== "function") continue;
      if (control.getAttribute("aria-orientation") === next) continue;
      control.setAttribute("aria-orientation", next);
    }
  }

  function bindUnderstandCaratToneOrientation() {
    var mq;
    if (caratToneOrientationBound || !window.matchMedia) return;
    caratToneOrientationBound = true;
    mq = window.matchMedia(CARAT_TONE_VERTICAL_MQ);
    function resync() {
      syncUnderstandCaratToneOrientation();
      applySizeTone(activeSizeTone);
    }
    if (mq.addEventListener) mq.addEventListener("change", resync);
    else if (mq.addListener) mq.addListener(resync);
    syncUnderstandCaratToneOrientation();
  }

  function bindSizeToneLayoutSync() {
    if (sizeToneLayoutBound || !window.matchMedia) return;
    sizeToneLayoutBound = true;
    var mq = window.matchMedia(SIZE_TONE_MOBILE_MQ);
    function resync() {
      applySizeTone(activeSizeTone);
    }
    if (mq.addEventListener) mq.addEventListener("change", resync);
    else if (mq.addListener) mq.addListener(resync);
  }

  function bindSizeTone(region) {
    var surfaces = collectSizeToneSurfaces(region);
    var i;
    var scope;
    var control;
    bindSizeToneLayoutSync();
    bindUnderstandCaratToneOrientation();
    preloadSizeToneAssets();
    applySizeTone(activeSizeTone, region);
    for (i = 0; i < surfaces.length; i += 1) {
      scope = surfaces[i];
      control = scope && scope.querySelector ? scope.querySelector("[data-size-tone-slider]") : null;
      if (!control || control.getAttribute("data-size-tone-bound") === "true") continue;
      control.setAttribute("data-size-tone-bound", "true");
      control.addEventListener("pointerdown", onSizeTonePointerDown);
      control.addEventListener("pointermove", onSizeTonePointerMove);
      control.addEventListener("pointerup", onSizeTonePointerUp);
      control.addEventListener("pointercancel", onSizeTonePointerUp);
      control.addEventListener("keydown", onSizeToneKeydown);
    }
  }

  function sizeShapeUrl(shapeKey) {
    var host = sizeHost();
    if (!host || !shapeKey) return "";
    return String(host.getAttribute("data-size-shape-" + shapeKey) || "").trim();
  }

  function mediaCapabilities(diamond) {
    diamond = diamond || {};
    var geometry = diamond.geometry && typeof diamond.geometry === "object" ? diamond.geometry : null;
    var size = usableSizeMeasurements(diamond);
    var proportions = resolveProportions(diamond);
    return {
      diamond: { available: true, status: "ready" },
      actualSize: {
        available: !!size,
        status: size ? "ready" : "unavailable",
        shape: diamond.shape || "",
        carat: diamond.carat == null ? null : diamond.carat,
        lengthMm: size ? size.lengthMm : null,
        widthMm: size ? size.widthMm : null,
      },
      proportions: {
        available: proportions.available,
        status: proportions.status,
        hasGeometry: !!(
          geometry &&
          (geometry.lengthMm || geometry.widthMm || geometry.tablePct || geometry.depthPct)
        ),
        lengthMm: proportions.lengthMm,
        widthMm: proportions.widthMm,
        depthMm: proportions.depthMm,
        lengthWidthRatio: proportions.lengthWidthRatio,
        tablePct: proportions.tablePct,
        depthPct: proportions.depthPct,
      },
      sideProfile: {
        available: proportions.sideProfileAvailable,
        status: proportions.sideProfileAvailable ? "ready" : "unavailable",
      },
      topView: {
        available: proportions.topViewAvailable,
        status: proportions.topViewAvailable ? "ready" : "unavailable",
      },
    };
  }

  function isKnownMediaMode(mode) {
    for (var i = 0; i < MEDIA_MODES.length; i += 1) {
      if (MEDIA_MODES[i].id === mode) return true;
    }
    return false;
  }

  function mediaModeMeta(mode) {
    for (var i = 0; i < MEDIA_MODES.length; i += 1) {
      if (MEDIA_MODES[i].id === mode) return MEDIA_MODES[i];
    }
    return MEDIA_MODES[0];
  }

  /**
   * Concrete available views only. Preferred order:
   * 360 → Photo → Size → Side Profile → Top View → Certificate.
   * Certificate is a modal action, not a main-stage replacement.
   * Never invent empty cells or a generic Diamond / Proportions thumbnail
   * when concrete media exists.
   */
  function collectMediaGalleryItems(diamond) {
    var items = [];
    var caps = diamondViewCapabilities(diamond);
    var proportions = resolveProportions(diamond);
    if (caps.turntable) {
      items.push({
        id: GALLERY_ITEM_360,
        mode: MEDIA_MODE_DIAMOND,
        view: DIAMOND_VIEW_360,
        label: "360\u00b0 VIEW",
        accessibleName: "360\u00b0 View",
        capability: "ready",
        kind: "media",
      });
    }
    if (caps.photo) {
      items.push({
        id: GALLERY_ITEM_PHOTO,
        mode: MEDIA_MODE_DIAMOND,
        view: DIAMOND_VIEW_PHOTO,
        label: "DIAMOND VIEW",
        accessibleName: "Diamond View",
        capability: "ready",
        kind: "media",
      });
    }
    if (usableSizeMeasurements(diamond)) {
      items.push({
        id: MEDIA_MODE_ACTUAL_SIZE,
        mode: MEDIA_MODE_ACTUAL_SIZE,
        view: "",
        label: "ON HAND",
        accessibleName: "On Hand",
        capability: "ready",
        kind: "mode",
      });
    }
    if (proportions.sideProfileAvailable) {
      items.push({
        id: MEDIA_MODE_SIDE_PROFILE,
        mode: MEDIA_MODE_SIDE_PROFILE,
        view: "",
        label: "SIDE PROFILE",
        accessibleName: "Side Profile",
        capability: "ready",
        kind: "mode",
      });
    }
    if (proportions.topViewAvailable) {
      items.push({
        id: MEDIA_MODE_TOP_VIEW,
        mode: MEDIA_MODE_TOP_VIEW,
        view: "",
        label: "TOP VIEW",
        accessibleName: "Top View",
        capability: "ready",
        kind: "mode",
      });
    }
    if (gradingReport(diamond)) {
      var certLab = formatCertificateLabLabel(diamond);
      items.push({
        id: MEDIA_MODE_CERTIFICATE,
        mode: MEDIA_MODE_CERTIFICATE,
        view: "",
        label: "CERTIFICATE",
        accessibleName: certLab ? certLab + " Certificate" : "Certificate",
        capability: "ready",
        kind: "action",
      });
    }
    return items;
  }

  function fallbackGalleryItem() {
    return {
      id: MEDIA_MODE_TOP_VIEW,
      mode: MEDIA_MODE_TOP_VIEW,
      view: "",
      label: "TOP VIEW",
      accessibleName: "Top View",
      capability: "unavailable",
      kind: "mode",
    };
  }

  function resolveGalleryItem(diamond, requestedId) {
    var items = collectMediaGalleryItems(diamond);
    var i;
    if (requestedId) {
      for (i = 0; i < items.length; i += 1) {
        if (items[i].id === requestedId) return items[i];
      }
      if (requestedId === MEDIA_MODE_DIAMOND) {
        for (i = 0; i < items.length; i += 1) {
          if (items[i].mode === MEDIA_MODE_DIAMOND) return items[i];
        }
      }
      if (requestedId === MEDIA_MODE_PROPORTIONS) {
        for (i = 0; i < items.length; i += 1) {
          if (items[i].id === MEDIA_MODE_SIDE_PROFILE) return items[i];
        }
        for (i = 0; i < items.length; i += 1) {
          if (items[i].id === MEDIA_MODE_TOP_VIEW) return items[i];
        }
      }
    }
    return items[0] || fallbackGalleryItem();
  }

  function galleryItemForDiamondView(diamond, view) {
    if (view === DIAMOND_VIEW_360) return resolveGalleryItem(diamond, GALLERY_ITEM_360);
    if (view === DIAMOND_VIEW_PHOTO) return resolveGalleryItem(diamond, GALLERY_ITEM_PHOTO);
    return resolveGalleryItem(diamond, MEDIA_MODE_DIAMOND);
  }

  function diamond360PosterUrl(diamond) {
    var raw = toEngineV360(nestedMedia(diamond).v360);
    var Engine = window.DiamondFrameEngine;
    if (Engine && typeof Engine.resolveFrameMedia === "function" && typeof Engine.buildFrameUrl === "function") {
      var resolved = Engine.resolveFrameMedia(raw);
      if (resolved && resolved.isPlayable) {
        var built = Engine.buildFrameUrl(resolved, resolved.topIndex);
        if (built) return String(built);
      }
    }
    if (raw && Array.isArray(raw.frames) && raw.frames.length) {
      var frameIndex = raw.top_index != null ? Number(raw.top_index) : 0;
      if (!Number.isFinite(frameIndex) || frameIndex < 0) frameIndex = 0;
      if (frameIndex >= raw.frames.length) frameIndex = 0;
      var entry = raw.frames[frameIndex];
      if (entry && entry.url) return String(entry.url);
      if (typeof entry === "string" && entry) return entry;
    }
    if (raw && raw.url) {
      var base = String(raw.url).replace(/\/+$/, "");
      var top = raw.top_index != null ? Number(raw.top_index) : 0;
      if (!Number.isFinite(top) || top < 0) top = 0;
      return base + "/" + top + ".jpg";
    }
    return "";
  }

  function diamondGalleryPreview(diamond) {
    var caps = diamondViewCapabilities(diamond);
    if (caps.turntable) {
      var poster = diamond360PosterUrl(diamond);
      if (poster) return { kind: "image", src: poster, source: "360" };
    }
    var photo = mediaImageUrl(diamond);
    if (photo) return { kind: "image", src: photo, source: "photo" };
    return { kind: "silhouette", src: "", source: "silhouette" };
  }

  function renderMediaThumbVisual(item, diamond) {
    if (!item) return "";
    if (item.id === GALLERY_ITEM_360) {
      var poster = diamond360PosterUrl(diamond);
      if (poster) {
        return (
          '<img class="diamond-details__media-thumb-image" data-media-thumb-source="360" src="' +
          escapeHtml(poster) +
          '" alt="" decoding="async">'
        );
      }
      return (
        '<span class="diamond-details__media-thumb-silhouette" data-media-thumb-source="360" aria-hidden="true">' +
        SILHOUETTE_THUMB_SVG +
        "</span>"
      );
    }
    if (item.id === GALLERY_ITEM_PHOTO) {
      var photo = mediaImageUrl(diamond);
      if (photo) {
        return (
          '<img class="diamond-details__media-thumb-image" data-media-thumb-source="photo" src="' +
          escapeHtml(photo) +
          '" alt="" decoding="async">'
        );
      }
      return (
        '<span class="diamond-details__media-thumb-silhouette" data-media-thumb-source="photo" aria-hidden="true">' +
        SILHOUETTE_THUMB_SVG +
        "</span>"
      );
    }
    if (item.id === MEDIA_MODE_ACTUAL_SIZE) {
      var handUrl = sizeHandUrl();
      if (handUrl) {
        return (
          '<img class="diamond-details__media-thumb-image diamond-details__media-thumb-hand" data-media-thumb-source="hand" src="' +
          escapeHtml(handUrl) +
          '" alt="" decoding="async">'
        );
      }
      return '<span class="diamond-details__media-thumb-silhouette" data-media-thumb-source="hand-missing" aria-hidden="true"></span>';
    }
    if (item.id === MEDIA_MODE_SIDE_PROFILE) {
      var sideResolved = resolveProportions(diamond);
      if (usesStepCutSideMaster(sideResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--side diamond-details__media-thumb-diagram--side-master" data-media-thumb-source="side-profile-preview" data-media-thumb-master="step" data-media-thumb-preview-scale="expanded" aria-hidden="true">' +
          proportionsStepCutSideMasterSvg(sideResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesFancySideMaster(sideResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--side diamond-details__media-thumb-diagram--side-master" data-media-thumb-source="side-profile-preview" data-media-thumb-master="fancy" data-media-thumb-preview-scale="expanded" aria-hidden="true">' +
          proportionsFancySideMasterSvg(sideResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesBrilliantSideMaster(sideResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--side diamond-details__media-thumb-diagram--side-master" data-media-thumb-source="side-profile-preview" data-media-thumb-master="brilliant" data-media-thumb-preview-scale="expanded" aria-hidden="true">' +
          proportionsBrilliantSideMasterSvg(sideResolved, { compact: true }) +
          "</span>"
        );
      }
      return (
        '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--side" data-media-thumb-source="side-profile-preview" data-media-thumb-master="fallback" data-media-thumb-preview-scale="expanded" aria-hidden="true">' +
        SIDE_PROFILE_THUMB_SVG +
        "</span>"
      );
    }
    if (item.id === MEDIA_MODE_TOP_VIEW) {
      var topResolved = resolveProportions(diamond);
      if (usesAsscherTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--asscher-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="asscher" data-media-thumb-preview-scale="expanded" data-media-thumb-asscher-fit="visible-bbox" aria-hidden="true">' +
          proportionsAsscherTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesEmeraldTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--emerald-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="emerald" data-media-thumb-preview-scale="expanded" data-media-thumb-emerald-fit="visible-bbox" aria-hidden="true">' +
          proportionsEmeraldTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesHeartTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--heart-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="heart" data-media-thumb-preview-scale="expanded" data-media-thumb-heart-fit="visible-bbox" aria-hidden="true">' +
          proportionsHeartTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesRadiantTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--radiant-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="radiant" data-media-thumb-preview-scale="expanded" data-media-thumb-radiant-fit="visible-bbox" aria-hidden="true">' +
          proportionsRadiantTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesPrincessTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--princess-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="princess" data-media-thumb-preview-scale="expanded" data-media-thumb-princess-fit="visible-bbox" aria-hidden="true">' +
          proportionsPrincessTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesCushionTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--cushion-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="cushion" data-media-thumb-preview-scale="expanded" data-media-thumb-cushion-fit="visible-bbox" aria-hidden="true">' +
          proportionsCushionTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesMarquiseTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--marquise-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="marquise" data-media-thumb-preview-scale="expanded" data-media-thumb-marquise-fit="visible-bbox" aria-hidden="true">' +
          proportionsMarquiseTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesPearTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--pear-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="pear" data-media-thumb-preview-scale="expanded" data-media-thumb-pear-fit="visible-bbox" aria-hidden="true">' +
          proportionsPearTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesOvalTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--oval-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="oval" data-media-thumb-preview-scale="expanded" data-media-thumb-oval-fit="visible-bbox" aria-hidden="true">' +
          proportionsOvalTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      if (usesRoundTopMaster(topResolved)) {
        return (
          '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top diamond-details__media-thumb-diagram--round-master" data-media-thumb-source="top-view-preview" data-media-thumb-master="round" data-media-thumb-preview-scale="expanded" aria-hidden="true">' +
          proportionsRoundTopMasterSvg(topResolved, { compact: true }) +
          "</span>"
        );
      }
      var aspect = topResolved.drawingAspect != null ? topResolved.drawingAspect : 1;
      return (
        '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--top" data-media-thumb-source="top-view-preview" data-media-thumb-master="fallback" data-media-thumb-preview-scale="expanded" style="--dd-prop-aspect:' +
        escapeHtml(String(aspect)) +
        ';" aria-hidden="true">' +
        proportionsTopViewSvg(topResolved, { omitTable: true }) +
        "</span>"
      );
    }
    if (item.id === MEDIA_MODE_CERTIFICATE) {
      return renderCertificateThumbVisual(diamond);
    }
    return item.visualHtml || "";
  }

  function renderMediaThumbButton(item, activeMode) {
    var selected = item.id === activeMode;
    var isAction = item.kind === "action" || item.id === MEDIA_MODE_CERTIFICATE;
    var attrs =
      '<button type="button" class="diamond-details__media-thumb"' +
      ' id="diamond-details-media-tab-' +
      escapeHtml(item.id) +
      '" data-media-mode="' +
      escapeHtml(item.mode || item.id) +
      '" data-media-gallery-item="' +
      escapeHtml(item.id) +
      '"' +
      (item.view ? ' data-media-view="' + escapeHtml(item.view) + '"' : "") +
      ' data-media-capability="' +
      escapeHtml(item.capability || "ready") +
      '" aria-label="' +
      escapeHtml(item.accessibleName || item.label) +
      '"';
    if (isAction) {
      attrs +=
        ' aria-haspopup="dialog" aria-controls="diamond-details-report-dialog"';
    } else {
      attrs +=
        ' role="tab" aria-selected="' +
        (selected ? "true" : "false") +
        '" aria-controls="diamond-details-media-panel" tabindex="' +
        (selected ? "0" : "-1") +
        '"';
    }
    return (
      attrs +
      ">" +
      '<span class="diamond-details__media-thumb-visual" aria-hidden="true"' +
      (item.thumbSource
        ? ' data-media-thumb-source="' + escapeHtml(item.thumbSource) + '"'
        : "") +
      (item.thumbMaster
        ? ' data-media-thumb-master="' + escapeHtml(item.thumbMaster) + '"'
        : "") +
      ">" +
      (item.visualHtml || "") +
      "</span>" +
      '<span class="diamond-details__media-thumb-label">' +
      escapeHtml(item.label) +
      "</span></button>"
    );
  }

  function renderMediaGalleryMarkup(items, activeMode) {
    items = items || [];
    var tabs = [];
    var actions = [];
    var html =
      '<div class="diamond-details__media-gallery" data-media-gallery data-media-gallery-columns="' +
      MEDIA_GALLERY_COLUMNS +
      '">';
    var i;
    for (i = 0; i < items.length; i += 1) {
      if (items[i].kind === "action" || items[i].id === MEDIA_MODE_CERTIFICATE) {
        actions.push(items[i]);
      } else {
        tabs.push(items[i]);
      }
    }
    html +=
      '<div class="diamond-details__media-gallery-tabs" role="tablist" aria-label="Diamond media views">';
    for (i = 0; i < tabs.length; i += 1) {
      html += renderMediaThumbButton(tabs[i], activeMode);
    }
    html += "</div>";
    for (i = 0; i < actions.length; i += 1) {
      html += renderMediaThumbButton(actions[i], activeMode);
    }
    html += "</div>";
    return html;
  }

  function renderMediaGallery(diamond, activeMode) {
    var items = collectMediaGalleryItems(diamond);
    var decorated = [];
    var i;
    for (i = 0; i < items.length; i += 1) {
      decorated.push({
        id: items[i].id,
        mode: items[i].mode,
        view: items[i].view,
        label: items[i].label,
        accessibleName: items[i].accessibleName,
        capability: items[i].capability,
        kind: items[i].kind,
        visualHtml: renderMediaThumbVisual(items[i], diamond),
        thumbSource: mediaThumbSource(items[i], diamond),
        thumbMaster: mediaThumbMaster(items[i], diamond),
      });
    }
    return renderMediaGalleryMarkup(decorated, activeMode);
  }

  function mediaThumbMaster(item, diamond) {
    if (!item) return "";
    if (item.id === MEDIA_MODE_SIDE_PROFILE) {
      return sideProfileMasterKind(resolveProportions(diamond));
    }
    if (item.id === MEDIA_MODE_TOP_VIEW) {
      return topViewMasterKind(resolveProportions(diamond));
    }
    return "";
  }

  function mediaThumbSource(item, diamond) {
    if (!item) return "";
    if (item.thumbSource) return item.thumbSource;
    if (item.id === GALLERY_ITEM_360) return "360";
    if (item.id === GALLERY_ITEM_PHOTO) return "photo";
    if (item.id === MEDIA_MODE_ACTUAL_SIZE) return sizeHandUrl() ? "hand" : "hand-missing";
    if (item.id === MEDIA_MODE_SIDE_PROFILE) return "side-profile-preview";
    if (item.id === MEDIA_MODE_TOP_VIEW) return "top-view-preview";
    if (item.id === MEDIA_MODE_CERTIFICATE) return "certificate";
    return "";
  }

  function gradingReport(diamond) {
    var cert = diamond && diamond.certificate;
    if (!cert || cert.hasGradingReport !== true) return null;
    var url = cert.url == null ? "" : String(cert.url).trim();
    if (!url || !/^https?:\/\//i.test(url)) return null;
    return { url: url, number: cert.number || "" };
  }

  function certificateReportNumber(diamond) {
    var cert = diamond && diamond.certificate;
    var raw = cert && cert.number != null ? String(cert.number).trim() : "";
    if (!isMeaningful(raw)) return "";
    var upper = raw.toUpperCase();
    if (upper === "UNDEFINED" || upper === "NULL" || upper === "[]") return "";
    return raw;
  }

  function formatCertificateLabLabel(diamond) {
    var lab = gradingLab(diamond);
    if (!lab) return "";
    var upper = lab.toUpperCase();
    if (CERTIFICATE_KNOWN_LABS[upper]) return upper;
    return lab.replace(/\s+/g, " ").trim();
  }

  /**
   * Customer-facing laboratory name for the intentional external-report state.
   * HRD is presented as HRD Antwerp; other known labs keep their short label.
   */
  function formatCertificateExternalLabName(diamond) {
    var lab = formatCertificateLabLabel(diamond);
    if (!lab) return "the laboratory";
    if (lab.toUpperCase() === "HRD") return "HRD Antwerp";
    return lab;
  }

  /**
   * Narrow host/path allowlist for certificate URLs proven to reject third-party
   * iframe embedding (X-Frame-Options / framing policy). Keyed by URL capability,
   * not by semantic lab label alone.
   */
  function isKnownNonEmbeddableCertificateUrl(url) {
    var raw = url == null ? "" : String(url).trim();
    if (!raw || !/^https?:\/\//i.test(raw)) return false;
    var parsed = null;
    try {
      parsed = new URL(raw);
    } catch (err) {
      return false;
    }
    var host = String(parsed.hostname || "").toLowerCase();
    var path = String(parsed.pathname || "").toLowerCase();
    if (host === "my.hrdantwerp.com") return true;
    if (host === "api.igi.org" && /\/viewpdf\.php$/i.test(path)) return true;
    return false;
  }

  function renderCertificateThumbVisual(diamond) {
    var lab = formatCertificateLabLabel(diamond);
    return (
      '<span class="diamond-details__media-thumb-diagram diamond-details__media-thumb-diagram--certificate" data-media-thumb-source="certificate" aria-hidden="true">' +
      '<span class="diamond-details__media-thumb-cert">' +
      CERTIFICATE_THUMB_SVG +
      (lab
        ? '<span class="diamond-details__media-thumb-cert-lab">' + escapeHtml(lab) + "</span>"
        : "") +
      "</span></span>"
    );
  }

  function recordId(diamond) {
    return diamond && diamond.id != null && diamond.id !== "" ? String(diamond.id) : "";
  }

  /**
   * Smallest adapter from nested Detail payload → flat Saved snapshot shape.
   * Does not change DiamondSaved storage semantics.
   */
  function toSavedSnapshot(diamond) {
    diamond = diamond || {};
    var cert = diamond.certificate && typeof diamond.certificate === "object" ? diamond.certificate : {};
    var media = diamond.media && typeof diamond.media === "object" ? diamond.media : {};
    return {
      origin: diamond.origin,
      shape: diamond.shape,
      carat: diamond.carat,
      cut: diamond.cut,
      color: diamond.color,
      clarity: diamond.clarity,
      lab: diamond.lab,
      certificateNumber: cert.number,
      hasGradingReport: cert.hasGradingReport === true,
      retailPriceUsd: diamond.retailPriceUsd,
      image: media.image,
      certificateUrl: cert.url,
      v360: media.v360,
      preparedTurntable: media.preparedTurntable,
    };
  }

  function buildSavedRecord(diamond) {
    var Saved = window.DiamondSaved;
    var snapshot =
      Saved && typeof Saved.buildSnapshot === "function"
        ? Saved.buildSnapshot(toSavedSnapshot(diamond))
        : toSavedSnapshot(diamond);
    return { id: recordId(diamond), snapshot: snapshot };
  }

  function shopName() {
    var name = rootEl && rootEl.getAttribute("data-shop-name");
    return name && String(name).trim() ? String(name).trim() : "Ivanov Jewelry";
  }

  function setDocumentTitle(prefix) {
    if (!prefix) return;
    window.document.title = prefix + " \u2013 " + shopName();
  }

  function buildUrl(id) {
    var identity = window.IvanovAPI.normalizeDiamondId(id);
    return PAGE_PATH + "?id=" + encodeURIComponent(identity.bareUuid);
  }

  function canonicalSharePath(id) {
    try {
      return buildUrl(id);
    } catch (err) {
      return PAGE_PATH;
    }
  }

  function shareHref(id) {
    var path = canonicalSharePath(id);
    var origin = window.location && window.location.origin;
    if (origin && /^https?:\/\//i.test(String(origin))) return String(origin).replace(/\/+$/, "") + path;
    return path;
  }

  function parseIdFromLocation(locationLike) {
    var loc = locationLike || window.location;
    var search = loc && loc.search != null ? loc.search : "";
    try {
      return new URLSearchParams(search).get("id");
    } catch (err) {
      return null;
    }
  }

  function queryState(name) {
    return rootEl ? rootEl.querySelector('[data-state="' + name + '"]') : null;
  }

  /**
   * Inactive-state H1s must leave the document, not remain hidden.
   * SUCCESS / UNAVAILABLE render the identity H1 in the panel.
   * NOT FOUND / ERROR inject a heading into [data-state-heading] only
   * while that state is active. LOADING keeps the document H1-free.
   */
  function setHeadingSlot(region, text) {
    if (!region) return;
    var slot = region.querySelector("[data-state-heading]");
    if (!slot) return;
    slot.innerHTML = text
      ? '<h1 class="diamond-details__state-heading">' + escapeHtml(text) + "</h1>"
      : "";
  }

  function clearInactiveExperience(activeName) {
    var success = queryState(STATE_SUCCESS);
    var unavailable = queryState(STATE_UNAVAILABLE);
    if (activeName !== STATE_SUCCESS && success) {
      teardownDetailFrameHost(success);
      success.innerHTML = "";
    }
    if (activeName !== STATE_UNAVAILABLE && unavailable) {
      unavailable.innerHTML = "";
    }
  }

  function setPageState(name) {
    if (!rootEl) return;
    var nodes = rootEl.querySelectorAll("[data-state]");
    for (var i = 0; i < nodes.length; i += 1) {
      var node = nodes[i];
      var stateName = node.getAttribute("data-state");
      var active = stateName === name;
      node.hidden = !active;
      if (!active) setHeadingSlot(node, "");
    }
    clearInactiveExperience(name);
    if (name === STATE_NOT_FOUND) {
      setHeadingSlot(queryState(STATE_NOT_FOUND), "Diamond Not Found");
    } else if (name === STATE_ERROR) {
      setHeadingSlot(queryState(STATE_ERROR), "Unable to Load Diamond");
    }
    if (name === STATE_LOADING) {
      rootEl.setAttribute("aria-busy", "true");
    } else {
      rootEl.setAttribute("aria-busy", "false");
    }
  }

  /**
   * Scale 360 frames from the decoded image aspect, never from shape names.
   * Tall → height-limited, wide → width-limited, square → limiting side.
   * Safe area is applied once: content fills MEDIA_FILL (100%) of the
   * limiting viewport side. Does not crop supplier frames.
   */
  function fitMediaBox(viewportWidth, viewportHeight, aspect, safeArea) {
    var safe = safeArea == null ? MEDIA_SAFE_AREA : Number(safeArea);
    var vw = Number(viewportWidth);
    var vh = Number(viewportHeight);
    var ar = Number(aspect);
    if (!Number.isFinite(safe) || safe < 0) safe = MEDIA_SAFE_AREA;
    if (!Number.isFinite(vw) || !Number.isFinite(vh) || vw < 1 || vh < 1) return null;
    if (!Number.isFinite(ar) || ar <= 0) return null;
    var usableW = vw * (1 - 2 * safe);
    var usableH = vh * (1 - 2 * safe);
    if (usableW < 1 || usableH < 1) return null;
    var usableAspect = usableW / usableH;
    if (ar >= usableAspect) {
      return { width: usableW, height: usableW / ar, limitedBy: "width" };
    }
    return { width: usableH * ar, height: usableH, limitedBy: "height" };
  }

  function measurePaintBox(el) {
    if (!el) return { width: 0, height: 0 };
    return {
      width: Number(el.clientWidth) || 0,
      height: Number(el.clientHeight) || 0,
    };
  }

  /**
   * Frame Engine paints into absolutely-positioned layers. The host must
   * have a definite non-zero box. The media-normalization pass used
   * 100cqh on an indefinite-height size container, which collapsed the
   * canvas to 0×0 after the first decoded frame. This keeps a paint box
   * around the existing engine instead of rewriting it.
   */
  function ensureFrameHostPaintBox(stageEl, canvasEl) {
    var stageBox = measurePaintBox(stageEl);
    if (stageEl && (stageBox.width < 1 || stageBox.height < 1)) {
      if (stageEl.style && typeof stageEl.style.setProperty === "function") {
        if (stageBox.height < 1) stageEl.style.setProperty("height", FRAME_HOST_MIN_PX + "px");
        if (stageBox.width < 1) stageEl.style.setProperty("width", "100%");
      }
      if (typeof stageEl.setAttribute === "function") {
        stageEl.setAttribute("data-frame-host-ready", "fallback");
      }
    } else if (stageEl && typeof stageEl.setAttribute === "function") {
      stageEl.setAttribute("data-frame-host-ready", "layout");
    }
    var canvasBox = measurePaintBox(canvasEl);
    if (canvasEl && (canvasBox.width < 1 || canvasBox.height < 1)) {
      if (canvasEl.style && typeof canvasEl.style.setProperty === "function") {
        canvasEl.style.setProperty("width", "100%");
        canvasEl.style.setProperty("height", "100%");
        canvasEl.style.setProperty("min-height", FRAME_HOST_MIN_PX + "px");
        canvasEl.style.setProperty("max-width", "100%");
        canvasEl.style.setProperty("max-height", "100%");
      }
    }
    return measurePaintBox(stageEl);
  }

  function applyMediaAspect(canvasEl, img) {
    if (!canvasEl || !img) return null;
    var width = Number(img.naturalWidth);
    var height = Number(img.naturalHeight);
    if (!width || !height) return null;
    var aspect = width / height;
    var prev =
      canvasEl.getAttribute && typeof canvasEl.getAttribute === "function"
        ? Number(canvasEl.getAttribute("data-aspect-value"))
        : NaN;
    // Lock the first decoded frame. Later rotation frames may have a
    // slightly different bounding box; they contain-fit inside this box
    // so the viewport does not resize or clip mid-sequence.
    if (Number.isFinite(prev) && prev > 0) return prev;
    if (canvasEl.style && typeof canvasEl.style.setProperty === "function") {
      canvasEl.style.setProperty("--dd-media-aspect", String(aspect));
    }
    if (typeof canvasEl.setAttribute === "function") {
      canvasEl.setAttribute("data-aspect-ready", "");
      canvasEl.setAttribute("data-aspect-value", String(aspect));
    }
    return aspect;
  }

  function renderPhotoMedia(diamond, identity) {
    var imageUrl = mediaImageUrl(diamond);
    if (!imageUrl) return PLACEHOLDER_HTML;
    return (
      '<img class="diamond-details__image" data-media-fit="contain" src="' +
      escapeHtml(imageUrl) +
      '" alt="' +
      escapeHtml(identity) +
      '">'
    );
  }

  function renderFrameStage() {
    return (
      '<div class="diamond-details__frame-stage" data-detail-frame-stage>' +
      '<div class="diamond-details__frame-canvas" data-detail-frame-canvas></div></div>'
    );
  }

  function render360Badge() {
    return (
      '<span class="diamond-details__media-360-badge" data-360-badge data-360-badge-style="pill" aria-hidden="true">' +
      '<svg class="diamond-details__media-360-icon" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true" focusable="false">' +
      '<path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M13.25 8A5.25 5.25 0 1 1 11.1 3.65"/>' +
      '<path fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" d="M11.3 2.1l.2 2.35 2.15-.85"/>' +
      "</svg>" +
      '<span class="diamond-details__media-360-text">360\u00b0</span>' +
      "</span>"
    );
  }

  function renderDiamondViewport(diamond, identity, view) {
    var inner =
      view === DIAMOND_VIEW_360
        ? render360Badge() + renderFrameStage()
        : view === DIAMOND_VIEW_PHOTO
          ? renderPhotoMedia(diamond, identity)
          : PLACEHOLDER_HTML;
    return (
      '<div class="diamond-details__diamond-viewport" data-diamond-viewport data-diamond-view="' +
      escapeHtml(view) +
      '">' +
      inner +
      "</div>"
    );
  }

  function renderDiamondSubControl(activeView) {
    function button(view, label) {
      var selected = view === activeView;
      return (
        '<button type="button" class="diamond-details__diamond-view" data-diamond-subview="' +
        view +
        '" aria-pressed="' +
        (selected ? "true" : "false") +
        '">' +
        escapeHtml(label) +
        "</button>"
      );
    }
    return (
      '<div class="diamond-details__diamond-views" data-diamond-views role="group" aria-label="Diamond media">' +
      button(DIAMOND_VIEW_360, "360\u00b0") +
      button(DIAMOND_VIEW_PHOTO, "Photo") +
      "</div>"
    );
  }

  function renderDiamondMedia(diamond, identity, activeView) {
    var caps = diamondViewCapabilities(diamond);
    var view = activeView || caps.defaultView;
    if (view === DIAMOND_VIEW_360 && !caps.turntable) view = caps.defaultView;
    if (view === DIAMOND_VIEW_PHOTO && !caps.photo) view = caps.defaultView;
    return renderDiamondViewport(diamond, identity, view);
  }

  function renderFutureMedia(mode) {
    var meta = mediaModeMeta(mode);
    return (
      '<div class="diamond-details__media-future" data-media-future="' +
      escapeHtml(meta.id) +
      '" data-media-ready="false">' +
      '<p class="diamond-details__media-future-copy">' +
      "This view arrives in a later stage." +
      "</p></div>"
    );
  }

  function renderTechUnavailable(kind, copy) {
    return (
      '<div class="diamond-details__tech diamond-details__tech--' +
      escapeHtml(kind) +
      '" data-' +
      escapeHtml(kind) +
      ' data-' +
      escapeHtml(kind) +
      '-ready="false">' +
      '<p class="diamond-details__tech-unavailable">' +
      escapeHtml(copy) +
      "</p></div>"
    );
  }

  function techDataAttrs(resolved, extras) {
    var html =
      ' data-proportions-schematic="true" data-proportions-shape="' +
      escapeHtml(resolved.shape.key) +
      '" data-proportions-side-family="' +
      escapeHtml(resolved.sideFamily) +
      '"';
    if (resolved.lengthMm != null) {
      html += ' data-proportions-length-mm="' + escapeHtml(String(resolved.lengthMm)) + '"';
    }
    if (resolved.widthMm != null) {
      html += ' data-proportions-width-mm="' + escapeHtml(String(resolved.widthMm)) + '"';
    }
    if (resolved.depthMm != null) {
      html += ' data-proportions-depth-mm="' + escapeHtml(String(resolved.depthMm)) + '"';
    }
    if (resolved.drawingAspect != null) {
      html += ' data-proportions-aspect="' + escapeHtml(String(resolved.drawingAspect)) + '"';
    }
    if (resolved.lengthWidthRatio != null) {
      html += ' data-proportions-ratio="' + escapeHtml(String(resolved.lengthWidthRatio)) + '"';
    }
    if (resolved.tablePct != null) {
      html += ' data-proportions-table="' + escapeHtml(String(resolved.tablePct)) + '"';
    }
    if (resolved.depthPct != null) {
      html += ' data-proportions-depth="' + escapeHtml(String(resolved.depthPct)) + '"';
    }
    if (resolved.crownAngle != null) {
      html += ' data-proportions-crown-angle="' + escapeHtml(String(resolved.crownAngle)) + '"';
    }
    if (resolved.crownHeight != null) {
      html += ' data-proportions-crown-height="' + escapeHtml(String(resolved.crownHeight)) + '"';
    }
    if (resolved.pavilionAngle != null) {
      html += ' data-proportions-pavilion-angle="' + escapeHtml(String(resolved.pavilionAngle)) + '"';
    }
    if (resolved.pavilionDepth != null) {
      html += ' data-proportions-pavilion-depth="' + escapeHtml(String(resolved.pavilionDepth)) + '"';
    }
    return html + (extras || "");
  }

  function renderTopViewLegend(resolved) {
    var html = "";
    if (resolved.widthMm != null) {
      html +=
        '<p class="diamond-details__tech-legend-item" data-top-view-field="width">Width: ' +
        escapeHtml(formatPropNumber(resolved.widthMm, 2)) +
        " mm</p>";
    }
    if (resolved.lengthMm != null) {
      html +=
        '<p class="diamond-details__tech-legend-item" data-top-view-field="length">Length: ' +
        escapeHtml(formatPropNumber(resolved.lengthMm, 2)) +
        " mm</p>";
    }
    if (resolved.tablePct != null) {
      html +=
        '<p class="diamond-details__tech-legend-item" data-top-view-field="table">Table: ' +
        escapeHtml(formatPropNumber(resolved.tablePct, 2)) +
        "%</p>";
    }
    if (resolved.lengthWidthRatio != null) {
      html +=
        '<p class="diamond-details__tech-legend-item" data-top-view-field="ratio">L/W Ratio: ' +
        escapeHtml(formatPropNumber(resolved.lengthWidthRatio, 3)) +
        "</p>";
    }
    if (resolved.lengthMm != null && resolved.widthMm != null) {
      html +=
        '<p class="diamond-details__tech-legend-item" data-top-view-field="measurements">Measurements: ' +
        escapeHtml(formatPropNumber(resolved.lengthMm, 2)) +
        " \u00d7 " +
        escapeHtml(formatPropNumber(resolved.widthMm, 2)) +
        " mm</p>";
    }
    return html;
  }

  function renderSideProfileMedia(diamond) {
    var resolved = resolveProportions(diamond);
    if (!resolved.sideProfileAvailable) {
      return renderTechUnavailable("side-profile", "Side profile unavailable");
    }
    var master = sideProfileMasterKind(resolved);
    if (master === TECH_MASTER_STEP_CUT_SIDE) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--side diamond-details__tech--step-cut-master" data-side-profile data-side-profile-ready="true" data-side-profile-master="' +
        master +
        '"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-side-profile-figure>' +
        proportionsStepCutSideMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(sideProfileAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (master === TECH_MASTER_FANCY_SIDE) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--side diamond-details__tech--fancy-master" data-side-profile data-side-profile-ready="true" data-side-profile-master="' +
        master +
        '"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-side-profile-figure>' +
        proportionsFancySideMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(sideProfileAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (master === TECH_MASTER_BRILLIANT_SIDE) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--side diamond-details__tech--brilliant-master" data-side-profile data-side-profile-ready="true" data-side-profile-master="' +
        master +
        '"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-side-profile-figure>' +
        proportionsBrilliantSideMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(sideProfileAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    return (
      '<div class="diamond-details__tech diamond-details__tech--side" data-side-profile data-side-profile-ready="true" data-side-profile-master="' +
      master +
      '"' +
      techDataAttrs(resolved) +
      ">" +
      '<figure class="diamond-details__tech-figure" data-side-profile-figure>' +
      '<div class="diamond-details__tech-stage diamond-details__tech-stage--side" data-side-profile-stage>' +
      proportionsSideProfileSvg(resolved) +
      "</div></figure>" +
      '<p class="diamond-details__sr-only">' +
      escapeHtml(sideProfileAccessibleText(diamond, resolved)) +
      "</p></div>"
    );
  }

  function renderTopViewMedia(diamond) {
    var resolved = resolveProportions(diamond);
    if (!resolved.topViewAvailable) {
      return renderTechUnavailable("top-view", "Top view unavailable");
    }
    if (usesAsscherTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--asscher-master" data-top-view data-top-view-ready="true" data-top-view-master="asscher"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsAsscherTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesEmeraldTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--emerald-master" data-top-view data-top-view-ready="true" data-top-view-master="emerald"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsEmeraldTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesHeartTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--heart-master" data-top-view data-top-view-ready="true" data-top-view-master="heart"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsHeartTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesRadiantTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--radiant-master" data-top-view data-top-view-ready="true" data-top-view-master="radiant"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsRadiantTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesPrincessTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--princess-master" data-top-view data-top-view-ready="true" data-top-view-master="princess"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsPrincessTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesCushionTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--cushion-master" data-top-view data-top-view-ready="true" data-top-view-master="cushion"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsCushionTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesMarquiseTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--marquise-master" data-top-view data-top-view-ready="true" data-top-view-master="marquise"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsMarquiseTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesPearTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--pear-master" data-top-view data-top-view-ready="true" data-top-view-master="pear"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsPearTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesOvalTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--oval-master" data-top-view data-top-view-ready="true" data-top-view-master="oval"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsOvalTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    if (usesRoundTopMaster(resolved)) {
      return (
        '<div class="diamond-details__tech diamond-details__tech--top diamond-details__tech--round-master" data-top-view data-top-view-ready="true" data-top-view-master="round"' +
        techDataAttrs(resolved) +
        ">" +
        '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
        proportionsRoundTopMasterSvg(resolved) +
        "</figure>" +
        '<p class="diamond-details__sr-only">' +
        escapeHtml(topViewAccessibleText(diamond, resolved)) +
        "</p></div>"
      );
    }
    var callouts = "";
    if (resolved.widthMm != null) {
      callouts +=
        '<p class="diamond-details__tech-callout diamond-details__tech-callout--width" data-top-view-field="width">Width: ' +
        escapeHtml(formatPropNumber(resolved.widthMm, 2)) +
        " mm</p>";
    }
    if (resolved.lengthMm != null) {
      callouts +=
        '<p class="diamond-details__tech-callout diamond-details__tech-callout--length" data-top-view-field="length">Length: ' +
        escapeHtml(formatPropNumber(resolved.lengthMm, 2)) +
        " mm</p>";
    }
    if (resolved.lengthWidthRatio != null) {
      callouts +=
        '<p class="diamond-details__tech-callout diamond-details__tech-callout--ratio" data-top-view-field="ratio">L/W Ratio: ' +
        escapeHtml(formatPropNumber(resolved.lengthWidthRatio, 3)) +
        "</p>";
    }
    if (resolved.tablePct != null) {
      callouts +=
        '<p class="diamond-details__tech-callout diamond-details__tech-callout--table" data-top-view-field="table">Table: ' +
        escapeHtml(formatPropNumber(resolved.tablePct, 2)) +
        "%</p>";
    }
    return (
      '<div class="diamond-details__tech diamond-details__tech--top" data-top-view data-top-view-ready="true" data-top-view-master="fallback"' +
      techDataAttrs(resolved, ' style="--dd-prop-aspect:' + escapeHtml(String(resolved.drawingAspect)) + ';"') +
      ">" +
      '<figure class="diamond-details__tech-figure" data-top-view-figure>' +
      '<div class="diamond-details__tech-board">' +
      callouts +
      '<div class="diamond-details__tech-stage diamond-details__tech-stage--top" data-top-view-stage>' +
      proportionsTopViewSvg(resolved) +
      "</div></div></figure>" +
      '<p class="diamond-details__sr-only">' +
      escapeHtml(topViewAccessibleText(diamond, resolved)) +
      "</p></div>"
    );
  }

  function renderSizeUnavailable() {
    return (
      '<div class="diamond-details__size" data-size-view data-size-ready="false">' +
      '<p class="diamond-details__size-unavailable">Size visualization unavailable</p>' +
      "</div>"
    );
  }

  function sizeAccessibleText(diamond, measurements) {
    var shapeLabel = formatShape(diamond && diamond.shape) || "Diamond";
    var caratLabel = formatCarat(diamond && diamond.carat);
    var mm =
      formatSizeMm(measurements.lengthMm) +
      " by " +
      formatSizeMm(measurements.widthMm) +
      " millimeters";
    return (caratLabel ? caratLabel + " carat " : "") + shapeLabel + ", " + mm;
  }

  function renderSizeMedia(diamond) {
    var measurements = usableSizeMeasurements(diamond);
    var handUrl = sizeHandUrl();
    if (!measurements || !handUrl) return renderSizeUnavailable();

    var metrics = sizeOverlayMetrics(measurements.lengthMm, measurements.widthMm);
    if (!metrics) return renderSizeUnavailable();

    var shapeKey = resolveSizeShape(diamond && diamond.shape);
    var shapeUrl = shapeKey ? sizeShapeUrl(shapeKey) : "";
    var caratLabel = formatCarat(diamond && diamond.carat);
    var shapeLabel = formatShape(diamond && diamond.shape);
    var identityLine = [caratLabel && caratLabel + " ct", shapeLabel].filter(Boolean).join(" ");
    var mmLine = formatSizeMm(metrics.lengthMm) + " \u00d7 " + formatSizeMm(metrics.widthMm) + " mm";
    var visibleLw = sizeVisibleRatio(metrics.lengthMm, metrics.widthMm);
    var shadowFrac = metrics.widthMm * SIZE_NATIVE_PX_PER_MM / SIZE_HAND_NATIVE_WIDTH;
    var tone = normalizeSizeTone(activeSizeTone);
    var toneUrl = sizeToneUrl(tone) || handUrl;
    var toneEnabled = countSizeToneAssets() >= 2;
    var tonePct = sizeTonePercent(tone);
    var tickHtml = "";
    var tki;
    var mediaStyle =
      "--dd-size-hand-native-w:" +
      SIZE_HAND_NATIVE_WIDTH +
      ";--dd-size-hand-native-h:" +
      SIZE_HAND_NATIVE_HEIGHT +
      ";--dd-size-hand-max-w:" +
      SIZE_HAND_DISPLAY_MAX_WIDTH +
      "px;--dd-size-native-px-per-mm:" +
      SIZE_NATIVE_PX_PER_MM +
      ";--dd-size-anchor-x:" +
      SIZE_ANCHOR_X +
      ";--dd-size-anchor-y:" +
      SIZE_ANCHOR_Y +
      ";--dd-size-length-mm:" +
      metrics.lengthMm +
      ";--dd-size-width-mm:" +
      metrics.widthMm +
      ";--dd-size-width-pct:" +
      metrics.widthPct +
      ";--dd-size-height-pct:" +
      metrics.heightPct +
      ";--dd-size-shadow-frac:" +
      shadowFrac;

    var html =
      '<div class="diamond-details__size" data-size-view data-size-ready="true" data-size-shape="' +
      escapeHtml(shapeKey) +
      '" data-size-length-mm="' +
      escapeHtml(String(metrics.lengthMm)) +
      '" data-size-width-mm="' +
      escapeHtml(String(metrics.widthMm)) +
      '" data-size-width-pct="' +
      escapeHtml(String(metrics.widthPct)) +
      '" data-size-height-pct="' +
      escapeHtml(String(metrics.heightPct)) +
      '" data-size-visible-lw="' +
      escapeHtml(String(visibleLw)) +
      '"' +
      (sizeShapeIsMaster(shapeKey) ? ' data-size-shape-master="true"' : "") +
      ">" +
      '<div class="diamond-details__size-frame">' +
      '<div class="diamond-details__size-media" data-size-media style="' +
      mediaStyle +
      '">' +
      '<div class="diamond-details__size-hands" aria-hidden="true">' +
      '<img class="diamond-details__size-hand" data-size-hand data-size-hand-layer="base" src="' +
      escapeHtml(toneUrl) +
      '" width="859" height="1024" alt="" decoding="async">' +
      '<img class="diamond-details__size-hand diamond-details__size-hand--fade" data-size-hand-layer="fade" src="' +
      escapeHtml(toneUrl) +
      '" width="859" height="1024" alt="" decoding="async">' +
      "</div>";
    if (shapeUrl) {
      html +=
        '<span class="diamond-details__size-contact-shadow" data-size-contact-shadow aria-hidden="true"></span>' +
        '<img class="diamond-details__size-stone' +
        (shapeKey === "pear" ? " diamond-details__size-stone--pear" : "") +
        '" data-size-overlay data-size-shape-asset="' +
        escapeHtml(shapeKey) +
        '" src="' +
        escapeHtml(shapeUrl) +
        '" alt="" aria-hidden="true" decoding="async"' +
        (sizeShapeIsMaster(shapeKey) ? ' data-size-master-kind="raster"' : "") +
        ">";
    }
    for (tki = 1; tki <= SIZE_SKIN_TONE_COUNT; tki += 1) {
      tickHtml +=
        '<span class="diamond-details__size-tone-tick" style="top:' +
        sizeTonePercent(tki) +
        '%"></span>';
    }
    html +=
      "</div>" +
      '<div class="diamond-details__size-tone" data-size-tone>' +
      '<span class="diamond-details__size-tone-label" aria-hidden="true">Lighter</span>' +
      '<div class="diamond-details__size-tone-slider" data-size-tone-slider role="slider" aria-label="Skin tone" aria-valuemin="1" aria-valuemax="' +
      SIZE_SKIN_TONE_COUNT +
      '" aria-valuenow="' +
      tone +
      '" aria-orientation="vertical" data-size-tone-value="' +
      tone +
      '" style="--dd-size-tone-pct:' +
      tonePct +
      '%"' +
      (toneEnabled ? ' tabindex="0"' : ' tabindex="-1" aria-disabled="true"') +
      ">" +
      '<span class="diamond-details__size-tone-rail" aria-hidden="true">' +
      tickHtml +
      "</span>" +
      '<span class="diamond-details__size-tone-thumb" aria-hidden="true"></span>' +
      "</div>" +
      '<span class="diamond-details__size-tone-label" aria-hidden="true">Darker</span>' +
      "</div></div>" +
      '<p class="diamond-details__size-copy" data-size-copy>' +
      '<span class="diamond-details__size-identity">' +
      escapeHtml(identityLine) +
      "</span>" +
      '<span class="diamond-details__size-mm">' +
      escapeHtml(mmLine) +
      "</span></p>" +
      '<p class="diamond-details__sr-only">' +
      escapeHtml(sizeAccessibleText(diamond, measurements)) +
      "</p></div>";
    return html;
  }

  function renderCertificateReportModal(diamond) {
    var report = gradingReport(diamond);
    if (!report) return "";
    var lab = formatCertificateLabLabel(diamond);
    var externalLab = formatCertificateExternalLabName(diamond);
    var number = certificateReportNumber(diamond);
    var title = lab ? lab + " GRADING REPORT" : "GRADING REPORT";
    var labelledBy = "diamond-details-report-title";
    var html =
      '<div class="diamond-details__report-modal" data-report-modal hidden>' +
      '<div class="diamond-details__report-modal-backdrop" data-report-modal-close tabindex="-1"></div>' +
      '<div class="diamond-details__report-modal-dialog" id="diamond-details-report-dialog" data-report-modal-dialog role="dialog" aria-modal="true" tabindex="-1" aria-labelledby="' +
      labelledBy +
      '">' +
      '<div class="diamond-details__report-modal-header">' +
      '<div class="diamond-details__report-modal-heading">' +
      '<h2 class="diamond-details__report-modal-title" id="' +
      labelledBy +
      '" data-report-modal-title>' +
      escapeHtml(title) +
      "</h2>";
    if (number) {
      html +=
        '<p class="diamond-details__report-modal-number" data-report-modal-number>Report #' +
        escapeHtml(number) +
        "</p>";
    }
    html +=
      "</div>" +
      '<button type="button" class="diamond-details__report-modal-close" data-report-modal-close aria-label="Close grading report">\u00d7</button>' +
      "</div>" +
      '<div class="diamond-details__report-modal-body" data-report-modal-body>' +
      '<div class="diamond-details__report-modal-external" data-report-modal-external hidden>' +
      '<span class="diamond-details__report-modal-external-icon" aria-hidden="true">' +
      CERTIFICATE_THUMB_SVG +
      "</span>" +
      '<p class="diamond-details__report-modal-external-lead">View your official grading report</p>' +
      '<p class="diamond-details__report-modal-external-copy">This grading report is hosted securely by ' +
      escapeHtml(externalLab) +
      " and opens on the laboratory's website.</p>" +
      "</div>" +
      '<p class="diamond-details__report-modal-fallback" data-report-modal-fallback hidden>' +
      "The grading report should be opened separately if it does not appear here." +
      "</p></div>" +
      '<div class="diamond-details__report-modal-footer">' +
      '<a class="diamond-details__report-modal-original" data-report-original href="' +
      escapeHtml(report.url) +
      '" target="_blank" rel="noopener noreferrer">OPEN ORIGINAL REPORT \u2197</a>' +
      "</div></div></div>";
    return html;
  }

  function renderMediaModeContent(diamond, identity, mode, diamondView) {
    if (mode === MEDIA_MODE_DIAMOND) return renderDiamondMedia(diamond, identity, diamondView);
    if (mode === MEDIA_MODE_ACTUAL_SIZE) return renderSizeMedia(diamond);
    if (mode === MEDIA_MODE_SIDE_PROFILE || mode === MEDIA_MODE_PROPORTIONS) {
      return renderSideProfileMedia(diamond);
    }
    if (mode === MEDIA_MODE_TOP_VIEW) return renderTopViewMedia(diamond);
    return renderFutureMedia(mode);
  }

  function renderMedia(diamond, identity, activeItemId, diamondView) {
    var item = resolveGalleryItem(diamond, activeItemId);
    var mode = item.mode || MEDIA_MODE_DIAMOND;
    var view = diamondView || item.view || diamondViewCapabilities(diamond).defaultView;
    return (
      '<div class="diamond-details__media-stage" data-detail-region="media">' +
      '<div class="diamond-details__media-explorer" data-media-explorer data-active-media-mode="' +
      mode +
      '" data-active-media-item="' +
      item.id +
      '">' +
      '<div class="diamond-details__media" id="diamond-details-media-panel" role="tabpanel" data-media-viewport data-media-safe-area="' +
      formatMediaPct(MEDIA_SAFE_AREA) +
      '" data-media-fill="' +
      formatMediaPct(MEDIA_FILL) +
      '" data-media-safe-layers="1" data-media-mode-panel="' +
      mode +
      '" aria-labelledby="diamond-details-media-tab-' +
      item.id +
      '">' +
      renderMediaModeContent(diamond, identity, mode, view) +
      "</div>" +
      renderMediaGallery(diamond, item.id) +
      "</div></div>"
    );
  }

  var ACCORDION_CHEVRON_SVG =
    '<svg class="diamond-details__accordion-chevron" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>' +
    "</svg>";

  function formatDiamondId(diamond) {
    var id = recordId(diamond);
    if (!id) return "";
    try {
      if (window.IvanovAPI && typeof window.IvanovAPI.normalizeDiamondId === "function") {
        var identity = window.IvanovAPI.normalizeDiamondId(id);
        if (identity && identity.bareUuid) return identity.bareUuid;
      }
    } catch (err) {}
    return id.replace(/^diamond\//i, "");
  }

  function formatMeasurementsLine(diamond) {
    var geometry = diamond && diamond.geometry && typeof diamond.geometry === "object" ? diamond.geometry : {};
    var lengthMm = toPositiveMm(geometry.lengthMm);
    var widthMm = toPositiveMm(geometry.widthMm);
    var depthMm = toPositiveMm(geometry.depthMm);
    var parts = [];
    if (lengthMm != null) parts.push(formatSizeMm(lengthMm));
    if (widthMm != null) parts.push(formatSizeMm(widthMm));
    if (depthMm != null) parts.push(formatSizeMm(depthMm));
    if (parts.length < 2) return "";
    return parts.join(" \u00d7 ") + " mm";
  }

  function collectDiamondDetailSpecs(diamond) {
    diamond = diamond || {};
    var resolved = resolveProportions(diamond);
    var rows = [];
    function add(key, label, value) {
      if (!isMeaningful(value)) return;
      rows.push({ key: key, label: label, value: String(value) });
    }
    function addPresent(key, label, value) {
      var text = value == null ? "" : String(value).trim();
      if (!text || isUnusableSpecToken(text)) return;
      rows.push({ key: key, label: label, value: text });
    }
    add("id", "Diamond ID", formatDiamondId(diamond));
    add("origin", "Origin", formatOrigin(diamond.origin));
    add("shape", "Shape", formatShape(diamond.shape));
    var carat = formatCarat(diamond.carat);
    add("carat", "Carat Weight", carat ? carat + " ct" : "");
    add("measurements", "Measurements", formatMeasurementsLine(diamond));
    if (resolved.lengthWidthRatio != null) {
      add("ratio", "L/W Ratio", formatPropNumber(resolved.lengthWidthRatio, 3));
    }
    add("cut", "Cut", formatCut(diamond.cut));
    add("color", "Color", isMeaningful(diamond.color) ? String(diamond.color).trim() : "");
    add("clarity", "Clarity", isMeaningful(diamond.clarity) ? String(diamond.clarity).trim() : "");
    if (resolved.tablePct != null) {
      add("table", "Table", formatPropNumber(resolved.tablePct, 2) + "%");
    }
    if (resolved.depthPct != null) {
      add("depth", "Depth", formatPropNumber(resolved.depthPct, 2) + "%");
    }
    var technical = detailTechnicalSources(diamond);
    addPresent("polish", "Polish", formatPolish(technical.polish));
    addPresent("symmetry", "Symmetry", formatSymmetry(technical.symmetry));
    addPresent("girdle", "Girdle", formatGirdle(technical.girdle));
    addPresent("culet", "Culet", formatCulet(technical.culet));
    addPresent("fluorescence", "Fluorescence", formatFluorescence(technical.fluorescence));
    return rows;
  }

  var SPEC_EDU_ICON_SVG =
    '<svg class="diamond-search__edu-icon" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="6.4" fill="none" stroke="currentColor" stroke-width="1.2"></circle>' +
    '<circle cx="8" cy="5.15" r="0.95" fill="currentColor"></circle>' +
    '<path d="M8 7.35v4.05" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round"></path>' +
    "</svg>";

  var SPEC_EDUCATION = {
    carat: { key: "carat", label: "Learn about carat weight" },
    cut: { key: "cut", label: "Learn about cut" },
    color: { key: "color", label: "Learn about color" },
    clarity: { key: "clarity", label: "Learn about clarity" },
    table: { key: "table", label: "Learn about table percentage" },
    depth: { key: "depth", label: "Learn about depth percentage" },
    ratio: { key: "ratio", label: "Learn about length-to-width ratio" },
    polish: { key: "polish", label: "Learn about polish" },
    symmetry: { key: "symmetry", label: "Learn about symmetry" },
    girdle: { key: "girdle", label: "Learn about girdle" },
    culet: { key: "culet", label: "Learn about culet" },
    fluorescence: { key: "fluorescence", label: "Learn about fluorescence" },
  };

  var SPEC_EDU_CUT_STATES = {
    ID: "ideal",
    IDEAL: "ideal",
    EX: "excellent",
    EXCELLENT: "excellent",
    VG: "very-good",
    "VERY GOOD": "very-good",
    GD: "good",
    GOOD: "good",
    FR: "fair",
    FAIR: "fair",
  };

  var SPEC_EDU_CLARITY_STATES = {
    si2: true,
    si1: true,
    vs2: true,
    vs1: true,
    vvs2: true,
    vvs1: true,
    if: true,
    fl: true,
  };

  function specEducationMeta(fieldKey) {
    return SPEC_EDUCATION[fieldKey] || null;
  }

  function specEducationCutState(cut) {
    var code = String(cut == null ? "" : cut)
      .trim()
      .toUpperCase()
      .replace(/[_-]+/g, " ");
    return SPEC_EDU_CUT_STATES[code] || "";
  }

  function specEducationColorState(color) {
    var raw = String(color == null ? "" : color).trim().toLowerCase();
    return /^[d-k]$/.test(raw) ? raw : "";
  }

  function specEducationClarityState(clarity) {
    var raw = String(clarity == null ? "" : clarity)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "");
    return SPEC_EDU_CLARITY_STATES[raw] ? raw : "";
  }

  function specEducationContext(fieldKey, diamond) {
    var meta = specEducationMeta(fieldKey);
    var ctx;
    diamond = diamond || {};
    if (!meta) return null;
    ctx = { key: meta.key, label: meta.label, stateId: "", carat: "", shape: "" };
    if (fieldKey === "carat") {
      if (Number.isFinite(Number(diamond.carat))) ctx.carat = String(Number(diamond.carat));
      if (isMeaningful(diamond.shape)) ctx.shape = String(diamond.shape).trim();
    } else if (fieldKey === "cut") {
      ctx.stateId = specEducationCutState(diamond.cut);
    } else if (fieldKey === "color") {
      ctx.stateId = specEducationColorState(diamond.color);
    } else if (fieldKey === "clarity") {
      ctx.stateId = specEducationClarityState(diamond.clarity);
    }
    return ctx;
  }

  function renderSpecEducationButton(fieldKey, diamond) {
    var ctx = specEducationContext(fieldKey, diamond);
    var html;
    if (!ctx) return "";
    html =
      '<button type="button" class="diamond-search__edu-btn diamond-details__spec-edu" data-edu-key="' +
      escapeHtml(ctx.key) +
      '" data-spec-edu="' +
      escapeHtml(fieldKey) +
      '" aria-label="' +
      escapeHtml(ctx.label) +
      '" aria-expanded="false" aria-haspopup="dialog" aria-controls="diamondSearchEduPanel"';
    if (ctx.stateId) html += ' data-edu-state="' + escapeHtml(ctx.stateId) + '"';
    if (ctx.carat) html += ' data-edu-carat="' + escapeHtml(ctx.carat) + '"';
    if (ctx.shape) html += ' data-edu-shape="' + escapeHtml(ctx.shape) + '"';
    html += ">" + SPEC_EDU_ICON_SVG + "</button>";
    return html;
  }

  function certificatePanelModel(diamond) {
    var lab = gradingLab(diamond);
    var number = certificateReportNumber(diamond);
    var report = gradingReport(diamond);
    return {
      lab: lab,
      number: number,
      report: report,
      title: lab ? "Certificate \u2014 " + lab : "Certificate",
      available: !!(lab || number || report),
    };
  }

  function renderSpecRows(rows, diamond) {
    var html = "";
    var i;
    for (i = 0; i < rows.length; i += 1) {
      html +=
        '<div class="diamond-details__spec-row" data-spec-row="' +
        escapeHtml(rows[i].key) +
        '">' +
        '<span class="diamond-details__spec-label">' +
        '<span class="diamond-details__spec-label-text">' +
        escapeHtml(rows[i].label) +
        "</span>" +
        renderSpecEducationButton(rows[i].key, diamond) +
        "</span>" +
        '<span class="diamond-details__spec-value" data-spec-value="' +
        escapeHtml(rows[i].key) +
        '">' +
        escapeHtml(rows[i].value) +
        "</span></div>";
    }
    return html;
  }

  function renderPanelAccordion(id, title, bodyHtml) {
    if (!bodyHtml) return "";
    var triggerId = "diamond-details-" + id + "-trigger";
    var panelId = "diamond-details-" + id + "-panel";
    return (
      '<div class="diamond-details__accordion diamond-details__accordion--' +
      escapeHtml(id) +
      '" data-panel-accordion="' +
      escapeHtml(id) +
      '" data-detail-region="' +
      escapeHtml(id) +
      '">' +
      '<button type="button" class="diamond-details__accordion-trigger" id="' +
      triggerId +
      '" data-accordion-trigger="' +
      escapeHtml(id) +
      '" aria-expanded="false" aria-controls="' +
      panelId +
      '">' +
      "<span>" +
      escapeHtml(title) +
      "</span>" +
      ACCORDION_CHEVRON_SVG +
      "</button>" +
      '<div class="diamond-details__accordion-panel" id="' +
      panelId +
      '" data-accordion-panel="' +
      escapeHtml(id) +
      '" role="region" aria-labelledby="' +
      triggerId +
      '" hidden>' +
      bodyHtml +
      "</div></div>"
    );
  }

  function renderDiamondDetailsAccordion(diamond) {
    var rows = collectDiamondDetailSpecs(diamond);
    if (!rows.length) return "";
    return renderPanelAccordion(
      "specs",
      "Diamond Details",
      '<div class="diamond-details__spec-list">' + renderSpecRows(rows, diamond) + "</div>"
    );
  }

  function renderCertificateAccordion(diamond) {
    var model = certificatePanelModel(diamond);
    var body = "";
    var facts = [];
    if (!model.available) return "";
    if (model.lab) facts.push({ key: "lab", label: "Lab", value: model.lab });
    if (model.number) facts.push({ key: "number", label: "Report Number", value: model.number });
    if (facts.length) {
      body += '<div class="diamond-details__spec-list">' + renderSpecRows(facts, diamond) + "</div>";
    }
    if (model.report) {
      body +=
        '<div class="diamond-details__certificate-preview" data-certificate-preview aria-hidden="true">' +
        '<span class="diamond-details__certificate-preview-mark">' +
        CERTIFICATE_THUMB_SVG +
        (model.lab
          ? '<span class="diamond-details__certificate-preview-lab">' + escapeHtml(model.lab) + "</span>"
          : "") +
        "</span></div>" +
        '<p class="diamond-details__certificate-action">' +
        '<a class="diamond-details__report-link" data-grading-report data-open-grading-report href="' +
        escapeHtml(model.report.url) +
        '">View Grading Report</a></p>';
    }
    return renderPanelAccordion("certificate", model.title, body);
  }

  var HINT_NAME_MAX = 80;
  var HINT_NOTE_MAX = 500;
  var HINT_PRIVACY_PATH = "/policies/privacy-policy";
  var HINT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var HINT_SUBMIT_LABEL = "Send Hint";
  var HINT_SENDING_LABEL = "Sending…";
  var HINT_SUBMIT_TIMEOUT_MS = 20000;
  var HINT_ERROR_COPY = {
    INVALID_REQUEST: "Please check the information and try again.",
    INVALID_DIAMOND_ID: "Please check the information and try again.",
    DIAMOND_NOT_FOUND: "This diamond is no longer available.",
    DIAMOND_UNAVAILABLE: "This diamond is no longer available.",
    RATE_LIMITED: "Too many attempts. Please try again later.",
    DROP_A_HINT_DISABLED: "Drop a Hint is temporarily unavailable.",
    SEND_FAILED: "We couldn't send your hint. Please try again.",
  };
  var HINT_GENERIC_SEND_FAILURE = HINT_ERROR_COPY.SEND_FAILED;

  function renderDropAHint() {
    return (
      '<button type="button" class="diamond-details__hint" data-drop-a-hint data-detail-region="hint" aria-haspopup="dialog" aria-controls="diamond-details-hint-dialog">' +
      "Drop a Hint</button>"
    );
  }

  function hintBareUuid(diamond) {
    return formatDiamondId(diamond);
  }

  function isValidHintEmail(value) {
    var email = String(value == null ? "" : value).trim();
    if (!email || email.length > 254) return false;
    return HINT_EMAIL_RE.test(email);
  }

  function buildDropAHintPayload(input) {
    input = input || {};
    var message = String(input.message == null ? "" : input.message).trim();
    if (message.length > HINT_NOTE_MAX) message = message.slice(0, HINT_NOTE_MAX);
    return {
      diamondId: String(input.diamondId == null ? "" : input.diamondId).trim(),
      recipientEmail: String(input.recipientEmail == null ? "" : input.recipientEmail).trim(),
      senderName: String(input.senderName == null ? "" : input.senderName).trim(),
      senderEmail: String(input.senderEmail == null ? "" : input.senderEmail).trim(),
      message: message,
      sendCopy: input.sendCopy === true,
    };
  }

  function validateDropAHintForm(input) {
    input = input || {};
    var errors = {};
    var recipientEmail = String(input.recipientEmail == null ? "" : input.recipientEmail).trim();
    var senderName = String(input.senderName == null ? "" : input.senderName).trim();
    var senderEmail = String(input.senderEmail == null ? "" : input.senderEmail).trim();
    var message = String(input.message == null ? "" : input.message).trim();

    if (!recipientEmail) errors.recipientEmail = "Enter the recipient's email.";
    else if (!isValidHintEmail(recipientEmail)) errors.recipientEmail = "Enter a valid email address.";

    if (!senderName) errors.senderName = "Enter your name.";
    else if (senderName.length > HINT_NAME_MAX) errors.senderName = "Enter a shorter name.";

    if (!senderEmail) errors.senderEmail = "Enter your email.";
    else if (!isValidHintEmail(senderEmail)) errors.senderEmail = "Enter a valid email address.";

    if (message.length > HINT_NOTE_MAX) errors.message = "Keep your note under 500 characters.";

    return {
      ok: Object.keys(errors).length === 0,
      errors: errors,
      values: {
        recipientEmail: recipientEmail,
        senderName: senderName,
        senderEmail: senderEmail,
        message: message,
        sendCopy: input.sendCopy === true,
      },
    };
  }

  function renderDropAHintSuccessShell() {
    return (
      '<div class="diamond-details__hint-result diamond-details__hint-result--success" data-hint-success hidden>' +
      '<p class="diamond-details__hint-result-title">Hint Sent</p>' +
      '<p class="diamond-details__hint-result-copy">Your hint has been sent.</p>' +
      '<button type="button" class="diamond-details__hint-done" data-hint-done>Done</button>' +
      "</div>"
    );
  }

  function renderDropAHintErrorShell() {
    return (
      '<div class="diamond-details__hint-result diamond-details__hint-result--error" data-hint-error hidden role="alert">' +
      '<p class="diamond-details__hint-result-copy" data-hint-error-copy>We couldn\'t send your hint. Please try again.</p>' +
      "</div>"
    );
  }

  function renderDropAHintCard(diamond) {
    var identity = formatStoneIdentity(diamond);
    var spec = formatSpecLine(diamond);
    var price = formatPriceUsd(diamond && diamond.retailPriceUsd);
    var imageUrl = mediaImageUrl(diamond);
    var mediaHtml = imageUrl
      ? '<img class="diamond-details__hint-card-image" src="' +
        escapeHtml(imageUrl) +
        '" alt="" width="88" height="88" loading="lazy">'
      : '<span class="diamond-details__hint-card-fallback" aria-hidden="true">' +
        PLACEHOLDER_HTML +
        "</span>";
    return (
      '<div class="diamond-details__hint-card" data-hint-card>' +
      '<div class="diamond-details__hint-card-media">' +
      mediaHtml +
      "</div>" +
      '<div class="diamond-details__hint-card-copy">' +
      '<p class="diamond-details__hint-card-identity">' +
      escapeHtml(identity) +
      "</p>" +
      (spec
        ? '<p class="diamond-details__hint-card-specs">' + escapeHtml(spec) + "</p>"
        : "") +
      (price
        ? '<p class="diamond-details__hint-card-price">' + escapeHtml(price) + "</p>"
        : "") +
      "</div></div>"
    );
  }

  function renderDropAHintModal(diamond) {
    var bareId = hintBareUuid(diamond);
    return (
      '<div class="diamond-details__hint-modal" data-hint-modal data-hint-state="idle" hidden>' +
      '<div class="diamond-details__hint-backdrop" data-hint-close tabindex="-1"></div>' +
      '<div class="diamond-details__hint-dialog" id="diamond-details-hint-dialog" role="dialog" aria-modal="true" tabindex="-1" aria-labelledby="diamond-details-hint-title" aria-describedby="diamond-details-hint-lede">' +
      '<button type="button" class="diamond-details__hint-close" data-hint-close aria-label="Close">' +
      '<span aria-hidden="true">&times;</span></button>' +
      '<h2 class="diamond-details__hint-title" id="diamond-details-hint-title">Drop a Hint</h2>' +
      '<p class="diamond-details__hint-lede" id="diamond-details-hint-lede">Found the one? Send someone a hint.</p>' +
      renderDropAHintCard(diamond) +
      '<form class="diamond-details__hint-form" data-hint-form novalidate>' +
      '<input type="hidden" data-hint-diamond-id value="' +
      escapeHtml(bareId) +
      '">' +
      '<div class="diamond-details__hint-field" data-hint-field="recipientEmail">' +
      '<label class="diamond-details__hint-label" for="diamond-details-hint-recipient-email">Recipient\'s Email</label>' +
      '<input id="diamond-details-hint-recipient-email" class="diamond-details__hint-input" data-hint-recipient-email type="email" autocomplete="email" required maxlength="254">' +
      '<p class="diamond-details__hint-field-error" data-hint-error-for="recipientEmail" hidden></p>' +
      "</div>" +
      '<div class="diamond-details__hint-field" data-hint-field="senderName">' +
      '<label class="diamond-details__hint-label" for="diamond-details-hint-sender-name">Your Name</label>' +
      '<input id="diamond-details-hint-sender-name" class="diamond-details__hint-input" data-hint-sender-name type="text" autocomplete="name" required maxlength="' +
      HINT_NAME_MAX +
      '">' +
      '<p class="diamond-details__hint-field-error" data-hint-error-for="senderName" hidden></p>' +
      "</div>" +
      '<div class="diamond-details__hint-field" data-hint-field="senderEmail">' +
      '<label class="diamond-details__hint-label" for="diamond-details-hint-sender-email">Your Email</label>' +
      '<input id="diamond-details-hint-sender-email" class="diamond-details__hint-input" data-hint-sender-email type="email" autocomplete="email" required maxlength="254">' +
      '<p class="diamond-details__hint-field-error" data-hint-error-for="senderEmail" hidden></p>' +
      "</div>" +
      '<div class="diamond-details__hint-field" data-hint-field="message">' +
      '<label class="diamond-details__hint-label" for="diamond-details-hint-message">Personal Note <span class="diamond-details__hint-optional">(optional)</span></label>' +
      '<textarea id="diamond-details-hint-message" class="diamond-details__hint-textarea" data-hint-message rows="3" maxlength="' +
      HINT_NOTE_MAX +
      '"></textarea>' +
      '<p class="diamond-details__hint-field-error" data-hint-error-for="message" hidden></p>' +
      "</div>" +
      '<label class="diamond-details__hint-check">' +
      '<input type="checkbox" class="diamond-details__hint-checkbox" data-hint-send-copy>' +
      '<span>Send me a copy of this email</span></label>' +
      '<button type="submit" class="diamond-details__hint-submit" data-hint-submit>Send Hint</button>' +
      "</form>" +
      renderDropAHintSuccessShell() +
      renderDropAHintErrorShell() +
      '<p class="diamond-details__hint-privacy">' +
      "By sending this hint, you agree that Ivanov Jewelry may use the information provided to deliver this message. See our " +
      '<a class="diamond-details__hint-privacy-link" href="' +
      HINT_PRIVACY_PATH +
      '">Privacy Policy</a>.</p>' +
      "</div></div>"
    );
  }

  function renderShareOpen(options) {
    options = options || {};
    var className =
      "diamond-details__share-open" +
      (options.panel ? " diamond-details__share-open--panel" : "");
    return (
      '<button type="button" class="' +
      className +
      '" data-share-open aria-haspopup="dialog">Share</button>'
    );
  }

  function renderSecondaryActions() {
    return (
      '<div class="diamond-details__secondary-actions" data-detail-region="secondary-actions">' +
      renderShareOpen() +
      renderDropAHint() +
      "</div>"
    );
  }

  function ivanovServiceContent() {
    var defaults = IVANOV_SERVICE_CONTENT;
    return {
      heading: hostDataAttr("data-service-heading") || defaults.heading,
      items: defaults.items.map(function (item) {
        return {
          id: item.id,
          title: hostDataAttr("data-service-" + item.id + "-title") || item.title,
          body: hostDataAttr("data-service-" + item.id + "-body") || item.body
        };
      })
    };
  }

  function serviceMultilineHtml(value) {
    return escapeHtml(value).replace(/\n/g, "<br>");
  }

  function renderIvanovServiceMark() {
    var src = hostDataAttr("data-service-mark");
    if (!src) return "";
    return (
      '<span class="diamond-details__service-mark" aria-hidden="true">' +
      '<img class="diamond-details__service-mark-img" src="' +
      escapeHtml(src) +
      '" alt="" width="27" height="27">' +
      "</span>"
    );
  }

  function renderIvanovService() {
    var content = ivanovServiceContent();
    var itemsHtml = content.items
      .map(function (item) {
        return (
          '<div class="diamond-details__service-item" data-service-item="' +
          escapeHtml(item.id) +
          '">' +
          '<span class="diamond-details__service-icon" aria-hidden="true">' +
          (IVANOV_SERVICE_ICONS[item.id] || "") +
          "</span>" +
          '<div class="diamond-details__service-copy">' +
          '<p class="diamond-details__service-item-title">' +
          serviceMultilineHtml(item.title) +
          "</p>" +
          '<p class="diamond-details__service-item-body">' +
          serviceMultilineHtml(item.body) +
          "</p></div></div>"
        );
      })
      .join("");
    return (
      '<div class="diamond-details__service" data-ivanov-service data-detail-region="service" aria-labelledby="diamond-details-service-title">' +
      '<div class="diamond-details__service-header">' +
      renderIvanovServiceMark() +
      '<h2 class="diamond-details__service-title heading-static" id="diamond-details-service-title">' +
      escapeHtml(content.heading) +
      "</h2></div>" +
      '<div class="diamond-details__service-grid">' +
      itemsHtml +
      "</div></div>"
    );
  }

  function renderUtilities() {
    return (
      '<div class="diamond-details__utilities" data-detail-region="utilities">' +
      '<button type="button" class="diamond-details__save" data-save aria-pressed="false" aria-label="Save diamond">' +
      HEART_SVG +
      '<span data-save-label>SAVE</span></button>' +
      "</div>"
    );
  }

  function renderSelect() {
    return (
      '<button type="button" class="diamond-details__select" data-select-this-diamond data-detail-region="select">' +
      "Select This Diamond</button>"
    );
  }

  function renderShareModal(diamond, identity) {
    var path = canonicalSharePath(recordId(diamond));
    var href = shareHref(recordId(diamond));
    return (
      '<div class="diamond-details__share" data-share-modal hidden>' +
      '<div class="diamond-details__share-backdrop" data-share-close tabindex="-1"></div>' +
      '<div class="diamond-details__share-dialog" role="dialog" aria-modal="true" tabindex="-1" aria-labelledby="diamond-details-share-title">' +
      '<h2 class="diamond-details__share-title" id="diamond-details-share-title" data-share-title>Share</h2>' +
      '<p class="diamond-details__share-identity" data-share-identity>' +
      escapeHtml(identity) +
      "</p>" +
      '<label class="diamond-details__share-label" for="diamond-details-share-url">Link</label>' +
      '<input id="diamond-details-share-url" class="diamond-details__share-url" data-share-url type="text" readonly value="' +
      escapeHtml(href) +
      '" data-share-path="' +
      escapeHtml(path) +
      '">' +
      '<div class="diamond-details__share-actions">' +
      '<button type="button" class="diamond-details__share-copy" data-share-copy>Copy Link</button>' +
      '<button type="button" class="diamond-details__share-dismiss" data-share-close>Close</button>' +
      "</div></div></div>"
    );
  }

  function renderRecoveryBlock(diamond) {
    return (
      '<div class="diamond-details__recovery" data-detail-region="recovery">' +
      '<p class="diamond-details__message">This diamond is no longer available.</p>' +
      '<div class="diamond-details__recovery-actions" data-detail-region="recovery-actions">' +
      renderSimilarDiamondsAction(diamond) +
      renderShareOpen({ panel: true }) +
      "</div></div>"
    );
  }

  function renderPanelBody(diamond, options) {
    options = options || {};
    var identity = formatStoneIdentity(diamond);
    var spec = formatSpecLine(diamond);
    var price = formatPriceUsd(diamond && diamond.retailPriceUsd);
    var available = !(diamond && diamond.availability === "unavailable");
    var availability = available ? "Available" : "Unavailable";
    var recovery = options.recoveryHtml || "";

    return (
      '<div class="diamond-details__panel" data-detail-region="panel">' +
      renderUtilities() +
      '<h1 class="diamond-details__identity diamond-details__title" data-detail-region="identity">' +
      '<span class="diamond-details__title-primary">' +
      escapeHtml(identity) +
      "</span>" +
      (spec
        ? '\n<span class="diamond-details__title-specs diamond-details__spec" data-detail-region="summary">' +
          escapeHtml(spec) +
          "</span>"
        : "") +
      "</h1>" +
      (price
        ? '<p class="diamond-details__price" data-detail-region="price">' + escapeHtml(price) + "</p>"
        : "") +
      '<p class="diamond-details__availability" data-detail-region="availability"' +
      (available ? ' data-available="true"' : "") +
      ">" +
      escapeHtml(availability) +
      "</p>" +
      (options.offerSelect ? renderSelect() : "") +
      (options.offerSelect ? renderSecondaryActions() : "") +
      recovery +
      renderDiamondDetailsAccordion(diamond) +
      renderCertificateAccordion(diamond) +
      renderIvanovService() +
      "</div>"
    );
  }

  function caratAecApi() {
    return window.IvanovCaratAec || null;
  }

  function understandModuleDomId(id) {
    return "diamond-details-understand-" + id;
  }

  function hostDataAttr(name) {
    var host = sizeHost();
    var value = host && host.getAttribute ? host.getAttribute(name) : "";
    return value ? String(value).trim() : "";
  }

  function understandCaratContent() {
    var defaults = UNDERSTAND_CARAT_CONTENT;
    return {
      index: hostDataAttr("data-carat-edu-index") || defaults.index,
      label: hostDataAttr("data-carat-edu-label") || defaults.label,
      headingLine1: hostDataAttr("data-carat-edu-heading-1") || defaults.headingLine1,
      headingLine2: hostDataAttr("data-carat-edu-heading-2") || defaults.headingLine2,
      lead: hostDataAttr("data-carat-edu-lead") || defaults.lead,
      body: hostDataAttr("data-carat-edu-body") || defaults.body,
      handRef: hostDataAttr("data-carat-edu-hand-ref") || defaults.handRef
    };
  }

  function formatUnderstandCaratCopy(carat) {
    var label = formatCarat(carat);
    return label ? label + " ct" : "";
  }

  function understandCaratTitleHtml(content) {
    var html = escapeHtml(content.headingLine1 || "");
    if (content.headingLine2) html += (html ? " " : "") + escapeHtml(content.headingLine2);
    return html;
  }

  function understandCaratKey(ct) {
    var n = Number(ct);
    if (!Number.isFinite(n)) return "";
    var factor = Math.pow(10, UNDERSTAND_CARAT_MATCH_DECIMALS);
    return (Math.round(n * factor) / factor).toFixed(UNDERSTAND_CARAT_MATCH_DECIMALS);
  }

  function isSelectedInventoryCarat(ct, selectedCt) {
    var left = understandCaratKey(ct);
    var right = understandCaratKey(selectedCt);
    return !!left && left === right;
  }

  function understandCaratSliderCeiling(carat) {
    var n = Number(carat);
    var last = UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    if (!Number.isFinite(n) || n <= UNDERSTAND_CARAT_SLIDER_BASE_MAX) return UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    while (last < n) {
      if (last < 40) last = 40;
      else if (last < 50) last = 50;
      else last += 10;
    }
    return last;
  }

  function understandCaratSliderMaxForDiamond(diamond) {
    return understandCaratSliderCeiling(selectedInventoryCarat(diamond));
  }

  function clampUnderstandCarat(ct, aec, scaleMax) {
    var n = Number(ct);
    var min = UNDERSTAND_CARAT_SLIDER_MIN;
    var max = Number(scaleMax);
    if (!Number.isFinite(max) || max < min) max = UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    if (!Number.isFinite(n)) return min;
    if (n < min) return min;
    if (n > max) return max;
    return n;
  }

  function snapUnderstandCaratSlider(ct, scaleMax, step) {
    var min = UNDERSTAND_CARAT_SLIDER_MIN;
    var max = Number(scaleMax);
    var increment = Number(step);
    var n = Number(ct);
    var stepped;
    if (!Number.isFinite(max) || max < min) max = UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    if (!Number.isFinite(increment) || increment <= 0) increment = UNDERSTAND_CARAT_SLIDER_STEP;
    if (!Number.isFinite(n)) return min;
    stepped = Math.round(n / increment) * increment;
    stepped = Math.round(stepped * 100) / 100;
    return clampUnderstandCarat(stepped, null, max);
  }

  function selectedInventoryCarat(diamond) {
    var n = Number(diamond && diamond.carat);
    return Number.isFinite(n) ? n : null;
  }

  function selectedSliderCarat(diamond, aec) {
    var selected = selectedInventoryCarat(diamond);
    if (selected == null) return aec && aec.DEFAULT_CARAT != null ? aec.DEFAULT_CARAT : 1;
    return clampUnderstandCarat(selected, aec, understandCaratSliderCeiling(selected));
  }

  function understandCaratSliderCheckpoints(scaleMax) {
    var max = Number(scaleMax);
    var points = UNDERSTAND_CARAT_SLIDER_CHECKPOINTS.slice();
    var last;
    var next;
    if (!Number.isFinite(max) || max < UNDERSTAND_CARAT_SLIDER_BASE_MAX) {
      max = UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    }
    last = points[points.length - 1];
    while (last < max - 1e-6) {
      if (last < 40) next = 40;
      else if (last < 50) next = 50;
      else next = last + 10;
      points.push(next);
      last = next;
    }
    return points;
  }

  function understandCaratThumbCenterPx(trackPos, trackWidth) {
    var p = Number(trackPos);
    var w = Number(trackWidth);
    var inset = UNDERSTAND_CARAT_TRAVEL_INSET_PX;
    if (!Number.isFinite(p)) p = 0;
    if (p < 0) p = 0;
    if (p > 1) p = 1;
    if (!(w > 0)) return inset;
    return inset + p * Math.max(0, w - 2 * inset);
  }

  function understandCaratVisualTrackPos(trackPos, trackWidth) {
    var w = Number(trackWidth);
    if (!Number.isFinite(w) || w <= 0) {
      var p = Number(trackPos);
      if (!Number.isFinite(p)) p = 0;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      return p;
    }
    return understandCaratThumbCenterPx(trackPos, w) / w;
  }

  function understandCaratToTrackPos(carat, aec, scaleMax) {
    var min = UNDERSTAND_CARAT_SLIDER_MIN;
    var max = Number(scaleMax);
    var ticks;
    var ct;
    var i;
    var a;
    var b;
    var t;
    if (!Number.isFinite(max) || max <= min) max = understandCaratSliderCeiling(carat);
    if (!Number.isFinite(max) || max <= min) max = UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    ticks = understandCaratSliderTicks(max);
    ct = clampUnderstandCarat(carat, aec, max);
    if (!ticks.length) return 0;
    if (ct <= ticks[0].ct) return ticks[0].pos;
    for (i = 1; i < ticks.length; i += 1) {
      if (ct <= ticks[i].ct + 1e-9) {
        a = ticks[i - 1];
        b = ticks[i];
        if (b.ct === a.ct) return b.pos;
        t = (ct - a.ct) / (b.ct - a.ct);
        return a.pos + (b.pos - a.pos) * t;
      }
    }
    return 1;
  }

  function understandCaratFromTrackPos(pos, selectedCt, aec, scaleMax) {
    var min = UNDERSTAND_CARAT_SLIDER_MIN;
    var max = Number(scaleMax);
    var t = Number(pos);
    var ticks;
    var i;
    var a;
    var b;
    var raw;
    var snapped;
    var selectedPos;
    var snappedPos;
    var step = aec && aec.CARAT_STEP != null ? aec.CARAT_STEP : UNDERSTAND_CARAT_SLIDER_STEP;
    if (!Number.isFinite(max) || max <= min) max = understandCaratSliderCeiling(selectedCt);
    if (!Number.isFinite(max) || max <= min) max = UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    if (!Number.isFinite(t)) t = 0;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    ticks = understandCaratSliderTicks(max);
    raw = ticks.length ? ticks[ticks.length - 1].ct : max;
    if (!ticks.length || t <= ticks[0].pos) raw = ticks.length ? ticks[0].ct : min;
    else {
      for (i = 1; i < ticks.length; i += 1) {
        if (t <= ticks[i].pos + 1e-9) {
          a = ticks[i - 1];
          b = ticks[i];
          if (b.pos === a.pos) raw = b.ct;
          else raw = a.ct + ((t - a.pos) / (b.pos - a.pos)) * (b.ct - a.ct);
          break;
        }
      }
    }
    snapped = snapUnderstandCaratSlider(raw, max, step);
    if (selectedCt == null || !Number.isFinite(Number(selectedCt))) return snapped;
    selectedPos = understandCaratToTrackPos(selectedCt, aec, max);
    snappedPos = understandCaratToTrackPos(snapped, aec, max);
    if (Math.abs(t - selectedPos) <= Math.abs(t - snappedPos)) {
      return selectedCt;
    }
    return snapped;
  }

  function understandCaratSliderTicks(scaleMax) {
    var points = understandCaratSliderCheckpoints(scaleMax);
    var last = points.length - 1;
    var ticks = [];
    var i;
    for (i = 0; i < points.length; i += 1) {
      ticks.push({
        ct: points[i],
        pos: last <= 0 ? 0 : i / last
      });
    }
    return ticks;
  }

  function formatUnderstandCaratTickLabel(ct) {
    var n = Number(ct);
    if (!Number.isFinite(n)) return "";
    if (Math.abs(n - Math.round(n)) < 1e-6) return String(Math.round(n));
    if (Math.abs(n * 10 - Math.round(n * 10)) < 1e-6) return String(Math.round(n * 10) / 10);
    return n.toFixed(2);
  }

  function understandCaratTickIsMinor(ct) {
    var n = Number(ct);
    return n === 0.5 || n === 2 || n === 3;
  }

  function understandCaratBubbleHalfPx(slider) {
    var bubble = slider && slider.querySelector ? slider.querySelector("[data-understand-carat-bubble]") : null;
    var width = bubble && Number(bubble.offsetWidth);
    var text = bubble && bubble.textContent != null ? String(bubble.textContent) : "";
    var estimated;
    if (Number.isFinite(width) && width > 0) return width / 2;
    estimated = text ? (text.length * 6.6 + 18) / 2 : UNDERSTAND_CARAT_BUBBLE_HALF_PX;
    return Math.max(UNDERSTAND_CARAT_BUBBLE_HALF_PX, estimated);
  }

  function clampUnderstandCaratBubbleLayout(trackPos, containerWidth, bubbleHalfPx) {
    var aec = caratAecApi();
    var p = Number(trackPos);
    var w = Number(containerWidth);
    var half = Number(bubbleHalfPx);
    var thumbX;
    var left;
    if (!Number.isFinite(p)) p = 0;
    if (p < 0) p = 0;
    if (p > 1) p = 1;
    if (!Number.isFinite(half) || half < 0) half = UNDERSTAND_CARAT_BUBBLE_HALF_PX;
    if (aec && typeof aec.clampBubbleLayout === "function") {
      return aec.clampBubbleLayout(understandCaratVisualTrackPos(p, w), w, half);
    }
    if (!Number.isFinite(w) || w <= 0) {
      return { leftPx: null, leftPct: p * 100, pointerShiftPx: 0 };
    }
    if (w < half * 2) half = w / 2;
    thumbX = understandCaratThumbCenterPx(p, w);
    left = thumbX;
    if (left < half) left = half;
    if (left > w - half) left = w - half;
    return {
      leftPx: left,
      leftPct: null,
      pointerShiftPx: thumbX - left
    };
  }

  function understandCaratTypicalRefMax(aec) {
    var max = aec && Number(aec.CARAT_MAX);
    if (!Number.isFinite(max) || max <= 0) return 10;
    return max;
  }

  function understandCaratTypicalRefMin(aec) {
    var min = aec && Number(aec.CARAT_MIN);
    if (!Number.isFinite(min) || min <= 0) return UNDERSTAND_CARAT_SLIDER_MIN;
    return min;
  }

  function scaleUnderstandTypicalSize(size, factor) {
    if (!size || !Number.isFinite(factor) || factor <= 0) return size || null;
    return {
      shape: size.shape,
      kind: size.kind,
      orientation: size.orientation,
      point: size.point,
      cleft: size.cleft,
      lengthMm: size.lengthMm != null ? size.lengthMm * factor : null,
      widthMm: size.widthMm != null ? size.widthMm * factor : null,
      diameterMm: size.diameterMm != null ? size.diameterMm * factor : null
    };
  }

  /*
   * Shape-specific typical L×W for the Detail Carat slider.
   * Inside Search AEC 0.25–10, reuse resolveSearchSize.
   * Above that ceiling, cube-root scale the last shape table so a real
   * 21–30 ct stone can grow instead of clamping to the 10 ct row.
   * Do not change Search AEC CARAT_MAX or its slider mapping.
   */
  function resolveUnderstandCaratTypicalSize(carat, shapeKey, aec) {
    var ct = Number(carat);
    var min;
    var max;
    var educational;
    if (!aec || typeof aec.resolveSearchSize !== "function" || !shapeKey) return null;
    if (!Number.isFinite(ct) || ct <= 0) return null;
    min = understandCaratTypicalRefMin(aec);
    max = understandCaratTypicalRefMax(aec);
    if (ct >= min && ct <= max) return aec.resolveSearchSize(ct, shapeKey);
    if (ct > max) {
      educational = aec.resolveSearchSize(max, shapeKey);
      return scaleUnderstandTypicalSize(educational, Math.pow(ct / max, 1 / 3));
    }
    educational = aec.resolveSearchSize(min, shapeKey);
    return scaleUnderstandTypicalSize(educational, Math.pow(ct / min, 1 / 3));
  }

  function resolveUnderstandCaratModel(diamond, sliderCarat) {
    var aec = caratAecApi();
    var selectedCt = selectedInventoryCarat(diamond);
    var selectedSlide = selectedSliderCarat(diamond, aec);
    var scaleMax = understandCaratSliderCeiling(selectedCt);
    var shapeKey = resolveSizeShape(diamond && diamond.shape);
    var actual = usableSizeMeasurements(diamond);
    var ct = sliderCarat == null ? selectedSlide : Number(sliderCarat);
    var selected;
    var educational;
    var lengthMm = null;
    var widthMm = null;
    var source = "unavailable";
    var displayCarat;
    var sizeCopy = "";
    var caratCopy;

    if (!Number.isFinite(ct)) ct = selectedSlide;
    ct = clampUnderstandCarat(ct, aec, scaleMax);
    selected = isSelectedInventoryCarat(ct, selectedSlide);
    displayCarat = selected && selectedCt != null ? selectedCt : ct;
    caratCopy = formatUnderstandCaratCopy(displayCarat);

    if (selected && actual) {
      lengthMm = actual.lengthMm;
      widthMm = actual.widthMm;
      source = "actual";
      sizeCopy = "Actual Size: " + formatSizeMm(lengthMm) + " \u00d7 " + formatSizeMm(widthMm) + " mm";
    } else if (shapeKey) {
      educational = resolveUnderstandCaratTypicalSize(ct, shapeKey, aec);
      if (educational && educational.lengthMm != null && educational.widthMm != null) {
        lengthMm = educational.lengthMm;
        widthMm = educational.widthMm;
        source = "educational";
        if (educational.kind === "diameter" && educational.diameterMm != null && typeof aec.formatTypicalDiameter === "function") {
          sizeCopy = aec.formatTypicalDiameter(educational.diameterMm);
        } else if (aec && typeof aec.formatTypicalSize === "function") {
          sizeCopy = aec.formatTypicalSize(lengthMm, widthMm);
        } else {
          sizeCopy = "Typical Size: " + formatSizeMm(lengthMm) + " \u00d7 " + formatSizeMm(widthMm) + " mm";
        }
      }
    }

    return {
      diamond: diamond,
      sliderCarat: ct,
      displayCarat: displayCarat,
      selected: selected,
      source: source,
      shapeKey: shapeKey,
      lengthMm: lengthMm,
      widthMm: widthMm,
      caratCopy: caratCopy,
      sizeCopy: sizeCopy,
      varyCopy: source === "educational" ? UNDERSTAND_CARAT_VARY : "",
      sliderMin: UNDERSTAND_CARAT_SLIDER_MIN,
      sliderMax: scaleMax
    };
  }

  function understandCaratMediaStyle(model) {
    var metrics = model.lengthMm != null && model.widthMm != null
      ? sizeOverlayMetrics(model.lengthMm, model.widthMm)
      : null;
    var shadowFrac = metrics ? metrics.widthMm * SIZE_NATIVE_PX_PER_MM / SIZE_HAND_NATIVE_WIDTH : 0;
    return (
      "--dd-size-hand-native-w:" +
      SIZE_HAND_NATIVE_WIDTH +
      ";--dd-size-hand-native-h:" +
      SIZE_HAND_NATIVE_HEIGHT +
      ";--dd-size-hand-max-w:" +
      SIZE_HAND_DISPLAY_MAX_WIDTH +
      "px;--dd-size-native-px-per-mm:" +
      SIZE_NATIVE_PX_PER_MM +
      ";--dd-size-anchor-x:" +
      SIZE_ANCHOR_X +
      ";--dd-size-anchor-y:" +
      SIZE_ANCHOR_Y +
      ";--dd-size-length-mm:" +
      (metrics ? metrics.lengthMm : "") +
      ";--dd-size-width-mm:" +
      (metrics ? metrics.widthMm : "") +
      ";--dd-size-width-pct:" +
      (metrics ? metrics.widthPct : 0) +
      ";--dd-size-height-pct:" +
      (metrics ? metrics.heightPct : 0) +
      ";--dd-size-shadow-frac:" +
      shadowFrac
    );
  }

  function renderUnderstandCaratTone() {
    var tone = normalizeSizeTone(activeSizeTone);
    var toneEnabled = countSizeToneAssets() >= 2;
    var tickHtml = "";
    var tki;
    for (tki = 1; tki <= SIZE_SKIN_TONE_COUNT; tki += 1) {
      tickHtml +=
        '<span class="diamond-details__size-tone-tick" style="left:' +
        sizeTonePercent(tki) +
        '%"></span>';
    }
    return (
      '<div class="diamond-details__understand-carat-tone" data-size-tone data-understand-carat-tone>' +
      '<p class="diamond-details__understand-carat-control-label" id="diamond-details-understand-skin-tone">Skin Tone</p>' +
      '<div class="diamond-details__understand-carat-tone-slider" data-size-tone-slider data-understand-carat-tone-slider role="slider" aria-labelledby="diamond-details-understand-skin-tone" aria-valuemin="1" aria-valuemax="' +
      SIZE_SKIN_TONE_COUNT +
      '" aria-valuenow="' +
      tone +
      '" aria-orientation="horizontal" data-size-tone-value="' +
      tone +
      '" style="--dd-size-tone-pct:' +
      sizeTonePercent(tone) +
      '%;--dd-size-tone-n:' +
      sizeToneLinear(tone) +
      '"' +
      (toneEnabled ? ' tabindex="0"' : ' tabindex="-1" aria-disabled="true"') +
      ">" +
      '<span class="diamond-details__understand-carat-tone-rail" aria-hidden="true">' +
      tickHtml +
      "</span>" +
      '<span class="diamond-details__understand-carat-tone-thumb" aria-hidden="true"></span>' +
      "</div>" +
      '<div class="diamond-details__understand-carat-tone-ends" aria-hidden="true">' +
      '<span class="diamond-details__understand-carat-tone-end">Lighter</span>' +
      '<span class="diamond-details__understand-carat-tone-end">Darker</span>' +
      "</div></div>"
    );
  }

  function renderUnderstandCaratPhysical(model) {
    var handUrl = sizeToneUrl(normalizeSizeTone(activeSizeTone)) || sizeHandUrl();
    var shapeUrl = model.shapeKey ? sizeShapeUrl(model.shapeKey) : "";
    var content = understandCaratContent();
    var html =
      '<div class="diamond-details__understand-carat-stage">' +
      '<div class="diamond-details__size-media diamond-details__understand-carat-media" data-understand-carat-media style="' +
      understandCaratMediaStyle(model) +
      '">' +
      '<div class="diamond-details__size-hands" aria-hidden="true">' +
      (handUrl
        ? '<img class="diamond-details__size-hand" data-size-hand-layer="base" src="' +
          escapeHtml(handUrl) +
          '" width="859" height="1024" alt="" decoding="async">' +
          '<img class="diamond-details__size-hand diamond-details__size-hand--fade" data-size-hand-layer="fade" src="' +
          escapeHtml(handUrl) +
          '" width="859" height="1024" alt="" decoding="async">'
        : "") +
      "</div>";
    if (shapeUrl && model.lengthMm != null && model.widthMm != null) {
      html +=
        '<span class="diamond-details__size-contact-shadow" data-understand-carat-shadow aria-hidden="true"></span>' +
        '<img class="diamond-details__size-stone' +
        (model.shapeKey === "pear" ? " diamond-details__size-stone--pear" : "") +
        '" data-understand-carat-overlay data-size-shape-asset="' +
        escapeHtml(model.shapeKey) +
        '" src="' +
        escapeHtml(shapeUrl) +
        '" alt="" aria-hidden="true" decoding="async"' +
        (sizeShapeIsMaster(model.shapeKey) ? ' data-size-master-kind="raster"' : "") +
        ">";
    }
    html +=
      "</div>" +
      (content.handRef
        ? '<p class="diamond-details__understand-carat-hand-ref" data-understand-carat-hand-ref>' +
          escapeHtml(content.handRef) +
          "</p>"
        : "") +
      "</div>";
    return html;
  }

  function renderUnderstandCaratSlider(model) {
    var aec = caratAecApi();
    var min = model && model.sliderMin != null ? model.sliderMin : UNDERSTAND_CARAT_SLIDER_MIN;
    var max = model && model.sliderMax != null ? model.sliderMax : UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    var ticks = understandCaratSliderTicks(max);
    var pos = understandCaratToTrackPos(model.sliderCarat, aec, max);
    var fillPct = pos * 100;
    var html =
      '<div class="diamond-details__understand-carat-scale">' +
      '<p class="diamond-details__understand-carat-control-label" id="diamond-details-understand-carat-weight">Carat Weight</p>' +
      '<div class="diamond-details__understand-carat-slider" data-understand-carat-slider role="slider" tabindex="0" aria-labelledby="diamond-details-understand-carat-weight" aria-valuemin="' +
      min +
      '" aria-valuemax="' +
      max +
      '" aria-valuenow="' +
      model.displayCarat +
      '" aria-valuetext="' +
      escapeHtml(model.caratCopy) +
      '" style="--dd-understand-carat-n:' +
      pos +
      ';--dd-understand-carat-track-pct:' +
      fillPct +
      '%;--dd-understand-carat-bubble-pointer-shift:0px">' +
      '<div class="diamond-details__understand-carat-bubble" data-understand-carat-bubble aria-hidden="true">' +
      escapeHtml(model.caratCopy) +
      "</div>" +
      '<div class="diamond-details__understand-carat-track" data-understand-carat-track>' +
      '<span class="diamond-details__understand-carat-rail" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-carat-fill" data-understand-carat-fill aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-carat-handle" data-understand-carat-handle aria-hidden="true"></span>' +
      "</div>" +
      '<div class="diamond-details__understand-carat-ticks" role="presentation">';
    var i;
    var tick;
    var tickLabel;
    for (i = 0; i < ticks.length; i += 1) {
      tick = ticks[i];
      tickLabel = formatUnderstandCaratTickLabel(tick.ct);
      html +=
        '<button type="button" class="diamond-details__understand-carat-tick" data-understand-carat-tick="' +
        tick.ct +
        '"' +
        (understandCaratTickIsMinor(tick.ct) ? ' data-understand-carat-tick-minor="true"' : "") +
        ' tabindex="-1" style="--dd-understand-carat-tick-n:' +
        tick.pos +
        '" aria-label="Set carat weight to ' +
        escapeHtml(formatUnderstandCaratCopy(tick.ct) || tickLabel + " ct") +
        '"><span class="diamond-details__understand-carat-tick-mark" aria-hidden="true"></span><span class="diamond-details__understand-carat-tick-label">' +
        escapeHtml(tickLabel) +
        "</span></button>";
    }
    html += "</div></div></div>";
    return html;
  }

  function renderUnderstandCarat(diamond) {
    var aec = caratAecApi();
    var model = resolveUnderstandCaratModel(diamond, selectedSliderCarat(diamond, aec));
    var content = understandCaratContent();
    return (
      '<div class="diamond-details__understand-carat" data-understand-carat data-understand-carat-state="' +
      escapeHtml(model.source) +
      '" data-understand-carat-shape="' +
      escapeHtml(model.shapeKey) +
      '" data-understand-carat-value="' +
      escapeHtml(understandCaratKey(model.displayCarat)) +
      '" data-understand-carat-slider-value="' +
      escapeHtml(understandCaratKey(model.sliderCarat)) +
      '"' +
      (model.lengthMm != null ? ' data-understand-carat-length-mm="' + escapeHtml(String(model.lengthMm)) + '"' : "") +
      (model.widthMm != null ? ' data-understand-carat-width-mm="' + escapeHtml(String(model.widthMm)) + '"' : "") +
      ">" +
      '<div class="diamond-details__understand-carat-chapter">' +
      '<div class="diamond-details__understand-carat-edu">' +
      '<p class="diamond-details__understand-carat-eyebrow" data-understand-carat-eyebrow>' +
      '<span class="diamond-details__understand-carat-index">' +
      escapeHtml(content.index) +
      "</span>" +
      '<span class="diamond-details__understand-carat-eyebrow-rule" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-carat-label">' +
      escapeHtml(content.label) +
      "</span></p>" +
      '<h2 class="diamond-details__understand-carat-title" id="diamond-details-understand-carat-title" data-understand-carat-title>' +
      understandCaratTitleHtml(content) +
      "</h2>" +
      '<p class="diamond-details__understand-carat-lead" data-understand-carat-lead>' +
      escapeHtml(content.lead) +
      "</p>" +
      '<p class="diamond-details__understand-carat-explain" data-understand-carat-explain>' +
      escapeHtml(content.body) +
      "</p></div>" +
      '<div class="diamond-details__understand-carat-visual">' +
      renderUnderstandCaratPhysical(model) +
      "</div>" +
      '<div class="diamond-details__understand-carat-controls">' +
      '<div class="diamond-details__understand-carat-info">' +
      '<p class="diamond-details__understand-carat-heading" data-understand-carat-heading>' +
      escapeHtml(model.caratCopy) +
      "</p>" +
      '<p class="diamond-details__understand-carat-size" data-understand-carat-size>' +
      escapeHtml(model.sizeCopy) +
      "</p>" +
      '<p class="diamond-details__understand-carat-vary" data-understand-carat-vary' +
      (model.varyCopy ? "" : " hidden") +
      ">" +
      escapeHtml(model.varyCopy) +
      "</p></div>" +
      renderUnderstandCaratSlider(model) +
      renderUnderstandCaratTone() +
      "</div></div></div>"
    );
  }

  function isPopulatedUnderstandModule(id) {
    return id === "carat" || id === "cut" || id === "color" || id === "clarity";
  }

  function understandCutContent() {
    var defaults = UNDERSTAND_CUT_CONTENT;
    return {
      index: hostDataAttr("data-cut-edu-index") || defaults.index,
      label: hostDataAttr("data-cut-edu-label") || defaults.label,
      headingLine1: hostDataAttr("data-cut-edu-heading-1") || defaults.headingLine1,
      headingLine2: hostDataAttr("data-cut-edu-heading-2") || defaults.headingLine2,
      lead: hostDataAttr("data-cut-edu-lead") || defaults.lead,
      body: hostDataAttr("data-cut-edu-body") || defaults.body,
      caption: hostDataAttr("data-cut-edu-caption") || defaults.caption,
      controlLabel: hostDataAttr("data-cut-edu-control-label") || defaults.controlLabel,
      fallback: hostDataAttr("data-cut-edu-fallback") || defaults.fallback,
      modelNote: hostDataAttr("data-cut-edu-model-note") || defaults.modelNote
    };
  }

  function understandCutTitleHtml(content) {
    var html = escapeHtml(content.headingLine1 || "");
    if (content.headingLine2) html += (html ? " " : "") + escapeHtml(content.headingLine2);
    return html;
  }

  function understandCutGrades() {
    var i;
    var grade;
    var id;
    var out = [];
    for (i = 0; i < UNDERSTAND_CUT_GRADES.length; i += 1) {
      grade = UNDERSTAND_CUT_GRADES[i];
      id = grade.id;
      out.push({
        id: id,
        label: hostDataAttr("data-cut-edu-" + id + "-label") || grade.label,
        caption: hostDataAttr("data-cut-edu-" + id + "-caption") || grade.caption,
        heading: hostDataAttr("data-cut-edu-" + id + "-heading") || grade.heading,
        body: hostDataAttr("data-cut-edu-" + id + "-body") || grade.body
      });
    }
    return out;
  }

  function understandCutGradeIndex(id) {
    var idx = UNDERSTAND_CUT_GRADE_IDS.indexOf(String(id || ""));
    return idx < 0 ? -1 : idx;
  }

  function understandCutGradeById(id) {
    var grades = understandCutGrades();
    var i;
    for (i = 0; i < grades.length; i += 1) {
      if (grades[i].id === id) return grades[i];
    }
    return grades[understandCutGradeIndex(UNDERSTAND_CUT_EDU_DEFAULT)] || grades[0] || null;
  }

  function resolveUnderstandCutInventoryGrade(diamond) {
    return specEducationCutState(diamond && diamond.cut) || "";
  }

  function understandCutVisualUrl(gradeId) {
    var key = String(gradeId || "").toLowerCase();
    var fromHost;
    var edu;
    var fromEdu;
    if (!key) return "";
    fromHost = hostDataAttr("data-cut-edu-" + key);
    if (fromHost) return fromHost;
    edu =
      window.document && typeof window.document.getElementById === "function"
        ? window.document.getElementById("diamondSearchEduModule")
        : null;
    if (edu && edu.getAttribute) {
      fromEdu = String(edu.getAttribute("data-edu-cut-" + key) || "").trim();
      if (fromEdu) return fromEdu;
    }
    return "";
  }

  function understandCutToTrackPos(index) {
    var last = UNDERSTAND_CUT_GRADE_IDS.length - 1;
    var n = Number(index);
    if (!Number.isFinite(n) || n < 0) n = 0;
    if (n > last) n = last;
    return last <= 0 ? 0 : n / last;
  }

  function understandCutFromTrackPos(pos) {
    var last = UNDERSTAND_CUT_GRADE_IDS.length - 1;
    var t = Number(pos);
    var index;
    if (!Number.isFinite(t)) t = 0;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    index = Math.round(t * last);
    if (index < 0) index = 0;
    if (index > last) index = last;
    return UNDERSTAND_CUT_GRADE_IDS[index];
  }

  function understandCutThumbCenterPx(trackPos, trackWidth) {
    return understandCaratThumbCenterPx(trackPos, trackWidth);
  }

  function understandCutVisualTrackPos(trackPos, trackWidth) {
    return understandCaratVisualTrackPos(trackPos, trackWidth);
  }

  function understandCutPosFromClientX(slider, clientX, currentId) {
    var track = slider && slider.querySelector ? slider.querySelector("[data-understand-cut-track]") : null;
    var rect = track && track.getBoundingClientRect
      ? track.getBoundingClientRect()
      : slider && slider.getBoundingClientRect
        ? slider.getBoundingClientRect()
        : null;
    var inset = UNDERSTAND_CUT_TRAVEL_INSET_PX;
    var usable;
    var t;
    if (!rect || !rect.width) return understandCutToTrackPos(understandCutGradeIndex(currentId));
    usable = Math.max(1, rect.width - 2 * inset);
    t = (clientX - rect.left - inset) / usable;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    return t;
  }

  function stepUnderstandCut(currentId, delta) {
    var last = UNDERSTAND_CUT_GRADE_IDS.length - 1;
    var index = understandCutGradeIndex(currentId);
    if (index < 0) index = understandCutGradeIndex(UNDERSTAND_CUT_EDU_DEFAULT);
    index += delta < 0 ? -1 : 1;
    if (index < 0) index = 0;
    if (index > last) index = last;
    return UNDERSTAND_CUT_GRADE_IDS[index];
  }

  function resolveUnderstandCutModel(diamond, selectedId, options) {
    var content = understandCutContent();
    var inventory = resolveUnderstandCutInventoryGrade(diamond);
    var source = inventory ? "actual" : "educational";
    var interacted = !!(options && options.interacted);
    var gradeId = selectedId || inventory || UNDERSTAND_CUT_EDU_DEFAULT;
    var grade;
    var index;
    var showGradeCopy;
    if (understandCutGradeIndex(gradeId) < 0) {
      gradeId = inventory || UNDERSTAND_CUT_EDU_DEFAULT;
    }
    grade = understandCutGradeById(gradeId);
    index = understandCutGradeIndex(gradeId);
    if (index < 0) index = 0;
    showGradeCopy = source === "actual" || interacted;
    return {
      source: source,
      inventoryGrade: inventory,
      gradeId: grade.id,
      index: index,
      pos: understandCutToTrackPos(index),
      label: grade.label,
      caption: grade.caption,
      heading: showGradeCopy ? grade.heading : "",
      body: showGradeCopy ? grade.body : content.fallback,
      visualUrl: understandCutVisualUrl(grade.id),
      showGradeCaption: showGradeCopy,
      showModelNote: source === "educational",
      fallback: content.fallback,
      modelNote: content.modelNote,
      visualCaption: content.caption
    };
  }

  function applyUnderstandCutBubbleLayout(slider, pos) {
    var layout;
    var half;
    var bubble;
    var width;
    if (!slider) return;
    bubble = slider.querySelector ? slider.querySelector("[data-understand-cut-bubble]") : null;
    width = bubble && Number(bubble.offsetWidth);
    half =
      Number.isFinite(width) && width > 0
        ? width / 2
        : UNDERSTAND_CUT_BUBBLE_HALF_PX;
    layout = clampUnderstandCaratBubbleLayout(pos, slider.clientWidth, half);
    if (!slider.style || typeof slider.style.setProperty !== "function") return;
    if (layout.leftPx != null) {
      slider.style.setProperty("--dd-understand-cut-bubble-left", layout.leftPx + "px");
    } else {
      slider.style.setProperty("--dd-understand-cut-bubble-left", layout.leftPct + "%");
    }
    slider.style.setProperty("--dd-understand-cut-bubble-pointer-shift", layout.pointerShiftPx + "px");
  }

  function applyUnderstandCutValue(root, diamond, selectedId, options) {
    var scope = root && root.querySelector
      ? root.querySelector("[data-understand-cut]") || root
      : root;
    var model = resolveUnderstandCutModel(diamond, selectedId, options);
    var fillPct = model.pos * 100;
    function findEl(sel) {
      var el = scope && scope.querySelector ? scope.querySelector(sel) : null;
      if (el) return el;
      if (root && root !== scope && root.querySelector) return root.querySelector(sel);
      return null;
    }
    function findAll(sel) {
      var list = scope && scope.querySelectorAll ? scope.querySelectorAll(sel) : [];
      if (list && list.length) return list;
      if (root && root !== scope && root.querySelectorAll) return root.querySelectorAll(sel) || [];
      return list || [];
    }
    var slider = findEl("[data-understand-cut-slider]");
    var bubble = findEl("[data-understand-cut-bubble]");
    var caption = findEl("[data-understand-cut-grade-caption]");
    var heading = findEl("[data-understand-cut-card-heading]");
    var body = findEl("[data-understand-cut-card-body]");
    var note = findEl("[data-understand-cut-model-note]");
    var imgs = findAll("[data-understand-cut-img]");
    var ticks = findAll("[data-understand-cut-tick]");
    var visual = findEl("[data-understand-cut-visual]");
    var i;
    var tickId;
    var imgId;

    if (scope && scope.setAttribute) {
      scope.setAttribute("data-understand-cut-state", model.source);
      scope.setAttribute("data-understand-cut-value", model.gradeId);
      scope.setAttribute("data-understand-cut-interacted", options && options.interacted ? "true" : "false");
      if (model.inventoryGrade) scope.setAttribute("data-understand-cut-inventory", model.inventoryGrade);
      else scope.removeAttribute("data-understand-cut-inventory");
    }
    if (visual) visual.setAttribute("data-understand-cut-visual-state", model.gradeId);
    for (i = 0; i < (imgs.length || 0); i += 1) {
      imgId = imgs[i].getAttribute("data-understand-cut-img");
      if (imgId === model.gradeId) {
        if (imgs[i].classList && imgs[i].classList.add) imgs[i].classList.add("is-active");
      } else if (imgs[i].classList && imgs[i].classList.remove) {
        imgs[i].classList.remove("is-active");
      }
    }
    if (caption) {
      caption.textContent = model.showGradeCaption ? model.caption : "";
      if (model.showGradeCaption) caption.removeAttribute("hidden");
      else caption.setAttribute("hidden", "");
    }
    if (note) {
      note.textContent = model.modelNote;
      if (model.showModelNote) note.removeAttribute("hidden");
      else note.setAttribute("hidden", "");
    }
    if (heading) {
      heading.textContent = model.heading;
      if (model.heading) heading.removeAttribute("hidden");
      else heading.setAttribute("hidden", "");
    }
    if (body) body.textContent = model.body;
    if (bubble) bubble.textContent = model.label;
    if (slider) {
      if (slider.style && typeof slider.style.setProperty === "function") {
        slider.style.setProperty("--dd-understand-cut-n", String(model.pos));
        slider.style.setProperty("--dd-understand-cut-track-pct", fillPct + "%");
      }
      applyUnderstandCutBubbleLayout(slider, model.pos);
      slider.setAttribute("aria-valuemin", "0");
      slider.setAttribute("aria-valuemax", String(UNDERSTAND_CUT_GRADE_IDS.length - 1));
      slider.setAttribute("aria-valuenow", String(model.index));
      slider.setAttribute("aria-valuetext", model.label);
    }
    for (i = 0; i < (ticks.length || 0); i += 1) {
      tickId = ticks[i].getAttribute("data-understand-cut-tick");
      if (tickId === model.gradeId) ticks[i].setAttribute("aria-current", "true");
      else ticks[i].removeAttribute("aria-current");
    }
    return model;
  }

  function renderUnderstandCutVisual(model) {
    var grades = understandCutGrades();
    var html =
      '<div class="diamond-details__understand-cut-stage">' +
      '<div class="diamond-details__understand-cut-media" data-understand-cut-visual data-understand-cut-visual-state="' +
      escapeHtml(model.gradeId) +
      '" aria-hidden="true">';
    var i;
    var grade;
    var url;
    for (i = 0; i < grades.length; i += 1) {
      grade = grades[i];
      url = understandCutVisualUrl(grade.id);
      if (!url) continue;
      html +=
        '<img class="diamond-details__understand-cut-img' +
        (grade.id === model.gradeId ? " is-active" : "") +
        '" data-understand-cut-img="' +
        escapeHtml(grade.id) +
        '" src="' +
        escapeHtml(url) +
        '" width="1536" height="1024" alt="" decoding="async">';
    }
    html +=
      "</div>" +
      '<p class="diamond-details__understand-cut-caption" data-understand-cut-caption>' +
      escapeHtml(model.visualCaption) +
      "</p>" +
      '<p class="diamond-details__understand-cut-grade-caption" data-understand-cut-grade-caption' +
      (model.showGradeCaption ? "" : " hidden") +
      ">" +
      escapeHtml(model.showGradeCaption ? model.caption : "") +
      "</p>" +
      '<p class="diamond-details__understand-cut-model-note" data-understand-cut-model-note' +
      (model.showModelNote ? "" : " hidden") +
      ">" +
      escapeHtml(model.modelNote) +
      "</p></div>";
    return html;
  }

  function renderUnderstandCutSlider(model) {
    var grades = understandCutGrades();
    var last = grades.length - 1;
    var pos = model.pos;
    var fillPct = pos * 100;
    var html =
      '<div class="diamond-details__understand-cut-scale">' +
      '<p class="diamond-details__understand-cut-control-label" id="diamond-details-understand-cut-grade">' +
      escapeHtml(understandCutContent().controlLabel) +
      "</p>" +
      '<div class="diamond-details__understand-cut-slider" data-understand-cut-slider role="slider" tabindex="0" aria-labelledby="diamond-details-understand-cut-grade" aria-valuemin="0" aria-valuemax="' +
      last +
      '" aria-valuenow="' +
      model.index +
      '" aria-valuetext="' +
      escapeHtml(model.label) +
      '" style="--dd-understand-cut-n:' +
      pos +
      ';--dd-understand-cut-track-pct:' +
      fillPct +
      '%;--dd-understand-cut-bubble-pointer-shift:0px">' +
      '<div class="diamond-details__understand-cut-bubble" data-understand-cut-bubble aria-hidden="true">' +
      escapeHtml(model.label) +
      "</div>" +
      '<div class="diamond-details__understand-cut-track" data-understand-cut-track>' +
      '<span class="diamond-details__understand-cut-rail" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-cut-fill" data-understand-cut-fill aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-cut-handle" data-understand-cut-handle aria-hidden="true"></span>' +
      "</div>" +
      '<div class="diamond-details__understand-cut-ticks" role="presentation">';
    var i;
    var grade;
    var tickPos;
    for (i = 0; i < grades.length; i += 1) {
      grade = grades[i];
      tickPos = last <= 0 ? 0 : i / last;
      html +=
        '<button type="button" class="diamond-details__understand-cut-tick" data-understand-cut-tick="' +
        escapeHtml(grade.id) +
        '"' +
        (grade.id === model.gradeId ? ' aria-current="true"' : "") +
        ' tabindex="-1" style="--dd-understand-cut-tick-n:' +
        tickPos +
        '" aria-label="Set cut grade to ' +
        escapeHtml(grade.label) +
        '"><span class="diamond-details__understand-cut-tick-mark" aria-hidden="true"></span><span class="diamond-details__understand-cut-tick-label">' +
        escapeHtml(grade.label) +
        "</span></button>";
    }
    html += "</div></div></div>";
    return html;
  }

  function renderUnderstandCutCard(model) {
    return (
      '<div class="diamond-details__understand-cut-card" data-understand-cut-card>' +
      '<p class="diamond-details__understand-cut-card-heading" data-understand-cut-card-heading' +
      (model.heading ? "" : " hidden") +
      ">" +
      escapeHtml(model.heading) +
      "</p>" +
      '<p class="diamond-details__understand-cut-card-body" data-understand-cut-card-body>' +
      escapeHtml(model.body) +
      "</p></div>"
    );
  }

  function renderUnderstandCut(diamond) {
    var model = resolveUnderstandCutModel(diamond, resolveUnderstandCutInventoryGrade(diamond) || UNDERSTAND_CUT_EDU_DEFAULT, {
      interacted: false
    });
    var content = understandCutContent();
    return (
      '<div class="diamond-details__understand-cut" data-understand-cut data-understand-cut-state="' +
      escapeHtml(model.source) +
      '" data-understand-cut-value="' +
      escapeHtml(model.gradeId) +
      '" data-understand-cut-interacted="false"' +
      (model.inventoryGrade ? ' data-understand-cut-inventory="' + escapeHtml(model.inventoryGrade) + '"' : "") +
      ">" +
      '<div class="diamond-details__understand-cut-chapter">' +
      '<div class="diamond-details__understand-cut-edu">' +
      '<p class="diamond-details__understand-cut-eyebrow" data-understand-cut-eyebrow>' +
      '<span class="diamond-details__understand-cut-index">' +
      escapeHtml(content.index) +
      "</span>" +
      '<span class="diamond-details__understand-cut-eyebrow-rule" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-cut-label">' +
      escapeHtml(content.label) +
      "</span></p>" +
      '<h2 class="diamond-details__understand-cut-title" id="diamond-details-understand-cut-title" data-understand-cut-title>' +
      understandCutTitleHtml(content) +
      "</h2>" +
      '<p class="diamond-details__understand-cut-lead" data-understand-cut-lead>' +
      escapeHtml(content.lead) +
      "</p>" +
      '<p class="diamond-details__understand-cut-explain" data-understand-cut-explain>' +
      escapeHtml(content.body) +
      "</p></div>" +
      '<div class="diamond-details__understand-cut-visual">' +
      renderUnderstandCutVisual(model) +
      "</div>" +
      '<div class="diamond-details__understand-cut-controls">' +
      renderUnderstandCutSlider(model) +
      renderUnderstandCutCard(model) +
      "</div></div></div>"
    );
  }

  function bindUnderstandCut(region, diamond) {
    var root = region && region.querySelector ? region.querySelector("[data-understand-cut]") : null;
    var slider;
    var inventory = resolveUnderstandCutInventoryGrade(diamond);
    var current = inventory || UNDERSTAND_CUT_EDU_DEFAULT;
    var interacted = false;

    if (!root) return;

    function setGrade(next, fromUser) {
      if (fromUser) interacted = true;
      var model = applyUnderstandCutValue(root, diamond, next, { interacted: interacted });
      current = model.gradeId;
      return model;
    }

    slider = root.querySelector("[data-understand-cut-slider]");
    applyUnderstandCutValue(root, diamond, current, { interacted: false });
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        applyUnderstandCutValue(root, diamond, current, { interacted: interacted });
      });
    }
    listenUnderstandAccordionOpen(root, function () {
      setGrade(current);
    });

    if (slider) {
      slider.addEventListener("pointerdown", function (e) {
        if (e.target && e.target.closest && e.target.closest("[data-understand-cut-tick]")) return;
        if (e.button != null && e.button !== 0) return;
        if (e.preventDefault) e.preventDefault();
        understandCutPointerBound = slider;
        if (slider.setPointerCapture && e.pointerId != null) {
          try {
            slider.setPointerCapture(e.pointerId);
          } catch (err) {}
        }
        setGrade(understandCutFromTrackPos(understandCutPosFromClientX(slider, e.clientX, current)), true);
        if (typeof slider.focus === "function") slider.focus();
      });
      slider.addEventListener("pointermove", function (e) {
        if (understandCutPointerBound !== slider) return;
        setGrade(understandCutFromTrackPos(understandCutPosFromClientX(slider, e.clientX, current)), true);
      });
      slider.addEventListener("pointerup", function () {
        understandCutPointerBound = null;
      });
      slider.addEventListener("pointercancel", function () {
        understandCutPointerBound = null;
      });
      slider.addEventListener("keydown", function (e) {
        var next = current;
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = stepUnderstandCut(current, -1);
        else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = stepUnderstandCut(current, 1);
        else if (e.key === "Home") next = UNDERSTAND_CUT_GRADE_IDS[0];
        else if (e.key === "End") next = UNDERSTAND_CUT_GRADE_IDS[UNDERSTAND_CUT_GRADE_IDS.length - 1];
        else return;
        if (e.preventDefault) e.preventDefault();
        setGrade(next, true);
      });
    }

    root.addEventListener("click", function (e) {
      var tick = e.target && e.target.closest ? e.target.closest("[data-understand-cut-tick]") : null;
      var id;
      if (!tick) return;
      id = tick.getAttribute("data-understand-cut-tick");
      if (!id) return;
      if (e.preventDefault) e.preventDefault();
      setGrade(id, true);
    });
  }

  function understandColorContent() {
    var defaults = UNDERSTAND_COLOR_CONTENT;
    return {
      index: hostDataAttr("data-color-edu-index") || defaults.index,
      label: hostDataAttr("data-color-edu-label") || defaults.label,
      headingLine1: hostDataAttr("data-color-edu-heading-1") || defaults.headingLine1,
      headingLine2: hostDataAttr("data-color-edu-heading-2") || defaults.headingLine2,
      lead: hostDataAttr("data-color-edu-lead") || defaults.lead,
      body: hostDataAttr("data-color-edu-body") || defaults.body,
      caption: hostDataAttr("data-color-edu-caption") || defaults.caption,
      controlLabel: hostDataAttr("data-color-edu-control-label") || defaults.controlLabel,
      fallback: hostDataAttr("data-color-edu-fallback") || defaults.fallback,
      modelNote: hostDataAttr("data-color-edu-model-note") || defaults.modelNote,
      stripLabel: hostDataAttr("data-color-edu-strip-label") || defaults.stripLabel
    };
  }

  function understandColorTitleHtml(content) {
    var html = escapeHtml(content.headingLine1 || "");
    if (content.headingLine2) html += (html ? " " : "") + escapeHtml(content.headingLine2);
    return html;
  }

  function understandColorGrades() {
    var i;
    var grade;
    var id;
    var out = [];
    for (i = 0; i < UNDERSTAND_COLOR_GRADES.length; i += 1) {
      grade = UNDERSTAND_COLOR_GRADES[i];
      id = grade.id;
      out.push({
        id: id,
        label: hostDataAttr("data-color-edu-" + id + "-label") || grade.label,
        caption: hostDataAttr("data-color-edu-" + id + "-caption") || grade.caption,
        family: hostDataAttr("data-color-edu-" + id + "-family") || grade.family,
        heading: hostDataAttr("data-color-edu-" + id + "-heading") || grade.heading,
        body: hostDataAttr("data-color-edu-" + id + "-body") || grade.body
      });
    }
    return out;
  }

  function understandColorGradeIndex(id) {
    var idx = UNDERSTAND_COLOR_GRADE_IDS.indexOf(String(id || "").toLowerCase());
    return idx < 0 ? -1 : idx;
  }

  function understandColorGradeById(id) {
    var grades = understandColorGrades();
    var i;
    var key = String(id || "").toLowerCase();
    for (i = 0; i < grades.length; i += 1) {
      if (grades[i].id === key) return grades[i];
    }
    return grades[understandColorGradeIndex(UNDERSTAND_COLOR_EDU_DEFAULT)] || grades[0] || null;
  }

  function resolveUnderstandColorInventoryGrade(diamond) {
    return specEducationColorState(diamond && diamond.color) || "";
  }

  function understandColorVisualUrl(gradeId) {
    var key = String(gradeId || "").toLowerCase();
    var fromHost;
    var edu;
    var fromEdu;
    if (!key) return "";
    fromHost = hostDataAttr("data-color-edu-" + key);
    if (fromHost) return fromHost;
    edu =
      window.document && typeof window.document.getElementById === "function"
        ? window.document.getElementById("diamondSearchEduModule")
        : null;
    if (edu && edu.getAttribute) {
      fromEdu = String(edu.getAttribute("data-edu-color-" + key) || "").trim();
      if (fromEdu) return fromEdu;
    }
    return "";
  }

  function understandColorToTrackPos(index) {
    var last = UNDERSTAND_COLOR_GRADE_IDS.length - 1;
    var n = Number(index);
    if (!Number.isFinite(n) || n < 0) n = 0;
    if (n > last) n = last;
    return last <= 0 ? 0 : n / last;
  }

  function understandColorFromTrackPos(pos) {
    var last = UNDERSTAND_COLOR_GRADE_IDS.length - 1;
    var t = Number(pos);
    var index;
    if (!Number.isFinite(t)) t = 0;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    index = Math.round(t * last);
    if (index < 0) index = 0;
    if (index > last) index = last;
    return UNDERSTAND_COLOR_GRADE_IDS[index];
  }

  function understandColorThumbCenterPx(trackPos, trackWidth) {
    return understandCaratThumbCenterPx(trackPos, trackWidth);
  }

  function understandColorVisualTrackPos(trackPos, trackWidth) {
    return understandCaratVisualTrackPos(trackPos, trackWidth);
  }

  function understandColorPosFromClientX(slider, clientX, currentId) {
    var track = slider && slider.querySelector ? slider.querySelector("[data-understand-color-track]") : null;
    var rect = track && track.getBoundingClientRect
      ? track.getBoundingClientRect()
      : slider && slider.getBoundingClientRect
        ? slider.getBoundingClientRect()
        : null;
    var inset = UNDERSTAND_COLOR_TRAVEL_INSET_PX;
    var usable;
    var t;
    if (!rect || !rect.width) return understandColorToTrackPos(understandColorGradeIndex(currentId));
    usable = Math.max(1, rect.width - 2 * inset);
    t = (clientX - rect.left - inset) / usable;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    return t;
  }

  function stepUnderstandColor(currentId, delta) {
    var last = UNDERSTAND_COLOR_GRADE_IDS.length - 1;
    var index = understandColorGradeIndex(currentId);
    if (index < 0) index = understandColorGradeIndex(UNDERSTAND_COLOR_EDU_DEFAULT);
    index += delta < 0 ? -1 : 1;
    if (index < 0) index = 0;
    if (index > last) index = last;
    return UNDERSTAND_COLOR_GRADE_IDS[index];
  }

  function understandColorStripIsMobileViewport(width) {
    var w = Number(width);
    if (!Number.isFinite(w) && window) w = Number(window.innerWidth);
    if (!Number.isFinite(w)) return false;
    return w <= UNDERSTAND_COLOR_STRIP_MOBILE_MAX_PX;
  }

  function understandColorStripRailNeedsFollow(rail, viewportWidth) {
    var scrollWidth;
    var clientWidth;
    if (!rail) return false;
    if (!understandColorStripIsMobileViewport(viewportWidth)) return false;
    scrollWidth = Number(rail.scrollWidth);
    clientWidth = Number(rail.clientWidth);
    if (!Number.isFinite(scrollWidth) || !Number.isFinite(clientWidth)) return false;
    return scrollWidth > clientWidth + UNDERSTAND_COLOR_STRIP_FOLLOW_EPSILON_PX;
  }

  function understandColorStripFollowScrollLeft(rail, item) {
    var railWidth = Number(rail && rail.clientWidth);
    var scrollWidth = Number(rail && rail.scrollWidth);
    var itemLeft = Number(item && item.offsetLeft);
    var itemWidth = Number(item && item.offsetWidth);
    var maxScroll;
    var target;
    if (!Number.isFinite(railWidth) || railWidth <= 0) return 0;
    if (!Number.isFinite(scrollWidth) || scrollWidth < railWidth) scrollWidth = railWidth;
    if (!Number.isFinite(itemLeft)) itemLeft = 0;
    if (!Number.isFinite(itemWidth) || itemWidth < 0) itemWidth = 0;
    maxScroll = Math.max(0, scrollWidth - railWidth);
    target = itemLeft + itemWidth / 2 - railWidth / 2;
    if (target < 0) target = 0;
    if (target > maxScroll) target = maxScroll;
    return target;
  }

  function understandColorStripItemIsFullyVisible(rail, item) {
    var left = Number(item && item.offsetLeft);
    var width = Number(item && item.offsetWidth);
    var viewLeft = Number(rail && rail.scrollLeft);
    var viewWidth = Number(rail && rail.clientWidth);
    if (!Number.isFinite(left) || !Number.isFinite(width) || !Number.isFinite(viewLeft) || !Number.isFinite(viewWidth) || viewWidth <= 0) {
      return false;
    }
    return left >= viewLeft - 0.5 && left + width <= viewLeft + viewWidth + 0.5;
  }

  function applyUnderstandColorStripFollow(rail, item, options) {
    var token;
    var target;
    var behavior;
    var currentLeft;
    options = options || {};
    if (!rail || !item) return 0;
    if (!understandColorStripRailNeedsFollow(rail, options.viewportWidth)) {
      return Number(rail.scrollLeft) || 0;
    }
    token = (understandColorStripFollowToken += 1);
    rail._understandColorStripFollowToken = token;
    target = understandColorStripFollowScrollLeft(rail, item);
    behavior = options.instant ? "auto" : "smooth";
    if (rail._understandColorStripFollowInFlight && behavior === "smooth") {
      behavior = "auto";
    }
    currentLeft = Number(rail.scrollLeft);
    if (!Number.isFinite(currentLeft)) currentLeft = 0;
    rail.scrollLeft = currentLeft;
    rail._understandColorStripFollowInFlight = true;
    rail._understandColorStripFollowIgnoreScroll = true;
    if (typeof rail.scrollTo === "function") {
      try {
        rail.scrollTo({ left: target, behavior: behavior });
      } catch (err) {
        rail.scrollLeft = target;
      }
    } else {
      rail.scrollLeft = target;
    }
    function clearFlight() {
      if (rail._understandColorStripFollowToken !== token) return;
      rail._understandColorStripFollowInFlight = false;
      rail._understandColorStripFollowIgnoreScroll = false;
    }
    if (behavior === "auto") {
      clearFlight();
    } else if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(clearFlight);
      });
    } else {
      clearFlight();
    }
    return target;
  }

  function followUnderstandColorStripRail(rail, gradeId, options) {
    var item;
    var followed;
    var instant;
    var force;
    var viewportWidth;
    options = options || {};
    if (!rail || !gradeId) return 0;
    item = rail.querySelector
      ? rail.querySelector('[data-understand-color-strip-item="' + gradeId + '"]')
      : null;
    followed = rail.getAttribute ? rail.getAttribute("data-understand-color-strip-followed") : "";
    force = !!options.forceFollow;
    if (!force && followed === gradeId) return Number(rail.scrollLeft) || 0;
    viewportWidth = options.viewportWidth;
    instant = options.instantFollow != null ? !!options.instantFollow : !options.interacted;
    if (item) applyUnderstandColorStripFollow(rail, item, { instant: instant, viewportWidth: viewportWidth });
    if (rail.setAttribute) rail.setAttribute("data-understand-color-strip-followed", gradeId);
    return Number(rail.scrollLeft) || 0;
  }

  function resolveUnderstandColorModel(diamond, selectedId, options) {
    var content = understandColorContent();
    var inventory = resolveUnderstandColorInventoryGrade(diamond);
    var source = inventory ? "actual" : "educational";
    var interacted = !!(options && options.interacted);
    var gradeId = selectedId || inventory || UNDERSTAND_COLOR_EDU_DEFAULT;
    var grade;
    var index;
    var showGradeCopy;
    if (understandColorGradeIndex(gradeId) < 0) {
      gradeId = inventory || UNDERSTAND_COLOR_EDU_DEFAULT;
    }
    grade = understandColorGradeById(gradeId);
    index = understandColorGradeIndex(gradeId);
    if (index < 0) index = 0;
    showGradeCopy = source === "actual" || interacted;
    return {
      source: source,
      inventoryGrade: inventory,
      gradeId: grade.id,
      index: index,
      pos: understandColorToTrackPos(index),
      label: grade.label,
      caption: grade.caption,
      family: grade.family,
      heading: showGradeCopy ? grade.heading : "",
      body: showGradeCopy ? grade.body : content.fallback,
      visualUrl: understandColorVisualUrl(grade.id),
      showGradeCaption: showGradeCopy,
      showModelNote: source === "educational",
      fallback: content.fallback,
      modelNote: content.modelNote,
      visualCaption: content.caption,
      stripLabel: content.stripLabel
    };
  }

  function applyUnderstandColorBubbleLayout(slider, pos) {
    var layout;
    var half;
    var bubble;
    var width;
    if (!slider) return;
    bubble = slider.querySelector ? slider.querySelector("[data-understand-color-bubble]") : null;
    width = bubble && Number(bubble.offsetWidth);
    half =
      Number.isFinite(width) && width > 0
        ? width / 2
        : UNDERSTAND_COLOR_BUBBLE_HALF_PX;
    layout = clampUnderstandCaratBubbleLayout(pos, slider.clientWidth, half);
    if (!slider.style || typeof slider.style.setProperty !== "function") return;
    if (layout.leftPx != null) {
      slider.style.setProperty("--dd-understand-color-bubble-left", layout.leftPx + "px");
    } else {
      slider.style.setProperty("--dd-understand-color-bubble-left", layout.leftPct + "%");
    }
    slider.style.setProperty("--dd-understand-color-bubble-pointer-shift", layout.pointerShiftPx + "px");
  }

  function applyUnderstandColorValue(root, diamond, selectedId, options) {
    var scope = root && root.querySelector
      ? root.querySelector("[data-understand-color]") || root
      : root;
    var model = resolveUnderstandColorModel(diamond, selectedId, options);
    var fillPct = model.pos * 100;
    function findEl(sel) {
      var el = scope && scope.querySelector ? scope.querySelector(sel) : null;
      if (el) return el;
      if (root && root !== scope && root.querySelector) return root.querySelector(sel);
      return null;
    }
    function findAll(sel) {
      var list = scope && scope.querySelectorAll ? scope.querySelectorAll(sel) : [];
      if (list && list.length) return list;
      if (root && root !== scope && root.querySelectorAll) return root.querySelectorAll(sel) || [];
      return list || [];
    }
    var slider = findEl("[data-understand-color-slider]");
    var bubble = findEl("[data-understand-color-bubble]");
    var caption = findEl("[data-understand-color-grade-caption]");
    var family = findEl("[data-understand-color-family]");
    var heading = findEl("[data-understand-color-card-heading]");
    var body = findEl("[data-understand-color-card-body]");
    var note = findEl("[data-understand-color-model-note]");
    var imgs = findAll("[data-understand-color-img]");
    var ticks = findAll("[data-understand-color-tick]");
    var stripItems = findAll("[data-understand-color-strip-item]");
    var stripRail = findEl("[data-understand-color-strip-rail]");
    var visual = findEl("[data-understand-color-visual]");
    var i;
    var tickId;
    var imgId;
    var stripId;

    if (scope && scope.setAttribute) {
      scope.setAttribute("data-understand-color-state", model.source);
      scope.setAttribute("data-understand-color-value", model.gradeId);
      scope.setAttribute("data-understand-color-interacted", options && options.interacted ? "true" : "false");
      if (model.inventoryGrade) scope.setAttribute("data-understand-color-inventory", model.inventoryGrade);
      else scope.removeAttribute("data-understand-color-inventory");
    }
    if (visual) visual.setAttribute("data-understand-color-visual-state", model.gradeId);
    for (i = 0; i < (imgs.length || 0); i += 1) {
      imgId = imgs[i].getAttribute("data-understand-color-img");
      if (imgId === model.gradeId) {
        if (imgs[i].classList && imgs[i].classList.add) imgs[i].classList.add("is-active");
      } else if (imgs[i].classList && imgs[i].classList.remove) {
        imgs[i].classList.remove("is-active");
      }
    }
    if (caption) {
      caption.textContent = model.showGradeCaption ? model.caption : "";
      if (model.showGradeCaption) caption.removeAttribute("hidden");
      else caption.setAttribute("hidden", "");
    }
    if (family) {
      family.textContent = model.showGradeCaption ? model.family : "";
      if (model.showGradeCaption && model.family) family.removeAttribute("hidden");
      else family.setAttribute("hidden", "");
    }
    if (note) {
      note.textContent = model.modelNote;
      if (model.showModelNote) note.removeAttribute("hidden");
      else note.setAttribute("hidden", "");
    }
    if (heading) {
      heading.textContent = model.heading;
      if (model.heading) heading.removeAttribute("hidden");
      else heading.setAttribute("hidden", "");
    }
    if (body) body.textContent = model.body;
    if (bubble) bubble.textContent = model.label;
    if (slider) {
      if (slider.style && typeof slider.style.setProperty === "function") {
        slider.style.setProperty("--dd-understand-color-n", String(model.pos));
        slider.style.setProperty("--dd-understand-color-track-pct", fillPct + "%");
      }
      applyUnderstandColorBubbleLayout(slider, model.pos);
      slider.setAttribute("aria-valuemin", "0");
      slider.setAttribute("aria-valuemax", String(UNDERSTAND_COLOR_GRADE_IDS.length - 1));
      slider.setAttribute("aria-valuenow", String(model.index));
      slider.setAttribute("aria-valuetext", model.label);
    }
    for (i = 0; i < (ticks.length || 0); i += 1) {
      tickId = ticks[i].getAttribute("data-understand-color-tick");
      if (tickId === model.gradeId) ticks[i].setAttribute("aria-current", "true");
      else ticks[i].removeAttribute("aria-current");
    }
    for (i = 0; i < (stripItems.length || 0); i += 1) {
      stripId = stripItems[i].getAttribute("data-understand-color-strip-item");
      if (stripId === model.gradeId) stripItems[i].setAttribute("aria-current", "true");
      else stripItems[i].removeAttribute("aria-current");
    }
    followUnderstandColorStripRail(stripRail, model.gradeId, options || {});
    return model;
  }

  function renderUnderstandColorVisual(model) {
    var grades = understandColorGrades();
    var html =
      '<div class="diamond-details__understand-color-stage">' +
      '<div class="diamond-details__understand-color-media" data-understand-color-visual data-understand-color-visual-state="' +
      escapeHtml(model.gradeId) +
      '" aria-hidden="true">';
    var i;
    var grade;
    var url;
    for (i = 0; i < grades.length; i += 1) {
      grade = grades[i];
      url = understandColorVisualUrl(grade.id);
      if (!url) continue;
      html +=
        '<img class="diamond-details__understand-color-img' +
        (grade.id === model.gradeId ? " is-active" : "") +
        '" data-understand-color-img="' +
        escapeHtml(grade.id) +
        '" src="' +
        escapeHtml(url) +
        '" width="960" height="564" alt="" decoding="async">';
    }
    html +=
      "</div>" +
      '<p class="diamond-details__understand-color-caption" data-understand-color-caption>' +
      escapeHtml(model.visualCaption) +
      "</p>" +
      '<p class="diamond-details__understand-color-grade-caption" data-understand-color-grade-caption' +
      (model.showGradeCaption ? "" : " hidden") +
      ">" +
      escapeHtml(model.showGradeCaption ? model.caption : "") +
      "</p>" +
      '<p class="diamond-details__understand-color-family" data-understand-color-family' +
      (model.showGradeCaption && model.family ? "" : " hidden") +
      ">" +
      escapeHtml(model.showGradeCaption ? model.family : "") +
      "</p>" +
      '<p class="diamond-details__understand-color-model-note" data-understand-color-model-note' +
      (model.showModelNote ? "" : " hidden") +
      ">" +
      escapeHtml(model.modelNote) +
      "</p></div>";
    return html;
  }

  function renderUnderstandColorSlider(model) {
    var grades = understandColorGrades();
    var last = grades.length - 1;
    var pos = model.pos;
    var fillPct = pos * 100;
    var html =
      '<div class="diamond-details__understand-color-scale">' +
      '<p class="diamond-details__understand-color-control-label" id="diamond-details-understand-color-grade">' +
      escapeHtml(understandColorContent().controlLabel) +
      "</p>" +
      '<div class="diamond-details__understand-color-slider" data-understand-color-slider role="slider" tabindex="0" aria-labelledby="diamond-details-understand-color-grade" aria-valuemin="0" aria-valuemax="' +
      last +
      '" aria-valuenow="' +
      model.index +
      '" aria-valuetext="' +
      escapeHtml(model.label) +
      '" style="--dd-understand-color-n:' +
      pos +
      ';--dd-understand-color-track-pct:' +
      fillPct +
      '%;--dd-understand-color-bubble-pointer-shift:0px">' +
      '<div class="diamond-details__understand-color-bubble" data-understand-color-bubble aria-hidden="true">' +
      escapeHtml(model.label) +
      "</div>" +
      '<div class="diamond-details__understand-color-track" data-understand-color-track>' +
      '<span class="diamond-details__understand-color-rail" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-color-fill" data-understand-color-fill aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-color-handle" data-understand-color-handle aria-hidden="true"></span>' +
      "</div>" +
      '<div class="diamond-details__understand-color-ticks" role="presentation">';
    var i;
    var grade;
    var tickPos;
    for (i = 0; i < grades.length; i += 1) {
      grade = grades[i];
      tickPos = last <= 0 ? 0 : i / last;
      html +=
        '<button type="button" class="diamond-details__understand-color-tick" data-understand-color-tick="' +
        escapeHtml(grade.id) +
        '"' +
        (grade.id === model.gradeId ? ' aria-current="true"' : "") +
        ' tabindex="-1" style="--dd-understand-color-tick-n:' +
        tickPos +
        '" aria-label="Set color grade to ' +
        escapeHtml(grade.label) +
        '"><span class="diamond-details__understand-color-tick-mark" aria-hidden="true"></span><span class="diamond-details__understand-color-tick-label">' +
        escapeHtml(grade.label) +
        "</span></button>";
    }
    html += "</div></div></div>";
    return html;
  }

  function renderUnderstandColorCard(model) {
    return (
      '<div class="diamond-details__understand-color-card" data-understand-color-card>' +
      '<p class="diamond-details__understand-color-card-heading" data-understand-color-card-heading' +
      (model.heading ? "" : " hidden") +
      ">" +
      escapeHtml(model.heading) +
      "</p>" +
      '<p class="diamond-details__understand-color-card-body" data-understand-color-card-body>' +
      escapeHtml(model.body) +
      "</p></div>"
    );
  }

  function renderUnderstandColorStrip(model) {
    var grades = understandColorGrades();
    var html =
      '<div class="diamond-details__understand-color-strip" data-understand-color-strip>' +
      '<p class="diamond-details__understand-color-strip-label" data-understand-color-strip-label>' +
      escapeHtml(model.stripLabel) +
      "</p>" +
      '<div class="diamond-details__understand-color-strip-rail" data-understand-color-strip-rail>';
    var i;
    var grade;
    var url;
    for (i = 0; i < grades.length; i += 1) {
      grade = grades[i];
      url = understandColorVisualUrl(grade.id);
      html +=
        '<button type="button" class="diamond-details__understand-color-strip-item" data-understand-color-strip-item="' +
        escapeHtml(grade.id) +
        '"' +
        (grade.id === model.gradeId ? ' aria-current="true"' : "") +
        ' aria-label="Set color grade to ' +
        escapeHtml(grade.label) +
        '">' +
        '<span class="diamond-details__understand-color-strip-media" aria-hidden="true">';
      if (url) {
        html +=
          '<img class="diamond-details__understand-color-strip-img" data-understand-color-strip-img="' +
          escapeHtml(grade.id) +
          '" src="' +
          escapeHtml(url) +
          '" width="960" height="564" alt="" decoding="async">';
      }
      html +=
        "</span>" +
        '<span class="diamond-details__understand-color-strip-grade">' +
        escapeHtml(grade.label) +
        "</span></button>";
    }
    html += "</div></div>";
    return html;
  }

  function renderUnderstandColor(diamond) {
    var model = resolveUnderstandColorModel(diamond, resolveUnderstandColorInventoryGrade(diamond) || UNDERSTAND_COLOR_EDU_DEFAULT, {
      interacted: false
    });
    var content = understandColorContent();
    return (
      '<div class="diamond-details__understand-color" data-understand-color data-understand-color-state="' +
      escapeHtml(model.source) +
      '" data-understand-color-value="' +
      escapeHtml(model.gradeId) +
      '" data-understand-color-interacted="false"' +
      (model.inventoryGrade ? ' data-understand-color-inventory="' + escapeHtml(model.inventoryGrade) + '"' : "") +
      ">" +
      '<div class="diamond-details__understand-color-chapter">' +
      '<div class="diamond-details__understand-color-edu">' +
      '<p class="diamond-details__understand-color-eyebrow" data-understand-color-eyebrow>' +
      '<span class="diamond-details__understand-color-index">' +
      escapeHtml(content.index) +
      "</span>" +
      '<span class="diamond-details__understand-color-eyebrow-rule" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-color-label">' +
      escapeHtml(content.label) +
      "</span></p>" +
      '<h2 class="diamond-details__understand-color-title" id="diamond-details-understand-color-title" data-understand-color-title>' +
      understandColorTitleHtml(content) +
      "</h2>" +
      '<p class="diamond-details__understand-color-lead" data-understand-color-lead>' +
      escapeHtml(content.lead) +
      "</p>" +
      '<p class="diamond-details__understand-color-explain" data-understand-color-explain>' +
      escapeHtml(content.body) +
      "</p></div>" +
      '<div class="diamond-details__understand-color-visual">' +
      renderUnderstandColorVisual(model) +
      "</div>" +
      '<div class="diamond-details__understand-color-controls">' +
      renderUnderstandColorSlider(model) +
      renderUnderstandColorCard(model) +
      "</div></div>" +
      renderUnderstandColorStrip(model) +
      "</div>"
    );
  }

  function bindUnderstandColor(region, diamond) {
    var root = region && region.querySelector ? region.querySelector("[data-understand-color]") : null;
    var slider;
    var inventory = resolveUnderstandColorInventoryGrade(diamond);
    var current = inventory || UNDERSTAND_COLOR_EDU_DEFAULT;
    var interacted = false;

    if (!root) return;

    function setGrade(next, fromUser) {
      if (fromUser) interacted = true;
      var model = applyUnderstandColorValue(root, diamond, next, { interacted: interacted });
      current = model.gradeId;
      return model;
    }

    slider = root.querySelector("[data-understand-color-slider]");
    applyUnderstandColorValue(root, diamond, current, { interacted: false, instantFollow: true });
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        applyUnderstandColorValue(root, diamond, current, {
          interacted: interacted,
          instantFollow: true,
          forceFollow: true
        });
      });
    }
    listenUnderstandAccordionOpen(root, function () {
      setGrade(current);
    });

    if (slider) {
      slider.addEventListener("pointerdown", function (e) {
        if (e.target && e.target.closest && e.target.closest("[data-understand-color-tick]")) return;
        if (e.button != null && e.button !== 0) return;
        if (e.preventDefault) e.preventDefault();
        understandColorPointerBound = slider;
        if (slider.setPointerCapture && e.pointerId != null) {
          try {
            slider.setPointerCapture(e.pointerId);
          } catch (err) {}
        }
        setGrade(understandColorFromTrackPos(understandColorPosFromClientX(slider, e.clientX, current)), true);
        if (typeof slider.focus === "function") slider.focus();
      });
      slider.addEventListener("pointermove", function (e) {
        if (understandColorPointerBound !== slider) return;
        setGrade(understandColorFromTrackPos(understandColorPosFromClientX(slider, e.clientX, current)), true);
      });
      slider.addEventListener("pointerup", function () {
        understandColorPointerBound = null;
      });
      slider.addEventListener("pointercancel", function () {
        understandColorPointerBound = null;
      });
      slider.addEventListener("keydown", function (e) {
        var next = current;
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = stepUnderstandColor(current, -1);
        else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = stepUnderstandColor(current, 1);
        else if (e.key === "Home") next = UNDERSTAND_COLOR_GRADE_IDS[0];
        else if (e.key === "End") next = UNDERSTAND_COLOR_GRADE_IDS[UNDERSTAND_COLOR_GRADE_IDS.length - 1];
        else return;
        if (e.preventDefault) e.preventDefault();
        setGrade(next, true);
      });
    }

    root.addEventListener("click", function (e) {
      var tick = e.target && e.target.closest ? e.target.closest("[data-understand-color-tick]") : null;
      var strip = e.target && e.target.closest ? e.target.closest("[data-understand-color-strip-item]") : null;
      var id;
      if (tick) {
        id = tick.getAttribute("data-understand-color-tick");
      } else if (strip) {
        id = strip.getAttribute("data-understand-color-strip-item");
      } else {
        return;
      }
      if (!id) return;
      if (e.preventDefault) e.preventDefault();
      setGrade(id, true);
    });
  }

  function understandClarityContent() {
    var defaults = UNDERSTAND_CLARITY_CONTENT;
    return {
      index: hostDataAttr("data-clarity-edu-index") || defaults.index,
      label: hostDataAttr("data-clarity-edu-label") || defaults.label,
      headingLine1: hostDataAttr("data-clarity-edu-heading-1") || defaults.headingLine1,
      headingLine2: hostDataAttr("data-clarity-edu-heading-2") || defaults.headingLine2,
      lead: hostDataAttr("data-clarity-edu-lead") || defaults.lead,
      body: hostDataAttr("data-clarity-edu-body") || defaults.body,
      caption: hostDataAttr("data-clarity-edu-caption") || defaults.caption,
      controlLabel: hostDataAttr("data-clarity-edu-control-label") || defaults.controlLabel,
      fallback: hostDataAttr("data-clarity-edu-fallback") || defaults.fallback,
      modelNote: hostDataAttr("data-clarity-edu-model-note") || defaults.modelNote
    };
  }

  function understandClarityTitleHtml(content) {
    var html = escapeHtml(content.headingLine1 || "");
    if (content.headingLine2) html += (html ? " " : "") + escapeHtml(content.headingLine2);
    return html;
  }

  function understandClarityGrades() {
    var i;
    var grade;
    var id;
    var out = [];
    for (i = 0; i < UNDERSTAND_CLARITY_GRADES.length; i += 1) {
      grade = UNDERSTAND_CLARITY_GRADES[i];
      id = grade.id;
      out.push({
        id: id,
        label: hostDataAttr("data-clarity-edu-" + id + "-label") || grade.label,
        caption: hostDataAttr("data-clarity-edu-" + id + "-caption") || grade.caption,
        family: hostDataAttr("data-clarity-edu-" + id + "-family") || grade.family,
        heading: hostDataAttr("data-clarity-edu-" + id + "-heading") || grade.heading,
        body: hostDataAttr("data-clarity-edu-" + id + "-body") || grade.body,
        inspect: grade.inspect || UNDERSTAND_CLARITY_INSPECT
      });
    }
    return out;
  }

  function understandClarityGradeIndex(id) {
    var idx = UNDERSTAND_CLARITY_GRADE_IDS.indexOf(String(id || "").toLowerCase());
    return idx < 0 ? -1 : idx;
  }

  function understandClarityGradeById(id) {
    var grades = understandClarityGrades();
    var i;
    var key = String(id || "").toLowerCase();
    for (i = 0; i < grades.length; i += 1) {
      if (grades[i].id === key) return grades[i];
    }
    return grades[understandClarityGradeIndex(UNDERSTAND_CLARITY_EDU_DEFAULT)] || grades[0] || null;
  }

  function resolveUnderstandClarityInventoryGrade(diamond) {
    return specEducationClarityState(diamond && diamond.clarity) || "";
  }

  function understandClarityEduAttr(name) {
    var edu =
      window.document && typeof window.document.getElementById === "function"
        ? window.document.getElementById("diamondSearchEduModule")
        : null;
    if (!edu || !edu.getAttribute) return "";
    return String(edu.getAttribute(name) || "").trim();
  }

  function understandClarityMasterUrl() {
    return hostDataAttr("data-clarity-edu-master") || understandClarityEduAttr("data-edu-clarity-master") || "";
  }

  function understandClarityDiamondUrl(gradeId) {
    var key = String(gradeId || "").toLowerCase();
    var fromHost;
    var fromEdu;
    if (key) {
      fromHost = hostDataAttr("data-clarity-edu-" + key);
      if (fromHost) return fromHost;
      fromEdu = understandClarityEduAttr("data-edu-clarity-" + key);
      if (fromEdu) return fromEdu;
    }
    return understandClarityMasterUrl();
  }

  function understandClarityLoupeUrl(gradeId) {
    var key = String(gradeId || "").toLowerCase();
    var fromHost;
    var fromEdu;
    if (key) {
      fromHost = hostDataAttr("data-clarity-edu-" + key + "-10x");
      if (fromHost) return fromHost;
      fromEdu = understandClarityEduAttr("data-edu-clarity-" + key + "-10x");
      if (fromEdu) return fromEdu;
    }
    return hostDataAttr("data-clarity-edu-10x") || understandClarityEduAttr("data-edu-clarity-10x") || "";
  }

  function understandClarityDiamondKey(gradeId) {
    var url = understandClarityDiamondUrl(gradeId);
    var master = understandClarityMasterUrl();
    if (!url || url === master) return "master";
    return String(gradeId || "").toLowerCase();
  }

  function understandClarityLoupeKey(gradeId) {
    var key = String(gradeId || "").toLowerCase();
    if (!key) return "master";
    if (hostDataAttr("data-clarity-edu-" + key + "-10x") || understandClarityEduAttr("data-edu-clarity-" + key + "-10x")) {
      return key;
    }
    return "master";
  }

  function understandClarityDiamondLayers() {
    var layers = [];
    var seen = {};
    var i;
    var id;
    var url;
    function add(key, src) {
      if (!src || seen[src]) return;
      seen[src] = true;
      layers.push({ key: key, src: src });
    }
    add("master", understandClarityMasterUrl());
    for (i = 0; i < UNDERSTAND_CLARITY_GRADE_IDS.length; i += 1) {
      id = UNDERSTAND_CLARITY_GRADE_IDS[i];
      url = hostDataAttr("data-clarity-edu-" + id) || understandClarityEduAttr("data-edu-clarity-" + id);
      add(id, url);
    }
    return layers;
  }

  function understandClarityLoupeLayers() {
    var layers = [];
    var seen = {};
    var i;
    var id;
    var url;
    function add(key, src) {
      if (!src || seen[src]) return;
      seen[src] = true;
      layers.push({ key: key, src: src });
    }
    add("master", understandClarityLoupeUrl(""));
    for (i = 0; i < UNDERSTAND_CLARITY_GRADE_IDS.length; i += 1) {
      id = UNDERSTAND_CLARITY_GRADE_IDS[i];
      url = understandClarityLoupeUrl(id);
      add(id, url);
    }
    return layers;
  }

  function understandClarityToTrackPos(index) {
    var last = UNDERSTAND_CLARITY_GRADE_IDS.length - 1;
    var n = Number(index);
    if (!Number.isFinite(n) || n < 0) n = 0;
    if (n > last) n = last;
    return last <= 0 ? 0 : n / last;
  }

  function understandClarityFromTrackPos(pos) {
    var last = UNDERSTAND_CLARITY_GRADE_IDS.length - 1;
    var t = Number(pos);
    var index;
    if (!Number.isFinite(t)) t = 0;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    index = Math.round(t * last);
    if (index < 0) index = 0;
    if (index > last) index = last;
    return UNDERSTAND_CLARITY_GRADE_IDS[index];
  }

  function understandClarityThumbCenterPx(trackPos, trackWidth) {
    return understandCaratThumbCenterPx(trackPos, trackWidth);
  }

  function understandClarityVisualTrackPos(trackPos, trackWidth) {
    return understandCaratVisualTrackPos(trackPos, trackWidth);
  }

  function understandClarityPosFromClientX(slider, clientX, currentId) {
    var track = slider && slider.querySelector ? slider.querySelector("[data-understand-clarity-track]") : null;
    var rect = track && track.getBoundingClientRect
      ? track.getBoundingClientRect()
      : slider && slider.getBoundingClientRect
        ? slider.getBoundingClientRect()
        : null;
    var inset = UNDERSTAND_CLARITY_TRAVEL_INSET_PX;
    var usable;
    var t;
    if (!rect || !rect.width) return understandClarityToTrackPos(understandClarityGradeIndex(currentId));
    usable = Math.max(1, rect.width - 2 * inset);
    t = (clientX - rect.left - inset) / usable;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    return t;
  }

  function stepUnderstandClarity(currentId, delta) {
    var last = UNDERSTAND_CLARITY_GRADE_IDS.length - 1;
    var index = understandClarityGradeIndex(currentId);
    if (index < 0) index = understandClarityGradeIndex(UNDERSTAND_CLARITY_EDU_DEFAULT);
    index += delta < 0 ? -1 : 1;
    if (index < 0) index = 0;
    if (index > last) index = last;
    return UNDERSTAND_CLARITY_GRADE_IDS[index];
  }

  function resolveUnderstandClarityModel(diamond, selectedId, options) {
    var content = understandClarityContent();
    var inventory = resolveUnderstandClarityInventoryGrade(diamond);
    var source = inventory ? "actual" : "educational";
    var interacted = !!(options && options.interacted);
    var gradeId = selectedId || inventory || UNDERSTAND_CLARITY_EDU_DEFAULT;
    var grade;
    var index;
    var showGradeCopy;
    if (understandClarityGradeIndex(gradeId) < 0) {
      gradeId = inventory || UNDERSTAND_CLARITY_EDU_DEFAULT;
    }
    grade = understandClarityGradeById(gradeId);
    index = understandClarityGradeIndex(gradeId);
    if (index < 0) index = 0;
    showGradeCopy = source === "actual" || interacted;
    return {
      source: source,
      inventoryGrade: inventory,
      gradeId: grade.id,
      index: index,
      pos: understandClarityToTrackPos(index),
      label: grade.label,
      caption: grade.caption,
      family: grade.family,
      heading: showGradeCopy ? grade.heading : "",
      body: showGradeCopy ? grade.body : content.fallback,
      diamondUrl: understandClarityDiamondUrl(grade.id),
      loupeUrl: understandClarityLoupeUrl(grade.id),
      diamondKey: understandClarityDiamondKey(grade.id),
      loupeKey: understandClarityLoupeKey(grade.id),
      inspect: grade.inspect || UNDERSTAND_CLARITY_INSPECT,
      showGradeCaption: showGradeCopy,
      showModelNote: source === "educational",
      fallback: content.fallback,
      modelNote: content.modelNote,
      visualCaption: content.caption
    };
  }

  function applyUnderstandClarityBubbleLayout(slider, pos) {
    var layout;
    var half;
    var bubble;
    var width;
    if (!slider) return;
    bubble = slider.querySelector ? slider.querySelector("[data-understand-clarity-bubble]") : null;
    width = bubble && Number(bubble.offsetWidth);
    half =
      Number.isFinite(width) && width > 0
        ? width / 2
        : UNDERSTAND_CLARITY_BUBBLE_HALF_PX;
    layout = clampUnderstandCaratBubbleLayout(pos, slider.clientWidth, half);
    if (!slider.style || typeof slider.style.setProperty !== "function") return;
    if (layout.leftPx != null) {
      slider.style.setProperty("--dd-understand-clarity-bubble-left", layout.leftPx + "px");
    } else {
      slider.style.setProperty("--dd-understand-clarity-bubble-left", layout.leftPct + "%");
    }
    slider.style.setProperty("--dd-understand-clarity-bubble-pointer-shift", layout.pointerShiftPx + "px");
  }

  function updateUnderstandClarityOptics(scope, model) {
    var loupe = scope && scope.querySelector ? scope.querySelector("[data-understand-clarity-loupe]") : null;
    var focus = scope && scope.querySelector ? scope.querySelector("[data-understand-clarity-focus]") : null;
    var leader = scope && scope.querySelector ? scope.querySelector("[data-understand-clarity-leader]") : null;
    var line = scope && scope.querySelector ? scope.querySelector("[data-understand-clarity-leader-line]") : null;
    var inspect = (model && model.inspect) || UNDERSTAND_CLARITY_INSPECT;
    var stage;
    var stageRect;
    var focusRect;
    var loupeRect;
    var x1;
    var y1;
    var x2;
    var y2;
    var dx;
    var dy;
    var dist;
    var rFocus;
    var rLoupe;
    if (focus) {
      focus.setAttribute("cx", String(Math.round(inspect.x * 960)));
      focus.setAttribute("cy", String(Math.round(inspect.y * 564)));
      focus.setAttribute("opacity", model && model.gradeId === "fl" ? "0.45" : "0.75");
    }
    if (!leader || !line || !loupe || !focus) return;
    stage = leader.parentNode;
    if (!stage || !stage.getBoundingClientRect) return;
    stageRect = stage.getBoundingClientRect();
    focusRect = focus.getBoundingClientRect ? focus.getBoundingClientRect() : null;
    loupeRect = loupe.getBoundingClientRect ? loupe.getBoundingClientRect() : null;
    if (!stageRect.width || !focusRect || !focusRect.width || !loupeRect || !loupeRect.width) return;
    leader.setAttribute("viewBox", "0 0 " + stageRect.width + " " + stageRect.height);
    leader.setAttribute("width", String(stageRect.width));
    leader.setAttribute("height", String(stageRect.height));
    x1 = focusRect.left + focusRect.width / 2 - stageRect.left;
    y1 = focusRect.top + focusRect.height / 2 - stageRect.top;
    x2 = loupeRect.left + loupeRect.width / 2 - stageRect.left;
    y2 = loupeRect.top + loupeRect.height / 2 - stageRect.top;
    dx = x2 - x1;
    dy = y2 - y1;
    dist = Math.sqrt(dx * dx + dy * dy) || 1;
    rFocus = Math.max(focusRect.width, focusRect.height) / 2;
    rLoupe = loupeRect.width / 2 + 1;
    line.setAttribute("x1", String(x1 + (dx / dist) * rFocus));
    line.setAttribute("y1", String(y1 + (dy / dist) * rFocus));
    line.setAttribute("x2", String(x2 - (dx / dist) * rLoupe));
    line.setAttribute("y2", String(y2 - (dy / dist) * rLoupe));
  }

  function applyUnderstandClarityValue(root, diamond, selectedId, options) {
    var scope = root && root.querySelector
      ? root.querySelector("[data-understand-clarity]") || root
      : root;
    var model = resolveUnderstandClarityModel(diamond, selectedId, options);
    var fillPct = model.pos * 100;
    function findEl(sel) {
      var el = scope && scope.querySelector ? scope.querySelector(sel) : null;
      if (el) return el;
      if (root && root !== scope && root.querySelector) return root.querySelector(sel);
      return null;
    }
    function findAll(sel) {
      var list = scope && scope.querySelectorAll ? scope.querySelectorAll(sel) : [];
      if (list && list.length) return list;
      if (root && root !== scope && root.querySelectorAll) return root.querySelectorAll(sel) || [];
      return list || [];
    }
    var slider = findEl("[data-understand-clarity-slider]");
    var bubble = findEl("[data-understand-clarity-bubble]");
    var caption = findEl("[data-understand-clarity-grade-caption]");
    var family = findEl("[data-understand-clarity-family]");
    var heading = findEl("[data-understand-clarity-card-heading]");
    var body = findEl("[data-understand-clarity-card-body]");
    var note = findEl("[data-understand-clarity-model-note]");
    var imgs = findAll("[data-understand-clarity-img]");
    var loupeImgs = findAll("[data-understand-clarity-loupe-img]");
    var ticks = findAll("[data-understand-clarity-tick]");
    var visual = findEl("[data-understand-clarity-visual]");
    var i;
    var tickId;
    var imgId;

    if (scope && scope.setAttribute) {
      scope.setAttribute("data-understand-clarity-state", model.source);
      scope.setAttribute("data-understand-clarity-value", model.gradeId);
      scope.setAttribute("data-understand-clarity-interacted", options && options.interacted ? "true" : "false");
      if (model.inventoryGrade) scope.setAttribute("data-understand-clarity-inventory", model.inventoryGrade);
      else scope.removeAttribute("data-understand-clarity-inventory");
    }
    if (visual) visual.setAttribute("data-understand-clarity-visual-state", model.gradeId);
    for (i = 0; i < (imgs.length || 0); i += 1) {
      imgId = imgs[i].getAttribute("data-understand-clarity-img");
      if (imgId === model.diamondKey) {
        if (imgs[i].classList && imgs[i].classList.add) imgs[i].classList.add("is-active");
      } else if (imgs[i].classList && imgs[i].classList.remove) {
        imgs[i].classList.remove("is-active");
      }
    }
    for (i = 0; i < (loupeImgs.length || 0); i += 1) {
      imgId = loupeImgs[i].getAttribute("data-understand-clarity-loupe-img");
      if (imgId === model.loupeKey) {
        if (loupeImgs[i].classList && loupeImgs[i].classList.add) loupeImgs[i].classList.add("is-active");
      } else if (loupeImgs[i].classList && loupeImgs[i].classList.remove) {
        loupeImgs[i].classList.remove("is-active");
      }
    }
    if (caption) {
      caption.textContent = model.showGradeCaption ? model.caption : "";
      if (model.showGradeCaption) caption.removeAttribute("hidden");
      else caption.setAttribute("hidden", "");
    }
    if (family) {
      family.textContent = model.showGradeCaption ? model.family : "";
      if (model.showGradeCaption && model.family) family.removeAttribute("hidden");
      else family.setAttribute("hidden", "");
    }
    if (note) {
      note.textContent = model.modelNote;
      if (model.showModelNote) note.removeAttribute("hidden");
      else note.setAttribute("hidden", "");
    }
    if (heading) {
      heading.textContent = model.heading;
      if (model.heading) heading.removeAttribute("hidden");
      else heading.setAttribute("hidden", "");
    }
    if (body) body.textContent = model.body;
    if (bubble) bubble.textContent = model.label;
    if (slider) {
      if (slider.style && typeof slider.style.setProperty === "function") {
        slider.style.setProperty("--dd-understand-clarity-n", String(model.pos));
        slider.style.setProperty("--dd-understand-clarity-track-pct", fillPct + "%");
      }
      applyUnderstandClarityBubbleLayout(slider, model.pos);
      slider.setAttribute("aria-valuemin", "0");
      slider.setAttribute("aria-valuemax", String(UNDERSTAND_CLARITY_GRADE_IDS.length - 1));
      slider.setAttribute("aria-valuenow", String(model.index));
      slider.setAttribute("aria-valuetext", model.label);
    }
    for (i = 0; i < (ticks.length || 0); i += 1) {
      tickId = ticks[i].getAttribute("data-understand-clarity-tick");
      if (tickId === model.gradeId) ticks[i].setAttribute("aria-current", "true");
      else ticks[i].removeAttribute("aria-current");
    }
    updateUnderstandClarityOptics(scope, model);
    return model;
  }

  function renderUnderstandClarityVisual(model) {
    var diamondLayers = understandClarityDiamondLayers();
    var loupeLayers = understandClarityLoupeLayers();
    var inspect = model.inspect || UNDERSTAND_CLARITY_INSPECT;
    var masterUrl = understandClarityMasterUrl();
    var html =
      '<div class="diamond-details__understand-clarity-stage">' +
      '<div class="diamond-details__understand-clarity-media" data-understand-clarity-visual data-understand-clarity-visual-state="' +
      escapeHtml(model.gradeId) +
      '" aria-hidden="true">';
    var i;
    var layer;
    if (masterUrl) {
      html +=
        '<img class="diamond-details__understand-clarity-sizer" src="' +
        escapeHtml(masterUrl) +
        '" width="960" height="564" alt="" decoding="async" aria-hidden="true">';
    }
    for (i = 0; i < diamondLayers.length; i += 1) {
      layer = diamondLayers[i];
      html +=
        '<img class="diamond-details__understand-clarity-img' +
        (layer.key === model.diamondKey ? " is-active" : "") +
        '" data-understand-clarity-img="' +
        escapeHtml(layer.key) +
        '" src="' +
        escapeHtml(layer.src) +
        '" width="960" height="564" alt="" decoding="async">';
    }
    html +=
      '<svg class="diamond-details__understand-clarity-chars" viewBox="0 0 960 564" preserveAspectRatio="xMidYMid meet" focusable="false">' +
      '<circle class="diamond-details__understand-clarity-focus" data-understand-clarity-focus cx="' +
      escapeHtml(String(Math.round(inspect.x * 960))) +
      '" cy="' +
      escapeHtml(String(Math.round(inspect.y * 564))) +
      '" r="20" fill="none" stroke="#b8b3ab" stroke-width="2.25" opacity="' +
      (model.gradeId === "fl" ? "0.45" : "0.75") +
      '"></circle></svg></div>' +
      '<div class="diamond-details__understand-clarity-inspect">' +
      '<div class="diamond-details__understand-clarity-loupe" data-understand-clarity-loupe>';
    for (i = 0; i < loupeLayers.length; i += 1) {
      layer = loupeLayers[i];
      html +=
        '<img class="diamond-details__understand-clarity-loupe-view' +
        (layer.key === model.loupeKey ? " is-active" : "") +
        '" data-understand-clarity-loupe-img="' +
        escapeHtml(layer.key) +
        '" src="' +
        escapeHtml(layer.src) +
        '" width="360" height="360" alt="" decoding="async">';
    }
    html +=
      "</div>" +
      '<span class="diamond-details__understand-clarity-loupe-label">10\u00d7</span></div>' +
      '<svg class="diamond-details__understand-clarity-leader" data-understand-clarity-leader focusable="false">' +
      '<line data-understand-clarity-leader-line x1="0" y1="0" x2="0" y2="0"></line></svg></div>' +
      '<p class="diamond-details__understand-clarity-caption" data-understand-clarity-caption>' +
      escapeHtml(model.visualCaption) +
      "</p>" +
      '<p class="diamond-details__understand-clarity-grade-caption" data-understand-clarity-grade-caption' +
      (model.showGradeCaption ? "" : " hidden") +
      ">" +
      escapeHtml(model.showGradeCaption ? model.caption : "") +
      "</p>" +
      '<p class="diamond-details__understand-clarity-family" data-understand-clarity-family' +
      (model.showGradeCaption && model.family ? "" : " hidden") +
      ">" +
      escapeHtml(model.showGradeCaption ? model.family : "") +
      "</p>" +
      '<p class="diamond-details__understand-clarity-model-note" data-understand-clarity-model-note' +
      (model.showModelNote ? "" : " hidden") +
      ">" +
      escapeHtml(model.modelNote) +
      "</p>";
    return html;
  }

  function renderUnderstandClaritySlider(model) {
    var grades = understandClarityGrades();
    var last = grades.length - 1;
    var pos = model.pos;
    var fillPct = pos * 100;
    var html =
      '<div class="diamond-details__understand-clarity-scale">' +
      '<p class="diamond-details__understand-clarity-control-label" id="diamond-details-understand-clarity-grade">' +
      escapeHtml(understandClarityContent().controlLabel) +
      "</p>" +
      '<div class="diamond-details__understand-clarity-slider" data-understand-clarity-slider role="slider" tabindex="0" aria-labelledby="diamond-details-understand-clarity-grade" aria-valuemin="0" aria-valuemax="' +
      last +
      '" aria-valuenow="' +
      model.index +
      '" aria-valuetext="' +
      escapeHtml(model.label) +
      '" style="--dd-understand-clarity-n:' +
      pos +
      ';--dd-understand-clarity-track-pct:' +
      fillPct +
      '%;--dd-understand-clarity-bubble-pointer-shift:0px">' +
      '<div class="diamond-details__understand-clarity-bubble" data-understand-clarity-bubble aria-hidden="true">' +
      escapeHtml(model.label) +
      "</div>" +
      '<div class="diamond-details__understand-clarity-track" data-understand-clarity-track>' +
      '<span class="diamond-details__understand-clarity-rail" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-clarity-fill" data-understand-clarity-fill aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-clarity-handle" data-understand-clarity-handle aria-hidden="true"></span>' +
      "</div>" +
      '<div class="diamond-details__understand-clarity-ticks" role="presentation">';
    var i;
    var grade;
    var tickPos;
    for (i = 0; i < grades.length; i += 1) {
      grade = grades[i];
      tickPos = last <= 0 ? 0 : i / last;
      html +=
        '<button type="button" class="diamond-details__understand-clarity-tick" data-understand-clarity-tick="' +
        escapeHtml(grade.id) +
        '"' +
        (grade.id === model.gradeId ? ' aria-current="true"' : "") +
        ' tabindex="-1" style="--dd-understand-clarity-tick-n:' +
        tickPos +
        '" aria-label="Set clarity grade to ' +
        escapeHtml(grade.label) +
        '"><span class="diamond-details__understand-clarity-tick-mark" aria-hidden="true"></span><span class="diamond-details__understand-clarity-tick-label">' +
        escapeHtml(grade.label) +
        "</span></button>";
    }
    html += "</div></div></div>";
    return html;
  }

  function renderUnderstandClarityCard(model) {
    return (
      '<div class="diamond-details__understand-clarity-card" data-understand-clarity-card>' +
      '<p class="diamond-details__understand-clarity-card-heading" data-understand-clarity-card-heading' +
      (model.heading ? "" : " hidden") +
      ">" +
      escapeHtml(model.heading) +
      "</p>" +
      '<p class="diamond-details__understand-clarity-card-body" data-understand-clarity-card-body>' +
      escapeHtml(model.body) +
      "</p></div>"
    );
  }

  function renderUnderstandClarity(diamond) {
    var model = resolveUnderstandClarityModel(diamond, resolveUnderstandClarityInventoryGrade(diamond) || UNDERSTAND_CLARITY_EDU_DEFAULT, {
      interacted: false
    });
    var content = understandClarityContent();
    return (
      '<div class="diamond-details__understand-clarity" data-understand-clarity data-understand-clarity-state="' +
      escapeHtml(model.source) +
      '" data-understand-clarity-value="' +
      escapeHtml(model.gradeId) +
      '" data-understand-clarity-interacted="false"' +
      (model.inventoryGrade ? ' data-understand-clarity-inventory="' + escapeHtml(model.inventoryGrade) + '"' : "") +
      ">" +
      '<div class="diamond-details__understand-clarity-chapter">' +
      '<div class="diamond-details__understand-clarity-edu">' +
      '<p class="diamond-details__understand-clarity-eyebrow" data-understand-clarity-eyebrow>' +
      '<span class="diamond-details__understand-clarity-index">' +
      escapeHtml(content.index) +
      "</span>" +
      '<span class="diamond-details__understand-clarity-eyebrow-rule" aria-hidden="true"></span>' +
      '<span class="diamond-details__understand-clarity-label">' +
      escapeHtml(content.label) +
      "</span></p>" +
      '<h2 class="diamond-details__understand-clarity-title" id="diamond-details-understand-clarity-title" data-understand-clarity-title>' +
      understandClarityTitleHtml(content) +
      "</h2>" +
      '<p class="diamond-details__understand-clarity-lead" data-understand-clarity-lead>' +
      escapeHtml(content.lead) +
      "</p>" +
      '<p class="diamond-details__understand-clarity-explain" data-understand-clarity-explain>' +
      escapeHtml(content.body) +
      "</p></div>" +
      '<div class="diamond-details__understand-clarity-visual">' +
      renderUnderstandClarityVisual(model) +
      "</div>" +
      '<div class="diamond-details__understand-clarity-controls">' +
      renderUnderstandClaritySlider(model) +
      renderUnderstandClarityCard(model) +
      "</div></div></div>"
    );
  }

  function bindUnderstandClarity(region, diamond) {
    var root = region && region.querySelector ? region.querySelector("[data-understand-clarity]") : null;
    var slider;
    var inventory = resolveUnderstandClarityInventoryGrade(diamond);
    var current = inventory || UNDERSTAND_CLARITY_EDU_DEFAULT;
    var interacted = false;

    if (!root) return;

    function setGrade(next, fromUser) {
      if (fromUser) interacted = true;
      var model = applyUnderstandClarityValue(root, diamond, next, { interacted: interacted });
      current = model.gradeId;
      return model;
    }

    slider = root.querySelector("[data-understand-clarity-slider]");
    applyUnderstandClarityValue(root, diamond, current, { interacted: false });
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        applyUnderstandClarityValue(root, diamond, current, { interacted: interacted });
      });
    }
    listenUnderstandAccordionOpen(root, function () {
      setGrade(current);
    });

    if (slider) {
      slider.addEventListener("pointerdown", function (e) {
        if (e.target && e.target.closest && e.target.closest("[data-understand-clarity-tick]")) return;
        if (e.button != null && e.button !== 0) return;
        if (e.preventDefault) e.preventDefault();
        understandClarityPointerBound = slider;
        if (slider.setPointerCapture && e.pointerId != null) {
          try {
            slider.setPointerCapture(e.pointerId);
          } catch (err) {}
        }
        setGrade(understandClarityFromTrackPos(understandClarityPosFromClientX(slider, e.clientX, current)), true);
        if (typeof slider.focus === "function") slider.focus();
      });
      slider.addEventListener("pointermove", function (e) {
        if (understandClarityPointerBound !== slider) return;
        setGrade(understandClarityFromTrackPos(understandClarityPosFromClientX(slider, e.clientX, current)), true);
      });
      slider.addEventListener("pointerup", function () {
        understandClarityPointerBound = null;
      });
      slider.addEventListener("pointercancel", function () {
        understandClarityPointerBound = null;
      });
      slider.addEventListener("keydown", function (e) {
        var next = current;
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = stepUnderstandClarity(current, -1);
        else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = stepUnderstandClarity(current, 1);
        else if (e.key === "Home") next = UNDERSTAND_CLARITY_GRADE_IDS[0];
        else if (e.key === "End") next = UNDERSTAND_CLARITY_GRADE_IDS[UNDERSTAND_CLARITY_GRADE_IDS.length - 1];
        else return;
        if (e.preventDefault) e.preventDefault();
        setGrade(next, true);
      });
    }

    root.addEventListener("click", function (e) {
      var tick = e.target && e.target.closest ? e.target.closest("[data-understand-clarity-tick]") : null;
      var id;
      if (!tick) return;
      id = tick.getAttribute("data-understand-clarity-tick");
      if (!id) return;
      if (e.preventDefault) e.preventDefault();
      setGrade(id, true);
    });
  }

  function understandGradeLabel(grades, id) {
    var key = String(id || "").toLowerCase();
    var i;
    if (!key || !grades) return "";
    for (i = 0; i < grades.length; i += 1) {
      if (grades[i] && String(grades[i].id || "").toLowerCase() === key) return grades[i].label || "";
    }
    return "";
  }

  function understandAccordionSummary(id, diamond) {
    var content = understandCaratContent();
    var value = "";
    var aec;
    var model;
    if (id === "cut") content = understandCutContent();
    else if (id === "color") content = understandColorContent();
    else if (id === "clarity") content = understandClarityContent();
    if (id === "carat") {
      aec = caratAecApi();
      model = resolveUnderstandCaratModel(diamond, selectedSliderCarat(diamond, aec));
      value = model && model.caratCopy ? model.caratCopy : "";
    } else if (id === "cut") {
      value = understandGradeLabel(UNDERSTAND_CUT_GRADES, resolveUnderstandCutInventoryGrade(diamond));
    } else if (id === "color") {
      value = understandGradeLabel(UNDERSTAND_COLOR_GRADES, resolveUnderstandColorInventoryGrade(diamond));
    } else if (id === "clarity") {
      value = understandGradeLabel(UNDERSTAND_CLARITY_GRADES, resolveUnderstandClarityInventoryGrade(diamond));
    }
    return {
      index: content.index || "",
      label: content.label || "",
      value: value || "\u2014"
    };
  }

  function renderUnderstandAccordionTrigger(id, diamond) {
    var summary = understandAccordionSummary(id, diamond);
    var panelId = understandModuleDomId(id) + "-panel";
    return (
      '<button type="button" class="diamond-details__4c-acc-trigger" data-4c-acc-trigger="' +
      escapeHtml(id) +
      '" aria-expanded="false" aria-controls="' +
      escapeHtml(panelId) +
      '">' +
      '<span class="diamond-details__4c-acc-index">' +
      escapeHtml(summary.index) +
      "</span>" +
      '<span class="diamond-details__4c-acc-name">' +
      escapeHtml(summary.label) +
      "</span>" +
      '<span class="diamond-details__4c-acc-value">' +
      escapeHtml(summary.value) +
      "</span>" +
      '<span class="diamond-details__4c-acc-mark" aria-hidden="true"></span></button>'
    );
  }

  function renderUnderstandYourDiamond(diamond, activeId) {
    var html =
      '<div class="diamond-details__understand" id="diamond-details-understand" data-detail-region="understand" data-understand-foundation>' +
      '<div class="diamond-details__understand-modules">';
    var i;
    var item;
    var headingId;
    var body;
    for (i = 0; i < UNDERSTAND_MODULES.length; i += 1) {
      item = UNDERSTAND_MODULES[i];
      headingId = "diamond-details-understand-" + item.id + "-title";
      body = "";
      if (item.id === "carat") body = renderUnderstandCarat(diamond);
      if (item.id === "cut") body = renderUnderstandCut(diamond);
      if (item.id === "color") body = renderUnderstandColor(diamond);
      if (item.id === "clarity") body = renderUnderstandClarity(diamond);
      html +=
        '<section class="diamond-details__understand-module diamond-details__understand-edu-card" id="' +
        understandModuleDomId(item.id) +
        '" data-understand-module="' +
        escapeHtml(item.id) +
        '" data-understand-edu-card="' +
        escapeHtml(item.id) +
        '" aria-labelledby="' +
        headingId +
        '"' +
        (isPopulatedUnderstandModule(item.id) ? "" : " hidden") +
        ">" +
        renderUnderstandAccordionTrigger(item.id, diamond) +
        '<div class="diamond-details__4c-acc-panel" id="' +
        understandModuleDomId(item.id) +
        '-panel" data-4c-acc-panel="' +
        escapeHtml(item.id) +
        '" hidden>' +
        body +
        "</div></section>";
    }
    html += "</div></div>";
    return html;
  }

  function applyUnderstandCaratBubbleLayout(slider, pos, aec) {
    var layout;
    var half;
    if (!slider) return;
    half = understandCaratBubbleHalfPx(slider);
    layout = clampUnderstandCaratBubbleLayout(pos, slider.clientWidth, half);
    if (!slider.style || typeof slider.style.setProperty !== "function") return;
    if (layout.leftPx != null) {
      slider.style.setProperty("--dd-understand-carat-bubble-left", layout.leftPx + "px");
    } else {
      slider.style.setProperty("--dd-understand-carat-bubble-left", layout.leftPct + "%");
    }
    slider.style.setProperty("--dd-understand-carat-bubble-pointer-shift", layout.pointerShiftPx + "px");
  }

  function applyUnderstandCaratMediaStyle(el, model) {
    var style;
    var parts;
    var i;
    var part;
    var idx;
    var name;
    var value;
    if (!el) return;
    style = understandCaratMediaStyle(model);
    if (el.setAttribute) el.setAttribute("style", style);
    if (!el.style || typeof el.style.setProperty !== "function") return;
    parts = String(style).split(";");
    for (i = 0; i < parts.length; i += 1) {
      part = parts[i];
      idx = part.indexOf(":");
      if (idx < 1) continue;
      name = part.slice(0, idx).trim();
      value = part.slice(idx + 1).trim();
      if (name) el.style.setProperty(name, value);
    }
  }

  function applyUnderstandCaratValue(root, diamond, sliderCarat) {
    var scope = root && root.querySelector
      ? root.querySelector("[data-understand-carat]") || root
      : root;
    var model = resolveUnderstandCaratModel(diamond, sliderCarat);
    var aec = caratAecApi();
    var scaleMax = model.sliderMax != null ? model.sliderMax : UNDERSTAND_CARAT_SLIDER_BASE_MAX;
    var pos = understandCaratToTrackPos(model.sliderCarat, aec, scaleMax);
    var fillPct = pos * 100;
    function findEl(sel) {
      var el = scope && scope.querySelector ? scope.querySelector(sel) : null;
      if (el) return el;
      if (root && root !== scope && root.querySelector) return root.querySelector(sel);
      return null;
    }
    function findAll(sel) {
      var list = scope && scope.querySelectorAll ? scope.querySelectorAll(sel) : [];
      if (list && list.length) return list;
      if (root && root !== scope && root.querySelectorAll) return root.querySelectorAll(sel) || [];
      return list || [];
    }
    var media = findEl("[data-understand-carat-media]");
    var overlay = findEl("[data-understand-carat-overlay]");
    var heading = findEl("[data-understand-carat-heading]");
    var sizeEl = findEl("[data-understand-carat-size]");
    var varyEl = findEl("[data-understand-carat-vary]");
    var bubble = findEl("[data-understand-carat-bubble]");
    var slider = findEl("[data-understand-carat-slider]");
    var ticks = findAll("[data-understand-carat-tick]");
    var i;
    var tickCt;

    if (scope && scope.setAttribute) {
      scope.setAttribute("data-understand-carat-state", model.source);
      scope.setAttribute("data-understand-carat-shape", model.shapeKey || "");
      scope.setAttribute("data-understand-carat-value", understandCaratKey(model.displayCarat));
      scope.setAttribute("data-understand-carat-slider-value", understandCaratKey(model.sliderCarat));
      if (model.lengthMm != null) scope.setAttribute("data-understand-carat-length-mm", String(model.lengthMm));
      else scope.removeAttribute("data-understand-carat-length-mm");
      if (model.widthMm != null) scope.setAttribute("data-understand-carat-width-mm", String(model.widthMm));
      else scope.removeAttribute("data-understand-carat-width-mm");
    }
    if (media) applyUnderstandCaratMediaStyle(media, model);
    if (overlay) {
      applyUnderstandCaratMediaStyle(overlay, model);
      if (model.shapeKey) overlay.setAttribute("data-size-shape-asset", model.shapeKey);
      if (model.shapeKey === "pear") {
        if (overlay.classList && overlay.classList.add) overlay.classList.add("diamond-details__size-stone--pear");
      } else if (overlay.classList && overlay.classList.remove) {
        overlay.classList.remove("diamond-details__size-stone--pear");
      }
    }
    if (heading) heading.textContent = model.caratCopy;
    if (sizeEl) sizeEl.textContent = model.sizeCopy;
    if (varyEl) {
      varyEl.textContent = model.varyCopy;
      if (model.varyCopy) varyEl.removeAttribute("hidden");
      else varyEl.setAttribute("hidden", "");
    }
    if (bubble) bubble.textContent = model.caratCopy;
    if (slider) {
      if (slider.style && typeof slider.style.setProperty === "function") {
        slider.style.setProperty("--dd-understand-carat-n", String(pos));
        slider.style.setProperty("--dd-understand-carat-track-pct", fillPct + "%");
      }
      applyUnderstandCaratBubbleLayout(slider, pos, aec);
      slider.setAttribute("aria-valuemin", String(model.sliderMin != null ? model.sliderMin : UNDERSTAND_CARAT_SLIDER_MIN));
      slider.setAttribute("aria-valuemax", String(scaleMax));
      slider.setAttribute("aria-valuenow", String(model.displayCarat));
      slider.setAttribute("aria-valuetext", model.caratCopy);
    }
    for (i = 0; i < (ticks.length || 0); i += 1) {
      tickCt = parseFloat(ticks[i].getAttribute("data-understand-carat-tick"));
      if (isSelectedInventoryCarat(tickCt, model.sliderCarat)) ticks[i].setAttribute("aria-current", "true");
      else ticks[i].removeAttribute("aria-current");
    }
    return model;
  }

  function understandCaratPosFromClientX(slider, clientX, aec, current, scaleMax) {
    var track = slider && slider.querySelector ? slider.querySelector("[data-understand-carat-track]") : null;
    var rect = track && track.getBoundingClientRect
      ? track.getBoundingClientRect()
      : slider && slider.getBoundingClientRect
        ? slider.getBoundingClientRect()
        : null;
    if (!rect || !rect.width) return understandCaratToTrackPos(current, aec, scaleMax);
    var inset = UNDERSTAND_CARAT_TRAVEL_INSET_PX;
    var usable = Math.max(1, rect.width - 2 * inset);
    var t = (clientX - rect.left - inset) / usable;
    if (t < 0) t = 0;
    if (t > 1) t = 1;
    return t;
  }

  function stepUnderstandCarat(current, delta, selectedCt, aec, scaleMax) {
    var step = aec && aec.CARAT_STEP != null ? aec.CARAT_STEP : UNDERSTAND_CARAT_SLIDER_STEP;
    var next;
    var snapped;
    var max = Number(scaleMax);
    if (!Number.isFinite(max) || max < UNDERSTAND_CARAT_SLIDER_MIN) max = understandCaratSliderCeiling(selectedCt);
    if (delta === 0) return current;
    if (isSelectedInventoryCarat(current, selectedCt) && selectedCt != null) {
      if (delta < 0) {
        snapped = snapUnderstandCaratSlider(selectedCt, max, step);
        if (snapped < selectedCt - 1e-6) return snapped;
        next = selectedCt + delta;
      } else {
        snapped = snapUnderstandCaratSlider(selectedCt + step, max, step);
        return clampUnderstandCarat(snapped, aec, max);
      }
    } else {
      next = current + delta;
    }
    next = snapUnderstandCaratSlider(next, max, step);
    if (
      selectedCt != null &&
      ((current < selectedCt && next > selectedCt) || (current > selectedCt && next < selectedCt))
    ) {
      return selectedCt;
    }
    return clampUnderstandCarat(next, aec, max);
  }

  function bindUnderstandCarat(region, diamond) {
    var root = region && region.querySelector ? region.querySelector("[data-understand-carat]") : null;
    var slider;
    var aec = caratAecApi();
    var selectedSlide = selectedSliderCarat(diamond, aec);
    var scaleMax = understandCaratSliderMaxForDiamond(diamond);
    var current = selectedSlide;

    if (!root) return;

    function setCarat(next) {
      var model = applyUnderstandCaratValue(root, diamond, next);
      current = model.sliderCarat;
      return model;
    }

    slider = root.querySelector("[data-understand-carat-slider]");
    applyUnderstandCaratValue(root, diamond, current);
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(function () {
        applyUnderstandCaratValue(root, diamond, current);
      });
    }
    listenUnderstandAccordionOpen(root, function () {
      setCarat(current);
    });

    if (slider) {
      slider.addEventListener("pointerdown", function (e) {
        if (e.target && e.target.closest && e.target.closest("[data-understand-carat-tick]")) return;
        if (e.button != null && e.button !== 0) return;
        if (e.preventDefault) e.preventDefault();
        understandCaratPointerBound = slider;
        if (slider.setPointerCapture && e.pointerId != null) {
          try {
            slider.setPointerCapture(e.pointerId);
          } catch (err) {}
        }
        setCarat(understandCaratFromTrackPos(understandCaratPosFromClientX(slider, e.clientX, aec, current, scaleMax), selectedSlide, aec, scaleMax));
        if (typeof slider.focus === "function") slider.focus();
      });
      slider.addEventListener("pointermove", function (e) {
        if (understandCaratPointerBound !== slider) return;
        setCarat(understandCaratFromTrackPos(understandCaratPosFromClientX(slider, e.clientX, aec, current, scaleMax), selectedSlide, aec, scaleMax));
      });
      slider.addEventListener("pointerup", function () {
        understandCaratPointerBound = null;
      });
      slider.addEventListener("pointercancel", function () {
        understandCaratPointerBound = null;
      });
      slider.addEventListener("keydown", function (e) {
        var next = current;
        var step = aec && aec.CARAT_STEP != null ? aec.CARAT_STEP : UNDERSTAND_CARAT_SLIDER_STEP;
        if (e.key === "ArrowLeft" || e.key === "ArrowDown") next = stepUnderstandCarat(current, -step, selectedSlide, aec, scaleMax);
        else if (e.key === "ArrowRight" || e.key === "ArrowUp") next = stepUnderstandCarat(current, step, selectedSlide, aec, scaleMax);
        else if (e.key === "PageDown") next = stepUnderstandCarat(current, -0.25, selectedSlide, aec, scaleMax);
        else if (e.key === "PageUp") next = stepUnderstandCarat(current, 0.25, selectedSlide, aec, scaleMax);
        else if (e.key === "Home") next = UNDERSTAND_CARAT_SLIDER_MIN;
        else if (e.key === "End") next = scaleMax;
        else return;
        if (e.preventDefault) e.preventDefault();
        setCarat(next);
      });
    }

    root.addEventListener("click", function (e) {
      var tick = e.target && e.target.closest ? e.target.closest("[data-understand-carat-tick]") : null;
      var ct;
      if (!tick) return;
      ct = parseFloat(tick.getAttribute("data-understand-carat-tick"));
      if (!isFinite(ct)) return;
      if (e.preventDefault) e.preventDefault();
      setCarat(ct);
    });
  }

  function listenUnderstandAccordionOpen(root, refresh) {
    if (!root || typeof root.addEventListener !== "function" || typeof refresh !== "function") return;
    root.addEventListener("diamond-details:4c-open", function () {
      refresh();
    });
  }

  function setUnderstandAccordion(region, id) {
    var triggers;
    var list;
    var i;
    var button;
    var panelId;
    var panel;
    var open;
    if (!region || !region.querySelectorAll) return;
    triggers = region.querySelectorAll("[data-4c-acc-trigger]");
    list = triggers && typeof triggers.length === "number" ? triggers : triggers ? [triggers] : [];
    for (i = 0; i < list.length; i += 1) {
      button = list[i];
      open = !!(id && button.getAttribute("data-4c-acc-trigger") === id);
      button.setAttribute("aria-expanded", open ? "true" : "false");
      panelId = button.getAttribute("aria-controls");
      panel = panelId && window.document && window.document.getElementById
        ? window.document.getElementById(panelId)
        : null;
      if (!panel && region.querySelector && panelId) {
        panel = region.querySelector("#" + panelId);
      }
      if (!panel) continue;
      if (open) {
        panel.removeAttribute("hidden");
        if (panel.hidden != null) panel.hidden = false;
        if (typeof window.CustomEvent === "function" && typeof panel.dispatchEvent === "function") {
          (function (opened) {
            var notify = function () {
              opened.dispatchEvent(new window.CustomEvent("diamond-details:4c-open", { bubbles: true }));
            };
            if (window.requestAnimationFrame) window.requestAnimationFrame(notify);
            else notify();
          })(panel);
        }
      } else {
        panel.setAttribute("hidden", "");
        if (panel.hidden != null) panel.hidden = true;
      }
    }
  }

  function bindUnderstandAccordion(region) {
    var triggers;
    var list;
    var i;
    if (!region || !region.querySelectorAll) return;
    triggers = region.querySelectorAll("[data-4c-acc-trigger]");
    list = triggers && typeof triggers.length === "number" ? triggers : triggers ? [triggers] : [];
    for (i = 0; i < list.length; i += 1) {
      list[i].addEventListener("click", function (event) {
        var button = (event && (event.currentTarget || event.target)) || this;
        var next = button && button.getAttribute ? button.getAttribute("data-4c-acc-trigger") : "";
        if (button && button.getAttribute("aria-expanded") === "true") next = "";
        setUnderstandAccordion(region, next);
      });
    }
  }

  function bindUnderstandNav(region, diamond) {
    if (!region) return;
    var links = region.querySelectorAll("[data-understand-target]");
    var modules = region.querySelectorAll("[data-understand-module]");
    var list = links && typeof links.length === "number" ? links : links ? [links] : [];
    var moduleList = modules && typeof modules.length === "number" ? modules : modules ? [modules] : [];

    function setCurrent(id) {
      var i;
      var moduleId;
      for (i = 0; i < list.length; i += 1) {
        if (list[i].getAttribute("data-understand-target") === id) {
          list[i].setAttribute("aria-current", "true");
        } else {
          list[i].removeAttribute("aria-current");
        }
      }
      for (i = 0; i < moduleList.length; i += 1) {
        moduleId = moduleList[i].getAttribute("data-understand-module");
        if (isPopulatedUnderstandModule(moduleId)) {
          moduleList[i].removeAttribute("hidden");
        } else {
          moduleList[i].setAttribute("hidden", "");
        }
      }
    }

    for (var i = 0; i < list.length; i += 1) {
      list[i].addEventListener("click", function (event) {
        var link = (event && (event.currentTarget || event.target)) || this;
        var id = link && link.getAttribute ? link.getAttribute("data-understand-target") : "";
        if (id) {
          setCurrent(id);
          setUnderstandAccordion(region, id);
        }
      });
    }

    var hash = window.location && window.location.hash;
    if (hash && hash.indexOf("#" + understandModuleDomId("")) === 0) {
      var fromHash = hash.slice(understandModuleDomId("").length + 1);
      if (fromHash) {
        setCurrent(fromHash);
        setUnderstandAccordion(region, fromHash);
      }
    }
    bindUnderstandCarat(region, diamond);
    bindUnderstandCut(region, diamond);
    bindUnderstandColor(region, diamond);
    bindUnderstandClarity(region, diamond);
    bindUnderstandAccordion(region);
  }

  function resolveGatewayShape(shape) {
    return resolveSizeShape(shape);
  }

  function gatewayRoundUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-round") || "").trim();
  }

  function gatewayOvalUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-oval") || "").trim();
  }

  function gatewayCushionUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-cushion") || "").trim();
  }

  function gatewayPrincessUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-princess") || "").trim();
  }

  function gatewayEmeraldUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-emerald") || "").trim();
  }

  function gatewayRadiantUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-radiant") || "").trim();
  }

  function gatewayPearUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-pear") || "").trim();
  }

  function gatewayHeartUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-heart") || "").trim();
  }

  function gatewayAsscherUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-asscher") || "").trim();
  }

  function gatewayMarquiseUrl() {
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-marquise") || "").trim();
  }

  function gatewayMasterUrl(shapeKey) {
    if (shapeKey === "round") return gatewayRoundUrl();
    if (shapeKey === "oval") return gatewayOvalUrl();
    if (shapeKey === "cushion") return gatewayCushionUrl();
    if (shapeKey === "princess") return gatewayPrincessUrl();
    if (shapeKey === "emerald") return gatewayEmeraldUrl();
    if (shapeKey === "radiant") return gatewayRadiantUrl();
    if (shapeKey === "pear") return gatewayPearUrl();
    if (shapeKey === "heart") return gatewayHeartUrl();
    if (shapeKey === "asscher") return gatewayAsscherUrl();
    if (shapeKey === "marquise") return gatewayMarquiseUrl();
    return "";
  }

  function gatewayEnvironmentUrl(shapeKey) {
    if (
      shapeKey !== "round" &&
      shapeKey !== "oval" &&
      shapeKey !== "cushion" &&
      shapeKey !== "princess" &&
      shapeKey !== "emerald" &&
      shapeKey !== "radiant" &&
      shapeKey !== "pear" &&
      shapeKey !== "heart" &&
      shapeKey !== "asscher" &&
      shapeKey !== "marquise"
    ) {
      return "";
    }
    var host = sizeHost();
    if (!host) return "";
    return String(host.getAttribute("data-4c-gateway-environment") || "").trim();
  }

  function renderGatewayColumn(id, value, label) {
    return (
      '<div class="diamond-details__gateway-col" data-gateway-col="' +
      escapeHtml(id) +
      '">' +
      '<p class="diamond-details__gateway-value" data-gateway-value="' +
      escapeHtml(id) +
      '">' +
      escapeHtml(value) +
      "</p>" +
      '<p class="diamond-details__gateway-label">' +
      escapeHtml(label) +
      "</p>" +
      '<a class="diamond-details__gateway-explore" href="#' +
      understandModuleDomId(id) +
      '" data-understand-target="' +
      escapeHtml(id) +
      '" data-gateway-explore="' +
      escapeHtml(id) +
      '">Explore \u2192</a></div>'
    );
  }

  function asCssUrl(url) {
    return "url(" + String(url || "").replace(/["'\\)\s]/g, "") + ")";
  }

  function renderGatewayEnvironment(envUrl) {
    if (!envUrl) return "";
    return (
      '<div class="diamond-details__gateway-environment" data-gateway-env-layer aria-hidden="true">' +
      '<span class="diamond-details__gateway-environment-fill"></span></div>'
    );
  }

  function renderGatewayVisual(shapeKey, masterUrl, visualMode) {
    if (!shapeKey || !masterUrl) return "";
    var src = escapeHtml(masterUrl);
    var mode = visualMode || "master";
    return (
      '<div class="diamond-details__gateway-visual" data-gateway-visual data-gateway-master="' +
      escapeHtml(shapeKey) +
      '" data-gateway-visual-mode="' +
      escapeHtml(mode) +
      '" aria-hidden="true">' +
      '<div class="diamond-details__gateway-figure">' +
      '<img class="diamond-details__gateway-diamond" data-gateway-diamond alt="" src="' +
      src +
      '">' +
      '<img class="diamond-details__gateway-reflection" data-gateway-reflection alt="" src="' +
      src +
      '">' +
      "</div></div>"
    );
  }

  function renderGatewayBanner(diamond) {
    diamond = diamond || {};
    var shapeKey = resolveGatewayShape(diamond.shape);
    var envUrl = gatewayEnvironmentUrl(shapeKey);
    var masterUrl = gatewayMasterUrl(shapeKey);
    var visualMode = envUrl && masterUrl ? "layered" : masterUrl ? "master" : "";
    return (
      '<section class="diamond-details__gateway" id="diamond-details-gateway" data-detail-region="gateway" data-4c-gateway data-gateway-shape="' +
      escapeHtml(shapeKey) +
      (visualMode ? '" data-gateway-visual-mode="' + escapeHtml(visualMode) : "") +
      '" data-gateway-has-visual="' +
      (masterUrl ? "true" : "false") +
      (envUrl ? '" data-gateway-environment="' + escapeHtml(envUrl) : "") +
      (envUrl ? '" style="--dd-gateway-environment:' + asCssUrl(envUrl) : "") +
      '" aria-labelledby="diamond-details-gateway-heading">' +
      renderGatewayEnvironment(envUrl) +
      '<div class="diamond-details__gateway-inner">' +
      '<div class="diamond-details__gateway-copy">' +
      '<p class="diamond-details__gateway-eyebrow">Understand Your Diamond</p>' +
      '<h2 class="diamond-details__gateway-heading" id="diamond-details-gateway-heading">The 4Cs of Your Diamond</h2>' +
      '<p class="diamond-details__gateway-lede">Discover what defines this diamond \u2014 and what makes it uniquely yours.</p>' +
      '<div class="diamond-details__gateway-cols" data-gateway-cols>' +
      renderGatewayColumn("carat", formatGatewayDisplay(formatGatewayCarat(diamond.carat)), "Carat") +
      renderGatewayColumn("cut", formatGatewayDisplay(formatCut(diamond.cut)), "Cut") +
      renderGatewayColumn("color", formatGatewayDisplay(formatColorGrade(diamond.color)), "Color") +
      renderGatewayColumn("clarity", formatGatewayDisplay(formatClarityGrade(diamond.clarity)), "Clarity") +
      "</div></div>" +
      renderGatewayVisual(shapeKey, masterUrl, visualMode) +
      "</div></section>"
    );
  }

  function renderCompareHost(diamond) {
    var available = !!(diamond && diamond.availability === "available");
    return (
      '<div class="diamond-details__compare-host" data-compare-host data-detail-region="compare-host"' +
      (available ? "" : " hidden") +
      "></div>"
    );
  }

  function abortRecentlyViewed() {
    recentlyViewedToken += 1;
    if (recentlyViewedController && typeof recentlyViewedController.abort === "function") {
      try {
        recentlyViewedController.abort();
      } catch (err) {
        // Ignore abort construction differences in tests / older browsers.
      }
    }
    recentlyViewedController = null;
  }

  function findRecentlyViewedHost(region) {
    return (
      (region && region.querySelector && region.querySelector("[data-recently-viewed-host]")) ||
      (rootEl && rootEl.querySelector && rootEl.querySelector("[data-recently-viewed-host]")) ||
      null
    );
  }

  function beginRecentlyViewed(diamond, region) {
    var RecentlyViewed = window.DiamondDetailRecentlyViewed;
    var host = findRecentlyViewedHost(region);
    if (!RecentlyViewed || typeof RecentlyViewed.start !== "function" || !host) return;

    abortRecentlyViewed();
    var token = recentlyViewedToken;

    var controller = null;
    if (typeof window.AbortController === "function") {
      controller = new window.AbortController();
      recentlyViewedController = controller;
    }

    var requestOptions = {};
    if (controller) requestOptions.signal = controller.signal;

    RecentlyViewed.start(host, diamond, {
      signal: requestOptions.signal,
      isCurrent: function () {
        return token === recentlyViewedToken;
      },
    });
  }

  function abortCompare() {
    compareToken += 1;
    if (compareController && typeof compareController.abort === "function") {
      try {
        compareController.abort();
      } catch (err) {
        // Ignore abort construction differences in tests / older browsers.
      }
    }
    compareController = null;
  }

  function beginCompare(diamond, region) {
    var Compare = window.DiamondDetailCompare;
    var host =
      (region && region.querySelector && region.querySelector("[data-compare-host]")) ||
      (rootEl && rootEl.querySelector("[data-compare-host]"));
    if (!Compare || typeof Compare.applySection !== "function" || !host) return;

    abortCompare();
    var token = compareToken;

    if (!diamond || diamond.availability !== "available") {
      Compare.applySection(host, "");
      return;
    }

    Compare.applySection(host, Compare.renderSection({ state: "loading" }));

    var api = window.IvanovAPI;
    if (!api || typeof api.getDiamondCompare !== "function") {
      Compare.applySection(host, "");
      return;
    }

    var controller = null;
    if (typeof window.AbortController === "function") {
      controller = new window.AbortController();
      compareController = controller;
    }

    var requestOptions = {};
    if (controller) requestOptions.signal = controller.signal;

    var requestedId = recordId(diamond);
    api
      .getDiamondCompare(requestedId, requestOptions)
      .then(function (data) {
        if (token !== compareToken) return;
        if (activeDiamond && !Compare.sameDiamondId(recordId(activeDiamond), requestedId)) return;
        if (
          data &&
          data.compare &&
          data.compare.currentDiamondId &&
          !Compare.sameDiamondId(data.compare.currentDiamondId, requestedId)
        ) {
          return;
        }
        Compare.applySection(
          host,
          Compare.renderSection({
            state: "ready",
            reference: diamond,
            compare: data && data.compare,
          })
        );
      })
      .catch(function (err) {
        if (token !== compareToken) return;
        if (isAbortError(err)) return;
        Compare.applySection(host, "");
      })
      .then(function () {
        if (token === compareToken) compareController = null;
      });
  }

  function renderExperience(diamond, options) {
    options = options || {};
    var identity = formatStoneIdentity(diamond);
    return (
      '<div class="diamond-details__layout" data-detail-layout>' +
      renderMedia(diamond, identity) +
      renderPanelBody(diamond, options) +
      "</div>" +
      renderGatewayBanner(diamond) +
      renderUnderstandYourDiamond(diamond) +
      renderCompareHost(diamond) +
      renderRecentlyViewedHost() +
      renderShareModal(diamond, identity) +
      (options.offerSelect ? renderDropAHintModal(diamond) : "") +
      renderCertificateReportModal(diamond)
    );
  }

  function renderRecentlyViewedHost() {
    return '<div class="diamond-details__recently-viewed-host" data-recently-viewed-host data-detail-region="recently-viewed-host" hidden></div>';
  }

  function attachBrokenImageFallback(region) {
    if (!region) return;
    var img = region.querySelector(".diamond-details__image");
    if (!img) return;
    img.addEventListener("error", function onBrokenImage() {
      img.removeEventListener("error", onBrokenImage);
      var slot = img.parentNode;
      if (slot) slot.innerHTML = PLACEHOLDER_HTML;
    });
  }

  var activeFrameHost = null;

  function cancelHostRaf(host) {
    if (!host) return;
    if (host.pendingDragRaf && typeof window.cancelAnimationFrame === "function") {
      window.cancelAnimationFrame(host.pendingDragRaf);
    }
    host.pendingDragRaf = 0;
    host.dragRafToken += 1;
    host.pendingDragDelta = null;
  }

  function cancelHostBufferWait(host) {
    if (!host || !host.bufferWaitCleanup) return;
    var cleanup = host.bufferWaitCleanup;
    host.bufferWaitCleanup = null;
    cleanup();
  }

  function teardownDetailFrameHost(region) {
    var host = (region && region._detailFrameHost) || activeFrameHost;
    if (!host) return;
    cancelHostRaf(host);
    cancelHostBufferWait(host);
    if (host.pollTimer && typeof window.clearTimeout === "function") {
      window.clearTimeout(host.pollTimer);
    }
    host.pollTimer = null;
    host.generation += 1;
    if (host.stageEl && host.onPointerDown) {
      host.stageEl.removeEventListener("pointerdown", host.onPointerDown);
      host.stageEl.removeEventListener("pointermove", host.onPointerMove);
      host.stageEl.removeEventListener("pointerup", host.onPointerUp);
      host.stageEl.removeEventListener("pointercancel", host.onPointerCancel);
    }
    if (host.presenter && typeof host.presenter.destroy === "function") {
      host.presenter.destroy();
    }
    if (host.engine && typeof host.engine.destroy === "function") {
      host.engine.destroy();
    }
    host.autoplayEnabled = true;
    host.pointerIsDrag = false;
    if (region) region._detailFrameHost = null;
    if (activeFrameHost === host) activeFrameHost = null;
  }

  function playWhenBuffered(host, engine, fromIndex) {
    cancelHostBufferWait(host);
    var FrameEngine = window.DiamondFrameEngine;
    if (!FrameEngine || !engine) return;
    var frameCount = engine.getFrameCount();
    var bufferSize = Math.min(START_THRESHOLD, Math.max(0, frameCount - 1));
    var targets = [];
    var step;
    for (step = 1; step <= bufferSize; step += 1) {
      targets.push(FrameEngine.normalizeFrameIndex(fromIndex + step, frameCount));
    }
    function isBuffered() {
      return targets.every(function (index) {
        return engine.getFrameState(index) === FrameEngine.FRAME_STATES.LOADED;
      });
    }
    function start() {
      cancelHostBufferWait(host);
      if (host.engine !== engine) return;
      if (!host.autoplayEnabled) return;
      if (engine.getState() === FrameEngine.STATES.IDLE) {
        engine.play({ fps: AUTOPLAY_FPS });
      }
    }
    if (!targets.length || isBuffered()) {
      start();
      return;
    }
    var off = engine.on("framestatechange", function () {
      if (isBuffered()) start();
    });
    var timer = window.setTimeout(start, INITIAL_BUFFER_TIMEOUT_MS);
    host.bufferWaitCleanup = function () {
      if (typeof off === "function") off();
      window.clearTimeout(timer);
    };
  }

  function startPreparedOrBuffered(host, engine, isCompletePreload) {
    var FrameEngine = window.DiamondFrameEngine;
    if (!FrameEngine || host.engine !== engine) return;
    if (engine.getState() !== FrameEngine.STATES.IDLE) return;
    if (isCompletePreload) {
      var n = engine.getFrameCount();
      var indices = [];
      var i;
      for (i = 0; i < n; i += 1) indices.push(i);
      engine.preload(indices);
      var started = Date.now();
      function poll() {
        if (host.engine !== engine) return;
        var ready = true;
        for (i = 0; i < n; i += 1) {
          if (engine.getFrameState(i) !== FrameEngine.FRAME_STATES.LOADED) {
            ready = false;
            break;
          }
        }
        if (ready || Date.now() - started > PREPARED_READY_TIMEOUT_MS) {
          host.interactionReady = true;
          playWhenBuffered(host, engine, engine.getCurrentFrame());
          return;
        }
        host.pollTimer = window.setTimeout(poll, 30);
      }
      poll();
      return;
    }
    host.interactionReady = true;
    playWhenBuffered(host, engine, engine.getCurrentFrame());
  }

  function bindFramePointer(host) {
    var stageEl = host.stageEl;
    if (!stageEl) return;

    host.onPointerDown = function (event) {
      if (!host.engine || !event || !event.isPrimary) return;
      if (host.interactionReady === false) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (typeof event.preventDefault === "function") event.preventDefault();
      cancelHostBufferWait(host);
      cancelHostRaf(host);
      host.dragPointerId = event.pointerId;
      host.dragStartX = event.clientX;
      host.pointerStartY = Number.isFinite(event.clientY) ? event.clientY : 0;
      host.pointerIsDrag = false;
      host.engine.beginDrag();
      if (stageEl.setPointerCapture) {
        try {
          stageEl.setPointerCapture(event.pointerId);
        } catch (err) {
          // Pointer capture is optional.
        }
      }
    };

    host.onPointerMove = function (event) {
      if (!host.engine || !event || host.dragPointerId !== event.pointerId) return;
      var dy = (Number.isFinite(event.clientY) ? event.clientY : 0) - host.pointerStartY;
      var distance = Math.hypot(event.clientX - host.dragStartX, dy);
      if (distance > CLICK_DRAG_THRESHOLD_PX) host.pointerIsDrag = true;
      if (!host.pointerIsDrag) return;
      host.pendingDragDelta = event.clientX - host.dragStartX;
      if (host.pendingDragRaf) return;
      var token = host.dragRafToken;
      host.pendingDragRaf = window.requestAnimationFrame(function () {
        host.pendingDragRaf = 0;
        if (token !== host.dragRafToken) return;
        if (!host.engine || host.dragPointerId == null) {
          host.pendingDragDelta = null;
          return;
        }
        var delta = host.pendingDragDelta;
        host.pendingDragDelta = null;
        if (delta == null) return;
        host.engine.updateDrag(delta);
      });
    };

    function flushPointerDrag(event) {
      host.pendingDragDelta = event.clientX - host.dragStartX;
      if (host.pendingDragRaf && typeof window.cancelAnimationFrame === "function") {
        window.cancelAnimationFrame(host.pendingDragRaf);
      }
      host.pendingDragRaf = 0;
      host.dragRafToken += 1;
      if (host.pendingDragDelta != null) {
        host.engine.updateDrag(host.pendingDragDelta);
      }
      host.pendingDragDelta = null;
    }

    // Viewer source of truth: drag release leaves autoplay stopped;
    // a click/tap toggles shopper-owned autoplay intent. endDrag()
    // never restarts playback by itself.
    host.onPointerUp = function (event) {
      if (!host.engine || !event || host.dragPointerId !== event.pointerId) return;
      var wasDrag = host.pointerIsDrag;
      if (wasDrag) flushPointerDrag(event);
      else cancelHostRaf(host);
      host.dragPointerId = null;
      host.pointerIsDrag = false;
      host.engine.endDrag();
      if (wasDrag) {
        host.autoplayEnabled = false;
        return;
      }
      host.autoplayEnabled = !host.autoplayEnabled;
      if (host.autoplayEnabled) {
        playWhenBuffered(host, host.engine, host.engine.getCurrentFrame());
      }
    };

    // Viewer source of truth: interrupted gestures are not taps.
    host.onPointerCancel = function (event) {
      if (!host.engine || !event || host.dragPointerId !== event.pointerId) return;
      if (host.pointerIsDrag) flushPointerDrag(event);
      else cancelHostRaf(host);
      host.dragPointerId = null;
      host.pointerIsDrag = false;
      host.autoplayEnabled = false;
      host.engine.endDrag();
    };

    stageEl.addEventListener("pointerdown", host.onPointerDown);
    stageEl.addEventListener("pointermove", host.onPointerMove);
    stageEl.addEventListener("pointerup", host.onPointerUp);
    stageEl.addEventListener("pointercancel", host.onPointerCancel);
  }

  function paintHostFrame(host, engine, index) {
    if (!host.presenter || !engine) return;
    var result = engine.getDisplayFrameUrl(index);
    if (!result) return;
    var img = result.image;
    applyMediaAspect(host.canvasEl, img);
    ensureFrameHostPaintBox(host.stageEl, host.canvasEl);
    var nextSrc = (img && img.src) || result.url;
    host.presenter.show(nextSrc);
  }

  function startFrameStage(region, diamond, v360Media, identity) {
    var EngineNS = window.DiamondFrameEngine;
    var PresenterNS = window.DiamondFramePresenter;
    var stageEl = region.querySelector("[data-detail-frame-stage]");
    var canvasEl = region.querySelector("[data-detail-frame-canvas]") || stageEl;
    if (!EngineNS || typeof EngineNS.createFrameEngine !== "function" || !stageEl) {
      return null;
    }
    var playable =
      (window.DiamondPreparedTurntableViewer &&
        typeof window.DiamondPreparedTurntableViewer.isPlayableV360Media === "function" &&
        window.DiamondPreparedTurntableViewer.isPlayableV360Media(v360Media)) ||
      isUsableV360(v360Media);
    if (!playable) return null;

    var isCompletePreload = !!(v360Media && (v360Media.__preparedTurntable || v360Media.__pocPrepared));
    var presenter =
      PresenterNS && typeof PresenterNS.createFramePresenter === "function"
        ? PresenterNS.createFramePresenter(canvasEl, { alt: identity })
        : null;
    var engine = EngineNS.createFrameEngine({
      media: v360Media,
      rollingBufferTarget: isCompletePreload ? 0 : ROLLING_TARGET,
      maxQueueSize: isCompletePreload
        ? Math.max(64, Number(v360Media.frame_count) || 64)
        : undefined,
    });
    var host = {
      engine: engine,
      presenter: presenter,
      stageEl: stageEl,
      canvasEl: canvasEl,
      generation: 0,
      autoplayEnabled: true,
      interactionReady: !isCompletePreload,
      pendingDragRaf: 0,
      pendingDragDelta: null,
      dragRafToken: 0,
      dragPointerId: null,
      dragStartX: 0,
      pointerStartY: 0,
      pointerIsDrag: false,
      pollTimer: null,
      bufferWaitCleanup: null,
    };

    engine.on("framechange", function (payload) {
      paintHostFrame(host, engine, payload.index);
    });
    engine.on("framestatechange", function (payload) {
      if (
        payload.state === EngineNS.FRAME_STATES.LOADED &&
        payload.index === engine.getCurrentFrame()
      ) {
        paintHostFrame(host, engine, payload.index);
      }
    });
    engine.on("error", function () {
      var viewport = region.querySelector("[data-diamond-viewport]");
      teardownDetailFrameHost(region);
      if (!viewport) return;
      if (mediaImageUrl(diamond)) {
        viewport.innerHTML = renderPhotoMedia(diamond, identity);
        attachBrokenImageFallback(region);
      } else {
        viewport.innerHTML = PLACEHOLDER_HTML;
      }
    });

    bindFramePointer(host);
    ensureFrameHostPaintBox(stageEl, canvasEl);
    region._detailFrameHost = host;
    activeFrameHost = host;

    engine.load().then(function () {
      if (host.engine !== engine) return;
      startPreparedOrBuffered(host, engine, isCompletePreload);
    });
    return host;
  }

  function mountDetailFrameHost(region, diamond) {
    if (!region) return null;
    teardownDetailFrameHost(region);
    if (!region.querySelector("[data-detail-frame-stage]")) return null;
    if (!window.DiamondFrameEngine || typeof window.DiamondFrameEngine.createFrameEngine !== "function") {
      return null;
    }

    var identity = formatStoneIdentity(diamond);
    var adapter = toFrameDiamond(diamond);
    var v360 = adapter.v360;
    var PreparedViewer = window.DiamondPreparedTurntableViewer;
    var generation = (region._frameGeneration || 0) + 1;
    region._frameGeneration = generation;

    function applyMedia(v360Media) {
      if (region._frameGeneration !== generation) return null;
      var host = startFrameStage(region, diamond, v360Media, identity);
      if (host) return host;
      var viewport = region.querySelector("[data-diamond-viewport]");
      if (!viewport) return null;
      if (mediaImageUrl(diamond)) {
        viewport.setAttribute("data-diamond-view", DIAMOND_VIEW_PHOTO);
        viewport.innerHTML = renderPhotoMedia(diamond, identity);
        attachBrokenImageFallback(region);
      } else {
        viewport.setAttribute("data-diamond-view", DIAMOND_VIEW_SILHOUETTE);
        viewport.innerHTML = PLACEHOLDER_HTML;
      }
      return null;
    }

    if (
      PreparedViewer &&
      typeof PreparedViewer.resolveViewerMediaForActivation === "function"
    ) {
      PreparedViewer.resolveViewerMediaForActivation(adapter, v360, {
        isStale: function () {
          return region._frameGeneration !== generation;
        },
      }).then(function (result) {
        if (!result || result.stale) return;
        applyMedia(result.media);
      });
      return { pending: true, path: "preparedTurntable" };
    }
    return applyMedia(v360);
  }

  function syncDiamondSubControl(region) {
    var slot = region ? region.querySelector("[data-diamond-views-slot]") : null;
    if (slot) slot.innerHTML = "";
  }

  function syncGallerySelection(region, itemId) {
    var tabs = region ? region.querySelectorAll("[data-media-gallery-item]") : null;
    var list = tabs && typeof tabs.length === "number" ? tabs : tabs ? [tabs] : [];
    var i;
    for (i = 0; i < list.length; i += 1) {
      if (isCertificateGalleryItem(list[i].getAttribute("data-media-gallery-item"))) continue;
      var selected = list[i].getAttribute("data-media-gallery-item") === itemId;
      list[i].setAttribute("aria-selected", selected ? "true" : "false");
      list[i].setAttribute("tabindex", selected ? "0" : "-1");
    }
  }

  function applyDiamondSubview(region, diamond, view) {
    var explorer = region ? region.querySelector("[data-media-explorer]") : null;
    var viewport = region ? region.querySelector("[data-media-viewport]") : null;
    if (!explorer || !viewport) return;
    if (explorer.getAttribute("data-active-media-mode") !== MEDIA_MODE_DIAMOND) return;
    var caps = diamondViewCapabilities(diamond);
    var next = view;
    if (next === DIAMOND_VIEW_360 && !caps.turntable) next = caps.defaultView;
    if (next === DIAMOND_VIEW_PHOTO && !caps.photo) next = caps.defaultView;
    if (
      next === DIAMOND_VIEW_360 &&
      region._detailFrameHost &&
      region._detailFrameHost.engine &&
      viewport.querySelector("[data-detail-frame-stage]")
    ) {
      var current360 = galleryItemForDiamondView(diamond, next);
      explorer.setAttribute("data-active-media-item", current360.id);
      viewport.setAttribute("aria-labelledby", "diamond-details-media-tab-" + current360.id);
      syncGallerySelection(region, current360.id);
      return;
    }
    teardownDetailFrameHost(region);
    var buttons = region.querySelectorAll("[data-diamond-subview]");
    var list = buttons && typeof buttons.length === "number" ? buttons : buttons ? [buttons] : [];
    var i;
    for (i = 0; i < list.length; i += 1) {
      list[i].setAttribute("aria-pressed", list[i].getAttribute("data-diamond-subview") === next ? "true" : "false");
    }
    var identity = formatStoneIdentity(diamond);
    var item = galleryItemForDiamondView(diamond, next);
    explorer.setAttribute("data-active-media-item", item.id);
    viewport.setAttribute("aria-labelledby", "diamond-details-media-tab-" + item.id);
    syncGallerySelection(region, item.id);
    viewport.innerHTML = renderDiamondMedia(diamond, identity, next);
    if (next === DIAMOND_VIEW_PHOTO) attachBrokenImageFallback(region);
    if (next === DIAMOND_VIEW_360) mountDetailFrameHost(region, diamond);
  }

  function bindDiamondSubControl(region, diamond) {
    if (!region) return;
    var buttons = region.querySelectorAll("[data-diamond-subview]");
    var list = buttons && typeof buttons.length === "number" ? buttons : buttons ? [buttons] : [];
    var i;
    for (i = 0; i < list.length; i += 1) {
      (function (button) {
        button.addEventListener("click", function (event) {
          if (event && typeof event.preventDefault === "function") event.preventDefault();
          applyDiamondSubview(region, diamond, button.getAttribute("data-diamond-subview"));
        });
      })(list[i]);
    }
  }

  function applySaveState(button, diamond) {
    var Saved = window.DiamondSaved;
    var id = recordId(diamond);
    if (button && id && typeof button.setAttribute === "function") {
      button.setAttribute("data-diamond-id", id);
    }
    if (!button || !Saved || typeof Saved.applyHeartState !== "function") return;
    Saved.applyHeartState(button, Saved.has(id));
  }

  var savedChangeSyncBound = false;

  function bindSavedChangeSync() {
    if (savedChangeSyncBound) return;
    if (typeof window.addEventListener !== "function") return;
    savedChangeSyncBound = true;
    window.addEventListener("ivanov:saved-diamonds-change", function () {
      if (!rootEl || typeof rootEl.querySelectorAll !== "function") return;
      var buttons = rootEl.querySelectorAll("[data-save]");
      var list = buttons && typeof buttons.length === "number" ? buttons : buttons ? [buttons] : [];
      var i;
      var button;
      var id;
      var Saved = window.DiamondSaved;
      if (!Saved || typeof Saved.applyHeartState !== "function") return;
      for (i = 0; i < list.length; i += 1) {
        button = list[i];
        if (!button) continue;
        id =
          (button.dataset && button.dataset.diamondId) ||
          (typeof button.getAttribute === "function" ? button.getAttribute("data-diamond-id") : "") ||
          "";
        Saved.applyHeartState(button, Saved.has(id));
      }
    });
  }

  function shareDialog(region) {
    if (!region || !region.querySelector) return null;
    return (
      region.querySelector(".diamond-details__share-dialog") ||
      region.querySelector('[aria-labelledby="diamond-details-share-title"]')
    );
  }

  function closeShareModal(region) {
    if (!region) return;
    var modal = region.querySelector("[data-share-modal]");
    if (!modal || modal.hidden) return;
    unlockPageScroll(modal);
    modal.hidden = true;
    var opener = modal._opener;
    modal._opener = null;
    if (opener && typeof opener.focus === "function") opener.focus();
  }

  function openShareModal(region, opener) {
    if (!region) return;
    var modal = region.querySelector("[data-share-modal]");
    var dialog;
    if (!modal) return;
    closeDropAHintModal(region);
    modal._opener = opener || region.querySelector("[data-share-open]") || null;
    modal.hidden = false;
    lockPageScroll(modal);
    dialog = shareDialog(region);
    if (dialog) {
      if (typeof dialog.setAttribute === "function" && dialog.getAttribute("tabindex") == null) {
        dialog.setAttribute("tabindex", "-1");
      }
      if (typeof dialog.focus === "function") dialog.focus();
    }
  }

  function hintDialog(region) {
    if (!region || !region.querySelector) return null;
    return (
      region.querySelector("#diamond-details-hint-dialog") ||
      region.querySelector(".diamond-details__hint-dialog")
    );
  }

  function clearHintFieldErrors(root) {
    if (!root || typeof root.querySelectorAll !== "function") return;
    var nodes = root.querySelectorAll("[data-hint-error-for]");
    var fields = root.querySelectorAll("[data-hint-field]");
    var inputs = root.querySelectorAll(".diamond-details__hint-input, .diamond-details__hint-textarea");
    var i;
    for (i = 0; i < nodes.length; i += 1) {
      nodes[i].hidden = true;
      nodes[i].textContent = "";
    }
    for (i = 0; i < fields.length; i += 1) {
      if (typeof fields[i].removeAttribute === "function") fields[i].removeAttribute("data-invalid");
    }
    for (i = 0; i < inputs.length; i += 1) {
      if (typeof inputs[i].removeAttribute === "function") {
        inputs[i].removeAttribute("aria-invalid");
        inputs[i].removeAttribute("aria-describedby");
      }
    }
  }

  function applyHintFieldErrors(root, errors) {
    clearHintFieldErrors(root);
    if (!root || !errors) return null;
    var firstInvalid = null;
    var keys = ["recipientEmail", "senderName", "senderEmail", "message"];
    var i;
    var key;
    var field;
    var input;
    var errNode;
    var errId;
    for (i = 0; i < keys.length; i += 1) {
      key = keys[i];
      if (!errors[key]) continue;
      field = root.querySelector('[data-hint-field="' + key + '"]');
      errNode = root.querySelector('[data-hint-error-for="' + key + '"]');
      input =
        (field &&
          field.querySelector &&
          (field.querySelector(".diamond-details__hint-input") ||
            field.querySelector(".diamond-details__hint-textarea"))) ||
        root.querySelector(
          key === "recipientEmail"
            ? "[data-hint-recipient-email]"
            : key === "senderName"
              ? "[data-hint-sender-name]"
              : key === "senderEmail"
                ? "[data-hint-sender-email]"
                : "[data-hint-message]"
        );
      if (field && typeof field.setAttribute === "function") field.setAttribute("data-invalid", "true");
      if (errNode) {
        errId = "diamond-details-hint-error-" + key;
        if (typeof errNode.setAttribute === "function") errNode.setAttribute("id", errId);
        errNode.hidden = false;
        errNode.textContent = errors[key];
      }
      if (input) {
        if (typeof input.setAttribute === "function") {
          input.setAttribute("aria-invalid", "true");
          if (errId) input.setAttribute("aria-describedby", errId);
        }
        if (!firstInvalid) firstInvalid = input;
      }
    }
    return firstInvalid;
  }

  function readHintFormValues(root) {
    if (!root) return {};
    var recipient = root.querySelector("[data-hint-recipient-email]");
    var name = root.querySelector("[data-hint-sender-name]");
    var email = root.querySelector("[data-hint-sender-email]");
    var note = root.querySelector("[data-hint-message]");
    var copy = root.querySelector("[data-hint-send-copy]");
    var diamondIdInput = root.querySelector("[data-hint-diamond-id]");
    return {
      diamondId: diamondIdInput ? diamondIdInput.value : "",
      recipientEmail: recipient ? recipient.value : "",
      senderName: name ? name.value : "",
      senderEmail: email ? email.value : "",
      message: note ? note.value : "",
      sendCopy: !!(copy && copy.checked),
    };
  }

  function closeDropAHintModal(region) {
    if (!region) return;
    var modal = region.querySelector("[data-hint-modal]");
    if (!modal || modal.hidden) return;
    abortDropAHintRequest(modal, { intentional: true });
    unlockPageScroll(modal);
    modal.hidden = true;
    resetDropAHintModal(region, modal);
    var opener = modal._opener;
    modal._opener = null;
    if (opener && typeof opener.focus === "function") opener.focus();
  }

  function openDropAHintModal(region, opener) {
    if (!region) return;
    var modal = region.querySelector("[data-hint-modal]");
    var dialog;
    var firstField;
    if (!modal) return;
    closeShareModal(region);
    abortDropAHintRequest(modal, { intentional: true });
    modal._opener = opener || region.querySelector("[data-drop-a-hint]") || null;
    resetDropAHintModal(region, modal);
    modal.hidden = false;
    lockPageScroll(modal);
    dialog = hintDialog(region);
    firstField = region.querySelector("[data-hint-recipient-email]");
    if (firstField && typeof firstField.focus === "function") firstField.focus();
    else if (dialog && typeof dialog.focus === "function") dialog.focus();
  }

  function hintSubmitTimeoutMs() {
    var exported =
      window.DiamondDetail && window.DiamondDetail.HINT_SUBMIT_TIMEOUT_MS;
    if (typeof exported === "number" && exported > 0) return exported;
    return HINT_SUBMIT_TIMEOUT_MS;
  }

  function abortDropAHintRequest(modal, options) {
    options = options || {};
    if (!modal) return;
    if (options.intentional) {
      modal._hintRequestSession = (modal._hintRequestSession || 0) + 1;
      modal._hintAbortIntentional = true;
    }
    if (modal._hintTimeoutId != null) {
      clearTimeout(modal._hintTimeoutId);
      modal._hintTimeoutId = null;
    }
    if (modal._hintAbortController && typeof modal._hintAbortController.abort === "function") {
      try {
        modal._hintAbortController.abort();
      } catch (err) {
        // Best-effort abort.
      }
    }
    modal._hintAbortController = null;
  }

  function clearHintFormFields(root) {
    if (!root) return;
    var recipient = root.querySelector("[data-hint-recipient-email]");
    var name = root.querySelector("[data-hint-sender-name]");
    var email = root.querySelector("[data-hint-sender-email]");
    var note = root.querySelector("[data-hint-message]");
    var copy = root.querySelector("[data-hint-send-copy]");
    if (recipient) recipient.value = "";
    if (name) name.value = "";
    if (email) email.value = "";
    if (note) note.value = "";
    if (copy) copy.checked = false;
  }

  function setHintSubmitBusy(root, busy) {
    var submit = root && root.querySelector("[data-hint-submit]");
    if (!submit) return;
    if (busy) {
      submit.disabled = true;
      if (typeof submit.setAttribute === "function") submit.setAttribute("aria-busy", "true");
      submit.textContent = HINT_SENDING_LABEL;
    } else {
      submit.disabled = false;
      if (typeof submit.removeAttribute === "function") submit.removeAttribute("aria-busy");
      else if (typeof submit.setAttribute === "function") submit.setAttribute("aria-busy", "false");
      submit.textContent = HINT_SUBMIT_LABEL;
    }
  }

  function setHintFormVisible(root, visible) {
    var form = root && root.querySelector("[data-hint-form]");
    if (!form) return;
    form.hidden = !visible;
    // Author display:flex can override UA [hidden]; keep layout zero-diff when shown.
    if (form.style) form.style.display = visible ? "" : "none";
  }

  function setHintErrorMessage(root, error, message) {
    var text = message || HINT_GENERIC_SEND_FAILURE;
    var copy =
      (root &&
        root.querySelector &&
        root.querySelector("[data-hint-error-copy]")) ||
      (error &&
        error.querySelector &&
        (error.querySelector("[data-hint-error-copy]") ||
          error.querySelector(".diamond-details__hint-result-copy"))) ||
      null;
    if (copy) {
      copy.textContent = text;
      return;
    }
    if (error) error.textContent = text;
  }

  function hideHintGlobalError(root) {
    var error = root && root.querySelector("[data-hint-error]");
    if (!error) return;
    error.hidden = true;
    setHintErrorMessage(root, error, HINT_GENERIC_SEND_FAILURE);
  }

  function showHintGlobalError(root, message) {
    var error = root && root.querySelector("[data-hint-error]");
    var success = root && root.querySelector("[data-hint-success]");
    if (success) success.hidden = true;
    setHintFormVisible(root, true);
    setHintSubmitBusy(root, false);
    if (!error) return;
    setHintErrorMessage(root, error, message);
    error.hidden = false;
    if (typeof error.focus === "function") {
      if (typeof error.setAttribute === "function" && error.getAttribute("tabindex") == null) {
        error.setAttribute("tabindex", "-1");
      }
      error.focus();
    }
  }

  function showHintSuccess(root, modal) {
    var success = root && root.querySelector("[data-hint-success]");
    var done = root && root.querySelector("[data-hint-done]");
    hideHintGlobalError(root);
    setHintFormVisible(root, false);
    setHintSubmitBusy(root, false);
    if (modal) {
      modal.setAttribute("data-hint-state", "success");
      if (typeof modal.removeAttribute === "function") modal.removeAttribute("data-hint-validated");
      modal._lastValidatedPayload = null;
    }
    if (success) success.hidden = false;
    if (done && typeof done.focus === "function") done.focus();
    else if (success && typeof success.focus === "function") {
      if (typeof success.setAttribute === "function" && success.getAttribute("tabindex") == null) {
        success.setAttribute("tabindex", "-1");
      }
      success.focus();
    }
  }

  function resetDropAHintModal(region, modal) {
    var root = region || modal;
    if (!root) return;
    modal = modal || (region && region.querySelector("[data-hint-modal]"));
    clearHintFormFields(root);
    clearHintFieldErrors(root);
    hideHintGlobalError(root);
    setHintFormVisible(root, true);
    setHintSubmitBusy(root, false);
    var success = root.querySelector("[data-hint-success]");
    if (success) success.hidden = true;
    if (modal) {
      modal.setAttribute("data-hint-state", "idle");
      if (typeof modal.removeAttribute === "function") modal.removeAttribute("data-hint-validated");
      modal._lastValidatedPayload = null;
      modal._hintAbortIntentional = false;
      modal._hintAbortController = null;
      if (modal._hintTimeoutId != null) {
        clearTimeout(modal._hintTimeoutId);
        modal._hintTimeoutId = null;
      }
    }
  }

  function mapDropAHintErrorMessage(err) {
    var code =
      err &&
      err.data &&
      err.data.error &&
      typeof err.data.error === "object" &&
      err.data.error.code
        ? String(err.data.error.code)
        : "";
    if (code && HINT_ERROR_COPY[code]) return HINT_ERROR_COPY[code];
    return HINT_GENERIC_SEND_FAILURE;
  }

  function isValidDropAHintSuccess(response) {
    return !!(response && response.success === true && response.ok === true);
  }

  /**
   * Validate → POST /drop-a-hint via IvanovAPI.sendDropAHint.
   * Never stores PII. Never logs emails/message. No mailto.
   */
  function handleDropAHintSubmit(region, modal) {
    var root = region || modal;
    if (!root) return Promise.resolve(null);
    modal = modal || (region && region.querySelector("[data-hint-modal]"));
    var raw;
    var validated;
    var payload;
    var firstInvalid;
    var success;
    var error;
    var session;
    var controller;
    var timeoutId;
    var api;
    var AbortCtor;

    if (modal && modal.getAttribute("data-hint-state") === "submitting") {
      return Promise.resolve(null);
    }

    raw = readHintFormValues(root);
    validated = validateDropAHintForm(raw);
    success = root.querySelector("[data-hint-success]");
    error = root.querySelector("[data-hint-error]");
    if (success) success.hidden = true;
    if (error) error.hidden = true;

    if (!validated.ok) {
      firstInvalid = applyHintFieldErrors(root, validated.errors);
      if (modal) {
        modal.setAttribute("data-hint-state", "idle");
        if (typeof modal.removeAttribute === "function") modal.removeAttribute("data-hint-validated");
        modal._lastValidatedPayload = null;
      }
      setHintSubmitBusy(root, false);
      setHintFormVisible(root, true);
      if (firstInvalid && typeof firstInvalid.focus === "function") firstInvalid.focus();
      return Promise.resolve(null);
    }

    clearHintFieldErrors(root);
    payload = buildDropAHintPayload({
      diamondId: raw.diamondId || hintBareUuid(activeDiamond),
      recipientEmail: validated.values.recipientEmail,
      senderName: validated.values.senderName,
      senderEmail: validated.values.senderEmail,
      message: validated.values.message,
      sendCopy: validated.values.sendCopy,
    });

    if (modal) {
      modal._lastValidatedPayload = payload;
      modal.setAttribute("data-hint-validated", "true");
      modal.setAttribute("data-hint-state", "submitting");
      modal._hintAbortIntentional = false;
      session = (modal._hintRequestSession || 0) + 1;
      modal._hintRequestSession = session;
    } else {
      session = 1;
    }

    setHintSubmitBusy(root, true);
    hideHintGlobalError(root);
    setHintFormVisible(root, true);

    AbortCtor = window.AbortController;
    if (typeof AbortCtor === "function") {
      controller = new AbortCtor();
      if (modal) modal._hintAbortController = controller;
    }

    timeoutId = setTimeout(function () {
      if (controller && typeof controller.abort === "function") {
        try {
          controller.abort();
        } catch (err) {
          // Timeout abort is best-effort.
        }
      }
    }, hintSubmitTimeoutMs());
    if (modal) modal._hintTimeoutId = timeoutId;

    api = window.IvanovAPI;
    if (!api || typeof api.sendDropAHint !== "function") {
      clearTimeout(timeoutId);
      if (modal) modal._hintTimeoutId = null;
      if (!modal || modal._hintRequestSession === session) {
        if (modal) modal.setAttribute("data-hint-state", "error");
        showHintGlobalError(root, HINT_GENERIC_SEND_FAILURE);
      }
      return Promise.resolve(payload);
    }

    return api
      .sendDropAHint(payload, controller ? { signal: controller.signal } : {})
      .then(function (response) {
        clearTimeout(timeoutId);
        if (modal) {
          modal._hintTimeoutId = null;
          modal._hintAbortController = null;
        }
        if (modal && modal._hintRequestSession !== session) return payload;
        if (!isValidDropAHintSuccess(response)) {
          if (modal) modal.setAttribute("data-hint-state", "error");
          showHintGlobalError(root, HINT_GENERIC_SEND_FAILURE);
          return payload;
        }
        showHintSuccess(root, modal);
        return payload;
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        if (modal) {
          modal._hintTimeoutId = null;
          modal._hintAbortController = null;
        }
        if (modal && modal._hintRequestSession !== session) return null;
        if (err && err.name === "AbortError") {
          if (modal && modal._hintAbortIntentional) {
            return null;
          }
          if (modal) modal.setAttribute("data-hint-state", "error");
          showHintGlobalError(root, HINT_GENERIC_SEND_FAILURE);
          return null;
        }
        if (modal) modal.setAttribute("data-hint-state", "error");
        showHintGlobalError(root, mapDropAHintErrorMessage(err));
        return null;
      });
  }

  /**
   * Shared Detail modal scroll-lock ownership (Hint / Share / Certificate).
   *
   * Layout-shift contract (classic OS scrollbars):
   * - Measure scrollbar width BEFORE hiding overflow.
   * - Lock overflow on html + body (scroll prevention).
   * - Compensate padding-right on body ONLY.
   *   Padding both html and body double-compensates and shifts centered
   *   content the opposite direction (observed ~scrollbarWidth / 2).
   * - Do not reserve a CSS scrollbar gutter here: overlay scrollbars
   *   (measured width 0) can gain a new gutter and shift layout.
   */
  var pageScrollLock = {
    owners: 0,
    snapshots: null,
  };

  function measureScrollbarWidth() {
    var doc = window.document;
    var root;
    var inner;
    var client;
    if (!doc || !doc.documentElement) return 0;
    root = doc.documentElement;
    inner = typeof window.innerWidth === "number" ? window.innerWidth : 0;
    client = typeof root.clientWidth === "number" ? root.clientWidth : 0;
    if (!(inner > 0) || !(client > 0)) return 0;
    return Math.max(0, inner - client);
  }

  function readPaddingRightPx(el) {
    var style;
    var inline;
    var computed;
    var value;
    if (!el) return 0;
    style = el.style || null;
    inline = style && style.paddingRight != null ? String(style.paddingRight) : "";
    if (inline) {
      value = parseFloat(inline);
      return isNaN(value) ? 0 : value;
    }
    computed =
      typeof window.getComputedStyle === "function" ? window.getComputedStyle(el) : null;
    if (computed && computed.paddingRight) {
      value = parseFloat(computed.paddingRight);
      return isNaN(value) ? 0 : value;
    }
    return 0;
  }

  function applyPageScrollLock() {
    var doc = window.document;
    var html = doc && doc.documentElement;
    var body = doc && doc.body;
    var scrollbarWidth = measureScrollbarWidth();
    var snapshots = [];
    var htmlStyle;
    var bodyStyle;
    var existingBodyPadding;
    if (!html && !body) {
      pageScrollLock.snapshots = snapshots;
      return;
    }
    if (html) {
      htmlStyle = html.style || (html.style = {});
      snapshots.push({
        el: html,
        overflow: htmlStyle.overflow || "",
        paddingRight: htmlStyle.paddingRight != null ? String(htmlStyle.paddingRight) : "",
      });
      htmlStyle.overflow = "hidden";
    }
    if (body && body !== html) {
      bodyStyle = body.style || (body.style = {});
      existingBodyPadding = readPaddingRightPx(body);
      snapshots.push({
        el: body,
        overflow: bodyStyle.overflow || "",
        paddingRight: bodyStyle.paddingRight != null ? String(bodyStyle.paddingRight) : "",
      });
      bodyStyle.overflow = "hidden";
      if (scrollbarWidth > 0) {
        bodyStyle.paddingRight = existingBodyPadding + scrollbarWidth + "px";
      }
    } else if (html && scrollbarWidth > 0) {
      /* Degenerate single-root documents: compensate the only available root. */
      existingBodyPadding = readPaddingRightPx(html);
      htmlStyle.paddingRight = existingBodyPadding + scrollbarWidth + "px";
    }
    pageScrollLock.snapshots = snapshots;
  }

  function restorePageScrollLock() {
    var snapshots = pageScrollLock.snapshots || [];
    var i;
    var entry;
    for (i = 0; i < snapshots.length; i += 1) {
      entry = snapshots[i];
      if (!entry || !entry.el || !entry.el.style) continue;
      entry.el.style.overflow = entry.overflow;
      entry.el.style.paddingRight = entry.paddingRight;
    }
    pageScrollLock.snapshots = null;
  }

  function lockPageScroll(modal) {
    if (!modal || modal._scrollLocked) return;
    if (!pageScrollLock.owners) applyPageScrollLock();
    pageScrollLock.owners += 1;
    modal._scrollLocked = true;
  }

  function unlockPageScroll(modal) {
    if (!modal || !modal._scrollLocked) return;
    modal._scrollLocked = false;
    if (pageScrollLock.owners > 0) pageScrollLock.owners -= 1;
    if (pageScrollLock.owners > 0) return;
    restorePageScrollLock();
  }

  function reportModalTitleText(modal, region) {
    var title =
      (region && region.querySelector("[data-report-modal-title]")) ||
      (modal && modal.querySelector && modal.querySelector("[data-report-modal-title]"));
    var text = title && (title.textContent || title.innerText || "");
    return String(text || "Grading Report").replace(/\s+/g, " ").trim();
  }

  function reportModalPart(modal, region, selector) {
    return (
      (region && region.querySelector(selector)) ||
      (modal && modal.querySelector && modal.querySelector(selector)) ||
      null
    );
  }

  function showReportFallback(modal, region) {
    if (!modal) return;
    var fallback = reportModalPart(modal, region, "[data-report-modal-fallback]");
    var external = reportModalPart(modal, region, "[data-report-modal-external]");
    var frame = modal._reportFrame || reportModalPart(modal, region, "[data-report-frame]");
    if (frame) frame.hidden = true;
    if (external) external.hidden = true;
    if (fallback) fallback.hidden = false;
    modal.setAttribute("data-report-modal-state", "fallback");
  }

  function showReportExternal(modal, region) {
    if (!modal) return;
    var fallback = reportModalPart(modal, region, "[data-report-modal-fallback]");
    var external = reportModalPart(modal, region, "[data-report-modal-external]");
    var frame = modal._reportFrame || reportModalPart(modal, region, "[data-report-frame]");
    if (frame) frame.hidden = true;
    if (fallback) fallback.hidden = true;
    if (external) external.hidden = false;
    modal.setAttribute("data-report-modal-state", "external");
  }

  function assessReportFrame(modal, region, frame) {
    if (!modal || !frame || frame.hidden) return;
    var win = null;
    try {
      win = frame.contentWindow || null;
    } catch (err) {
      return;
    }
    if (!win) return;
    try {
      var href = win.location && win.location.href;
      if (!href || href === "about:blank") showReportFallback(modal, region);
    } catch (err) {
      // Cross-origin access denied usually means the public report rendered.
    }
  }

  function unloadReportFrame(modal, region) {
    var frame =
      (modal && modal._reportFrame) ||
      (region && region._reportFrame) ||
      (modal && modal.querySelector && modal.querySelector("[data-report-frame]"));
    if (frame) {
      try {
        frame.src = "about:blank";
        if (typeof frame.setAttribute === "function") frame.setAttribute("src", "about:blank");
      } catch (err) {
        // Clearing the source is best-effort.
      }
      if (frame.parentNode && typeof frame.parentNode.removeChild === "function") {
        try {
          frame.parentNode.removeChild(frame);
        } catch (err) {
          frame.hidden = true;
        }
      } else {
        frame.hidden = true;
      }
    }
    if (modal) modal._reportFrame = null;
    if (region) region._reportFrame = null;
    var fallback = reportModalPart(modal, region, "[data-report-modal-fallback]");
    var external = reportModalPart(modal, region, "[data-report-modal-external]");
    if (fallback) fallback.hidden = true;
    if (external) external.hidden = true;
    if (modal && typeof modal.removeAttribute === "function") {
      modal.removeAttribute("data-report-modal-state");
    }
  }

  function loadReportFrame(modal, region) {
    if (!modal) return;
    unloadReportFrame(modal, region);
    var original = reportModalPart(modal, region, "[data-report-original]");
    var url = original && typeof original.getAttribute === "function" ? original.getAttribute("href") : "";
    url = url == null ? "" : String(url).trim();
    var fallback = reportModalPart(modal, region, "[data-report-modal-fallback]");
    var external = reportModalPart(modal, region, "[data-report-modal-external]");
    if (!url || !/^https?:\/\//i.test(url)) {
      if (external) external.hidden = true;
      if (fallback) fallback.hidden = false;
      modal.setAttribute("data-report-modal-state", "fallback");
      return;
    }
    if (isKnownNonEmbeddableCertificateUrl(url)) {
      showReportExternal(modal, region);
      return;
    }
    if (fallback) fallback.hidden = true;
    if (external) external.hidden = true;
    var body = reportModalPart(modal, region, "[data-report-modal-body]");
    var doc = window.document;
    if (!body || !doc || typeof doc.createElement !== "function") {
      if (fallback) fallback.hidden = false;
      modal.setAttribute("data-report-modal-state", "fallback");
      return;
    }
    var frame = doc.createElement("iframe");
    frame.className = "diamond-details__report-modal-frame";
    if (typeof frame.setAttribute === "function") {
      frame.setAttribute("data-report-frame", "true");
      frame.setAttribute("title", reportModalTitleText(modal, region));
      frame.setAttribute("src", url);
    }
    frame.src = url;
    if (typeof frame.addEventListener === "function") {
      frame.addEventListener("error", function () {
        showReportFallback(modal, region);
      });
      frame.addEventListener("load", function () {
        assessReportFrame(modal, region, frame);
      });
    }
    if (typeof body.appendChild === "function") {
      body.appendChild(frame);
    }
    modal._reportFrame = frame;
    if (region) region._reportFrame = frame;
    modal.setAttribute("data-report-modal-state", "loaded");
  }

  function closeGradingReportModal(region) {
    var modal = region ? region.querySelector("[data-report-modal]") : null;
    if (!modal || modal.hidden) return;
    unloadReportFrame(modal, region);
    unlockPageScroll(modal);
    modal.hidden = true;
    var opener = modal._opener;
    modal._opener = null;
    if (opener && typeof opener.focus === "function") opener.focus();
  }

  function openGradingReportModal(region, opener) {
    if (!region) return;
    var modal = region.querySelector("[data-report-modal]");
    if (!modal) return;
    closeShareModal(region);
    modal._opener = opener || null;
    modal.hidden = false;
    lockPageScroll(modal);
    loadReportFrame(modal, region);
    var dialog = region.querySelector("[data-report-modal-dialog]");
    if (dialog && typeof dialog.focus === "function") dialog.focus();
  }

  function bindReportModal(region) {
    if (!region) return;
    var modal = region.querySelector("[data-report-modal]");
    if (!modal || modal.getAttribute("data-bound-report-modal") === "true") return;
    modal.setAttribute("data-bound-report-modal", "true");
    var closers = region.querySelectorAll("[data-report-modal-close]");
    var closeList = closers && typeof closers.length === "number" ? closers : closers ? [closers] : [];
    var i;
    for (i = 0; i < closeList.length; i += 1) {
      closeList[i].addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        closeGradingReportModal(region);
      });
    }
  }

  function bindReportModalOpeners(region) {
    if (!region) return;
    var viewport = region.querySelector("[data-media-viewport]");
    var roots = [region];
    if (viewport && viewport !== region) roots.push(viewport);
    var r;
    for (r = 0; r < roots.length; r += 1) {
      var openers = roots[r].querySelectorAll("[data-open-grading-report]");
      var list = openers && typeof openers.length === "number" ? openers : openers ? [openers] : [];
      var i;
      for (i = 0; i < list.length; i += 1) {
        if (list[i].getAttribute("data-bound-report-modal") === "true") continue;
        list[i].setAttribute("data-bound-report-modal", "true");
        list[i].addEventListener("click", function (event) {
          if (event && typeof event.preventDefault === "function") event.preventDefault();
          openGradingReportModal(region, event && (event.currentTarget || event.target));
        });
      }
    }
  }

  function markCopied(button) {
    if (!button) return;
    button.setAttribute("data-copied", "true");
    if ("textContent" in button) button.textContent = "Copied";
    else button.innerHTML = "Copied";
  }

  function copyShareLink(region) {
    var input = region ? region.querySelector("[data-share-url]") : null;
    var button = region ? region.querySelector("[data-share-copy]") : null;
    var path = input && typeof input.getAttribute === "function" ? input.getAttribute("data-share-path") : "";
    var value = (input && (input.value || input.getAttribute("value"))) || path || "";
    if (!value) return;

    function done() {
      markCopied(button);
    }

    var nav = window.navigator;
    if (nav && nav.clipboard && typeof nav.clipboard.writeText === "function") {
      try {
        var wrote = nav.clipboard.writeText(value);
        if (wrote && typeof wrote.then === "function") {
          wrote.then(done).catch(done);
          return;
        }
        done();
        return;
      } catch (err) {
        // Fall through to the input selection fallback.
      }
    }

    if (input && typeof input.select === "function") {
      try {
        input.focus();
        input.select();
      } catch (err) {
        // Ignore selection failures in non-DOM harnesses.
      }
    }
    try {
      if (window.document && typeof window.document.execCommand === "function") {
        window.document.execCommand("copy");
      }
    } catch (err) {
      // Copy may be denied; the URL remains visible for manual copy.
    }
    done();
  }

  function isCertificateGalleryItem(itemId) {
    return itemId === MEDIA_MODE_CERTIFICATE;
  }

  function certificateThumb(region) {
    return region ? region.querySelector('[data-media-gallery-item="' + MEDIA_MODE_CERTIFICATE + '"]') : null;
  }

  function openCertificateReport(region, diamond, opener) {
    if (!region || !gradingReport(diamond)) return;
    openGradingReportModal(region, opener || certificateThumb(region));
  }

  function applyMediaMode(region, diamond, itemId) {
    if (isCertificateGalleryItem(itemId)) {
      openCertificateReport(region, diamond, certificateThumb(region));
      return;
    }
    var explorer = region ? region.querySelector("[data-media-explorer]") : null;
    var viewport = region ? region.querySelector("[data-media-viewport]") : null;
    if (!explorer || !viewport) return;
    var item = resolveGalleryItem(diamond, itemId);
    if (!item) return;
    var mode = item.mode || item.id;
    var view = item.view || "";
    var identity = formatStoneIdentity(diamond);
    if (item.id === explorer.getAttribute("data-active-media-item")) {
      syncGallerySelection(region, item.id);
      return;
    }
    teardownDetailFrameHost(region);
    explorer.setAttribute("data-active-media-mode", mode);
    explorer.setAttribute("data-active-media-item", item.id);
    syncGallerySelection(region, item.id);
    viewport.setAttribute("data-media-mode-panel", mode);
    viewport.setAttribute("aria-labelledby", "diamond-details-media-tab-" + item.id);
    syncDiamondSubControl(region);
    viewport.innerHTML = renderMediaModeContent(diamond, identity, mode, view);
    if (mode === MEDIA_MODE_DIAMOND && view === DIAMOND_VIEW_PHOTO) {
      attachBrokenImageFallback(region);
    }
    if (mode === MEDIA_MODE_DIAMOND && view === DIAMOND_VIEW_360) {
      mountDetailFrameHost(region, diamond);
    }
    if (mode === MEDIA_MODE_ACTUAL_SIZE) bindSizeTone(region);
    bindReportModalOpeners(region);
  }

  function bindMediaExplorer(region, diamond) {
    if (!region) return;
    var items = region.querySelectorAll("[data-media-gallery-item]");
    var all = items && typeof items.length === "number" ? items : items ? [items] : [];
    var list = [];
    var certificateBtn = null;
    var i;

    for (i = 0; i < all.length; i += 1) {
      if (isCertificateGalleryItem(all[i].getAttribute("data-media-gallery-item"))) {
        certificateBtn = all[i];
      } else {
        list.push(all[i]);
      }
    }

    function tabAt(index) {
      if (!list.length) return null;
      if (index < 0) index = list.length - 1;
      if (index >= list.length) index = 0;
      return list[index];
    }

    for (i = 0; i < list.length; i += 1) {
      (function (button, index) {
        button.addEventListener("click", function (event) {
          if (event && typeof event.preventDefault === "function") event.preventDefault();
          applyMediaMode(region, diamond, button.getAttribute("data-media-gallery-item"));
        });
        button.addEventListener("keydown", function (event) {
          if (!event || !event.key) return;
          var next = null;
          if (event.key === "ArrowRight" || event.key === "ArrowDown") next = tabAt(index + 1);
          else if (event.key === "ArrowLeft" || event.key === "ArrowUp") next = tabAt(index - 1);
          else if (event.key === "Home") next = tabAt(0);
          else if (event.key === "End") next = tabAt(list.length - 1);
          if (!next) return;
          if (typeof event.preventDefault === "function") event.preventDefault();
          applyMediaMode(region, diamond, next.getAttribute("data-media-gallery-item"));
          if (typeof next.focus === "function") next.focus();
        });
      })(list[i], i);
    }

    if (certificateBtn) {
      certificateBtn.addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        openCertificateReport(region, diamond, certificateBtn);
      });
    }
  }

  function bindPanelAccordions(region) {
    if (!region) return;
    var triggers = region.querySelectorAll("[data-accordion-trigger]");
    var list = triggers && typeof triggers.length === "number" ? triggers : triggers ? [triggers] : [];
    var i;
    for (i = 0; i < list.length; i += 1) {
      if (list[i].getAttribute("data-bound-accordion") === "true") continue;
      list[i].setAttribute("data-bound-accordion", "true");
      list[i].addEventListener("click", function (event) {
        var trigger = event && (event.currentTarget || event.target);
        var panelKey;
        var panel;
        var expanded;
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        if (!trigger || !trigger.getAttribute) return;
        panelKey = trigger.getAttribute("data-accordion-trigger");
        panel = panelKey ? region.querySelector('[data-accordion-panel="' + panelKey + '"]') : null;
        expanded = trigger.getAttribute("aria-expanded") === "true";
        trigger.setAttribute("aria-expanded", expanded ? "false" : "true");
        if (!panel) return;
        if (expanded) panel.setAttribute("hidden", "");
        else panel.removeAttribute("hidden");
      });
    }
  }

  function bindExperience(region, diamond, options) {
    options = options || {};
    if (!region) return;

    var saveBtn = region.querySelector("[data-save]");
    if (saveBtn) {
      bindSavedChangeSync();
      applySaveState(saveBtn, diamond);
      saveBtn.addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        var Saved = window.DiamondSaved;
        var id = recordId(diamond);
        if (!Saved || !id) return;
        var nowSaved;
        if (typeof Saved.toggle === "function") {
          nowSaved = Saved.toggle(buildSavedRecord(diamond));
        } else if (Saved.has(id)) {
          Saved.remove(id);
          nowSaved = false;
        } else {
          Saved.save(buildSavedRecord(diamond));
          nowSaved = true;
        }
        if (typeof Saved.applyHeartState === "function") {
          Saved.applyHeartState(saveBtn, nowSaved);
        }
      });
    }

    var shareOpen = region.querySelector("[data-share-open]");
    if (shareOpen) {
      shareOpen.addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        openShareModal(region, shareOpen);
      });
    }

    var hintOpen = region.querySelector("[data-drop-a-hint]");
    var hintModal = region.querySelector("[data-hint-modal]");
    if (hintOpen && hintModal) {
      hintOpen.addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        openDropAHintModal(region, hintOpen);
      });
      var hintClosers = region.querySelectorAll("[data-hint-close]");
      var hintCloseList =
        hintClosers && typeof hintClosers.length === "number"
          ? hintClosers
          : hintClosers
            ? [hintClosers]
            : [];
      var hintCloseIndex;
      for (hintCloseIndex = 0; hintCloseIndex < hintCloseList.length; hintCloseIndex += 1) {
        hintCloseList[hintCloseIndex].addEventListener("click", function (event) {
          if (event && typeof event.preventDefault === "function") event.preventDefault();
          closeDropAHintModal(region);
        });
      }
      var hintForm = region.querySelector("[data-hint-form]");
      if (hintForm) {
        hintForm.addEventListener("submit", function (event) {
          if (event && typeof event.preventDefault === "function") event.preventDefault();
          if (event && typeof event.stopPropagation === "function") event.stopPropagation();
          handleDropAHintSubmit(region, hintModal);
        });
      }
      var hintDone = region.querySelector("[data-hint-done]");
      if (hintDone) {
        hintDone.addEventListener("click", function (event) {
          if (event && typeof event.preventDefault === "function") event.preventDefault();
          closeDropAHintModal(region);
        });
      }
    }

    var closers = region.querySelectorAll("[data-share-close]");
    var closeList = closers && typeof closers.length === "number" ? closers : closers ? [closers] : [];
    for (var i = 0; i < closeList.length; i += 1) {
      closeList[i].addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        closeShareModal(region);
      });
    }

    var copyBtn = region.querySelector("[data-share-copy]");
    if (copyBtn) {
      copyBtn.addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        copyShareLink(region);
      });
    }

    bindPanelAccordions(region);
    bindMediaExplorer(region, diamond);
    bindSizeTone(region);
    bindUnderstandNav(region, diamond);
    beginCompare(diamond, region);
    bindDiamondSubControl(region, diamond);
    bindReportModal(region);
    bindReportModalOpeners(region);
    if (diamondViewCapabilities(diamond).defaultView === DIAMOND_VIEW_360) {
      mountDetailFrameHost(region, diamond);
    }

    var selectBtn = region.querySelector("[data-select-this-diamond]");
    if (selectBtn && options.offerSelect) {
      selectBtn.addEventListener("click", function (event) {
        if (event && typeof event.preventDefault === "function") event.preventDefault();
        if (!diamond || diamond.availability === "unavailable") return;
        var doc = window.document;
        if (!doc || typeof doc.dispatchEvent !== "function") return;
        var EventCtor = window.CustomEvent;
        var evt;
        if (typeof EventCtor === "function") {
          evt = new EventCtor(SELECT_EVENT, { detail: { diamond: diamond, id: recordId(diamond) } });
        } else if (typeof doc.createEvent === "function") {
          evt = doc.createEvent("Event");
          evt.initEvent(SELECT_EVENT, true, true);
          evt.detail = { diamond: diamond, id: recordId(diamond) };
        } else {
          evt = { type: SELECT_EVENT, detail: { diamond: diamond, id: recordId(diamond) } };
        }
        doc.dispatchEvent(evt);
      });
    }
  }

  function renderSuccess(diamond) {
    var region = queryState(STATE_SUCCESS);
    if (!region) return;
    var identity = formatStoneIdentity(diamond);
    activeDiamond = diamond;
    region.innerHTML = renderExperience(diamond, { offerSelect: true });
    attachBrokenImageFallback(region);
    bindExperience(region, diamond, { offerSelect: true });
    beginRecentlyViewed(diamond, region);
    setDocumentTitle(identity);
  }

  function renderUnavailable(diamond) {
    var region = queryState(STATE_UNAVAILABLE);
    if (!region) return;
    var identity = formatStoneIdentity(diamond);
    activeDiamond = diamond;
    region.innerHTML = renderExperience(diamond, {
      offerSelect: false,
      recoveryHtml: renderRecoveryBlock(diamond)
    });
    attachBrokenImageFallback(region);
    bindExperience(region, diamond, { offerSelect: false });
    setDocumentTitle(identity);
  }

  function isAbortError(err) {
    if (!err) return false;
    if (err.name === "AbortError") return true;
    return !!(err.cause && err.cause.name === "AbortError");
  }

  function classifyPayload(data) {
    if (!data || typeof data !== "object" || !data.diamond || typeof data.diamond !== "object") {
      return STATE_ERROR;
    }
    if (data.diamond.availability === "available") return STATE_SUCCESS;
    if (data.diamond.availability === "unavailable") return STATE_UNAVAILABLE;
    return STATE_ERROR;
  }

  function classifyError(err) {
    if (isAbortError(err)) return "abort";
    var status = err && err.status;
    if (status === 400 || status === 404) return STATE_NOT_FOUND;
    return STATE_ERROR;
  }

  function abortActive() {
    if (activeController && typeof activeController.abort === "function") {
      try {
        activeController.abort();
      } catch (err) {
        // Ignore abort construction differences in tests / older browsers.
      }
    }
    activeController = null;
    inFlight = false;
    abortCompare();
    abortRecentlyViewed();
    teardownDetailFrameHost(rootEl);
  }

  function showNotFound() {
    activeDiamond = null;
    setPageState(STATE_NOT_FOUND);
    setDocumentTitle("Diamond Not Found");
  }

  function showError() {
    activeDiamond = null;
    setPageState(STATE_ERROR);
    setDocumentTitle("Unable to Load Diamond");
  }

  function loadFromLocation() {
    if (inFlight) return;

    var rawId = parseIdFromLocation(window.location);
    if (rawId == null || String(rawId).trim() === "") {
      showNotFound();
      return;
    }

    var api = window.IvanovAPI;
    if (!api || typeof api.normalizeDiamondId !== "function" || typeof api.getDiamond !== "function") {
      showError();
      return;
    }

    try {
      api.normalizeDiamondId(rawId);
    } catch (err) {
      showNotFound();
      return;
    }

    inFlight = true;
    abortCompare();
    abortRecentlyViewed();
    setPageState(STATE_LOADING);

    var controller = null;
    if (typeof window.AbortController === "function") {
      controller = new window.AbortController();
      activeController = controller;
    }

    var requestOptions = {};
    if (controller) requestOptions.signal = controller.signal;

    api
      .getDiamond(rawId, requestOptions)
      .then(function (data) {
        var next = classifyPayload(data);
        if (next === STATE_SUCCESS) {
          renderSuccess(data.diamond);
          setPageState(STATE_SUCCESS);
          return;
        }
        if (next === STATE_UNAVAILABLE) {
          renderUnavailable(data.diamond);
          setPageState(STATE_UNAVAILABLE);
          return;
        }
        showError();
      })
      .catch(function (err) {
        var next = classifyError(err);
        if (next === "abort") return;
        if (next === STATE_NOT_FOUND) {
          showNotFound();
          return;
        }
        showError();
      })
      .then(function () {
        inFlight = false;
        activeController = null;
      });
  }

  function onRetry(event) {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    if (inFlight) return;
    loadFromLocation();
  }

  function bindRetry() {
    if (!rootEl) return;
    var retry = rootEl.querySelector("[data-retry]");
    if (!retry || retry.getAttribute("data-bound") === "true") return;
    retry.setAttribute("data-bound", "true");
    retry.addEventListener("click", onRetry);
  }

  function bindPagehide() {
    if (pageListenerBound) return;
    pageListenerBound = true;
    window.addEventListener("pagehide", abortActive);
  }

  function bindEscape() {
    if (escapeBound) return;
    escapeBound = true;
    window.addEventListener("keydown", function (event) {
      if (!event || event.key !== "Escape") return;
      if (!rootEl) return;
      var reportModal = rootEl.querySelector("[data-report-modal]");
      if (reportModal && reportModal.hidden === false) {
        var reportRegion =
          (rootEl.querySelector && rootEl.querySelector('[data-state="success"]')) ||
          (rootEl.querySelector && rootEl.querySelector('[data-state="unavailable"]')) ||
          rootEl;
        closeGradingReportModal(reportRegion);
        return;
      }
      var hintModal = rootEl.querySelector("[data-hint-modal]");
      if (hintModal && hintModal.hidden === false) {
        var hintRegion =
          (rootEl.querySelector && rootEl.querySelector('[data-state="success"]')) ||
          (rootEl.querySelector && rootEl.querySelector('[data-state="unavailable"]')) ||
          rootEl;
        closeDropAHintModal(hintRegion);
        return;
      }
      var openModal = rootEl.querySelector("[data-share-modal]");
      if (openModal && openModal.hidden === false) {
        var shareRegion =
          (rootEl.querySelector && rootEl.querySelector('[data-state="success"]')) ||
          (rootEl.querySelector && rootEl.querySelector('[data-state="unavailable"]')) ||
          rootEl;
        closeShareModal(shareRegion);
      }
    });
  }

  function detailLocationUrl() {
    var loc = window.location || {};
    if (loc.href) return String(loc.href);
    return String(loc.origin || "") + String(loc.pathname || PAGE_PATH) + String(loc.search || "");
  }

  function sessionStorageStore() {
    try {
      return window.sessionStorage || null;
    } catch (err) {
      return null;
    }
  }

  function clearSearchReturnMarker() {
    var storage = sessionStorageStore();
    if (!storage || typeof storage.removeItem !== "function") return;
    try {
      storage.removeItem(SEARCH_RETURN_MARKER_KEY);
    } catch (err) {
      // Storage may be blocked. Leaving the page is still safe.
    }
  }

  function writeSearchReturnMarker(target) {
    var storage = sessionStorageStore();
    if (!storage || typeof storage.setItem !== "function" || typeof storage.getItem !== "function") {
      return false;
    }
    var payload = JSON.stringify({
      pathname: SEARCH_PATH,
      search: target.search || ""
    });
    try {
      storage.setItem(SEARCH_RETURN_MARKER_KEY, payload);
      return storage.getItem(SEARCH_RETURN_MARKER_KEY) === payload;
    } catch (err) {
      clearSearchReturnMarker();
      return false;
    }
  }

  // Same-origin canonical Diamond Search only. Exact pathname, never a substring.
  function parseSearchReturnReferrer() {
    var referrer = "";
    try {
      referrer = (window.document && window.document.referrer) || "";
    } catch (err) {
      return null;
    }
    if (!referrer) return null;
    try {
      var UrlCtor = window.URL;
      if (typeof UrlCtor !== "function") return null;
      var here = new UrlCtor(detailLocationUrl(), "https://ivanovjewelry.com");
      var from = new UrlCtor(referrer, here.href);
      if (from.protocol !== "http:" && from.protocol !== "https:") return null;
      if (from.username || from.password) return null;
      if (from.origin !== here.origin) return null;
      if (from.pathname !== SEARCH_PATH) return null;
      if (typeof from.search !== "string") return null;
      return {
        pathname: SEARCH_PATH,
        search: from.search,
        hash: typeof from.hash === "string" ? from.hash : ""
      };
    } catch (err) {
      return null;
    }
  }

  function validatedSearchDestination(target) {
    var search = target && typeof target.search === "string" ? target.search : "";
    if (search !== "" && search.charAt(0) !== "?") search = "";
    var hash = target && typeof target.hash === "string" ? target.hash : "";
    if (hash !== "" && (hash.charAt(0) !== "#" || hash.length > 256 || /[\s\\]/.test(hash))) {
      hash = "";
    }
    return SEARCH_PATH + search + hash;
  }

  function assignLocation(url) {
    if (window.location && typeof window.location.assign === "function") {
      window.location.assign(url);
      return;
    }
    if (window.location) window.location.href = url;
  }

  function canHistoryBack() {
    try {
      return !!(window.history && typeof window.history.back === "function");
    } catch (err) {
      return false;
    }
  }

  function assignValidatedSearch(target) {
    try {
      assignLocation(validatedSearchDestination(target));
      return true;
    } catch (err) {
      clearSearchReturnMarker();
      return false;
    }
  }

  function hasMeaningfulDetailHistory() {
    var referrer = "";
    try {
      referrer = (window.document && window.document.referrer) || "";
    } catch (err) {
      referrer = "";
    }
    if (!referrer) return false;
    try {
      var UrlCtor = window.URL;
      if (typeof UrlCtor !== "function") return false;
      var here = new UrlCtor(detailLocationUrl(), "https://ivanovjewelry.com");
      var from = new UrlCtor(referrer, here.href);
      if (from.origin !== here.origin) return false;
      if (from.pathname === here.pathname && from.search === here.search) return false;
      return true;
    } catch (err) {
      return false;
    }
  }

  function closeDiamondDetails() {
    var searchReturn = parseSearchReturnReferrer();
    if (searchReturn) {
      if (!writeSearchReturnMarker(searchReturn)) {
        assignValidatedSearch(searchReturn);
        return "search";
      }
      if (canHistoryBack()) {
        try {
          window.history.back();
          return "back";
        } catch (err) {
          assignValidatedSearch(searchReturn);
          return "search";
        }
      }
      assignValidatedSearch(searchReturn);
      return "search";
    }
    if (hasMeaningfulDetailHistory() && canHistoryBack()) {
      try {
        window.history.back();
        return "back";
      } catch (err) {
        // Fall through to the generic Search path.
      }
    }
    assignLocation(SEARCH_PATH);
    return "search";
  }

  // Browser Back does not run Detail X. Arm the same marker when this page
  // was entered from canonical Diamond Search so Search pageshow can close
  // the restored Viewer. Release it if the shopper then follows another
  // document link or form, so an unrelated Viewer is not closed later.
  // Detail X still writes the marker and calls history.back() once.
  function armSearchReturnMarker() {
    var searchReturn = parseSearchReturnReferrer();
    if (!searchReturn) return false;
    return writeSearchReturnMarker(searchReturn);
  }

  function nodeTagName(node) {
    return node && node.tagName ? String(node.tagName).toLowerCase() : "";
  }

  function eventTargetNode(event) {
    var node = event && event.target;
    if (node && node.nodeType === 3) node = node.parentNode;
    return node || null;
  }

  function walkAncestors(node, visit) {
    var current = node;
    while (current && current !== window.document) {
      if (visit(current)) return current;
      current = current.parentNode;
    }
    return null;
  }

  function isDetailCloseControl(event) {
    return !!walkAncestors(eventTargetNode(event), function (node) {
      return typeof node.getAttribute === "function" && node.getAttribute("data-detail-close") != null;
    });
  }

  function departureAnchor(event) {
    return walkAncestors(eventTargetNode(event), function (node) {
      return (
        nodeTagName(node) === "a" &&
        typeof node.getAttribute === "function" &&
        node.getAttribute("href") != null
      );
    });
  }

  function isModifiedClick(event) {
    return !!(event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey));
  }

  // Hash changes and the current Detail URL stay in this document. They
  // must not release the marker that Browser Back still needs.
  function staysOnDetailDocument(href) {
    var trimmed = String(href == null ? "" : href).replace(/^\s+/, "");
    if (!trimmed || trimmed.charAt(0) === "#") return true;
    try {
      var UrlCtor = window.URL;
      if (typeof UrlCtor !== "function") return false;
      var here = new UrlCtor(detailLocationUrl(), "https://ivanovjewelry.com");
      var dest = new UrlCtor(trimmed, here.href);
      if (dest.protocol !== "http:" && dest.protocol !== "https:") return false;
      if (dest.origin !== here.origin) return false;
      return dest.pathname === here.pathname && dest.search === here.search;
    } catch (err) {
      return false;
    }
  }

  function isCurrentDocumentDeparture(event) {
    var form;
    var formTarget;
    var anchor;
    var target;
    var href;
    var trimmed;
    if (!event || event.defaultPrevented || isDetailCloseControl(event)) return false;
    if (event.type === "submit") {
      form = event.target;
      if (!form || nodeTagName(form) !== "form") return false;
      formTarget = typeof form.getAttribute === "function" ? form.getAttribute("target") : "";
      if (formTarget && formTarget !== "_self") return false;
      return true;
    }
    if (event.type !== "click") return false;
    if (typeof event.button === "number" && event.button !== 0) return false;
    if (isModifiedClick(event)) return false;
    anchor = departureAnchor(event);
    if (!anchor) return false;
    if (typeof anchor.hasAttribute === "function" && anchor.hasAttribute("download")) return false;
    target = anchor.getAttribute("target");
    if (target && target !== "_self") return false;
    href = anchor.getAttribute("href");
    if (href == null) return false;
    trimmed = String(href).replace(/^\s+/, "");
    if (/^javascript:/i.test(trimmed)) return false;
    if (staysOnDetailDocument(trimmed)) return false;
    return true;
  }

  function releaseSearchReturnForDeparture(event) {
    if (!isCurrentDocumentDeparture(event)) return false;
    clearSearchReturnMarker();
    return true;
  }

  function bindSearchReturnLifecycle() {
    if (searchReturnBound) return;
    searchReturnBound = true;
    window.addEventListener("pageshow", armSearchReturnMarker);
    if (window.document && typeof window.document.addEventListener === "function") {
      window.document.addEventListener("click", releaseSearchReturnForDeparture);
      window.document.addEventListener("submit", releaseSearchReturnForDeparture);
    }
  }

  function bindClose() {
    if (!rootEl) return;
    var button = rootEl.querySelector("[data-detail-close]");
    if (!button || button.getAttribute("data-bound") === "true") return;
    button.setAttribute("data-bound", "true");
    button.addEventListener("click", function (event) {
      if (event && typeof event.preventDefault === "function") event.preventDefault();
      closeDiamondDetails();
    });
  }

  function boot() {
    rootEl = window.document.querySelector("[data-diamond-details]");
    if (!rootEl) return;
    bindRetry();
    bindClose();
    bindPagehide();
    bindEscape();
    bindSearchReturnLifecycle();
    armSearchReturnMarker();
    loadFromLocation();
  }

  window.DiamondDetail = {
    PAGE_PATH: PAGE_PATH,
    SEARCH_PATH: SEARCH_PATH,
    SEARCH_RETURN_MARKER_KEY: SEARCH_RETURN_MARKER_KEY,
    normalizeSearchOriginParam: normalizeSearchOriginParam,
    buildSimilarSearchPath: buildSimilarSearchPath,
    closeDiamondDetails: closeDiamondDetails,
    armSearchReturnMarker: armSearchReturnMarker,
    releaseSearchReturnForDeparture: releaseSearchReturnForDeparture,
    hasMeaningfulDetailHistory: hasMeaningfulDetailHistory,
    SELECT_EVENT: SELECT_EVENT,
    HINT_NAME_MAX: HINT_NAME_MAX,
    HINT_NOTE_MAX: HINT_NOTE_MAX,
    HINT_PRIVACY_PATH: HINT_PRIVACY_PATH,
    HINT_SUBMIT_TIMEOUT_MS: HINT_SUBMIT_TIMEOUT_MS,
    HINT_ERROR_COPY: HINT_ERROR_COPY,
    hintBareUuid: hintBareUuid,
    isValidHintEmail: isValidHintEmail,
    buildDropAHintPayload: buildDropAHintPayload,
    validateDropAHintForm: validateDropAHintForm,
    renderDropAHintSuccessShell: renderDropAHintSuccessShell,
    renderDropAHintErrorShell: renderDropAHintErrorShell,
    mapDropAHintErrorMessage: mapDropAHintErrorMessage,
    isValidDropAHintSuccess: isValidDropAHintSuccess,
    openDropAHintModal: openDropAHintModal,
    closeDropAHintModal: closeDropAHintModal,
    handleDropAHintSubmit: handleDropAHintSubmit,
    TRUST_LINE: TRUST_LINE,
    FALLBACK_DELIVERY: FALLBACK_DELIVERY,
    IVANOV_SERVICE_CONTENT: IVANOV_SERVICE_CONTENT,
    ivanovServiceContent: ivanovServiceContent,
    buildUrl: buildUrl,
    parseIdFromLocation: parseIdFromLocation,
    formatStoneIdentity: formatStoneIdentity,
    formatCarat: formatCarat,
    formatPriceUsd: formatPriceUsd,
    formatCut: formatCut,
    formatCutSegment: formatCutSegment,
    formatColorGrade: formatColorGrade,
    formatColorSegment: formatColorSegment,
    formatClarityGrade: formatClarityGrade,
    formatClaritySegment: formatClaritySegment,
    formatGatewayCarat: formatGatewayCarat,
    formatSpecLine: formatSpecLine,
    gradingLab: gradingLab,
    formatDeliveryLine: formatDeliveryLine,
    toSavedSnapshot: toSavedSnapshot,
    buildSavedRecord: buildSavedRecord,
    canonicalSharePath: canonicalSharePath,
    gradingReport: gradingReport,
    certificateReportNumber: certificateReportNumber,
    formatCertificateLabLabel: formatCertificateLabLabel,
    formatCertificateExternalLabName: formatCertificateExternalLabName,
    isKnownNonEmbeddableCertificateUrl: isKnownNonEmbeddableCertificateUrl,
    formatDiamondId: formatDiamondId,
    formatMeasurementsLine: formatMeasurementsLine,
    collectDiamondDetailSpecs: collectDiamondDetailSpecs,
    detailTechnicalSources: detailTechnicalSources,
    formatPolish: formatPolish,
    formatSymmetry: formatSymmetry,
    formatGirdle: formatGirdle,
    formatCulet: formatCulet,
    formatFluorescence: formatFluorescence,
    specEducationMeta: specEducationMeta,
    specEducationContext: specEducationContext,
    certificatePanelModel: certificatePanelModel,
    renderCertificateReportModal: renderCertificateReportModal,
    openGradingReportModal: openGradingReportModal,
    closeGradingReportModal: closeGradingReportModal,
    MEDIA_MODE_DIAMOND: MEDIA_MODE_DIAMOND,
    MEDIA_MODE_ACTUAL_SIZE: MEDIA_MODE_ACTUAL_SIZE,
    MEDIA_MODE_PROPORTIONS: MEDIA_MODE_PROPORTIONS,
    MEDIA_MODE_SIDE_PROFILE: MEDIA_MODE_SIDE_PROFILE,
    MEDIA_MODE_TOP_VIEW: MEDIA_MODE_TOP_VIEW,
    MEDIA_MODE_CERTIFICATE: MEDIA_MODE_CERTIFICATE,
    MEDIA_GALLERY_COLUMNS: MEDIA_GALLERY_COLUMNS,
    MEDIA_MODES: MEDIA_MODES,
    GALLERY_ITEM_360: GALLERY_ITEM_360,
    GALLERY_ITEM_PHOTO: GALLERY_ITEM_PHOTO,
    collectMediaGalleryItems: collectMediaGalleryItems,
    resolveGalleryItem: resolveGalleryItem,
    diamondGalleryPreview: diamondGalleryPreview,
    diamond360PosterUrl: diamond360PosterUrl,
    renderMediaGalleryMarkup: renderMediaGalleryMarkup,
    renderMediaGallery: renderMediaGallery,
    DIAMOND_VIEW_360: DIAMOND_VIEW_360,
    DIAMOND_VIEW_PHOTO: DIAMOND_VIEW_PHOTO,
    DIAMOND_VIEW_SILHOUETTE: DIAMOND_VIEW_SILHOUETTE,
    mediaCapabilities: mediaCapabilities,
    usableSizeMeasurements: usableSizeMeasurements,
    sizeOverlayMetrics: sizeOverlayMetrics,
    sizeCompositionAtHandWidth: sizeCompositionAtHandWidth,
    resolveSizeShape: resolveSizeShape,
    SIZE_NATIVE_PX_PER_MM: SIZE_NATIVE_PX_PER_MM,
    SIZE_HAND_NATIVE_WIDTH: SIZE_HAND_NATIVE_WIDTH,
    SIZE_HAND_NATIVE_HEIGHT: SIZE_HAND_NATIVE_HEIGHT,
    SIZE_HAND_DISPLAY_MAX_WIDTH: SIZE_HAND_DISPLAY_MAX_WIDTH,
    SIZE_ANCHOR_X: SIZE_ANCHOR_X,
    SIZE_ANCHOR_Y: SIZE_ANCHOR_Y,
    SIZE_ANCHOR_X_PX: SIZE_ANCHOR_X_PX,
    SIZE_ANCHOR_Y_PX: SIZE_ANCHOR_Y_PX,
    SIZE_SHAPE_KEYS: SIZE_SHAPE_KEYS,
    SIZE_SHAPE_MASTERS: SIZE_SHAPE_MASTERS,
    SIZE_MASTER_KIND: SIZE_MASTER_KIND,
    SIZE_PEAR_ASSET_ALIGN: SIZE_PEAR_ASSET_ALIGN,
    SIZE_SKIN_TONE_COUNT: SIZE_SKIN_TONE_COUNT,
    SIZE_DEFAULT_SKIN_TONE: SIZE_DEFAULT_SKIN_TONE,
    SIZE_TONE_MOBILE_INSET_PX: SIZE_TONE_MOBILE_INSET_PX,
    SIZE_TONE_HORIZONTAL_INSET_PX: SIZE_TONE_HORIZONTAL_INSET_PX,
    sizeTonePercent: sizeTonePercent,
    sizeToneLinear: sizeToneLinear,
    sizeToneHorizontalThumbCenterPx: sizeToneHorizontalThumbCenterPx,
    sizeToneFromPointer: sizeToneFromPointer,
    sizeToneUrl: sizeToneUrl,
    isUsableSizeToneUrl: isUsableSizeToneUrl,
    preloadSizeToneAssets: preloadSizeToneAssets,
    sizeTonePreloadStatus: sizeTonePreloadStatus,
    applySizeToneHand: applySizeToneHand,
    countSizeToneAssets: countSizeToneAssets,
    normalizeSizeTone: normalizeSizeTone,
    getActiveSizeTone: getActiveSizeTone,
    applySizeTone: applySizeTone,
    UNDERSTAND_DEFAULT_MODULE: UNDERSTAND_DEFAULT_MODULE,
    UNDERSTAND_MODULES: UNDERSTAND_MODULES,
    UNDERSTAND_CARAT_CONTENT: UNDERSTAND_CARAT_CONTENT,
    UNDERSTAND_CARAT_INTRO: UNDERSTAND_CARAT_INTRO,
    UNDERSTAND_CARAT_EXPLAIN: UNDERSTAND_CARAT_EXPLAIN,
    UNDERSTAND_CARAT_MATCH_DECIMALS: UNDERSTAND_CARAT_MATCH_DECIMALS,
    UNDERSTAND_CARAT_SLIDER_MIN: UNDERSTAND_CARAT_SLIDER_MIN,
    UNDERSTAND_CARAT_SLIDER_BASE_MAX: UNDERSTAND_CARAT_SLIDER_BASE_MAX,
    UNDERSTAND_CARAT_SLIDER_STEP: UNDERSTAND_CARAT_SLIDER_STEP,
    UNDERSTAND_CARAT_SLIDER_CHECKPOINTS: UNDERSTAND_CARAT_SLIDER_CHECKPOINTS,
    UNDERSTAND_CARAT_HANDLE_SIZE_PX: UNDERSTAND_CARAT_HANDLE_SIZE_PX,
    UNDERSTAND_CARAT_TRAVEL_INSET_PX: UNDERSTAND_CARAT_TRAVEL_INSET_PX,
    UNDERSTAND_CUT_CONTENT: UNDERSTAND_CUT_CONTENT,
    UNDERSTAND_CUT_GRADES: UNDERSTAND_CUT_GRADES,
    UNDERSTAND_CUT_GRADE_IDS: UNDERSTAND_CUT_GRADE_IDS,
    UNDERSTAND_CUT_EDU_DEFAULT: UNDERSTAND_CUT_EDU_DEFAULT,
    UNDERSTAND_CUT_HANDLE_SIZE_PX: UNDERSTAND_CUT_HANDLE_SIZE_PX,
    UNDERSTAND_CUT_TRAVEL_INSET_PX: UNDERSTAND_CUT_TRAVEL_INSET_PX,
    UNDERSTAND_COLOR_CONTENT: UNDERSTAND_COLOR_CONTENT,
    UNDERSTAND_COLOR_GRADES: UNDERSTAND_COLOR_GRADES,
    UNDERSTAND_COLOR_GRADE_IDS: UNDERSTAND_COLOR_GRADE_IDS,
    UNDERSTAND_COLOR_EDU_DEFAULT: UNDERSTAND_COLOR_EDU_DEFAULT,
    UNDERSTAND_COLOR_HANDLE_SIZE_PX: UNDERSTAND_COLOR_HANDLE_SIZE_PX,
    UNDERSTAND_COLOR_TRAVEL_INSET_PX: UNDERSTAND_COLOR_TRAVEL_INSET_PX,
    UNDERSTAND_COLOR_STRIP_MOBILE_MAX_PX: UNDERSTAND_COLOR_STRIP_MOBILE_MAX_PX,
    UNDERSTAND_CLARITY_CONTENT: UNDERSTAND_CLARITY_CONTENT,
    UNDERSTAND_CLARITY_GRADES: UNDERSTAND_CLARITY_GRADES,
    UNDERSTAND_CLARITY_GRADE_IDS: UNDERSTAND_CLARITY_GRADE_IDS,
    UNDERSTAND_CLARITY_EDU_DEFAULT: UNDERSTAND_CLARITY_EDU_DEFAULT,
    UNDERSTAND_CLARITY_HANDLE_SIZE_PX: UNDERSTAND_CLARITY_HANDLE_SIZE_PX,
    UNDERSTAND_CLARITY_TRAVEL_INSET_PX: UNDERSTAND_CLARITY_TRAVEL_INSET_PX,
    understandCaratContent: understandCaratContent,
    isHorizontalSizeTone: isHorizontalSizeTone,
    understandCaratKey: understandCaratKey,
    isSelectedInventoryCarat: isSelectedInventoryCarat,
    understandCaratSliderCeiling: understandCaratSliderCeiling,
    understandCaratSliderCheckpoints: understandCaratSliderCheckpoints,
    understandCaratSliderTicks: understandCaratSliderTicks,
    snapUnderstandCaratSlider: snapUnderstandCaratSlider,
    clampUnderstandCaratBubbleLayout: clampUnderstandCaratBubbleLayout,
    resolveUnderstandCaratModel: resolveUnderstandCaratModel,
    resolveUnderstandCaratTypicalSize: resolveUnderstandCaratTypicalSize,
    understandCaratThumbCenterPx: understandCaratThumbCenterPx,
    understandCaratVisualTrackPos: understandCaratVisualTrackPos,
    understandCaratToTrackPos: understandCaratToTrackPos,
    understandCaratFromTrackPos: understandCaratFromTrackPos,
    understandCaratPosFromClientX: understandCaratPosFromClientX,
    applyUnderstandCaratValue: applyUnderstandCaratValue,
    renderUnderstandYourDiamond: renderUnderstandYourDiamond,
    renderCompareHost: renderCompareHost,
    beginCompare: beginCompare,
    abortCompare: abortCompare,
    renderUnderstandCarat: renderUnderstandCarat,
    isPopulatedUnderstandModule: isPopulatedUnderstandModule,
    understandCutContent: understandCutContent,
    understandCutGrades: understandCutGrades,
    understandCutGradeIndex: understandCutGradeIndex,
    understandCutGradeById: understandCutGradeById,
    resolveUnderstandCutInventoryGrade: resolveUnderstandCutInventoryGrade,
    understandCutVisualUrl: understandCutVisualUrl,
    understandCutToTrackPos: understandCutToTrackPos,
    understandCutFromTrackPos: understandCutFromTrackPos,
    understandCutThumbCenterPx: understandCutThumbCenterPx,
    understandCutVisualTrackPos: understandCutVisualTrackPos,
    understandCutPosFromClientX: understandCutPosFromClientX,
    stepUnderstandCut: stepUnderstandCut,
    resolveUnderstandCutModel: resolveUnderstandCutModel,
    applyUnderstandCutValue: applyUnderstandCutValue,
    renderUnderstandCut: renderUnderstandCut,
    specEducationCutState: specEducationCutState,
    specEducationColorState: specEducationColorState,
    understandColorContent: understandColorContent,
    understandColorGrades: understandColorGrades,
    understandColorGradeIndex: understandColorGradeIndex,
    understandColorGradeById: understandColorGradeById,
    resolveUnderstandColorInventoryGrade: resolveUnderstandColorInventoryGrade,
    understandColorVisualUrl: understandColorVisualUrl,
    understandColorToTrackPos: understandColorToTrackPos,
    understandColorFromTrackPos: understandColorFromTrackPos,
    understandColorThumbCenterPx: understandColorThumbCenterPx,
    understandColorVisualTrackPos: understandColorVisualTrackPos,
    understandColorPosFromClientX: understandColorPosFromClientX,
    stepUnderstandColor: stepUnderstandColor,
    resolveUnderstandColorModel: resolveUnderstandColorModel,
    applyUnderstandColorValue: applyUnderstandColorValue,
    understandColorStripIsMobileViewport: understandColorStripIsMobileViewport,
    understandColorStripRailNeedsFollow: understandColorStripRailNeedsFollow,
    understandColorStripFollowScrollLeft: understandColorStripFollowScrollLeft,
    understandColorStripItemIsFullyVisible: understandColorStripItemIsFullyVisible,
    applyUnderstandColorStripFollow: applyUnderstandColorStripFollow,
    followUnderstandColorStripRail: followUnderstandColorStripRail,
    renderUnderstandColor: renderUnderstandColor,
    specEducationClarityState: specEducationClarityState,
    understandClarityContent: understandClarityContent,
    understandClarityGrades: understandClarityGrades,
    understandClarityGradeIndex: understandClarityGradeIndex,
    understandClarityGradeById: understandClarityGradeById,
    resolveUnderstandClarityInventoryGrade: resolveUnderstandClarityInventoryGrade,
    understandClarityDiamondUrl: understandClarityDiamondUrl,
    understandClarityLoupeUrl: understandClarityLoupeUrl,
    understandClarityDiamondKey: understandClarityDiamondKey,
    understandClarityLoupeKey: understandClarityLoupeKey,
    understandClarityToTrackPos: understandClarityToTrackPos,
    understandClarityFromTrackPos: understandClarityFromTrackPos,
    understandClarityThumbCenterPx: understandClarityThumbCenterPx,
    understandClarityVisualTrackPos: understandClarityVisualTrackPos,
    understandClarityPosFromClientX: understandClarityPosFromClientX,
    stepUnderstandClarity: stepUnderstandClarity,
    resolveUnderstandClarityModel: resolveUnderstandClarityModel,
    applyUnderstandClarityValue: applyUnderstandClarityValue,
    renderUnderstandClarity: renderUnderstandClarity,
    renderGatewayBanner: renderGatewayBanner,
    resolveGatewayShape: resolveGatewayShape,
    gatewayMasterUrl: gatewayMasterUrl,
    gatewayRoundUrl: gatewayRoundUrl,
    gatewayOvalUrl: gatewayOvalUrl,
    gatewayCushionUrl: gatewayCushionUrl,
    gatewayPrincessUrl: gatewayPrincessUrl,
    gatewayEmeraldUrl: gatewayEmeraldUrl,
    gatewayRadiantUrl: gatewayRadiantUrl,
    gatewayPearUrl: gatewayPearUrl,
    gatewayHeartUrl: gatewayHeartUrl,
    gatewayAsscherUrl: gatewayAsscherUrl,
    gatewayMarquiseUrl: gatewayMarquiseUrl,
    gatewayEnvironmentUrl: gatewayEnvironmentUrl,
    formatGatewayDisplay: formatGatewayDisplay,
    sizeShapeIsMaster: sizeShapeIsMaster,
    sizeVisibleRatio: sizeVisibleRatio,
    diamondViewCapabilities: diamondViewCapabilities,
    resolveProportions: resolveProportions,
    resolveProportionsShape: resolveProportionsShape,
    usesRoundTopMaster: usesRoundTopMaster,
    usesOvalTopMaster: usesOvalTopMaster,
    usesPearTopMaster: usesPearTopMaster,
    usesMarquiseTopMaster: usesMarquiseTopMaster,
    usesCushionTopMaster: usesCushionTopMaster,
    usesPrincessTopMaster: usesPrincessTopMaster,
    usesRadiantTopMaster: usesRadiantTopMaster,
    usesHeartTopMaster: usesHeartTopMaster,
    usesEmeraldTopMaster: usesEmeraldTopMaster,
    usesAsscherTopMaster: usesAsscherTopMaster,
    usesBrilliantSideMaster: usesBrilliantSideMaster,
    usesFancySideMaster: usesFancySideMaster,
    usesStepCutSideMaster: usesStepCutSideMaster,
    sideProfileMasterKind: sideProfileMasterKind,
    topViewMasterKind: topViewMasterKind,
    proportionsRoundTopMasterSvg: proportionsRoundTopMasterSvg,
    proportionsOvalTopMasterSvg: proportionsOvalTopMasterSvg,
    proportionsPearTopMasterSvg: proportionsPearTopMasterSvg,
    proportionsMarquiseTopMasterSvg: proportionsMarquiseTopMasterSvg,
    proportionsCushionTopMasterSvg: proportionsCushionTopMasterSvg,
    proportionsPrincessTopMasterSvg: proportionsPrincessTopMasterSvg,
    proportionsRadiantTopMasterSvg: proportionsRadiantTopMasterSvg,
    proportionsHeartTopMasterSvg: proportionsHeartTopMasterSvg,
    proportionsEmeraldTopMasterSvg: proportionsEmeraldTopMasterSvg,
    proportionsAsscherTopMasterSvg: proportionsAsscherTopMasterSvg,
    proportionsStepCutSideMasterSvg: proportionsStepCutSideMasterSvg,
    ROUND_TOP_VIEW_MASTER_ASSET: ROUND_TOP_VIEW_MASTER_ASSET,
    ROUND_TOP_VIEW_MASTER_VB_W: ROUND_TOP_VIEW_MASTER_VB_W,
    ROUND_TOP_VIEW_MASTER_VB_H: ROUND_TOP_VIEW_MASTER_VB_H,
    OVAL_TOP_VIEW_MASTER_ASSET: OVAL_TOP_VIEW_MASTER_ASSET,
    OVAL_TOP_VIEW_MASTER_VB_W: OVAL_TOP_VIEW_MASTER_VB_W,
    OVAL_TOP_VIEW_MASTER_VB_H: OVAL_TOP_VIEW_MASTER_VB_H,
    OVAL_TOP_VISIBLE_BBOX: OVAL_TOP_VISIBLE_BBOX,
    OVAL_TOP_ZONES: OVAL_TOP_ZONES,
    TECH_MASTER_OVAL_TOP: TECH_MASTER_OVAL_TOP,
    PEAR_TOP_VIEW_MASTER_ASSET: PEAR_TOP_VIEW_MASTER_ASSET,
    PEAR_TOP_VIEW_MASTER_VB_W: PEAR_TOP_VIEW_MASTER_VB_W,
    PEAR_TOP_VIEW_MASTER_VB_H: PEAR_TOP_VIEW_MASTER_VB_H,
    PEAR_TOP_VISIBLE_BBOX: PEAR_TOP_VISIBLE_BBOX,
    PEAR_TOP_ZONES: PEAR_TOP_ZONES,
    TECH_MASTER_PEAR_TOP: TECH_MASTER_PEAR_TOP,
    MARQUISE_TOP_VIEW_MASTER_ASSET: MARQUISE_TOP_VIEW_MASTER_ASSET,
    MARQUISE_TOP_VIEW_MASTER_VB_W: MARQUISE_TOP_VIEW_MASTER_VB_W,
    MARQUISE_TOP_VIEW_MASTER_VB_H: MARQUISE_TOP_VIEW_MASTER_VB_H,
    MARQUISE_TOP_VISIBLE_BBOX: MARQUISE_TOP_VISIBLE_BBOX,
    MARQUISE_TOP_ZONES: MARQUISE_TOP_ZONES,
    MARQUISE_TOP_LENGTH_GAP_PX: MARQUISE_TOP_LENGTH_GAP_PX,
    MARQUISE_TOP_WIDTH_GAP_PX: MARQUISE_TOP_WIDTH_GAP_PX,
    MARQUISE_TOP_INFO_GAP_PX: MARQUISE_TOP_INFO_GAP_PX,
    TECH_MASTER_MARQUISE_TOP: TECH_MASTER_MARQUISE_TOP,
    CUSHION_TOP_VIEW_MASTER_ASSET: CUSHION_TOP_VIEW_MASTER_ASSET,
    CUSHION_TOP_VIEW_MASTER_VB_W: CUSHION_TOP_VIEW_MASTER_VB_W,
    CUSHION_TOP_VIEW_MASTER_VB_H: CUSHION_TOP_VIEW_MASTER_VB_H,
    CUSHION_TOP_VISIBLE_BBOX: CUSHION_TOP_VISIBLE_BBOX,
    CUSHION_TOP_ZONES: CUSHION_TOP_ZONES,
    CUSHION_TOP_WIDTH_GAP_PX: CUSHION_TOP_WIDTH_GAP_PX,
    CUSHION_TOP_LENGTH_GAP_PX: CUSHION_TOP_LENGTH_GAP_PX,
    CUSHION_TOP_INFO_GAP_PX: CUSHION_TOP_INFO_GAP_PX,
    TECH_MASTER_CUSHION_TOP: TECH_MASTER_CUSHION_TOP,
    PRINCESS_TOP_VIEW_MASTER_ASSET: PRINCESS_TOP_VIEW_MASTER_ASSET,
    PRINCESS_TOP_VIEW_MASTER_VB_W: PRINCESS_TOP_VIEW_MASTER_VB_W,
    PRINCESS_TOP_VIEW_MASTER_VB_H: PRINCESS_TOP_VIEW_MASTER_VB_H,
    PRINCESS_TOP_VISIBLE_BBOX: PRINCESS_TOP_VISIBLE_BBOX,
    PRINCESS_TOP_ZONES: PRINCESS_TOP_ZONES,
    PRINCESS_TOP_WIDTH_GAP_PX: PRINCESS_TOP_WIDTH_GAP_PX,
    PRINCESS_TOP_LENGTH_GAP_PX: PRINCESS_TOP_LENGTH_GAP_PX,
    PRINCESS_TOP_INFO_GAP_PX: PRINCESS_TOP_INFO_GAP_PX,
    TECH_MASTER_PRINCESS_TOP: TECH_MASTER_PRINCESS_TOP,
    RADIANT_TOP_VIEW_MASTER_ASSET: RADIANT_TOP_VIEW_MASTER_ASSET,
    RADIANT_TOP_VIEW_MASTER_VB_W: RADIANT_TOP_VIEW_MASTER_VB_W,
    RADIANT_TOP_VIEW_MASTER_VB_H: RADIANT_TOP_VIEW_MASTER_VB_H,
    RADIANT_TOP_VISIBLE_BBOX: RADIANT_TOP_VISIBLE_BBOX,
    RADIANT_TOP_ZONES: RADIANT_TOP_ZONES,
    RADIANT_TOP_WIDTH_GAP_PX: RADIANT_TOP_WIDTH_GAP_PX,
    RADIANT_TOP_LENGTH_GAP_PX: RADIANT_TOP_LENGTH_GAP_PX,
    RADIANT_TOP_INFO_GAP_PX: RADIANT_TOP_INFO_GAP_PX,
    TECH_MASTER_RADIANT_TOP: TECH_MASTER_RADIANT_TOP,
    HEART_TOP_VIEW_MASTER_ASSET: HEART_TOP_VIEW_MASTER_ASSET,
    HEART_TOP_VIEW_MASTER_VB_W: HEART_TOP_VIEW_MASTER_VB_W,
    HEART_TOP_VIEW_MASTER_VB_H: HEART_TOP_VIEW_MASTER_VB_H,
    HEART_TOP_VISIBLE_BBOX: HEART_TOP_VISIBLE_BBOX,
    HEART_TOP_ZONES: HEART_TOP_ZONES,
    HEART_TOP_WIDTH_GAP_PX: HEART_TOP_WIDTH_GAP_PX,
    HEART_TOP_LENGTH_GAP_PX: HEART_TOP_LENGTH_GAP_PX,
    HEART_TOP_INFO_GAP_PX: HEART_TOP_INFO_GAP_PX,
    TECH_MASTER_HEART_TOP: TECH_MASTER_HEART_TOP,
    EMERALD_TOP_VIEW_MASTER_ASSET: EMERALD_TOP_VIEW_MASTER_ASSET,
    EMERALD_TOP_VIEW_MASTER_VB_W: EMERALD_TOP_VIEW_MASTER_VB_W,
    EMERALD_TOP_VIEW_MASTER_VB_H: EMERALD_TOP_VIEW_MASTER_VB_H,
    EMERALD_TOP_VISIBLE_BBOX: EMERALD_TOP_VISIBLE_BBOX,
    EMERALD_TOP_ZONES: EMERALD_TOP_ZONES,
    EMERALD_TOP_WIDTH_GAP_PX: EMERALD_TOP_WIDTH_GAP_PX,
    EMERALD_TOP_LENGTH_GAP_PX: EMERALD_TOP_LENGTH_GAP_PX,
    EMERALD_TOP_INFO_GAP_PX: EMERALD_TOP_INFO_GAP_PX,
    TECH_MASTER_EMERALD_TOP: TECH_MASTER_EMERALD_TOP,
    ASSCHER_TOP_VIEW_MASTER_ASSET: ASSCHER_TOP_VIEW_MASTER_ASSET,
    ASSCHER_TOP_VIEW_MASTER_VB_W: ASSCHER_TOP_VIEW_MASTER_VB_W,
    ASSCHER_TOP_VIEW_MASTER_VB_H: ASSCHER_TOP_VIEW_MASTER_VB_H,
    ASSCHER_TOP_VISIBLE_BBOX: ASSCHER_TOP_VISIBLE_BBOX,
    ASSCHER_TOP_ZONES: ASSCHER_TOP_ZONES,
    ASSCHER_TOP_WIDTH_GAP_PX: ASSCHER_TOP_WIDTH_GAP_PX,
    ASSCHER_TOP_LENGTH_GAP_PX: ASSCHER_TOP_LENGTH_GAP_PX,
    ASSCHER_TOP_INFO_GAP_PX: ASSCHER_TOP_INFO_GAP_PX,
    TECH_MASTER_ASSCHER_TOP: TECH_MASTER_ASSCHER_TOP,
    FANCY_SIDE_PROFILE_MASTER_ASSET: FANCY_SIDE_PROFILE_MASTER_ASSET,
    FANCY_SIDE_PROFILE_MASTER_VB_W: FANCY_SIDE_PROFILE_MASTER_VB_W,
    FANCY_SIDE_PROFILE_MASTER_VB_H: FANCY_SIDE_PROFILE_MASTER_VB_H,
    FANCY_SIDE_ZONES: FANCY_SIDE_ZONES,
    FANCY_SIDE_LEFT_SAFE_PX: FANCY_SIDE_LEFT_SAFE_PX,
    TECH_MASTER_FANCY_SIDE: TECH_MASTER_FANCY_SIDE,
    fancySideProfileMasterUrl: fancySideProfileMasterUrl,
    proportionsFancySideMasterSvg: proportionsFancySideMasterSvg,
    STEP_CUT_SIDE_PROFILE_MASTER_ASSET: STEP_CUT_SIDE_PROFILE_MASTER_ASSET,
    STEP_CUT_SIDE_PROFILE_MASTER_VB_W: STEP_CUT_SIDE_PROFILE_MASTER_VB_W,
    STEP_CUT_SIDE_PROFILE_MASTER_VB_H: STEP_CUT_SIDE_PROFILE_MASTER_VB_H,
    STEP_CUT_SIDE_ZONES: STEP_CUT_SIDE_ZONES,
    STEP_CUT_SIDE_LEFT_SAFE_PX: STEP_CUT_SIDE_LEFT_SAFE_PX,
    TECH_MASTER_STEP_CUT_SIDE: TECH_MASTER_STEP_CUT_SIDE,
    stepCutSideProfileMasterUrl: stepCutSideProfileMasterUrl,
    princessTopViewMasterUrl: princessTopViewMasterUrl,
    radiantTopViewMasterUrl: radiantTopViewMasterUrl,
    heartTopViewMasterUrl: heartTopViewMasterUrl,
    emeraldTopViewMasterUrl: emeraldTopViewMasterUrl,
    asscherTopViewMasterUrl: asscherTopViewMasterUrl,
    cushionTopViewMasterUrl: cushionTopViewMasterUrl,
    roundTopViewMasterUrl: roundTopViewMasterUrl,
    ovalTopViewMasterUrl: ovalTopViewMasterUrl,
    pearTopViewMasterUrl: pearTopViewMasterUrl,
    marquiseTopViewMasterUrl: marquiseTopViewMasterUrl,
    proportionsBrilliantSideMasterSvg: proportionsBrilliantSideMasterSvg,
    BRILLIANT_SIDE_PROFILE_MASTER_ASSET: BRILLIANT_SIDE_PROFILE_MASTER_ASSET,
    BRILLIANT_SIDE_PROFILE_MASTER_VB_W: BRILLIANT_SIDE_PROFILE_MASTER_VB_W,
    BRILLIANT_SIDE_PROFILE_MASTER_VB_H: BRILLIANT_SIDE_PROFILE_MASTER_VB_H,
    BRILLIANT_SIDE_ZONES: BRILLIANT_SIDE_ZONES,
    brilliantSideProfileMasterUrl: brilliantSideProfileMasterUrl,
    TECH_STROKE_OUTLINE: TECH_STROKE_OUTLINE,
    TECH_STROKE_FACET: TECH_STROKE_FACET,
    TECH_STROKE_DIM: TECH_STROKE_DIM,
    TECH_STROKE_TICK: TECH_STROKE_TICK,
    TECH_MASTER_ROUND_TOP: TECH_MASTER_ROUND_TOP,
    TECH_MASTER_BRILLIANT_SIDE: TECH_MASTER_BRILLIANT_SIDE,
    proportionsTopPath: proportionsTopPath,
    PROPORTIONS_TABLE_PCT_MIN: PROPORTIONS_TABLE_PCT_MIN,
    PROPORTIONS_TABLE_PCT_MAX: PROPORTIONS_TABLE_PCT_MAX,
    PROPORTIONS_DEPTH_PCT_MIN: PROPORTIONS_DEPTH_PCT_MIN,
    PROPORTIONS_DEPTH_PCT_MAX: PROPORTIONS_DEPTH_PCT_MAX,
    toFrameDiamond: toFrameDiamond,
    toEngineV360: toEngineV360,
    renderMediaModeContent: renderMediaModeContent,
    applyDiamondSubview: applyDiamondSubview,
    applyMediaMode: applyMediaMode,
    applyMediaAspect: applyMediaAspect,
    ensureFrameHostPaintBox: ensureFrameHostPaintBox,
    fitMediaBox: fitMediaBox,
    MEDIA_SAFE_AREA: MEDIA_SAFE_AREA,
    MEDIA_FILL: MEDIA_FILL,
    FRAME_HOST_MIN_PX: FRAME_HOST_MIN_PX,
  };

  if (window.document.readyState === "loading") {
    window.document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})(window);
